import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import gifshot from 'gifshot';
import html2canvas from 'html2canvas';
import LouderHubView from './LouderHubView';
import MetaAccessView from './MetaAccessView';
import PublicArtistOnboardingView from './PublicArtistOnboardingView';
import LouderAppSwitcher from './LouderAppSwitcher';

const BACKEND_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5001'
  : ''; 

// --- AMBIENT DEEP PURPLE + GOLDEN AMBER DESIGN SYSTEM ---
// --- AMBIENT FROSTY GLASS + GOLDEN AMBER DESIGN SYSTEM ---
const BRAND_BASE_DARK = '#f8f7fa';     // Warm ivory/alabaster base canvas
const BRAND_PLUM_SIDEBAR = '#f1f0f5';  // Recessed lavender-grey sidebar panels
const BRAND_VIOLET_CARD = '#ffffff';   // Crisp white raised layout containers
const BRAND_AMBER_GOLD = '#d97706';    // Deep warm copper-amber gold highlights
const BRAND_GOLD_GLOW = '0 4px 20px rgba(217, 119, 6, 0.15)'; // Premium light shadow glow

const formatFileSize = (bytes) => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return 'Unknown size';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const studioPanelCard = {
  backgroundColor: BRAND_VIOLET_CARD,
  border: '1px solid rgba(168, 85, 247, 0.12)',
  borderRadius: '24px',
  padding: '24px',
  marginBottom: '20px',
  boxShadow: '0 12px 40px -10px rgba(15, 9, 28, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
};

const studioBlockButtonStyle = {
  backgroundColor: 'rgba(15, 9, 28, 0.02)',
  color: '#1f2937',
  border: '1px solid rgba(15, 9, 28, 0.06)',
  padding: '14px 16px',
  borderRadius: '16px',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '13px',
  letterSpacing: '0.02em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
};

// ==========================================
// REUSABLE GREYSCALE VECTOR ICON LIBRARY
// ==========================================
const Icon = ({ name, size = 14, style = {}, className = "" }) => {
  const icons = {
    warning: <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />,
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </>
    ),
    tools: <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />,
    lab: <path d="M6 3h12M12 3v13M9 16l-4 5h14l-4-5M9 16h6" />,
    plus: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
    folder: <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />,
    save: (
      <>
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
      </>
    ),
    edit: <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />,
    trash: (
      <>
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
        <line x1="10" y1="11" x2="10" y2="17" />
        <line x1="14" y1="11" x2="14" y2="17" />
      </>
    ),
    copy: (
      <>
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
      </>
    ),
    sparkles: <path d="M9.01 14L2 12l7.01-2L11 3l1.99 7 7.01 2-7.01 2L11 21l-1.99-7zM19 6l-1.01-2L17 2l-.99 2L15 6l1.01 2L17 10l.99-2L19 6z" />,
    search: (
      <>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </>
    ),
    link: (
      <>
        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
      </>
    ),
    upload: (
      <>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2V15" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </>
    ),
    download: (
      <>
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2V15" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </>
    ),
    desktop: (
      <>
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </>
    ),
    tablet: (
      <>
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </>
    ),
    mobile: (
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="19" x2="12.01" y2="19" />
      </>
    ),
    moon: <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />,
    sun: (
      <>
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </>
    ),
    image: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </>
    ),
    stack: (
      <>
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polygon points="2 17 12 22 22 17" />
        <polygon points="2 12 12 17 22 12" />
      </>
    ),
    "arrow-horizontal": (
      <>
        <polyline points="20 8 24 12 20 16" />
        <polyline points="4 16 0 12 4 8" />
        <line x1="0" y1="12" x2="24" y2="12" />
      </>
    ),
    "arrow-left": <polyline points="15 18 9 12 15 6" />,
    "arrow-right": <polyline points="9 18 15 12 9 6" />,
    "arrow-down": <polyline points="6 9 12 15 18 9" />,
    button: (
      <>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    line: <line x1="5" y1="12" x2="19" y2="12" />,
    video: (
      <>
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </>
    ),
    loading: <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 11-.57-8.38l5.67-5.67" />,
    lock: (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0110 0v4" />
      </>
    ),
    key: <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.778-7.778zM12 8.5V12h3.5m0 0l2-2M15.5 12h2" />,
    brain: <path d="M9.5 2A2.5 2.5 0 0112 4.5v15a2.5 2.5 0 01-4.96-.44 2.5 2.5 0 010-3.12 3 3 0 010-5.88A2.5 2.5 0 019.5 2zM14.5 2A2.5 2.5 0 0012 4.5v15a2.5 2.5 0 004.96-.44 2.5 2.5 0 000-3.12 3 3 0 000-5.88A2.5 2.5 0 0014.5 2z" />,
    crown: <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" />,
    send: (
      <>
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </>
    ),
    brick: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M3 15h18M9 9v12M15 3v6M15 15v6" />
      </>
    ),
    box: (
      <>
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </>
    ),
    ticket: (
      <>
        <path d="M2 9a3 3 0 010 6v2a2 2 0 002 2h16a2 2 0 002-2v-2a3 3 0 010-6V7a2 2 0 00-2-2H4a2 2 0 00-2 2v2z" />
        <line x1="9" y1="9" x2="9" y2="15" />
        <line x1="15" y1="9" x2="15" y2="15" />
      </>
    ),
    rocket: (
      <>
        <path d="M4.5 16.5c-1.5 1.25-2.5 3.5-2.5 3.5s2.25-1 3.5-2.5M12 5l-8 8V20h7l8-8zM9 15l6-6M11.5 7.5L16.5 12.5M16 3l5 5" />
      </>
    ),
    siren: (
      <>
        <path d="M12 2a5 5 0 00-5 5v3H5a2 2 0 00-2 2v6a2 2 0 002 2h14a2 2 0 002-2v-6a2 2 0 00-2-2h-2V7a5 5 0 00-5-5z" />
        <path d="M12 7v3M9 17h6" />
      </>
    ),
    flame: <path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 11-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 002.5 3.5z" />,
    target: (
      <>
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </>
    ),
    gift: (
      <>
        <rect x="3" y="8" width="18" height="12" rx="2" />
        <line x1="12" y1="22" x2="12" y2="8" />
        <path d="M12 8H7.5a2.5 2.5 0 010-5C11 3 12 8 12 8z" />
        <path d="M12 8h4.5a2.5 2.5 0 000-5C13 3 12 8 12 8z" />
        <line x1="3" y1="12" x2="21" y2="12" />
      </>
    ),
    handshake: <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M20 8v6M17 11h6M9 7a4 4 0 11-8 0 4 4 0 018 0z" />,
    palette: (
      <>
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 14.7255 3.09032 17.1962 4.85857 19C5.35857 19.5 5.35857 20 4.85857 20.5C4.35857 21 3.5 21 2.5 20C2 21 2 22 3.5 22H12Z" />
        <circle cx="7.5" cy="10.5" r="1.5" />
        <circle cx="11.5" cy="7.5" r="1.5" />
        <circle cx="16.5" cy="9.5" r="1.5" />
        <circle cx="15.5" cy="14.5" r="1.5" />
      </>
    ),
    users: (
      <>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </>
    ),
    info: (
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </>
    ),
    user: (
      <>
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </>
    ),
    square: <rect x="3" y="3" width="18" height="18" rx="2" />,
    circle: <circle cx="12" cy="12" r="10" />,
    triangle: <polygon points="12 3 22 21 2 21" />,
    star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
    scissors: (
      <>
        <circle cx="6" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <line x1="9.8" y1="8.2" x2="20" y2="17" />
        <line x1="9.8" y1="15.8" x2="20" y2="7" />
      </>
    ),
    zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
    check: <polyline points="20 6 9 17 4 12" />,
    text: (
      <>
        <polyline points="4 7 4 4 20 4 20 7" />
        <line x1="9" y1="20" x2="15" y2="20" />
        <line x1="12" y1="4" x2="12" y2="20" />
      </>
    ),
    header: <path d="M6 4v16M18 4v16M6 12h12" />,
    code: (
      <>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </>
    )
  };

  const path = icons[name];
  if (!path) return null;

  return (
    <svg
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        display: "inline-flex",
        alignSelf: "center",
        verticalAlign: "middle",
        flexShrink: 0,
        ...style
      }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
};

const studioInputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: '16px',
  border: '1px solid rgba(168, 85, 247, 0.18)',
  backgroundColor: '#ffffff',
  color: '#0f091c',
  fontSize: '14px',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  outline: 'none',
  boxSizing: 'border-box'
};

const studioLabelStyle = {
  display: 'block',
  marginBottom: '10px',
  fontSize: '11px',
  color: '#5b6275',
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const formInputStyle = { 
  width: '100%', 
  padding: '14px 16px', 
  borderRadius: '16px', 
  border: '1px solid rgba(168, 85, 247, 0.18)', 
  backgroundColor: '#ffffff', 
  color: '#0f091c', 
  boxSizing: 'border-box',
  outline: 'none'
};

function getAuthUser() {
  const session = localStorage.getItem('louder_session');
  return session ? JSON.parse(session) : null;
}

function ProtectedRoute({ children }) {
  const user = getAuthUser();
  return user ? children : <Navigate to="/login" replace />;
}

// ==========================================
// REUSABLE MICRO-UTILITY: COPY TO CLIPBOARD BUTTON
// ==========================================
const copyTextToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (successful) {
        return Promise.resolve();
      } else {
        return Promise.reject(new Error('Failed to copy text using fallback.'));
      }
    } catch (err) {
      document.body.removeChild(textarea);
      return Promise.reject(err);
    }
  }
};

function InlineCopyPill({ targetUrl, style = {} }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    copyTextToClipboard(targetUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Fallback copy failed:', err);
      });
  };

  return (
    <div style={{ 
      display: 'inline-flex', 
      alignItems: 'center', 
      gap: '8px', 
      backgroundColor: 'rgba(255, 255, 255, 0.7)', 
      border: '1px solid rgba(168, 85, 247, 0.15)', 
      padding: '6px 12px', 
      borderRadius: '12px',
      maxWidth: '240px',
      ...style 
    }}>
      <span style={{ color: '#5b6275', fontSize: '11px', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {targetUrl}
      </span>
      <button 
        onClick={handleCopy}
        style={{
          backgroundColor: copied ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.1)',
          border: copied ? '1px solid #22c55e' : `1px solid ${BRAND_AMBER_GOLD}`,
          color: copied ? '#22c55e' : BRAND_AMBER_GOLD,
          padding: '4px 8px',
          borderRadius: '8px',
          fontSize: '10px',
          fontWeight: '700',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        {copied ? '✓ COPIED' : 'COPY'}
      </button>
    </div>
  );
}

// ==========================================
// CUSTOM COMPONENT: SEARCHABLE FONT SELECTOR DROPDOWN
// ==========================================
function SearchableFontDropdown({ value, onChange, systemFonts, customFonts, compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredSystem = (systemFonts || []).filter(f => 
    f.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Group custom fonts by family name under a single entry
  const customFontOptions = (customFonts || []).map(f => ({
    displayName: f.family, // e.g. "Inter"
    value: f.family,       // e.g. "Inter"
    family: f.family
  }));

  const filteredCustomOptions = customFontOptions.filter(opt => 
    opt.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (fontFamily) => {
    onChange(fontFamily);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Resolve display name for trigger label (handles both family name and legacy font variation names)
  let displayLabel = value;
  const matchedFamily = (customFonts || []).find(f => f.family.toLowerCase() === value.toLowerCase());
  if (matchedFamily) {
    displayLabel = matchedFamily.family;
  } else {
    let foundVarLabel = null;
    (customFonts || []).forEach(f => {
      (f.variations || []).forEach(v => {
        const urlParts = v.url.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const extIndex = fileName.lastIndexOf('.');
        const fontName = extIndex !== -1 ? fileName.substring(0, extIndex) : fileName;
        if (fontName.toLowerCase() === value.toLowerCase()) {
          foundVarLabel = `${f.family} ${v.name}`;
        }
      });
    });
    if (foundVarLabel) {
      displayLabel = foundVarLabel;
    }
  }

  const triggerStyle = compact ? {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '4px 8px',
    fontSize: '11px',
    height: '28px',
    width: '110px',
    borderRadius: '4px',
    border: '1px solid #c7c7c7',
    backgroundColor: '#ffffff',
    color: '#444746',
    transition: 'all 0.2s',
    userSelect: 'none',
    boxSizing: 'border-box'
  } : {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '12px 16px',
    fontSize: '14px',
    height: '46px',
    width: '100%',
    borderRadius: '16px',
    border: '1px solid rgba(168, 85, 247, 0.18)',
    backgroundColor: '#ffffff',
    color: '#0f091c',
    transition: 'all 0.2s',
    userSelect: 'none',
    boxSizing: 'border-box'
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', width: compact ? '110px' : '100%', display: 'inline-block' }}>
      <div 
        onClick={() => setIsOpen(!isOpen)} 
        style={triggerStyle}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#444746'; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.borderColor = compact ? '#c7c7c7' : 'rgba(168, 85, 247, 0.18)'; }}
      >
        <span style={{ 
          fontFamily: value, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: compact ? '75px' : 'calc(100% - 20px)'
        }}>
          {displayLabel}
        </span>
        <span style={{ fontSize: compact ? '8px' : '10px', color: '#444746' }}>{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '105%',
          left: 0,
          right: 0,
          zIndex: 9999,
          borderRadius: '16px',
          border: '1px solid rgba(168, 85, 247, 0.15)',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 10px 25px -5px rgba(15, 9, 28, 0.15), 0 8px 10px -6px rgba(15, 9, 28, 0.1)',
          padding: '8px',
          width: compact ? '220px' : '100%',
          boxSizing: 'border-box'
        }}>
          <input 
            type="text"
            placeholder="Search fonts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(168, 85, 247, 0.12)',
              backgroundColor: 'rgba(15, 9, 28, 0.02)',
              color: '#0f091c',
              fontSize: '12px',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: '8px'
            }}
            autoFocus
          />
          <div style={{ maxHeight: '200px', overflowY: 'auto', textAlign: 'left' }}>
            {filteredCustomOptions.length > 0 && (
              <>
                <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#a855f7', padding: '4px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Custom Fonts</div>
                {filteredCustomOptions.map(opt => (
                  <div 
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontFamily: opt.value,
                      backgroundColor: value === opt.value ? 'rgba(217, 119, 6, 0.08)' : 'transparent',
                      color: value === opt.value ? BRAND_AMBER_GOLD : '#1f2937',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = value === opt.value ? 'rgba(217, 119, 6, 0.08)' : 'transparent'; }}
                  >
                    {opt.displayName}
                  </div>
                ))}
              </>
            )}

            {filteredSystem.length > 0 && (
              <>
                <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#a855f7', padding: '4px 6px', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>System Fonts</div>
                {filteredSystem.map(f => (
                  <div 
                    key={f}
                    onClick={() => handleSelect(f)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontFamily: f,
                      backgroundColor: value === f ? 'rgba(217, 119, 6, 0.08)' : 'transparent',
                      color: value === f ? BRAND_AMBER_GOLD : '#1f2937',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = value === f ? 'rgba(217, 119, 6, 0.08)' : 'transparent'; }}
                  >
                    {f}
                  </div>
                ))}
              </>
            )}

            {filteredSystem.length === 0 && filteredCustomOptions.length === 0 && (
              <div style={{ padding: '8px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>No fonts found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: WORKSPACE PREVIEW CARD
// ==========================================
function MailerPreviewCard({ tmpl, onEdit }) {
  const [previewHtml, setPreviewHtml] = useState('<div style="color:#6b7280;text-align:center;padding-top:90px;font-size:12px;">Loading Preview...</div>');
  const generatedImportUrl = `${window.location.origin}/view/mailer/${tmpl.id}`;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/templates/${tmpl.id}`)
      .then(res => res.json())
      .then(data => {
        const modules = data.modules || [];
        const globals = data.globalSettings || {};
        if (modules.length === 0) {
          setPreviewHtml('<div style="color:#4b5563;text-align:center;padding-top:90px;font-size:11px;">[ EMPTY CANVAS OVERLAY ]</div>');
          return;
        }

        let masterBg = 'background-color:#000000;';
        if (globals.bgType === 'gradient') {
          masterBg = `background:linear-gradient(${globals.gradientAngle || 180}deg, ${globals.gradientColor1 || '#111'}, ${globals.gradientColor2 || '#000'});`;
        } else if (globals.bgType === 'solid' && globals.bgColor) {
          masterBg = `background-color:${globals.bgColor};`;
        } else if (globals.bgType === 'image' && globals.bgImageUrl) {
          const imgUrl = (globals.bgImageUrl || '').startsWith('http') ? globals.bgImageUrl : `${BACKEND_URL}${globals.bgImageUrl}`;
          if (globals.fallbackBgType === 'gradient') {
            const angle = globals.fallbackBgGradientAngle !== undefined ? globals.fallbackBgGradientAngle : 180;
            const col1 = globals.fallbackBgGradientColor1 || '#111215';
            const col2 = globals.fallbackBgGradientColor2 || '#000000';
            masterBg = `background: url(${imgUrl}) no-repeat center center, linear-gradient(${angle}deg, ${col1} 0%, ${col2} 100%); background-size: cover;`;
          } else {
            const fallbackColor = globals.fallbackBgColor || '#010717';
            masterBg = `background: url(${imgUrl}) no-repeat center center; background-size: cover; background-color: ${fallbackColor};`;
          }
        }

        let layoutMarkup = `<div style="${masterBg} padding:15px; min-height:100%; box-sizing:border-box; font-family:sans-serif; text-transform:uppercase;">`;
        modules.forEach(mod => {
          const fontStyle = mod.font ? `font-family:'${mod.font}';` : '';
          const weight = mod.isBold ? 'font-weight:bold;' : 'font-weight:normal;';
          const style = mod.isItalic ? 'font-style:italic;' : 'font-style:normal;';
          const size = mod.fontSize ? `font-size:${Math.round(mod.fontSize * 0.5)}px;` : ''; 
          const tracking = mod.letterSpacing ? `letter-spacing:${mod.letterSpacing}px;` : '';
          
          let blockBg = '';
          if (!globals.overrideBlockBg) {
            if (mod.blockBgType === 'solid') {
              blockBg = `background-color:${mod.blockBgColor || '#111215'};`;
            } else if (mod.blockBgType === 'gradient') {
              const angle = mod.blockBgGradientAngle !== undefined ? mod.blockBgGradientAngle : 135;
              const c1 = mod.blockBgGradientColor1 || '#667eea';
              const c2 = mod.blockBgGradientColor2 || '#764ba2';
              blockBg = `background:linear-gradient(${angle}deg, ${c1}, ${c2});`;
            }
          }
          const blockStroke = (mod.blockStrokeWidth && mod.blockStrokeWidth > 0)
            ? `border:${mod.blockStrokeWidth}px ${mod.blockStrokeStyle || 'solid'} ${mod.blockStrokeColor || '#000000'};`
            : '';
          const borderRadiusStyle = mod.blockStrokeRadius !== undefined ? `border-radius:${mod.blockStrokeRadius}px;` : '';

          const padTop = mod.paddingTop !== undefined ? mod.paddingTop : (mod.type === 'header' ? 25 : mod.type === 'text' || mod.type === 'button' ? 20 : mod.type === 'divider' ? 10 : 0);
          const padBottom = mod.paddingBottom !== undefined ? mod.paddingBottom : (mod.type === 'header' ? 25 : mod.type === 'text' || mod.type === 'button' ? 20 : mod.type === 'divider' ? 10 : 0);
          const padLeft = mod.paddingLeft !== undefined ? mod.paddingLeft : (mod.type === 'header' ? 25 : mod.type === 'text' || mod.type === 'button' ? 20 : mod.type === 'divider' ? 20 : 0);
          const padRight = mod.paddingRight !== undefined ? mod.paddingRight : (mod.type === 'header' ? 25 : mod.type === 'text' || mod.type === 'button' ? 20 : mod.type === 'divider' ? 20 : 0);
          const paddingStyle = `padding-top:${Math.round(padTop * 0.5)}px; padding-bottom:${Math.round(padBottom * 0.5)}px; padding-left:${Math.round(padLeft * 0.5)}px; padding-right:${Math.round(padRight * 0.5)}px;`;

          const combinedStyles = `${fontStyle} ${weight} ${style} ${size} ${tracking} ${blockBg} ${blockStroke} ${borderRadiusStyle} ${paddingStyle}`;

          if (mod.type === 'header') {
            if (mod.listType === 'bullet' || mod.listType === 'numbered') {
              const tag = mod.listType === 'bullet' ? 'ul' : 'ol';
              const styleType = mod.listType === 'bullet' ? 'disc' : 'decimal';
              const items = (mod.text || '').split('\n').map(line => `<li style="margin-bottom:2px;">${line}</li>`).join('');
              layoutMarkup += `<${tag} style="color:${mod.color || '#fff'}; font-size:11px; text-align:left; margin:0 0 10px 0; padding-left:16px; list-style-type:${styleType}; ${combinedStyles}">${items}</${tag}>`;
            } else {
              layoutMarkup += `<div style="color:${mod.color || '#fff'}; font-size:11px; text-align:center; margin-bottom:10px; white-space:pre-line; ${combinedStyles}">${mod.text}</div>`;
            }
          } else if (mod.type === 'artwork' && mod.url) {
            layoutMarkup += `<div style="margin-bottom:10px; ${blockBg}"><img src="${mod.url}" style="width:100%; height:auto; display:block; border-radius:4px;" /></div>`;
          } else if (mod.type === 'text') {
            if (mod.listType === 'bullet' || mod.listType === 'numbered') {
              const tag = mod.listType === 'bullet' ? 'ul' : 'ol';
              const styleType = mod.listType === 'bullet' ? 'disc' : 'decimal';
              const items = (mod.text || '').split('\n').map(line => `<li style="margin-bottom:2px;">${line}</li>`).join('');
              layoutMarkup += `<${tag} style="color:${mod.color || '#aaa'}; font-size:8px; text-align:left; margin:0 0 10px 0; padding-left:16px; list-style-type:${styleType}; line-height:1.4; ${combinedStyles}">${items}</${tag}>`;
            } else {
              layoutMarkup += `<div style="color:${mod.color || '#aaa'}; font-size:8px; text-align:center; margin-bottom:10px; line-height:1.4; white-space:pre-line; ${combinedStyles}">${mod.text}</div>`;
            }
          } else if (mod.type === 'divider') {
            layoutMarkup += `<div style="margin:12px 0; border-top:${mod.thickness || '1px'} solid ${mod.color || '#333'}; ${blockBg} ${paddingStyle}"></div>`;
          } else if (mod.type === 'button') {
            layoutMarkup += `<div style="text-align:center; margin-bottom:10px; ${blockBg} ${paddingStyle}"><span style="background-color:${mod.bgColor || '#fff'}; color:${mod.textColor || '#000'}; padding:3px 10px; font-size:8px; border-radius:3px; display:inline-block; ${fontStyle} ${weight} ${style} ${size} ${tracking}">${mod.text}</span></div>`;
          } else if (mod.type === 'html') {
            layoutMarkup += `<div style="margin-bottom:10px; ${blockBg}">${mod.htmlContent || ''}</div>`;
          } else if (mod.type === 'columns') {
            const colCount = mod.columns ? mod.columns.length : 1;
            const padTop = mod.paddingTop !== undefined ? Math.round(mod.paddingTop * 0.5) : 5;
            const padBottom = mod.paddingBottom !== undefined ? Math.round(mod.paddingBottom * 0.5) : 5;
            const blockStroke = (mod.blockStrokeWidth && mod.blockStrokeWidth > 0)
              ? `border:${mod.blockStrokeWidth}px ${mod.blockStrokeStyle || 'solid'} ${mod.blockStrokeColor || '#000000'};`
              : '';
            const borderRadiusStyle = mod.blockStrokeRadius !== undefined ? `border-radius:${mod.blockStrokeRadius}px;` : '';
            layoutMarkup += `<div style="padding-top:${padTop}px; padding-bottom:${padBottom}px; display:grid; grid-template-columns:repeat(${colCount}, 1fr); gap:6px; ${blockBg} ${blockStroke} ${borderRadiusStyle} margin-bottom:10px;">`;
            if (mod.columns && mod.columns.length > 0) {
              mod.columns.forEach(col => {
                layoutMarkup += `<div style="padding:2px; display:flex; flex-direction:column; gap:4px; background-color:rgba(0,0,0,0.15); border-radius:4px;">`;
                if (col.blocks && col.blocks.length > 0) {
                  col.blocks.forEach(subMod => {
                    const subFontStyle = subMod.font ? `font-family:'${subMod.font}';` : '';
                    const subWeight = subMod.isBold ? 'font-weight:bold;' : 'font-weight:normal;';
                    const subStyle = subMod.isItalic ? 'font-style:italic;' : 'font-style:normal;';
                    const subSize = subMod.fontSize ? `font-size:${Math.round(subMod.fontSize * 0.5)}px;` : '';
                    const subTracking = subMod.letterSpacing ? `letter-spacing:${subMod.letterSpacing}px;` : '';
                    let subBlockBg = '';
                    if (!globals.overrideBlockBg) {
                      if (subMod.blockBgType === 'solid') {
                        subBlockBg = `background-color:${subMod.blockBgColor || '#111215'};`;
                      } else if (subMod.blockBgType === 'gradient') {
                        const angle = subMod.blockBgGradientAngle !== undefined ? subMod.blockBgGradientAngle : 135;
                        const c1 = subMod.blockBgGradientColor1 || '#667eea';
                        const c2 = subMod.blockBgGradientColor2 || '#764ba2';
                        subBlockBg = `background:linear-gradient(${angle}deg, ${c1}, ${c2});`;
                      }
                    }
                    const subBlockStroke = (subMod.blockStrokeWidth && subMod.blockStrokeWidth > 0)
                      ? `border:${subMod.blockStrokeWidth}px ${subMod.blockStrokeStyle || 'solid'} ${subMod.blockStrokeColor || '#000000'};`
                      : '';
                    const subBorderRadiusStyle = subMod.blockStrokeRadius !== undefined ? `border-radius:${subMod.blockStrokeRadius}px;` : '';

                    const subPadTop = subMod.paddingTop !== undefined ? subMod.paddingTop : (subMod.type === 'header' ? 25 : subMod.type === 'text' || subMod.type === 'button' ? 20 : subMod.type === 'divider' ? 10 : 0);
                    const subPadBottom = subMod.paddingBottom !== undefined ? subMod.paddingBottom : (subMod.type === 'header' ? 25 : subMod.type === 'text' || subMod.type === 'button' ? 20 : subMod.type === 'divider' ? 10 : 0);
                    const subPadLeft = subMod.paddingLeft !== undefined ? subMod.paddingLeft : (subMod.type === 'header' ? 25 : subMod.type === 'text' || subMod.type === 'button' ? 20 : subMod.type === 'divider' ? 20 : 0);
                    const subPadRight = subMod.paddingRight !== undefined ? subMod.paddingRight : (subMod.type === 'header' ? 25 : subMod.type === 'text' || subMod.type === 'button' ? 20 : subMod.type === 'divider' ? 20 : 0);
                    const subPaddingStyle = `padding-top:${Math.round(subPadTop * 0.5)}px; padding-bottom:${Math.round(subPadBottom * 0.5)}px; padding-left:${Math.round(subPadLeft * 0.5)}px; padding-right:${Math.round(subPadRight * 0.5)}px;`;

                    const subCombinedStyles = `${subFontStyle} ${subWeight} ${subStyle} ${subSize} ${subTracking} ${subBlockBg} ${subBlockStroke} ${subBorderRadiusStyle} ${subPaddingStyle}`;

                    if (subMod.type === 'header') {
                      if (subMod.listType === 'bullet' || subMod.listType === 'numbered') {
                        const tag = subMod.listType === 'bullet' ? 'ul' : 'ol';
                        const styleType = subMod.listType === 'bullet' ? 'disc' : 'decimal';
                        const items = (subMod.text || '').split('\n').map(line => `<li style="margin-bottom:2px;">${line}</li>`).join('');
                        layoutMarkup += `<${tag} style="color:${subMod.color || '#fff'}; font-size:11px; text-align:left; margin:0 0 4px 0; padding-left:16px; list-style-type:${styleType}; ${subCombinedStyles}">${items}</${tag}>`;
                      } else {
                        layoutMarkup += `<div style="color:${subMod.color || '#fff'}; font-size:11px; text-align:center; margin-bottom:4px; white-space:pre-line; ${subCombinedStyles}">${subMod.text}</div>`;
                      }
                    } else if (subMod.type === 'artwork' && subMod.url) {
                      layoutMarkup += `<div style="margin-bottom:4px; ${subBlockBg}"><img src="${subMod.url}" style="width:100%; height:auto; display:block; border-radius:4px;" /></div>`;
                    } else if (subMod.type === 'text') {
                      if (subMod.listType === 'bullet' || subMod.listType === 'numbered') {
                        const tag = subMod.listType === 'bullet' ? 'ul' : 'ol';
                        const styleType = subMod.listType === 'bullet' ? 'disc' : 'decimal';
                        const items = (subMod.text || '').split('\n').map(line => `<li style="margin-bottom:2px;">${line}</li>`).join('');
                        layoutMarkup += `<${tag} style="color:${subMod.color || '#fff'}; font-size:11px; text-align:left; margin:0 0 4px 0; padding-left:16px; list-style-type:${styleType}; ${subCombinedStyles}">${items}</${tag}>`;
                      } else {
                        layoutMarkup += `<div style="color:${subMod.color || '#fff'}; font-size:11px; text-align:center; margin-bottom:4px; white-space:pre-line; ${subCombinedStyles}">${subMod.text}</div>`;
                      }
                    } else if (subMod.type === 'divider') {
                      layoutMarkup += `<div style="margin:6px 0; border-top:${subMod.thickness || '1px'} solid ${subMod.color || '#333'}; ${subBlockBg} ${subPaddingStyle}"></div>`;
                    } else if (subMod.type === 'button') {
                      layoutMarkup += `<div style="text-align:center; margin-bottom:4px; ${subBlockBg} ${subPaddingStyle}"><span style="background-color:${subMod.bgColor || '#fff'}; color:${subMod.textColor || '#000'}; padding:3px 10px; font-size:8px; border-radius:3px; display:inline-block; ${subFontStyle} ${subWeight} ${subStyle} ${subSize} ${subTracking}">${subMod.text}</span></div>`;
                    } else if (subMod.type === 'html') {
                      layoutMarkup += `<div style="margin-bottom:4px; ${subBlockBg}">${subMod.htmlContent || ''}</div>`;
                    }
                  });
                }
                layoutMarkup += `</div>`;
              });
            }
            layoutMarkup += `</div>`;
          }
        });
        setPreviewHtml(layoutMarkup + `</div>`);
      })
      .catch(() => setPreviewHtml('<div style="color:#ef4444;text-align:center;padding-top:90px;font-size:11px;">Preview Load Fault</div>'));
  }, [tmpl.id]);

  return (
    <div style={{ height: '220px', backgroundColor: '#000000', position: 'relative', borderBottom: '1px solid #1f2937', overflowY: 'auto', overflowX: 'hidden' }}>
      <div dangerouslySetInnerHTML={{ __html: previewHtml }} style={{ width: '100%', height: '100%' }} />
      <InlineCopyPill targetUrl={generatedImportUrl} style={{ position: 'absolute', bottom: '12px', left: '12px', zIndex: 10, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, zIndex: 5, cursor: 'pointer', background: 'transparent' }} onClick={onEdit} />
    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: LOGIN PANEL VIEW
// ==========================================
function LoginView() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unrecognized credentials.');
        return data;
      })
      .then(data => {
        localStorage.setItem('louder_session', JSON.stringify(data.user));
        navigate('/hub');
      })
      .catch(err => setError(err.message));
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotError('');
    setForgotSuccess('');
    setIsForgotSubmitting(true);

    fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: forgotEmail })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to request password reset.');
        return data;
      })
      .then(data => {
        setForgotSuccess(data.message || 'If this email address exists in our system, a secure password recovery link has been dispatched.');
        setForgotEmail('');
      })
      .catch(err => setForgotError(err.message))
      .finally(() => setIsForgotSubmitting(false));
  };

  if (showForgotPassword) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f7fa', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
        {/* Immersive radial glows in background */}
        <div className="float-slow" style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, transparent 75%)', filter: 'blur(50px)', zIndex: 1 }} />
        <div className="float-slow" style={{ position: 'absolute', bottom: '10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.04) 0%, transparent 75%)', filter: 'blur(60px)', zIndex: 1, animationDelay: '-3s' }} />

        <form onSubmit={handleForgotSubmit} className="glass-panel fade-in-up" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '390px', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
          <div style={{ textAlign: 'center', marginBottom: '35px' }}>
            <h2 style={{ margin: '0 0 8px 0', letterSpacing: '2px', color: '#0f091c', fontSize: '26px', fontWeight: '800' }}>RESET PASSWORD</h2>
            <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '600' }}>Louder Mailer Studio</span>
          </div>

          {forgotError && (
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
              ⚠️ {forgotError}
            </div>
          )}

          {forgotSuccess && (
            <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.25)', color: '#22c55e', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
              ✓ {forgotSuccess}
            </div>
          )}

          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>EMAIL ADDRESS</label>
            <input type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className="glass-input" style={{ width: '100%' }} placeholder="you@loudertools.uk" />
          </div>

          <button type="submit" disabled={isForgotSubmitting} className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '16px', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}>
            {isForgotSubmitting ? 'SENDING RECOVERY LINK...' : 'SEND RECOVERY EMAIL'}
          </button>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <span 
              onClick={() => { setShowForgotPassword(false); setForgotError(''); setForgotSuccess(''); }} 
              style={{ fontSize: '11px', color: '#a855f7', cursor: 'pointer', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.03em' }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND_AMBER_GOLD}
              onMouseLeave={e => e.currentTarget.style.color = '#a855f7'}
            >
              ← Back to login
            </span>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f7fa', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
      {/* Immersive radial glows in background */}
      <div className="float-slow" style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, transparent 75%)', filter: 'blur(50px)', zIndex: 1 }} />
      <div className="float-slow" style={{ position: 'absolute', bottom: '10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.04) 0%, transparent 75%)', filter: 'blur(60px)', zIndex: 1, animationDelay: '-3s' }} />

      <form onSubmit={handleLoginSubmit} className="glass-panel fade-in-up" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '390px', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
        <div style={{ textAlign: 'center', marginBottom: '35px' }}>
          <h2 style={{ margin: '0 0 8px 0', letterSpacing: '2px', color: '#0f091c', fontSize: '26px', fontWeight: '800' }}>LOUDER STUDIO</h2>
          <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '600' }}>Campaign Design Studio</span>
        </div>

        {error && (
          <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>EMAIL ADDRESS</label>
          <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="glass-input" style={{ width: '100%' }} />
        </div>

        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>SYSTEM PASSKEY</label>
            <span 
              onClick={() => setShowForgotPassword(true)} 
              style={{ fontSize: '11px', color: '#a855f7', cursor: 'pointer', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.03em', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND_AMBER_GOLD}
              onMouseLeave={e => e.currentTarget.style.color = '#a855f7'}
            >
              Forgot Password?
            </span>
          </div>
          <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="glass-input" style={{ width: '100%' }} />
        </div>

        <button type="submit" className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '16px', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}>
          ACCESS WORKSPACE
        </button>
      </form>
    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: TEMPLATE DASHBOARD HUB
// ==========================================
function DashboardView() {
  const [templates, setTemplates] = useState([]);
  const navigate = useNavigate();
  const user = getAuthUser();

  const fetchInventory = () => {
    fetch(`${BACKEND_URL}/api/templates`)
      .then(res => res.json())
      .then(data => setTemplates(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  };

  useEffect(() => {
    fetchInventory();
  }, []);

  const executePurge = (id, name, e) => {
    e.stopPropagation();
    if (window.confirm(`⚠️ Permanently purge template layout files for: "${name.toUpperCase()}"?`)) {
      fetch(`${BACKEND_URL}/api/templates/${id}`, { method: 'DELETE' })
        .then(() => fetchInventory());
    }
  };

  const executeDuplicate = (id, name, e) => {
    e.stopPropagation();
    fetch(`${BACKEND_URL}/api/templates/duplicate/${id}`, { method: 'POST' })
      .then(res => {
        if (!res.ok) throw new Error('Server duplication error.');
        return res.json();
      })
      .then(data => {
        if (data.success) {
          fetchInventory();
        } else {
          alert('Failed to duplicate campaign: ' + (data.error || 'Unknown error'));
        }
      })
      .catch(err => {
        console.error(err);
        alert('Error duplicating campaign.');
      });
  };

  return (
    <div style={{ padding: '48px 40px', backgroundColor: '#f8f7fa', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1f2937', position: 'relative', overflow: 'hidden' }}>
      {/* Dynamic ambient vector glows */}
      <div className="float-slow" style={{ position: 'absolute', top: '-10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.04) 0%, transparent 70%)', filter: 'blur(60px)', zIndex: 1 }} />
      <div className="float-slow" style={{ position: 'absolute', bottom: '10%', left: '5%', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.03) 0%, transparent 70%)', filter: 'blur(50px)', zIndex: 1, animationDelay: '-4s' }} />

      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* UPPER MAIN HEADER BAR */}
        <div style={{ display: 'flex', justifycontent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(168, 85, 247, 0.15)', paddingBottom: '24px', marginBottom: '40px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #0f091c 0%, #a855f7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Mailer Campaign Hub</h1>
            <p style={{ margin: '0', fontSize: '13px', color: '#5b6275', fontWeight: '500' }}>Active Promoter: <span style={{ color: BRAND_AMBER_GOLD, fontWeight: '700' }}>{user?.name}</span></p>
          </div>
          <div style={{ display: 'flex', gap: '14px' }}>
            <button onClick={() => navigate('/hub')} style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(15, 9, 28, 0.15)', color: '#1f2937', padding: '10px 20px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              ⚡ Louder Hub
            </button>
            {(user?.role === 'support' || user?.role === 'admin') && (
              <button onClick={() => navigate('/support-dashboard')} style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid #10b981', color: '#10b981', padding: '10px 20px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.1)' }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.05)'; e.currentTarget.style.boxShadow = '0 0 14px rgba(16, 185, 129, 0.15)'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.1)'; }}>
                <Icon name="tools" size={15} /> Support Console
              </button>
            )}
            <button onClick={() => navigate('/element-studio')} style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: `1px solid ${BRAND_AMBER_GOLD}`, color: BRAND_AMBER_GOLD, padding: '10px 20px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.1)' }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(217, 119, 6, 0.05)'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 119, 6, 0.1)'; }}>
              <Icon name="lab" size={15} /> Element Design Studio
            </button>
            <button onClick={() => { localStorage.removeItem('louder_session'); navigate('/login'); }} style={{ background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '10px 20px', borderRadius: '14px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.05)'; }}>
              Logout
            </button>
          </div>
        </div>

        {/* START FROM SCRATCH BANNER SECTION */}
        <div className="glass-panel" style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr', gap: '40px', alignItems: 'center', padding: '40px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.15)', marginBottom: '48px', background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(241, 240, 245, 0.8) 100%)' }}>
          <div>
            <h2 style={{ margin: '0 0 14px 0', fontSize: '22px', fontWeight: '800', letterSpacing: '-0.01em', color: '#0f091c' }}>Start From Scratch</h2>
            <button onClick={() => navigate('/editor/new')} className="pulse-gold" style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', border: 'none', padding: '15px 30px', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.02em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}>
              <span><Icon name="plus" size={13} style={{ marginRight: '6px' }} /> CREATE NEW MAILER</span>
            </button>
          </div>
          <div style={{ borderLeft: '1px solid rgba(168, 85, 247, 0.15)', paddingLeft: '40px', fontSize: '14px', color: '#5b6275', lineHeight: '1.6', fontWeight: '500' }}>
            Configure layout frames, upload brand graphics, and compile flashing promotional GIFs seamlessly using raw local file streams in our advanced GIF Creator panel. Once configured, fetch import-ready responsive HTML snippets instantly.
          </div>
        </div>

        {/* ACTIVE CAMPAIGNS HEADER */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f091c', margin: 0, letterSpacing: '0.5px' }}>Saved Campaigns Registry</h3>
          <span style={{ backgroundColor: 'rgba(168, 85, 247, 0.12)', color: '#a855f7', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
            {templates.length} layout{templates.length === 1 ? '' : 's'}
          </span>
        </div>
        
        {templates.length === 0 ? (
          <div className="glass-panel" style={{ textAlign: 'center', padding: '80px', borderRadius: '28px', border: '1px dashed rgba(168, 85, 247, 0.2)', color: '#5b6275', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <Icon name="folder" size={32} style={{ color: '#888ea8' }} />
            <p style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>No active layout files detected on disk registry paths.</p>
            <p style={{ margin: 0, fontSize: '12px', color: '#888ea8' }}>Click "Create New Mailer" to begin designing your template.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '30px' }}>
            {templates.map(tmpl => (
              <div key={tmpl.id} className="glass-panel fade-in-up" style={{ borderRadius: '24px', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column', background: 'rgba(255, 255, 255, 0.5)', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
                {/* Purge button badge */}
                <button 
                  onClick={(e) => executePurge(tmpl.id, tmpl.name, e)} 
                  title="Purge Campaign"
                  style={{ 
                    position: 'absolute', 
                    top: '12px', 
                    right: '12px', 
                    zIndex: '20', 
                    backgroundColor: 'rgba(239, 68, 68, 0.15)', 
                    border: '1px solid rgba(239, 68, 68, 0.3)', 
                    color: '#ef4444', 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    cursor: 'pointer', 
                    fontWeight: 'bold', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    transition: 'all 0.2s ease',
                    fontSize: '11px'
                  }} 
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.color = '#fff'; }} 
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#ef4444'; }}
                >
                  ✕
                </button>

                <MailerPreviewCard tmpl={tmpl} onEdit={() => navigate(`/editor/${tmpl.id}`)} />

                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1, backgroundColor: 'rgba(241, 240, 245, 0.5)' }}>
                  <h4 style={{ margin: '0', fontSize: '16px', fontWeight: '700', color: '#0f091c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '0.01em' }}>{tmpl.name}</h4>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button 
                      onClick={() => navigate(`/editor/${tmpl.id}`)} 
                      style={{ 
                        flex: 1.2, 
                        backgroundColor: 'rgba(15, 9, 28, 0.02)', 
                        color: '#1f2937', 
                        border: '1px solid rgba(15, 9, 28, 0.06)', 
                        padding: '12px', 
                        borderRadius: '14px', 
                        fontSize: '11px', 
                        fontWeight: '700', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '6px', 
                        transition: 'all 0.25s ease',
                        letterSpacing: '0.03em'
                      }} 
                      onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND_AMBER_GOLD; e.currentTarget.style.color = BRAND_AMBER_GOLD; e.currentTarget.style.boxShadow = '0 0 12px rgba(217, 119, 6, 0.15)'; }} 
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.06)'; e.currentTarget.style.color = '#1f2937'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <Icon name="settings" size={12} /> EDIT
                    </button>
                    <button 
                      onClick={(e) => executeDuplicate(tmpl.id, tmpl.name, e)} 
                      style={{ 
                        flex: 1, 
                        backgroundColor: 'rgba(15, 9, 28, 0.02)', 
                        color: '#1f2937', 
                        border: '1px solid rgba(15, 9, 28, 0.06)', 
                        padding: '12px', 
                        borderRadius: '14px', 
                        fontSize: '11px', 
                        fontWeight: '700', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '6px', 
                        transition: 'all 0.25s ease',
                        letterSpacing: '0.03em'
                      }} 
                      onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND_AMBER_GOLD; e.currentTarget.style.color = BRAND_AMBER_GOLD; e.currentTarget.style.boxShadow = '0 0 12px rgba(217, 119, 6, 0.15)'; }} 
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.06)'; e.currentTarget.style.color = '#1f2937'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <Icon name="copy" size={12} /> COPY
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: SMOOTHED ADOBE EXPRESS ELEMENT STUDIO
// ==========================================
const interpolateColor = (colorA, colorB, factor) => {
  const f = Math.max(0, Math.min(1, factor));
  const hex = (x) => {
    const s = x.toString(16);
    return s.length === 1 ? '0' + s : s;
  };
  
  const cleanA = colorA.startsWith('#') ? colorA : '#000000';
  const cleanB = colorB.startsWith('#') ? colorB : '#ffffff';

  const rA = parseInt(cleanA.slice(1, 3), 16) || 0;
  const gA = parseInt(cleanA.slice(3, 5), 16) || 0;
  const bA = parseInt(cleanA.slice(5, 7), 16) || 0;
  
  const rB = parseInt(cleanB.slice(1, 3), 16) || 0;
  const gB = parseInt(cleanB.slice(3, 5), 16) || 0;
  const bB = parseInt(cleanB.slice(5, 7), 16) || 0;
  
  const r = Math.round(rA + f * (rB - rA));
  const g = Math.round(gA + f * (gB - gA));
  const b = Math.round(bA + f * (bB - bA));
  
  return `#${hex(r)}${hex(g)}${hex(b)}`;
};

const motionTypes = [
  {
    id: 'none',
    name: 'None',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </svg>
    )
  },
  {
    id: 'liquid',
    name: 'Liquid Gradient',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2Z" strokeDasharray="3 1" opacity="0.5" />
        <path d="M2 12c4.5-3 7.5 3 12 0s3.5-3 8 0" strokeWidth="3" />
        <path d="M4 16c3-1.5 5 1.5 8 0s3-1.5 6 0" opacity="0.75" />
      </svg>
    )
  },
  {
    id: 'shimmer',
    name: 'Premium Shimmer',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M4 12h16" opacity="0.3" strokeWidth="1.5" />
        <path d="M12 4v16" opacity="0.3" strokeWidth="1.5" />
        <path d="m5 5 14 14" strokeWidth="3" />
        <path d="m19 5-3 3M8 16l-3 3" opacity="0.6" strokeWidth="1.5" />
      </svg>
    )
  },
  {
    id: 'bounce',
    name: 'Bounce & Squish',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M12 3v12M12 15l-4-4M12 15l4-4" />
        <path d="M4 20c4-1.5 12-1.5 16 0" />
      </svg>
    )
  },
  {
    id: 'blinking',
    name: 'Blinking',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
        <path d="M12 5V3M5 6L3.5 4.5M19 6l1.5-1.5" strokeWidth="2" opacity="0.8" />
      </svg>
    )
  },
  {
    id: 'flicker',
    name: 'Flicker Glow',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    )
  },
  {
    id: 'spin',
    name: 'Rotational Spin',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.72 2.78L21 8" />
        <path d="M21 3v5h-5" />
      </svg>
    )
  },
  {
    id: 'breathe',
    name: 'Organic Breathe',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <circle cx="12" cy="12" r="10" strokeDasharray="3 3" opacity="0.6" />
        <circle cx="12" cy="12" r="5" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" strokeWidth="2" opacity="0.8" />
      </svg>
    )
  },
  {
    id: 'pulse',
    name: 'Pulse Strobe',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    )
  },
  {
    id: 'shake',
    name: 'Earthquake Shake',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="m16 3-4 4-4-4" />
        <path d="m8 21 4-4 4 4" />
        <path d="M12 7v10" />
        <path d="M3 12h18" strokeDasharray="2 2" />
      </svg>
    )
  },
  {
    id: 'shear',
    name: 'Skew Wave',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M5 21h14l3-18H8L5 21z" />
      </svg>
    )
  },
  {
    id: 'rainbow',
    name: 'Prism Shift',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M12 3L2 21h20L12 3z" />
        <path d="m12 3 5 18M12 3 7 18" opacity="0.6" strokeWidth="1.5" />
      </svg>
    )
  },
  {
    id: 'drift',
    name: 'Floating Drift',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 1 0 0-8c-2 0-4 1.33-6 4Z" />
      </svg>
    )
  },
  {
    id: 'glitch',
    name: 'Cyber Glitch',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M3 7h18M6 12h12M3 17h18" strokeWidth="3" opacity="0.7" strokeDasharray="4 6" />
        <path d="M8 7v10M16 7v10" strokeWidth="1.5" opacity="0.5" />
      </svg>
    )
  },
  {
    id: 'zoom',
    name: 'Pulsing Scale',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
        <line x1="11" y1="8" x2="11" y2="14" />
        <line x1="8" y1="11" x2="14" y2="11" />
      </svg>
    )
  },
  {
    id: 'tilt',
    name: 'Pendulum Tilt',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <line x1="12" y1="2" x2="12" y2="16" />
        <circle cx="12" cy="18" r="3" />
        <path d="M4 12c4-3 12-3 16 0" strokeDasharray="3 3" opacity="0.5" />
      </svg>
    )
  },
  {
    id: 'shadow',
    name: 'Glow Shadow',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <circle cx="12" cy="12" r="6" strokeWidth="4" />
        <circle cx="12" cy="12" r="10" strokeWidth="1" strokeDasharray="2 2" opacity="0.6" />
      </svg>
    )
  },
  {
    id: 'swing',
    name: 'Side Swing',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M2 12c5-7 5 7 10 0s5-7 10 0" />
      </svg>
    )
  },
  {
    id: 'wobble',
    name: 'Jelly Wobble',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M12 3c-5 0-9 4-9 9 0 4.5 4 6.5 6 9h6c2-2.5 6-4.5 6-9 0-5-4-9-9-9Z" />
      </svg>
    )
  },
  {
    id: 'underline',
    name: 'Underline Wave',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M4 9h16M4 15h16" opacity="0.3" strokeWidth="1.5" />
        <path d="M2 19c4-2 6-2 10 0s6 2 10 0" strokeWidth="3" />
      </svg>
    )
  },
  {
    id: 'spotlight',
    name: 'Backlight Sweep',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <circle cx="12" cy="12" r="10" strokeDasharray="3 3" opacity="0.4" />
        <circle cx="12" cy="12" r="5" fill={color} opacity="0.2" stroke="none" />
        <path d="m19 19-3-3M5 5l3 3" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )
  },
  {
    id: 'bracket',
    name: 'Focus Brackets',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M6 5H4v14h2M18 5h2v14h-2" />
        <rect x="8" y="9" width="8" height="6" rx="1" opacity="0.5" strokeWidth="1.5" />
      </svg>
    )
  },
  {
    id: 'sparkle',
    name: 'Celestial Twinkle',
    icon: (color) => (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'all 0.3s ease' }}>
        <path d="M12 3v4M12 17v4M3 12h4M17 12h4" strokeWidth="2" />
        <path d="m7.5 7.5 3 3M13.5 13.5l3 3M16.5 7.5l-3 3M10.5 13.5l-3 3" opacity="0.6" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="2" fill={color} stroke="none" />
      </svg>
    )
  }
];

function ElementStudioView() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  // Canvas Output Dimensions
  const [esCanvasWidth, setEsCanvasWidth] = useState(500);   // preview canvas width (px)
  const [esCanvasHeight, setEsCanvasHeight] = useState(140);  // preview canvas height (px)
  const [exportedFileSize, setExportedFileSize] = useState(null);

  const [layers, setLayers] = useState([
    {
      id: 'l-1',
      name: 'Text Layer 1',
      type: 'text',
      visible: true,
      xOffset: 0,
      yOffset: 0,
      rotationOffset: 0,
      text: 'WORRIED ABOUT HENRY',
      font: 'Arial',
      fontSize: 38,
      letterSpacing: 0,
      color1: '#0f091c',
      color2: '#d97706',
      strokeColor: '#0f091c',
      animationType: 'bounce',
      speed: 50,
      distance: 25,
      squish: 30,
      direction: 'down',
      smoothingMode: 'linear',
      shapeType: 'rect',
      shapeWidth: 200,
      shapeHeight: 100,
      fillStyle: 'solid',
      outlineStyle: 'transparent',
      strokeWidth: 3
    }
  ]);
  const [activeLayerId, setActiveLayerId] = useState('l-1');
  const [lockAspectRatio, setLockAspectRatio] = useState(true);
  const [elementName, setElementName] = useState('StudioElement');
  
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [librarySelectionTarget, setLibrarySelectionTarget] = useState(null); 
  const [libraryTab, setLibraryTab] = useState('images'); 
  const [libraryData, setLibraryData] = useState({ images: [], gifs: [], createdAssets: [] });
  const [libraryTotals, setLibraryTotals] = useState({ images: 0, gifs: 0, createdAssets: 0 });
  const [libraryPage, setLibraryPage] = useState(1);
  const LIBRARY_PAGE_SIZE = 18;
  const [libraryLoading, setLibraryLoading] = useState(false);

  const handleLibraryItemSelect = (url) => {
    if (librarySelectionTarget) {
      const { layerId } = librarySelectionTarget;
      setLayers(prev => prev.map(l => l.id === layerId ? { ...l, imageFillUrl: url } : l));
    }
    setLibrarySelectionTarget(null);
    setShowLibraryModal(false);
  };

  const triggerLibraryFetchOpen = () => {
    setShowLibraryModal(true);
    setLibraryLoading(true);
    setLibraryPage(1);
    setLibraryData({ images: [], gifs: [], createdAssets: [] });
    fetch(`${BACKEND_URL}/api/uploads-library?page=1&limit=${LIBRARY_PAGE_SIZE}&refresh=1`)
      .then(res => res.json())
      .then(data => {
        setLibraryData({ images: data.images || [], gifs: data.gifs || [], createdAssets: data.createdAssets || [] });
        setLibraryTotals(data.totals || { images: data.images?.length || 0, gifs: data.gifs?.length || 0, createdAssets: data.createdAssets?.length || 0 });
        setLibraryLoading(false);
      })
      .catch(() => setLibraryLoading(false));
  };

  const loadMoreLibraryItems = () => {
    const nextPage = libraryPage + 1;
    setLibraryLoading(true);
    fetch(`${BACKEND_URL}/api/uploads-library?page=${nextPage}&limit=${LIBRARY_PAGE_SIZE}`)
      .then(res => res.json())
      .then(data => {
        setLibraryData(prev => ({
          images: libraryTab === 'images' ? [...prev.images, ...(data.images || [])] : prev.images,
          gifs: libraryTab === 'gifs' ? [...prev.gifs, ...(data.gifs || [])] : prev.gifs,
          createdAssets: libraryTab === 'createdAssets' ? [...prev.createdAssets, ...(data.createdAssets || [])] : prev.createdAssets
        }));
        setLibraryPage(nextPage);
        setLibraryLoading(false);
      })
      .catch(() => setLibraryLoading(false));
  };
  
  const [isTransparent, setIsTransparent] = useState(false);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [fps, setFps] = useState(12); 
  const [showMotionSettings, setShowMotionSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('geometry'); // 'geometry' | 'styling' | 'animation' | 'placement'
  const [renamingLayerId, setRenamingLayerId] = useState(null);
  const [renamingName, setRenamingName] = useState('');

  // Click & Drag Positioning States
  const [isCanvasDragging, setIsCanvasDragging] = useState(false);
  const [draggedLayerId, setDraggedLayerId] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hoveredCanvasLayerId, setHoveredCanvasLayerId] = useState(null);

  // Sidebar List Drag & Drop Reordering
  const [draggedListItemId, setDraggedListItemId] = useState(null);

  const handleDragLayerStart = (e, layerId) => {
    pushUndoPoint(layers);
    setDraggedListItemId(layerId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', layerId);
  };

  const handleDragLayerOver = (e, targetLayerId) => {
    e.preventDefault();
    if (!draggedListItemId || draggedListItemId === targetLayerId) return;

    const draggedIdx = layers.findIndex(l => l.id === draggedListItemId);
    const targetIdx = layers.findIndex(l => l.id === targetLayerId);

    if (draggedIdx !== -1 && targetIdx !== -1) {
      const updatedLayers = [...layers];
      const [draggedLayer] = updatedLayers.splice(draggedIdx, 1);
      updatedLayers.splice(targetIdx, 0, draggedLayer);
      setLayers(updatedLayers);
    }
  };

  const handleDragLayerEnd = () => {
    setDraggedListItemId(null);
  };

  const activeLayer = layers.find(l => l.id === activeLayerId) || layers[0] || {};

  const updateActiveLayerProperty = (property, value) => {
    if (!activeLayerId) return;

    const now = Date.now();
    const isSliderProp = [
      'opacity', 'shapeWidth', 'shapeHeight', 'cornerRadius', 'strokeWidth',
      'imageBlur', 'imageDistort', 'speed', 'distance', 'squish',
      'xOffset', 'yOffset', 'rotationOffset', 'letterSpacing', 'fontSize'
    ].includes(property);

    const timeDiff = now - esLastHistoryPushRef.current;
    const sameProp = esLastUpdatedPropertyRef.current === property;
    const sameLayer = esLastUpdatedLayerIdRef.current === activeLayerId;

    if (!isSliderProp || !sameProp || !sameLayer || timeDiff > 800) {
      pushUndoPoint(layers);
      esLastHistoryPushRef.current = now;
      esLastUpdatedPropertyRef.current = property;
      esLastUpdatedLayerIdRef.current = activeLayerId;
    }

    setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, [property]: value } : l));
  };

  const updateActiveLayerProperties = (updates) => {
    if (!activeLayerId) return;

    const now = Date.now();
    const property = Object.keys(updates)[0];
    const isSliderProp = [
      'opacity', 'shapeWidth', 'shapeHeight', 'cornerRadius', 'strokeWidth',
      'imageBlur', 'imageDistort', 'speed', 'distance', 'squish',
      'xOffset', 'yOffset', 'rotationOffset', 'letterSpacing', 'fontSize'
    ].includes(property);

    const timeDiff = now - esLastHistoryPushRef.current;
    const sameProp = esLastUpdatedPropertyRef.current === property;
    const sameLayer = esLastUpdatedLayerIdRef.current === activeLayerId;

    if (!isSliderProp || !sameProp || !sameLayer || timeDiff > 800) {
      pushUndoPoint(layers);
      esLastHistoryPushRef.current = now;
      esLastUpdatedPropertyRef.current = property;
      esLastUpdatedLayerIdRef.current = activeLayerId;
    }

    setLayers(prev => prev.map(l => l.id === activeLayerId ? { ...l, ...updates } : l));
  };

  const handleAddTextLayer = () => {
    pushUndoPoint(layers);
    const newId = 'l-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    const newLayer = {
      id: newId,
      name: `Text Layer ${layers.length + 1}`,
      type: 'text',
      visible: true,
      xOffset: 0,
      yOffset: 0,
      rotationOffset: 0,
      text: 'NEW TEXT LAYER',
      font: 'Arial',
      fontSize: 28,
      fontWeight: '400',
      fontStyle: 'normal',
      letterSpacing: 0,
      color1: '#0f091c',
      color2: '#d97706',
      animationType: 'none',
      speed: 50,
      distance: 25,
      squish: 30,
      direction: 'down',
      smoothingMode: 'linear',
      shapeType: 'rect',
      shapeWidth: 200,
      shapeHeight: 100,
      fillStyle: 'solid',
      outlineStyle: 'transparent',
      strokeWidth: 3
    };
    setLayers([...layers, newLayer]);
    setActiveLayerId(newId);
  };

  const handleAddShapeLayer = () => {
    pushUndoPoint(layers);
    const newId = 'l-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    const newLayer = {
      id: newId,
      name: `Shape Layer ${layers.length + 1}`,
      type: 'shape',
      visible: true,
      xOffset: 0,
      yOffset: 0,
      rotationOffset: 0,
      text: '',
      font: 'Arial',
      fontSize: 28,
      letterSpacing: 0,
      color1: '#d97706',
      color2: '#0f091c',
      strokeColor: '#0f091c',
      animationType: 'none',
      speed: 50,
      distance: 25,
      squish: 30,
      direction: 'down',
      smoothingMode: 'linear',
      shapeType: 'rect',
      shapeWidth: 160,
      shapeHeight: 160,
      fillStyle: 'solid',
      outlineStyle: 'transparent',
      strokeWidth: 4
    };
    setLayers([...layers, newLayer]);
    setActiveLayerId(newId);
  };

  const handleAddImageLayer = () => {
    pushUndoPoint(layers);
    const newId = 'l-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    const newLayer = {
      id: newId,
      name: `Image Layer ${layers.length + 1}`,
      type: 'image',
      visible: true,
      xOffset: 0,
      yOffset: 0,
      rotationOffset: 0,
      text: '',
      font: 'Arial',
      fontSize: 28,
      letterSpacing: 0,
      color1: '#d97706',
      color2: '#0f091c',
      strokeColor: '#0f091c',
      animationType: 'none',
      speed: 50,
      distance: 25,
      squish: 30,
      direction: 'down',
      smoothingMode: 'linear',
      shapeType: 'rect',
      shapeWidth: 160,
      shapeHeight: 160,
      fillStyle: 'solid',
      outlineStyle: 'transparent',
      strokeWidth: 4,
      imageFillUrl: '',
      imageBlur: 0,
      imageDistort: 0
    };
    setLayers([...layers, newLayer]);
    setActiveLayerId(newId);
  };

  const handleDeleteLayer = (id) => {
    if (layers.length <= 1) {
      alert("⚠️ You must keep at least one layer in the composition.");
      return;
    }
    pushUndoPoint(layers);
    const filtered = layers.filter(l => l.id !== id);
    setLayers(filtered);
    if (activeLayerId === id) {
      setActiveLayerId(filtered[filtered.length - 1].id);
    }
  };

  const handleDuplicateLayer = (layerToClone) => {
    pushUndoPoint(layers);
    const newId = 'l-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    const cloned = {
      ...layerToClone,
      id: newId,
      name: `${layerToClone.name} (Copy)`,
      xOffset: layerToClone.xOffset + 15,
      yOffset: layerToClone.yOffset + 15
    };
    setLayers([...layers, cloned]);
    setActiveLayerId(newId);
  };

  // Drag & Snapping Event Handlers
  const handleDragStart = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = ((clientX - rect.left) / rect.width) * canvas.width;
    const mouseY = ((clientY - rect.top) / rect.height) * canvas.height;

    const getLayerBounds = (layer) => {
      const ctx = canvas.getContext('2d');
      ctx.save();
      ctx.letterSpacing = `${layer.letterSpacing || 0}px`;
      applyCanvasFontMapping(ctx, layer.font, layer.fontSize || 38, layer.fontWeight || '400', layer.fontStyle || 'normal');
      const w = layer.type === 'text'
        ? ctx.measureText((layer.text || '').toUpperCase()).width
        : (layer.shapeWidth || 160);
      const h = layer.type === 'text'
        ? (layer.fontSize || 38)
        : (layer.shapeHeight || 160);
      ctx.restore();
      
      const lx = canvas.width / 2 + (layer.xOffset || 0);
      const ly = canvas.height / 2 + (layer.yOffset || 0);
      return {
        left: lx - w / 2,
        right: lx + w / 2,
        top: ly - h / 2,
        bottom: ly + h / 2
      };
    };

    const clickedLayer = [...layers].reverse().find(layer => {
      if (!layer.visible) return false;
      const bounds = getLayerBounds(layer);
      return mouseX >= bounds.left - 8 && mouseX <= bounds.right + 8 &&
             mouseY >= bounds.top - 8 && mouseY <= bounds.bottom + 8;
    });

    if (clickedLayer) {
      pushUndoPoint(layers);
      setActiveLayerId(clickedLayer.id);
      setIsCanvasDragging(true);
      setDraggedLayerId(clickedLayer.id);
      
      const lx = canvas.width / 2 + (clickedLayer.xOffset || 0);
      const ly = canvas.height / 2 + (clickedLayer.yOffset || 0);
      setDragOffset({ x: mouseX - lx, y: mouseY - ly });
    }
  };

  const handleDragMove = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = ((clientX - rect.left) / rect.width) * canvas.width;
    const mouseY = ((clientY - rect.top) / rect.height) * canvas.height;

    if (isCanvasDragging && draggedLayerId) {
      const targetLx = mouseX - dragOffset.x;
      const targetLy = mouseY - dragOffset.y;

      let newXOffset = Math.round(targetLx - canvas.width / 2);
      let newYOffset = Math.round(targetLy - canvas.height / 2);

      // Snapping to vertical center and horizontal center within 10px
      if (Math.abs(newXOffset) < 10) newXOffset = 0;
      if (Math.abs(newYOffset) < 10) newYOffset = 0;

      setLayers(prev => prev.map(l => l.id === draggedLayerId ? { ...l, xOffset: newXOffset, yOffset: newYOffset } : l));
    } else {
      const getLayerBounds = (layer) => {
        const ctx = canvas.getContext('2d');
        ctx.save();
        ctx.letterSpacing = `${layer.letterSpacing || 0}px`;
        applyCanvasFontMapping(ctx, layer.font, layer.fontSize || 38, layer.fontWeight || '400', layer.fontStyle || 'normal');
        const w = layer.type === 'text'
          ? ctx.measureText((layer.text || '').toUpperCase()).width
          : (layer.shapeWidth || 160);
        const h = layer.type === 'text'
          ? (layer.fontSize || 38)
          : (layer.shapeHeight || 160);
        ctx.restore();
        
        const lx = canvas.width / 2 + (layer.xOffset || 0);
        const ly = canvas.height / 2 + (layer.yOffset || 0);
        return {
          left: lx - w / 2,
          right: lx + w / 2,
          top: ly - h / 2,
          bottom: ly + h / 2
        };
      };

      const hoveredLayer = [...layers].reverse().find(layer => {
        if (!layer.visible) return false;
        const bounds = getLayerBounds(layer);
        return mouseX >= bounds.left - 8 && mouseX <= bounds.right + 8 &&
               mouseY >= bounds.top - 8 && mouseY <= bounds.bottom + 8;
      });

      if (hoveredLayer) {
        setHoveredCanvasLayerId(hoveredLayer.id);
      } else {
        setHoveredCanvasLayerId(null);
      }
    }
  };

  const handleDragEnd = () => {
    setIsCanvasDragging(false);
    setDraggedLayerId(null);
  };
 

  const [isCompiling, setIsCompiling] = useState(false);
  const [systemFonts, setSystemFonts] = useState([]);
  const [customFonts, setCustomFonts] = useState([]);

  // Active Asset Editing state tracker
  const [editingAssetFile, setEditingAssetFile] = useState(null);
  const [showSaveChoiceModal, setShowSaveChoiceModal] = useState(false);

  // Element Studio Project States
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [originalProjectName, setOriginalProjectName] = useState('');
  const [projectsList, setProjectsList] = useState([]);
  const [showProjectsModal, setShowProjectsModal] = useState(false);

  // Element Studio Undo/Redo Stacks
  const [esUndoStack, setEsUndoStack] = useState([]);
  const [esRedoStack, setEsRedoStack] = useState([]);
  const esLastHistoryPushRef = useRef(0);
  const esLastUpdatedPropertyRef = useRef('');
  const esLastUpdatedLayerIdRef = useRef('');

  const pushUndoPoint = (customPrevLayers = null) => {
    const snap = JSON.stringify({
      layers: customPrevLayers || layers,
      activeLayerId,
      isTransparent,
      bgColor,
      esCanvasWidth,
      esCanvasHeight
    });
    setEsUndoStack(prev => [...prev, snap]);
    setEsRedoStack([]);
  };

  const restoreEsSnapshot = (snapshotStr) => {
    try {
      const snap = JSON.parse(snapshotStr);
      setLayers(snap.layers || []);
      setActiveLayerId(snap.activeLayerId || null);
      setIsTransparent(snap.isTransparent !== undefined ? snap.isTransparent : false);
      setBgColor(snap.bgColor || '#ffffff');
      setEsCanvasWidth(snap.esCanvasWidth || 500);
      setEsCanvasHeight(snap.esCanvasHeight || 140);
    } catch (e) {
      console.error('Failed to restore snapshot:', e);
    }
  };

  const handleUndo = () => {
    if (esUndoStack.length === 0) return;
    const currentSnap = JSON.stringify({
      layers,
      activeLayerId,
      isTransparent,
      bgColor,
      esCanvasWidth,
      esCanvasHeight
    });
    const nextUndoStack = [...esUndoStack];
    const prevSnap = nextUndoStack.pop();
    
    setEsRedoStack(prev => [...prev, currentSnap]);
    setEsUndoStack(nextUndoStack);
    restoreEsSnapshot(prevSnap);
  };

  const handleRedo = () => {
    if (esRedoStack.length === 0) return;
    const currentSnap = JSON.stringify({
      layers,
      activeLayerId,
      isTransparent,
      bgColor,
      esCanvasWidth,
      esCanvasHeight
    });
    const nextRedoStack = [...esRedoStack];
    const nextSnap = nextRedoStack.pop();
    
    setEsUndoStack(prev => [...prev, currentSnap]);
    setEsRedoStack(nextRedoStack);
    restoreEsSnapshot(nextSnap);
  };

  // Keyboard shortcut listener for Element Studio
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (document.activeElement && (
        document.activeElement.tagName === 'INPUT' || 
        document.activeElement.tagName === 'TEXTAREA' || 
        document.activeElement.isContentEditable
      )) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        if (e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            handleRedo();
          } else {
            handleUndo();
          }
        } else if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          handleRedo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [esUndoStack, esRedoStack, layers, activeLayerId, isTransparent, bgColor, esCanvasWidth, esCanvasHeight]);

  // Query parameter editor loading checker
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editFile = params.get('edit');
    const projectId = params.get('project');

    if (editFile) {
      setEditingAssetFile(editFile);
      setExportedFileSize(null);
      fetch(`${BACKEND_URL}/public/uploads/created-assets/${editFile}`, { method: 'HEAD' })
        .then(res => {
          const size = res.headers.get('content-length');
          if (size) setExportedFileSize(parseInt(size, 10));
        })
        .catch(() => {});

      // Strip extension and fetch JSON
      const jsonFilename = editFile.replace(/\.(png|gif|jpg|jpeg)$/i, '.json');
      fetch(`${BACKEND_URL}/public/uploads/created-assets/${jsonFilename}`)
        .then(res => {
          if (!res.ok) throw new Error('Asset metadata not found');
          return res.json();
        })
        .then(data => {
          if (data.layers) setLayers(data.layers);
          if (data.activeLayerId) setActiveLayerId(data.activeLayerId);
          if (data.elementName) setElementName(data.elementName);
          if (data.isTransparent !== undefined) setIsTransparent(data.isTransparent);
          if (data.bgColor) setBgColor(data.bgColor);
          if (data.esCanvasWidth) setEsCanvasWidth(data.esCanvasWidth);
          if (data.esCanvasHeight) setEsCanvasHeight(data.esCanvasHeight);
          
          // Seed elementName from filename if not inside metadata
          if (!data.elementName) {
            setElementName(editFile.replace(/\.(png|gif|json)$/i, ''));
          }
        })
        .catch(err => {
          console.warn('Failed to load asset metadata:', err);
          // Fallback: seed name from filename anyway
          setElementName(editFile.replace(/\.(png|gif)$/i, ''));
        });
    } else if (projectId) {
      setCurrentProjectId(projectId);
      fetch(`${BACKEND_URL}/api/element-studio/projects/${projectId}`)
        .then(res => {
          if (!res.ok) throw new Error('Project not found');
          return res.json();
        })
        .then(data => {
          if (data.layers) setLayers(data.layers);
          if (data.activeLayerId) setActiveLayerId(data.activeLayerId || data.layers[0]?.id || '');
          if (data.elementName) {
            setElementName(data.elementName);
            setOriginalProjectName(data.elementName);
          }
          if (data.isTransparent !== undefined) setIsTransparent(data.isTransparent);
          if (data.bgColor) setBgColor(data.bgColor);
          if (data.esCanvasWidth) setEsCanvasWidth(data.esCanvasWidth);
          if (data.esCanvasHeight) setEsCanvasHeight(data.esCanvasHeight);
          if (data.fps) setFps(data.fps);
        })
        .catch(err => {
          console.error('Failed to load project details:', err);
        });
    }
  }, []);

  // Canvas Output Dimensions moved to top of ElementStudioView to avoid hoisting/TDZ ReferenceErrors

  // Last 10 used colors history
  const [usedColors, setUsedColors] = useState(() => {
    try {
      const saved = localStorage.getItem('louder_studio_used_colors');
      return saved ? JSON.parse(saved) : ['#0f091c', '#d97706', '#ffffff', '#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];
    } catch (e) {
      return ['#0f091c', '#d97706', '#ffffff', '#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];
    }
  });

  const addColorToHistory = (color) => {
    if (!color || color === 'transparent') return;
    const hex = color.toLowerCase();
    setUsedColors(prev => {
      const filtered = prev.filter(c => c.toLowerCase() !== hex);
      const updated = [color, ...filtered].slice(0, 10);
      try {
        localStorage.setItem('louder_studio_used_colors', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const renderRecentSwatches = (onClickCallback) => {
    if (!usedColors || usedColors.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px', alignItems: 'center' }}>
        {usedColors.slice(0, 10).map((c, idx) => (
          <button
            key={`swatch-${c}-${idx}`}
            title={c}
            type="button"
            onClick={() => onClickCallback(c)}
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '5px',
              backgroundColor: c,
              border: '1px solid rgba(15, 9, 28, 0.12)',
              cursor: 'pointer',
              padding: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'transform 0.1s ease',
              flexShrink: 0
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        ))}
      </div>
    );
  };

  const renderColorInput = (label, value, onChange, onBlurCallback = null) => {
    const handleBlur = (e) => {
      const val = e.target.value;
      addColorToHistory(val);
      if (onBlurCallback) onBlurCallback(val);
    };

    return (
      <div style={{ marginBottom: '14px', width: '100%' }}>
        {label && <label style={studioLabelStyle}>{label}</label>}
        <div className="glass-input" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', padding: '10px 12px', borderRadius: '16px', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input 
              type="color" 
              value={value || '#ffffff'} 
              onChange={e => onChange(e.target.value)} 
              onBlur={handleBlur}
              style={{ width: '28px', height: '28px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
            />
            <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#1f2937', fontWeight: '700' }}>
              {(value || '#FFFFFF').toUpperCase()}
            </span>
          </div>
          {usedColors && usedColors.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', borderTop: '1px solid rgba(15, 9, 28, 0.05)', paddingTop: '6px' }}>
              {usedColors.slice(0, 8).map((c, idx) => (
                <div 
                  key={`swatch-picker-${c}-${idx}`}
                  title={c}
                  onClick={() => {
                    onChange(c);
                    addColorToHistory(c);
                    if (onBlurCallback) onBlurCallback(c);
                  }}
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '4px',
                    backgroundColor: c,
                    cursor: 'pointer',
                    border: '1px solid rgba(0,0,0,0.15)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'transform 0.15s ease'
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Aspect-locked Cropper Modal states
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropRawImage, setCropRawImage] = useState(null);
  const [cropZoom, setCropZoom] = useState(1.0);
  const [cropPanX, setCropPanX] = useState(0);
  const [cropPanY, setCropPanY] = useState(0);
  const [cropIsDragging, setCropIsDragging] = useState(false);
  const [cropDragStart, setCropDragStart] = useState({ x: 0, y: 0 });
  const [cropFileName, setCropFileName] = useState('');
  const cropCanvasRef = useRef(null);

  // Compression & aspect states for Design Studio
  const [cropAspect, setCropAspect] = useState(1.0);
  const [cropOriginalAspect, setCropOriginalAspect] = useState(1.0);
  const [cropScale, setCropScale] = useState('original');
  const [cropFormat, setCropFormat] = useState('jpeg');
  const [cropQuality, setCropQuality] = useState(0.85);
  const [cropOriginalSize, setCropOriginalSize] = useState(0);
  const [cropEstimatedSize, setCropEstimatedSize] = useState(0);

  // Redraw the interactive crop canvas when states or aspect ratio updates
  useEffect(() => {
    if (!cropModalOpen || !cropRawImage || !cropCanvasRef.current) return;
    const canvas = cropCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = cropRawImage;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = canvas.height / img.height;
      } else {
        baseScale = canvas.width / img.width;
      }

      const drawScale = baseScale * cropZoom;
      const centerX = (canvas.width - img.width * drawScale) / 2;
      const centerY = (canvas.height - img.height * drawScale) / 2;

      ctx.save();
      ctx.drawImage(
        img,
        centerX + cropPanX,
        centerY + cropPanY,
        img.width * drawScale,
        img.height * drawScale
      );
      ctx.restore();
    };
  }, [cropModalOpen, cropRawImage, cropZoom, cropPanX, cropPanY, cropAspect]);

  // Effect to estimate cropped layer image file size reactively
  useEffect(() => {
    if (!cropModalOpen || !cropRawImage) {
      setCropEstimatedSize(0);
      return;
    }

    const img = new Image();
    img.src = cropRawImage;
    img.onload = () => {
      const wVal = activeLayer ? (activeLayer.shapeWidth || 320) : 320;
      const hVal = activeLayer ? (activeLayer.shapeHeight || 80) : 80;
      const aspect = cropAspect || (wVal / hVal);

      let viewWidth = 450;
      let viewHeight = 450 / aspect;
      if (viewHeight > 300) {
        viewHeight = 300;
        viewWidth = 300 * aspect;
      }

      const canvasRatio = viewWidth / viewHeight;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = viewHeight / img.height;
      } else {
        baseScale = viewWidth / img.width;
      }

      const drawScale = baseScale * cropZoom;
      const originalCropWidth = viewWidth / drawScale;

      let outputWidth = originalCropWidth;
      if (cropScale !== 'original') {
        outputWidth = originalCropWidth * parseFloat(cropScale);
      }
      outputWidth = Math.round(outputWidth);
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      const resScale = outputWidth / viewWidth;
      const highResScale = drawScale * resScale;
      const highResPanX = cropPanX * resScale;
      const highResPanY = cropPanY * resScale;

      const centerX = (outputWidth - img.width * highResScale) / 2;
      const centerY = (outputHeight - img.height * highResScale) / 2;

      oCtx.save();
      oCtx.drawImage(
        img,
        centerX + highResPanX,
        centerY + highResPanY,
        img.width * highResScale,
        img.height * highResScale
      );
      oCtx.restore();

      const mimeType = cropFormat === 'png' ? 'image/png' : (cropFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const quality = cropFormat === 'png' ? undefined : cropQuality;
      const dataUrl = offscreen.toDataURL(mimeType, quality);
      const sizeBytes = Math.round((dataUrl.length * 3) / 4);
      setCropEstimatedSize(sizeBytes);
    };
  }, [
    cropModalOpen,
    cropRawImage,
    cropZoom,
    cropPanX,
    cropPanY,
    cropAspect,
    cropScale,
    cropFormat,
    cropQuality,
    activeLayer
  ]);

  const handleCropMouseDown = (e) => {
    setCropIsDragging(true);
    setCropDragStart({
      x: e.clientX - cropPanX,
      y: e.clientY - cropPanY
    });
  };

  const handleCropMouseMove = (e) => {
    if (!cropIsDragging) return;
    setCropPanX(e.clientX - cropDragStart.x);
    setCropPanY(e.clientY - cropDragStart.y);
  };

  const handleCropMouseUpOrLeave = () => {
    setCropIsDragging(false);
  };

  const handleCropTouchStart = (e) => {
    if (e.touches.length === 1) {
      setCropIsDragging(true);
      setCropDragStart({
        x: e.touches[0].clientX - cropPanX,
        y: e.touches[0].clientY - cropPanY
      });
    }
  };

  const handleCropTouchMove = (e) => {
    if (!cropIsDragging || e.touches.length !== 1) return;
    setCropPanX(e.touches[0].clientX - cropDragStart.x);
    setCropPanY(e.touches[0].clientY - cropDragStart.y);
  };

  const handleApplyCrop = () => {
    if (!cropRawImage) return;
    const img = new Image();
    img.src = cropRawImage;
    img.onload = () => {
      const wVal = activeLayer ? (activeLayer.shapeWidth || 320) : 320;
      const hVal = activeLayer ? (activeLayer.shapeHeight || 80) : 80;
      const aspect = cropAspect || (wVal / hVal);

      let viewWidth = 450;
      let viewHeight = 450 / aspect;
      if (viewHeight > 300) {
        viewHeight = 300;
        viewWidth = 300 * aspect;
      }

      const canvasRatio = viewWidth / viewHeight;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = viewHeight / img.height;
      } else {
        baseScale = viewWidth / img.width;
      }

      const drawScale = baseScale * cropZoom;
      const originalCropWidth = viewWidth / drawScale;
      let outputWidth = originalCropWidth;
      if (cropScale !== 'original') {
        outputWidth = originalCropWidth * parseFloat(cropScale);
      }
      outputWidth = Math.round(outputWidth);
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      const resScale = outputWidth / viewWidth;
      const highResScale = drawScale * resScale;
      const highResPanX = cropPanX * resScale;
      const highResPanY = cropPanY * resScale;

      const centerX = (outputWidth - img.width * highResScale) / 2;
      const centerY = (outputHeight - img.height * highResScale) / 2;

      oCtx.save();
      oCtx.drawImage(
        img,
        centerX + highResPanX,
        centerY + highResPanY,
        img.width * highResScale,
        img.height * highResScale
      );
      oCtx.restore();

      const mimeType = cropFormat === 'png' ? 'image/png' : (cropFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const croppedDataUrl = cropFormat === 'png'
        ? offscreen.toDataURL(mimeType)
        : offscreen.toDataURL(mimeType, cropQuality);

      setLayers(prev => prev.map(l => l.id === activeLayerId ? {
        ...l,
        imageFillUrl: croppedDataUrl,
        shapeHeight: Math.round(l.shapeWidth / aspect)
      } : l));
      setCropModalOpen(false);
      setCropRawImage(null);
      if (elementName === 'StudioElement' || !elementName.trim()) {
        setElementName(`${cropFileName}_Shape`);
      }
    };
  };

  const handleSkipCrop = () => {
    if (!cropRawImage) return;
    const img = new Image();
    img.src = cropRawImage;
    img.onload = () => {
      let outputWidth = img.width;
      if (cropScale !== 'original') {
        outputWidth = Math.round(img.width * parseFloat(cropScale));
      }
      const aspect = img.width / img.height;
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      oCtx.drawImage(img, 0, 0, outputWidth, outputHeight);

      const mimeType = cropFormat === 'png' ? 'image/png' : (cropFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const processedDataUrl = cropFormat === 'png'
        ? offscreen.toDataURL(mimeType)
        : offscreen.toDataURL(mimeType, cropQuality);

      setLayers(prev => prev.map(l => l.id === activeLayerId ? {
        ...l,
        imageFillUrl: processedDataUrl,
        shapeHeight: Math.round(l.shapeWidth / aspect)
      } : l));
      setCropModalOpen(false);
      setCropRawImage(null);
      if (elementName === 'StudioElement' || !elementName.trim()) {
        setElementName(`${cropFileName}_Shape`);
      }
    };
    img.onerror = () => {
      updateActiveLayerProperty('imageFillUrl', cropRawImage);
      setCropModalOpen(false);
      setCropRawImage(null);
      if (elementName === 'StudioElement' || !elementName.trim()) {
        setElementName(`${cropFileName}_Shape`);
      }
    };
  };

  // Bulletproof Rounded Rect Path Helper
  const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    if (ctx.roundRect) {
      ctx.roundRect(x, y, width, height, radius);
    } else {
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
    }
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/fonts`)
      .then(res => res.json())
      .then(data => {
        setSystemFonts(data.baseFonts || ['Arial', 'Impact', 'Trebuchet MS']);
        setCustomFonts(data.mappedCustomFonts || []);

        // DYNAMIC ORIGIN FIX: Inject an absolute font registry stylesheet inside the head context
        const styleNodeId = 'dynamic-studio-font-face-registry-wrapper';
        let styleNode = document.getElementById(styleNodeId);
        if (!styleNode) {
          styleNode = document.createElement('style');
          styleNode.id = styleNodeId;
          document.head.appendChild(styleNode);
        }
        
        let markup = '';
        (data.mappedCustomFonts || []).forEach(f => {
          (f.variations || []).forEach(v => {
            const absoluteFontUrl = v.url.startsWith('http') ? v.url : `${BACKEND_URL}${v.url}`;
            const urlParts = v.url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            const extIndex = fileName.lastIndexOf('.');
            const ext = extIndex !== -1 ? fileName.substring(extIndex).toLowerCase() : '';
            
            let formatStr = '';
            if (ext === '.ttf') formatStr = " format('truetype')";
            else if (ext === '.otf') formatStr = " format('opentype')";
            else if (ext === '.woff') formatStr = " format('woff')";
            else if (ext === '.woff2') formatStr = " format('woff2')";
            
            // 1. Grouped family mapping (enables bold/italic toggles to resolve natively)
            markup += `@font-face { font-family: '${f.family}'; src: url('${absoluteFontUrl}')${formatStr}; font-weight: ${v.weight}; font-style: ${v.style}; }\n`;
            
            // 2. Direct variation mapping (enables selection as independent font-family)
            const fontName = extIndex !== -1 ? fileName.substring(0, extIndex) : fileName;
            markup += `@font-face { font-family: '${fontName}'; src: url('${absoluteFontUrl}')${formatStr}; }\n`;
          });
        });
        styleNode.innerHTML = markup;
      });
  }, [BACKEND_URL]);

  const applyCanvasFontMapping = (ctx, fontTarget, sizeTarget, weightTarget = '400', styleTarget = 'normal') => {
    const weightStr = weightTarget === '700' ? 'bold' : (weightTarget === '400' ? 'normal' : weightTarget);
    const styleStr = styleTarget || 'normal';
    let size = parseInt(sizeTarget);
    if (isNaN(size) || size <= 0) {
      size = 16;
    }
    ctx.font = `${styleStr} ${weightStr} ${size}px "${fontTarget}", sans-serif`;
  };

  const hexToRgba = (hex, alpha) => {
    if (!hex || hex === 'transparent') return `rgba(0, 0, 0, 0)`;
    let cleanHex = hex.replace('#', '');
    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split('').map(char => char + char).join('');
    }
    const num = parseInt(cleanHex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const convertColorToTransparent = (color) => {
    if (!color) return 'rgba(0,0,0,0)';
    if (color.startsWith('rgb')) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        return `rgba(${matches[0]}, ${matches[1]}, ${matches[2]}, 0)`;
      }
    }
    return hexToRgba(color, 0);
  };

  const buildOverlayStyle = (targetCtx, layer, width, height, opacityVal, centerOffsetX = 0, centerOffsetY = 0) => {
    const c1 = layer.overlayColor || '#ff0000';
    if (layer.overlayType === 'gradient') {
      const angleRad = ((layer.overlayGradientAngle !== undefined ? layer.overlayGradientAngle : 90) * Math.PI) / 180;
      const dx = Math.cos(angleRad);
      const dy = Math.sin(angleRad);
      const projLength = Math.abs(width * Math.cos(angleRad)) + Math.abs(height * Math.sin(angleRad));
      const scaleVal = layer.overlayGradientLength !== undefined ? layer.overlayGradientLength : 100;
      const gradSpan = projLength * (scaleVal / 100);
      
      const x0 = centerOffsetX - (gradSpan / 2) * dx;
      const y0 = centerOffsetY - (gradSpan / 2) * dy;
      const x1 = centerOffsetX + (gradSpan / 2) * dx;
      const y1 = centerOffsetY + (gradSpan / 2) * dy;
      
      const grad = targetCtx.createLinearGradient(x0, y0, x1, y1);
      const c2 = layer.overlayColor2 || '#000000';
      grad.addColorStop(0, hexToRgba(c1, opacityVal));
      if (c2 === 'transparent') {
        grad.addColorStop(1, hexToRgba(c1, 0));
      } else {
        grad.addColorStop(1, hexToRgba(c2, opacityVal));
      }
      return grad;
    }
    return hexToRgba(c1, opacityVal);
  };

// LIVE RUNTIME WORKBENCH VIEWPORT MOTOR LOOP - FORMAT SYNCHRONIZED
useEffect(() => {
  let animationFrameId;
  const canvas = canvasRef.current;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');

  // Quantize time steps to match the exact hardcoded centisecond delay of the GIF format
  const frameDelayMs = 1000 / fps; 
  const storedCentiseconds = Math.round(frameDelayMs / 10);
  const exactPlaybackDelayMs = storedCentiseconds * 10; // e.g., 40ms for 24fps instead of 41.66ms

  let lastTimestamp = 0;
  let accumulatedSimulatedTime = 0;

  const renderLoop = (timestamp) => {
    if (!lastTimestamp) lastTimestamp = timestamp;
    const delta = timestamp - lastTimestamp;
    lastTimestamp = timestamp;

    // Track accumulated time using the exact playback interval rate of the saved asset
    accumulatedSimulatedTime += delta;

    if (isTransparent) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    
    // MULTI-LAYER GRAPHIC STUDIO PREVIEW RENDERER
    layers.forEach((layer) => {
      if (!layer.visible) return;

      ctx.save();
      // Apply isolated layer opacity
      ctx.globalAlpha = layer.opacity !== undefined ? layer.opacity : 1.0;
      const overlayOpacityVal = layer.overlayOpacity !== undefined ? layer.overlayOpacity / 100 : 1.0;
      ctx.letterSpacing = `${layer.letterSpacing || 0}px`;
      applyCanvasFontMapping(ctx, layer.font, layer.fontSize || 38, layer.fontWeight || '400', layer.fontStyle || 'normal');

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      let x = canvas.width / 2 + (layer.xOffset || 0);
      let y = canvas.height / 2 + (layer.yOffset || 0);
      let scaleX = 1;
      let scaleY = 1;
      let rotation = ((layer.rotationOffset || 0) * Math.PI) / 180;
      let skewX = 0;

      const timeFactor = accumulatedSimulatedTime * ((layer.speed || 50) * 0.0001);
      let rawT = Math.sin(timeFactor);
      let t = rawT;
      
      if (layer.smoothingMode === 'easeInOut') {
        t = rawT * rawT * (3 - 2 * rawT);
      } else if (layer.smoothingMode === 'sharp') {
        t = rawT > 0 ? 1 : -1;
      }

      // Measure boundaries for dynamic placement effects
      const elementWidth = layer.type === 'text'
        ? ctx.measureText((layer.text || '').toUpperCase()).width
        : (layer.shapeWidth || 160);
      const elementHeight = layer.type === 'text'
        ? (layer.fontSize || 38)
        : (layer.shapeHeight || 160);

      switch (layer.animationType) {
        case 'bounce':
          const cycleMs = (2 * Math.PI) / ((layer.speed || 50) * 0.0001);
          const loopMs = cycleMs / 2;
          const progress = (accumulatedSimulatedTime % loopMs) / loopMs;
          const bounceOffset = Math.abs(Math.sin(progress * Math.PI)) * (layer.distance || 25);
          if (layer.direction === 'down') y += bounceOffset;
          if (layer.direction === 'up') y -= bounceOffset;
          if (layer.direction === 'left') x -= bounceOffset;
          if (layer.direction === 'right') x += bounceOffset;
          if (Math.abs(Math.sin(progress * Math.PI)) > 0.7) {
            scaleY = 1 - ((layer.squish || 30) / 180);
            scaleX = 1 + ((layer.squish || 30) / 260);
          }
          break;
        case 'blinking':
          if (Math.floor(timeFactor * 2) % 2 !== 0) {
            ctx.restore();
            return;
          }
          break;
        case 'spin':
          rotation += timeFactor * ((layer.distance || 25) / 12);
          break;
        case 'breathe':
          const breatheScale = 1 + (Math.sin(timeFactor) * ((layer.distance || 25) / 240));
          scaleX = breatheScale;
          scaleY = breatheScale;
          break;
        case 'pulse':
          const pulseScale = Math.floor(timeFactor * 4) % 2 === 0 ? 1 : 1 + ((layer.squish || 30) / 120);
          scaleX = pulseScale;
          scaleY = pulseScale;
          break;
        case 'shake':
          x += Math.sin(timeFactor * 18) * (layer.distance || 25) * 0.3;
          y += Math.cos(timeFactor * 22) * (layer.distance || 25) * 0.3;
          break;
        case 'shear':
          skewX = t * ((layer.distance || 25) / 60);
          break;
        case 'rainbow':
          // prism color blending is calculated as fill color
          break;
        case 'drift':
          x += Math.sin(timeFactor * 2) * (layer.distance || 25) * 0.7;
          y += Math.sin(timeFactor) * Math.cos(timeFactor) * (layer.distance || 25) * 0.7;
          break;
        case 'glitch':
          const glitchStep = Math.floor(timeFactor * 6);
          const glitchNoise = Math.sin(glitchStep * 12345.67) * 100;
          const isGlitchFrame = (glitchNoise - Math.floor(glitchNoise)) > 0.75;
          if (isGlitchFrame) {
            x += (Math.sin(timeFactor * 30) > 0 ? 1 : -1) * (layer.distance || 25) * 0.3;
          }
          break;
        case 'zoom':
          const zoomFactor = 1 + (t * ((layer.distance || 25) / 240));
          scaleX = zoomFactor;
          scaleY = zoomFactor;
          break;
        case 'tilt':
          rotation += t * ((layer.distance || 25) * 0.015);
          break;
        case 'shadow':
          ctx.shadowColor = layer.color2;
          ctx.shadowBlur = ((t + 1) / 2) * (layer.distance || 25) * 0.8;
          break;
        case 'swing':
          x += t * (layer.distance || 25) * 0.8;
          break;
        case 'wobble':
          scaleX = 1 + t * ((layer.squish || 30) / 150);
          scaleY = 1 - t * ((layer.squish || 30) / 150);
          break;
        case 'underline':
          ctx.save();
          ctx.strokeStyle = layer.color2;
          ctx.lineWidth = 1.5 + ((layer.distance || 25) / 15);
          ctx.lineCap = 'round';
          ctx.shadowColor = layer.color2;
          ctx.shadowBlur = ((layer.distance || 25) * 0.25);
          ctx.beginPath();
          const activeWidthUnder = (elementWidth + 20) * (0.5 + 0.5 * ((t + 1) / 2));
          ctx.moveTo(x - activeWidthUnder / 2, y + elementHeight * 0.65);
          ctx.lineTo(x + activeWidthUnder / 2, y + elementHeight * 0.65);
          ctx.stroke();
          ctx.restore();
          break;
        case 'spotlight':
          const rSpot = parseInt((layer.color2 || '#d97706').slice(1, 3), 16) || 0;
          const gSpot = parseInt((layer.color2 || '#d97706').slice(3, 5), 16) || 0;
          const bSpot = parseInt((layer.color2 || '#d97706').slice(5, 7), 16) || 0;
          const alphaSpot = 0.2 + 0.15 * Math.abs(t);
          const spotlightRadius = elementWidth * ((layer.distance || 25) / 50);
          
          ctx.save();
          const sweepX = t * elementWidth * 0.6;
          const grad = ctx.createRadialGradient(
            x + sweepX, y, 5,
            x + sweepX, y, spotlightRadius
          );
          grad.addColorStop(0, `rgba(${rSpot}, ${gSpot}, ${bSpot}, ${alphaSpot})`);
          grad.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(x, y, spotlightRadius * 1.3, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
          break;
        case 'bracket':
          const bOffset = (((layer.distance || 25) * 0.8) + ((t + 1) / 2) * ((layer.distance || 25) * 0.4));
          const bL = x - elementWidth / 2 - bOffset;
          const bR = x + elementWidth / 2 + bOffset;
          const bT = y - elementHeight * 0.55;
          const bB = y + elementHeight * 0.55;
          const bLen = 6 + ((layer.distance || 25) / 3);
          
          ctx.save();
          ctx.strokeStyle = layer.color2;
          ctx.lineWidth = 1.5 + ((layer.distance || 25) / 15);
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.beginPath();
          ctx.moveTo(bL + bLen, bT);
          ctx.lineTo(bL, bT);
          ctx.lineTo(bL, bB);
          ctx.lineTo(bL + bLen, bB);
          ctx.stroke();
          
          ctx.beginPath();
          ctx.moveTo(bR - bLen, bT);
          ctx.lineTo(bR, bT);
          ctx.lineTo(bR, bB);
          ctx.lineTo(bR - bLen, bB);
          ctx.stroke();
          ctx.restore();
          break;
        case 'sparkle':
          const sparkPoints = [
            { cx: x - elementWidth / 2 - (layer.distance || 25) * 0.8, cy: y - elementHeight * 0.45, phase: 0 },
            { cx: x + elementWidth / 2 + (layer.distance || 25) * 0.8, cy: y - elementHeight * 0.25, phase: Math.PI / 2 },
            { cx: x - elementWidth / 2 - (layer.distance || 25) * 1.2, cy: y + elementHeight * 0.35, phase: Math.PI },
            { cx: x + elementWidth / 2 + (layer.distance || 25) * 1.2, cy: y + elementHeight * 0.45, phase: Math.PI * 1.5 }
          ];
          
          ctx.save();
          const renderSparkle = (cxVal, cyVal, sizeVal) => {
            if (sizeVal <= 0) return;
            ctx.fillStyle = layer.color2;
            ctx.beginPath();
            ctx.moveTo(cxVal, cyVal - sizeVal);
            ctx.quadraticCurveTo(cxVal, cyVal, cxVal + sizeVal, cyVal);
            ctx.quadraticCurveTo(cxVal, cyVal, cxVal, cyVal + sizeVal);
            ctx.quadraticCurveTo(cxVal, cyVal, cxVal - sizeVal, cyVal);
            ctx.quadraticCurveTo(cxVal, cyVal, cxVal, cyVal - sizeVal);
            ctx.fill();
          };
          
          sparkPoints.forEach(pt => {
            const activeT = Math.sin(timeFactor * 3.5 + pt.phase);
            const sparkSize = Math.max(0, activeT) * ((layer.distance || 25) / 4);
            renderSparkle(pt.cx, pt.cy, sparkSize);
          });
          ctx.restore();
          break;
      }

      // Apply Fill custom animations
      let finalFillStyle = layer.color1;
      if (layer.fillType === 'gradient') {
        const w = layer.type === 'text' ? elementWidth : (layer.shapeWidth || 160);
        const h = layer.type === 'text' ? elementHeight : (layer.shapeHeight || 160);
        const angleRad = ((layer.gradientAngle !== undefined ? layer.gradientAngle : 90) * Math.PI) / 180;
        const dx = Math.cos(angleRad);
        const dy = Math.sin(angleRad);
        const projLength = Math.abs(w * Math.cos(angleRad)) + Math.abs(h * Math.sin(angleRad));
        const gradSpan = projLength * ((layer.gradientLength !== undefined ? layer.gradientLength : 100) / 100);
        
        const x0 = -(gradSpan / 2) * dx;
        const y0 = -(gradSpan / 2) * dy;
        const x1 = (gradSpan / 2) * dx;
        const y1 = (gradSpan / 2) * dy;
        
        const grad = ctx.createLinearGradient(x0, y0, x1, y1);
        const c1 = layer.gradientColor1 || layer.color1 || '#d97706';
        let c2 = layer.gradientColor2 || layer.color2 || '#0f091c';
        if (layer.gradientFadeToTransparent) {
          c2 = convertColorToTransparent(c1);
        }
        grad.addColorStop(0, c1);
        grad.addColorStop(1, c2);
        finalFillStyle = grad;
      } else {
        if (layer.animationType === 'flicker') {
          const randSeed = Math.sin(accumulatedSimulatedTime * 0.05) * 1000;
          finalFillStyle = (randSeed - Math.floor(randSeed)) > 0.3 ? layer.color1 : layer.color2;
        } else if (layer.animationType === 'rainbow') {
          const prismFactor = (t + 1) / 2;
          finalFillStyle = interpolateColor(layer.color1, layer.color2, prismFactor);
        } else if (layer.animationType === 'glitch') {
          const glitchStep = Math.floor(timeFactor * 6);
          const glitchNoise = Math.sin(glitchStep * 12345.67) * 100;
          const isGlitchFrame = (glitchNoise - Math.floor(glitchNoise)) > 0.75;
          finalFillStyle = isGlitchFrame ? layer.color2 : layer.color1;
        } else if (layer.animationType === 'liquid') {
          const W = elementWidth;
          const H = elementHeight;
          const L = Math.sqrt(W * W + H * H) || 100;
          const angle = Math.PI / 4;
          const dx = Math.cos(angle);
          const dy = Math.sin(angle);
          const shift = Math.sin(timeFactor * 0.5) * L;
          const x0 = -W/2 + shift * dx - L * dx;
          const y0 = -H/2 + shift * dy - L * dy;
          const x1 = W/2 + shift * dx + L * dx;
          const y1 = H/2 + shift * dy + L * dy;
          const grad = ctx.createLinearGradient(x0, y0, x1, y1);
          const col1 = layer.liquidColor1 || '#ff0055';
          const col2 = layer.liquidColor2 || '#7a00ff';
          const col3 = layer.liquidColor3 || '#00e5ff';
          grad.addColorStop(0, col1);
          grad.addColorStop(0.33, col2);
          grad.addColorStop(0.66, col3);
          grad.addColorStop(1, col1);
          finalFillStyle = grad;
        } else if (layer.animationType === 'shimmer') {
          const W = elementWidth;
          const progress = (timeFactor * 0.3) % 2.0;
          const shift = (progress - 1.0) * W;
          const x0 = -W/2 + shift - W/2;
          const y0 = 0;
          const x1 = W/2 + shift + W/2;
          const y1 = 0;
          const grad = ctx.createLinearGradient(x0, y0, x1, y1);
          const baseColor = layer.shimmerBaseColor || layer.color1 || '#d97706';
          const lightColor = layer.shimmerLightColor || '#ffffff';
          grad.addColorStop(0, baseColor);
          grad.addColorStop(0.4, baseColor);
          grad.addColorStop(0.5, lightColor);
          grad.addColorStop(0.6, baseColor);
          grad.addColorStop(1, baseColor);
          finalFillStyle = grad;
        }
      }

      // Stroke customization
      let finalStrokeStyle = layer.strokeColor || layer.color2 || '#0f091c';
      if (layer.type === 'shape' && layer.fillStyle === 'transparent' && ['rainbow', 'flicker', 'glitch', 'liquid', 'shimmer'].includes(layer.animationType)) {
        if (['liquid', 'shimmer'].includes(layer.animationType)) {
          finalStrokeStyle = finalFillStyle;
        } else {
          const baseStroke = layer.strokeColor || layer.color2 || '#0f091c';
          if (layer.animationType === 'flicker') {
            const randSeed = Math.sin(accumulatedSimulatedTime * 0.05) * 1000;
            finalStrokeStyle = (randSeed - Math.floor(randSeed)) > 0.3 ? baseStroke : (layer.color2 || '#d97706');
          } else if (layer.animationType === 'rainbow') {
            const prismFactor = (t + 1) / 2;
            finalStrokeStyle = interpolateColor(baseStroke, layer.color2 || '#d97706', prismFactor);
          } else if (layer.animationType === 'glitch') {
            const glitchStep = Math.floor(timeFactor * 6);
            const glitchNoise = Math.sin(glitchStep * 12345.67) * 100;
            const isGlitchFrame = (glitchNoise - Math.floor(glitchNoise)) > 0.75;
            finalStrokeStyle = isGlitchFrame ? (layer.color2 || '#d97706') : baseStroke;
          }
        }
      }

      // Apply transformations
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.scale(scaleX, scaleY);
      if (skewX !== 0) {
        ctx.transform(1, 0, skewX, 1, 0, 0);
      }

      // Configure drop shadow if enabled
      if (layer.shadowEnabled) {
        ctx.shadowColor = layer.shadowColor || 'rgba(0,0,0,0.4)';
        ctx.shadowBlur = layer.shadowBlur !== undefined ? layer.shadowBlur : 10;
        ctx.shadowOffsetX = layer.shadowOffsetX !== undefined ? layer.shadowOffsetX : 4;
        ctx.shadowOffsetY = layer.shadowOffsetY !== undefined ? layer.shadowOffsetY : 4;
      } else {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      if (layer.type === 'text') {
        ctx.fillStyle = finalFillStyle;
        ctx.fillText((layer.text || '').toUpperCase(), 0, 0);
        if (layer.overlayColor) {
          ctx.save();
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          ctx.fillStyle = buildOverlayStyle(ctx, layer, elementWidth, elementHeight, overlayOpacityVal, 0, 0);
          ctx.fillText((layer.text || '').toUpperCase(), 0, 0);
          ctx.restore();
        }
      } else if (layer.type === 'shape') {
        ctx.beginPath();
        const w = layer.shapeWidth || 160;
        const h = layer.shapeHeight || 160;
        
        if (layer.shapeType === 'rect') {
          const rad = layer.cornerRadius !== undefined ? layer.cornerRadius : 16;
          drawRoundedRect(ctx, -w / 2, -h / 2, w, h, rad);
        } else if (layer.shapeType === 'circle') {
          const rad = Math.min(w, h) / 2;
          ctx.arc(0, 0, rad, 0, Math.PI * 2);
        } else if (layer.shapeType === 'triangle') {
          ctx.moveTo(0, -h / 2);
          ctx.lineTo(w / 2, h / 2);
          ctx.lineTo(-w / 2, h / 2);
          ctx.closePath();
        } else if (layer.shapeType === 'star') {
          const spikes = 5;
          const outer = Math.min(w, h) / 2;
          const inner = outer * 0.4;
          let rot = (Math.PI / 2) * 3;
          let step = Math.PI / spikes;
          ctx.moveTo(0, -outer);
          for (let idx = 0; idx < spikes; idx++) {
            ctx.lineTo(Math.cos(rot) * outer, Math.sin(rot) * outer);
            rot += step;
            ctx.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner);
            rot += step;
          }
          ctx.lineTo(0, -outer);
          ctx.closePath();
        }

        // Shape fills
        if (layer.fillStyle === 'solid') {
          ctx.fillStyle = finalFillStyle;
          ctx.fill();
          if (layer.overlayColor) {
            ctx.save();
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.fillStyle = buildOverlayStyle(ctx, layer, w, h, overlayOpacityVal, 0, 0);
            ctx.fill();
            ctx.restore();
          }
        } else if (layer.fillStyle === 'image' && layer.imageFillUrl) {
          ctx.save();
          ctx.clip();
          
          if (!layer._imgRef || layer._imgRef.src !== layer.imageFillUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = layer.imageFillUrl;
            img.onload = () => {
              layer._imgRef = img;
            };
          }
          
          if (layer._imgRef && layer._imgRef.complete) {
            const blurVal = layer.imageBlur || 0;
            const distortVal = layer.imageDistort || 0;
            
            const offscreen = document.createElement('canvas');
            offscreen.width = w;
            offscreen.height = h;
            const oCtx = offscreen.getContext('2d');
            
            oCtx.save();
            if (blurVal > 0) {
              oCtx.filter = `blur(${blurVal}px)`;
            }
            
            const img = layer._imgRef;
            if (distortVal > 0) {
              for (let dy = 0; dy < h; dy += 2) {
                const sineOffset = Math.sin(dy / 12 + timeFactor * 6) * distortVal * 0.3;
                oCtx.drawImage(
                  img,
                  0, Math.floor(dy * (img.height / h)), img.width, Math.max(1, Math.floor(2 * (img.height / h))),
                  sineOffset, dy, w, 2
                );
              }
            } else {
              oCtx.drawImage(img, 0, 0, w, h);
            }
            oCtx.restore();
            
            if (layer.overlayColor) {
              oCtx.save();
              oCtx.globalCompositeOperation = 'source-atop';
              oCtx.fillStyle = buildOverlayStyle(oCtx, layer, w, h, overlayOpacityVal, w / 2, h / 2);
              oCtx.fillRect(0, 0, w, h);
              oCtx.restore();
            }
            
            ctx.drawImage(offscreen, -w / 2, -h / 2);
          }
          ctx.restore();
        }

        if (layer.outlineStyle === 'solid') {
          ctx.strokeStyle = finalStrokeStyle;
          ctx.lineWidth = layer.strokeWidth || 3;
          ctx.stroke();
          if (layer.overlayColor) {
            ctx.save();
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.strokeStyle = buildOverlayStyle(ctx, layer, w, h, overlayOpacityVal, 0, 0);
            ctx.stroke();
            ctx.restore();
          }
        }
      } else if (layer.type === 'image') {
        const w = layer.shapeWidth || 160;
        const h = layer.shapeHeight || 160;
        if (layer.imageFillUrl) {
          if (!layer._imgRef || layer._imgRef.src !== layer.imageFillUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = layer.imageFillUrl;
            img.onload = () => {
              layer._imgRef = img;
            };
          }

          if (layer._imgRef && layer._imgRef.complete) {
            const blurVal = layer.imageBlur || 0;
            const distortVal = layer.imageDistort || 0;

            const offscreen = document.createElement('canvas');
            offscreen.width = w;
            offscreen.height = h;
            const oCtx = offscreen.getContext('2d');

            oCtx.save();
            if (blurVal > 0) {
              oCtx.filter = `blur(${blurVal}px)`;
            }

            const img = layer._imgRef;
            if (distortVal > 0) {
              for (let dy = 0; dy < h; dy += 2) {
                const sineOffset = Math.sin(dy / 12 + timeFactor * 6) * distortVal * 0.3;
                oCtx.drawImage(
                  img,
                  0, Math.floor(dy * (img.height / h)), img.width, Math.max(1, Math.floor(2 * (img.height / h))),
                  sineOffset, dy, w, 2
                );
              }
            } else {
              oCtx.drawImage(img, 0, 0, w, h);
            }
            oCtx.restore();
            
            if (layer.overlayColor) {
              oCtx.save();
              oCtx.globalCompositeOperation = 'source-atop';
              oCtx.fillStyle = buildOverlayStyle(oCtx, layer, w, h, overlayOpacityVal, w / 2, h / 2);
              oCtx.fillRect(0, 0, w, h);
              oCtx.restore();
            }

            ctx.drawImage(offscreen, -w / 2, -h / 2);
          }
        } else {
          ctx.save();
          ctx.strokeStyle = '#a855f7';
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 4]);
          ctx.strokeRect(-w / 2, -h / 2, w, h);
          ctx.fillStyle = 'rgba(168, 85, 247, 0.05)';
          ctx.fillRect(-w / 2, -h / 2, w, h);
          ctx.fillStyle = '#a855f7';
          ctx.font = 'bold 11px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('EMPTY IMAGE LAYER', 0, -6);
          ctx.font = '9px sans-serif';
          ctx.fillStyle = '#6b7280';
          ctx.fillText('Select image in Styling tab', 0, 10);
          ctx.restore();
        }
      }

      ctx.restore(); // restore layer transformation matrix
      ctx.restore(); // restore layer styles / custom font configuration
    });

    // Render Snapping & Alignment Guides if dragging
    if (isCanvasDragging && draggedLayerId) {
      const draggedLayer = layers.find(l => l.id === draggedLayerId);
      if (draggedLayer) {
        ctx.save();
        ctx.lineWidth = 2.0;
        ctx.setLineDash([8, 4]);
        ctx.strokeStyle = '#a855f7'; // Purple brand guide lines
        ctx.shadowColor = 'rgba(168, 85, 247, 0.5)';
        ctx.shadowBlur = 6;

        // Vertical center guide
        if (draggedLayer.xOffset === 0) {
          ctx.beginPath();
          ctx.moveTo(canvas.width / 2, 0);
          ctx.lineTo(canvas.width / 2, canvas.height);
          ctx.stroke();
        }

        // Horizontal center guide
        if (draggedLayer.yOffset === 0) {
          ctx.beginPath();
          ctx.moveTo(0, canvas.height / 2);
          ctx.lineTo(canvas.width, canvas.height / 2);
          ctx.stroke();
        }
        
        ctx.restore();
      }
    }

    animationFrameId = requestAnimationFrame(renderLoop);
  };

  animationFrameId = requestAnimationFrame(renderLoop);
  return () => cancelAnimationFrame(animationFrameId);
}, [layers, activeLayerId, isTransparent, bgColor, fps, customFonts, esCanvasWidth, esCanvasHeight, isCanvasDragging, draggedLayerId]);

// HIGH-DPI SUPERSAMPLED AUTOMATED PERIOD-LOOP MATCHING ENGINE
const generateAnimatedModuleGif = async (forcedFilename = null) => {
  setIsCompiling(true);
  const frameSequences = [];
  let exactGifFormatDelayMs = 100;
  const ULTRA_DPI_WIDTH = esCanvasWidth * 3.0;
  const ULTRA_DPI_HEIGHT = esCanvasHeight * 3.0;

  // MULTI-LAYER HIGH-DPI EXPORTER
  // 1. Base the loop duration on the active layer's speed (or the first layer)
  const activeLayer = layers.find(l => l.id === activeLayerId) || layers[0] || {};
  const refSpeed = activeLayer.speed || 50;
  const radiansPerMs = refSpeed * 0.0001;
  let singleCycleDurationMs = (2 * Math.PI) / radiansPerMs;

  if (activeLayer.animationType === 'bounce' || activeLayer.animationType === 'breathe') {
    singleCycleDurationMs = singleCycleDurationMs / 2;
  }

  // 2. Map out frame timing delays
  const targetedFrameDelayMs = 1000 / fps;
  const roundedCentiseconds = Math.round(targetedFrameDelayMs / 10);
  exactGifFormatDelayMs = roundedCentiseconds * 10; // Locked centiseconds

  let baseRequiredFrames = Math.round(singleCycleDurationMs / exactGifFormatDelayMs);

  if ((activeLayer.animationType === 'blinking' || activeLayer.animationType === 'pulse') && baseRequiredFrames % 2 !== 0) {
    baseRequiredFrames += 1;
  }

  let cycleMultiplierPadding = 1;
  if (baseRequiredFrames <= 8) {
    cycleMultiplierPadding = 3;
  } else if (baseRequiredFrames <= 12) {
    cycleMultiplierPadding = 2;
  }

  const hasAnimation = layers.some(l => l.animationType && l.animationType !== 'none');
  const optimizedTotalFrames = hasAnimation ? (baseRequiredFrames * cycleMultiplierPadding) : 1;

  const compileCanvas = document.createElement('canvas');
  compileCanvas.width = ULTRA_DPI_WIDTH;
  compileCanvas.height = ULTRA_DPI_HEIGHT;
  const compileCtx = compileCanvas.getContext('2d', { willReadFrequently: true });

  const scaleMultiplier = 3.0;

  // Render each synchronized frame in succession
  for (let i = 0; i < optimizedTotalFrames; i++) {
    if (isTransparent) {
      compileCtx.clearRect(0, 0, ULTRA_DPI_WIDTH, ULTRA_DPI_HEIGHT);
    } else {
      compileCtx.fillStyle = bgColor;
      compileCtx.fillRect(0, 0, ULTRA_DPI_WIDTH, ULTRA_DPI_HEIGHT);
    }
    compileCtx.shadowBlur = 0;
    compileCtx.shadowColor = 'transparent';

    // Iterate through all active layers back-to-front
    layers.forEach((layer) => {
      if (!layer.visible) return;

      compileCtx.save();
      // Apply isolated layer opacity in compiled output
      compileCtx.globalAlpha = layer.opacity !== undefined ? layer.opacity : 1.0;
      const overlayOpacityVal = layer.overlayOpacity !== undefined ? layer.overlayOpacity / 100 : 1.0;
      compileCtx.letterSpacing = `${(layer.letterSpacing || 0) * scaleMultiplier}px`;
      
      // High-DPI font styling setup
      const superFontSize = (layer.fontSize || 38) * scaleMultiplier;
      applyCanvasFontMapping(compileCtx, layer.font, superFontSize, layer.fontWeight || '400', layer.fontStyle || 'normal');

      compileCtx.textAlign = 'center';
      compileCtx.textBaseline = 'middle';

      let x = ULTRA_DPI_WIDTH / 2 + (layer.xOffset || 0) * scaleMultiplier;
      let y = ULTRA_DPI_HEIGHT / 2 + (layer.yOffset || 0) * scaleMultiplier;
      let scaleX = 1;
      let scaleY = 1;
      let rotation = ((layer.rotationOffset || 0) * Math.PI) / 180;
      let skewX = 0;

      const currentFrameTimeMs = i * exactGifFormatDelayMs;
      const timeFactor = currentFrameTimeMs * ((layer.speed || 50) * 0.0001);
      let rawT = Math.sin(timeFactor);
      let t = rawT;
      
      if (layer.smoothingMode === 'easeInOut') {
        t = rawT * rawT * (3 - 2 * rawT);
      } else if (layer.smoothingMode === 'sharp') {
        t = rawT > 0 ? 1 : -1;
      }

      const adjustedDistance = (layer.distance || 25) * scaleMultiplier;
      
      // Dynamic boundary size calculations at high-DPI scale
      const elementWidth = layer.type === 'text'
        ? compileCtx.measureText((layer.text || '').toUpperCase()).width
        : (layer.shapeWidth || 160) * scaleMultiplier;
      const elementHeight = layer.type === 'text'
        ? superFontSize
        : (layer.shapeHeight || 160) * scaleMultiplier;

      switch (layer.animationType) {
        case 'bounce':
          const cycleMs = (2 * Math.PI) / ((layer.speed || 50) * 0.0001);
          const loopMs = cycleMs / 2;
          const progress = (currentFrameTimeMs % loopMs) / loopMs;
          const bounceOffset = Math.abs(Math.sin(progress * Math.PI)) * adjustedDistance;
          if (layer.direction === 'down') y += bounceOffset;
          if (layer.direction === 'up') y -= bounceOffset;
          if (layer.direction === 'left') x -= bounceOffset;
          if (layer.direction === 'right') x += bounceOffset;
          if (Math.abs(Math.sin(progress * Math.PI)) > 0.7) {
            scaleY = 1 - ((layer.squish || 30) / 180);
            scaleX = 1 + ((layer.squish || 30) / 260);
          }
          break;
        case 'blinking':
          if (Math.floor(timeFactor * 2) % 2 !== 0) {
            compileCtx.restore();
            return;
          }
          break;
        case 'spin':
          rotation += timeFactor * ((layer.distance || 25) / 12);
          break;
        case 'breathe':
          const breatheScale = 1 + (Math.sin(timeFactor) * (adjustedDistance / 240));
          scaleX = breatheScale;
          scaleY = breatheScale;
          break;
        case 'pulse':
          const pulseScale = Math.floor(timeFactor * 4) % 2 === 0 ? 1 : 1 + ((layer.squish || 30) / 120);
          scaleX = pulseScale;
          scaleY = pulseScale;
          break;
        case 'shake':
          x += Math.sin(timeFactor * 18) * adjustedDistance * 0.3;
          y += Math.cos(timeFactor * 22) * adjustedDistance * 0.3;
          break;
        case 'shear':
          skewX = t * ((layer.distance || 25) / 60);
          break;
        case 'rainbow':
          // prism color blending is calculated as fill color
          break;
        case 'drift':
          x += Math.sin(timeFactor * 2) * adjustedDistance * 0.7;
          y += Math.sin(timeFactor) * Math.cos(timeFactor) * adjustedDistance * 0.7;
          break;
        case 'glitch':
          const glitchStep = Math.floor(timeFactor * 6);
          const glitchNoise = Math.sin(glitchStep * 12345.67) * 100;
          const isGlitchFrame = (glitchNoise - Math.floor(glitchNoise)) > 0.75;
          if (isGlitchFrame) {
            x += (Math.sin(timeFactor * 30) > 0 ? 1 : -1) * adjustedDistance * 0.3;
          }
          break;
        case 'zoom':
          const zoomFactor = 1 + (t * (adjustedDistance / 240));
          scaleX = zoomFactor;
          scaleY = zoomFactor;
          break;
        case 'tilt':
          rotation += t * ((layer.distance || 25) * 0.015);
          break;
        case 'shadow':
          compileCtx.shadowColor = layer.color2;
          compileCtx.shadowBlur = ((t + 1) / 2) * adjustedDistance * 0.8;
          break;
        case 'swing':
          x += t * adjustedDistance * 0.8;
          break;
        case 'wobble':
          scaleX = 1 + t * ((layer.squish || 30) / 150);
          scaleY = 1 - t * ((layer.squish || 30) / 150);
          break;
        case 'underline':
          compileCtx.save();
          compileCtx.strokeStyle = layer.color2;
          compileCtx.lineWidth = (1.5 + ((layer.distance || 25) / 15)) * scaleMultiplier;
          compileCtx.lineCap = 'round';
          compileCtx.shadowColor = layer.color2;
          compileCtx.shadowBlur = ((layer.distance || 25) * 0.25) * scaleMultiplier;
          compileCtx.beginPath();
          const activeWidthUnder = (elementWidth + 20 * scaleMultiplier) * (0.5 + 0.5 * ((t + 1) / 2));
          compileCtx.moveTo(x - activeWidthUnder / 2, y + elementHeight * 0.65);
          compileCtx.lineTo(x + activeWidthUnder / 2, y + elementHeight * 0.65);
          compileCtx.stroke();
          compileCtx.restore();
          break;
        case 'spotlight':
          const rSpot = parseInt((layer.color2 || '#d97706').slice(1, 3), 16) || 0;
          const gSpot = parseInt((layer.color2 || '#d97706').slice(3, 5), 16) || 0;
          const bSpot = parseInt((layer.color2 || '#d97706').slice(5, 7), 16) || 0;
          const alphaSpot = 0.2 + 0.15 * Math.abs(t);
          const spotlightRadius = elementWidth * ((layer.distance || 25) / 50);
          
          compileCtx.save();
          const sweepX = t * elementWidth * 0.6;
          const grad = compileCtx.createRadialGradient(
            x + sweepX, y, 5 * scaleMultiplier,
            x + sweepX, y, spotlightRadius
          );
          grad.addColorStop(0, `rgba(${rSpot}, ${gSpot}, ${bSpot}, ${alphaSpot})`);
          grad.addColorStop(1, 'rgba(0,0,0,0)');
          compileCtx.fillStyle = grad;
          compileCtx.beginPath();
          compileCtx.arc(x, y, spotlightRadius * 1.3, 0, Math.PI * 2);
          compileCtx.fill();
          compileCtx.restore();
          break;
        case 'bracket':
          const bOffset = (((layer.distance || 25) * 0.8) + ((t + 1) / 2) * ((layer.distance || 25) * 0.4)) * scaleMultiplier;
          const bL = x - elementWidth / 2 - bOffset;
          const bR = x + elementWidth / 2 + bOffset;
          const bT = y - elementHeight * 0.55;
          const bB = y + elementHeight * 0.55;
          const bLen = (6 + ((layer.distance || 25) / 3)) * scaleMultiplier;
          
          compileCtx.save();
          compileCtx.strokeStyle = layer.color2;
          compileCtx.lineWidth = (1.5 + ((layer.distance || 25) / 15)) * scaleMultiplier;
          compileCtx.lineCap = 'round';
          compileCtx.lineJoin = 'round';
          compileCtx.beginPath();
          compileCtx.moveTo(bL + bLen, bT);
          compileCtx.lineTo(bL, bT);
          compileCtx.lineTo(bL, bB);
          compileCtx.lineTo(bL + bLen, bB);
          compileCtx.stroke();
          
          compileCtx.beginPath();
          compileCtx.moveTo(bR - bLen, bT);
          compileCtx.lineTo(bR, bT);
          compileCtx.lineTo(bR, bB);
          compileCtx.lineTo(bR - bLen, bB);
          compileCtx.stroke();
          compileCtx.restore();
          break;
        case 'sparkle':
          const sparkPoints = [
            { cx: x - elementWidth / 2 - (layer.distance || 25) * 0.8 * scaleMultiplier, cy: y - elementHeight * 0.45, phase: 0 },
            { cx: x + elementWidth / 2 + (layer.distance || 25) * 0.8 * scaleMultiplier, cy: y - elementHeight * 0.25, phase: Math.PI / 2 },
            { cx: x - elementWidth / 2 - (layer.distance || 25) * 1.2 * scaleMultiplier, cy: y + elementHeight * 0.35, phase: Math.PI },
            { cx: x + elementWidth / 2 + (layer.distance || 25) * 1.2 * scaleMultiplier, cy: y + elementHeight * 0.45, phase: Math.PI * 1.5 }
          ];
          
          compileCtx.save();
          const renderSparkle = (cxVal, cyVal, sizeVal) => {
            if (sizeVal <= 0) return;
            compileCtx.fillStyle = layer.color2;
            compileCtx.beginPath();
            compileCtx.moveTo(cxVal, cyVal - sizeVal);
            compileCtx.quadraticCurveTo(cxVal, cyVal, cxVal + sizeVal, cyVal);
            compileCtx.quadraticCurveTo(cxVal, cyVal, cxVal, cyVal + sizeVal);
            compileCtx.quadraticCurveTo(cxVal, cyVal, cxVal - sizeVal, cyVal);
            compileCtx.quadraticCurveTo(cxVal, cyVal, cxVal, cyVal - sizeVal);
            compileCtx.fill();
          };
          
          sparkPoints.forEach(pt => {
            const activeT = Math.sin(timeFactor * 3.5 + pt.phase);
            const sparkSize = Math.max(0, activeT) * ((layer.distance || 25) * scaleMultiplier / 4);
            renderSparkle(pt.cx, pt.cy, sparkSize);
          });
          compileCtx.restore();
          break;
      }

      // Set active layer drawing colors inside compiler
      let finalFillStyle = layer.color1;
      if (layer.fillType === 'gradient') {
        const w = layer.type === 'text' ? elementWidth : (layer.shapeWidth || 160) * scaleMultiplier;
        const h = layer.type === 'text' ? elementHeight : (layer.shapeHeight || 160) * scaleMultiplier;
        const angleRad = ((layer.gradientAngle !== undefined ? layer.gradientAngle : 90) * Math.PI) / 180;
        const dx = Math.cos(angleRad);
        const dy = Math.sin(angleRad);
        const projLength = Math.abs(w * Math.cos(angleRad)) + Math.abs(h * Math.sin(angleRad));
        const gradSpan = projLength * ((layer.gradientLength !== undefined ? layer.gradientLength : 100) / 100);
        
        const x0 = -(gradSpan / 2) * dx;
        const y0 = -(gradSpan / 2) * dy;
        const x1 = (gradSpan / 2) * dx;
        const y1 = (gradSpan / 2) * dy;
        
        const grad = compileCtx.createLinearGradient(x0, y0, x1, y1);
        const c1 = layer.gradientColor1 || layer.color1 || '#d97706';
        let c2 = layer.gradientColor2 || layer.color2 || '#0f091c';
        if (layer.gradientFadeToTransparent) {
          c2 = convertColorToTransparent(c1);
        }
        grad.addColorStop(0, c1);
        grad.addColorStop(1, c2);
        finalFillStyle = grad;
      } else {
        if (layer.animationType === 'flicker') {
          const randSeed = Math.sin(currentFrameTimeMs * 0.05) * 1000;
          finalFillStyle = (randSeed - Math.floor(randSeed)) > 0.3 ? layer.color1 : layer.color2;
        } else if (layer.animationType === 'rainbow') {
          const prismFactor = (t + 1) / 2;
          finalFillStyle = interpolateColor(layer.color1, layer.color2, prismFactor);
        } else if (layer.animationType === 'glitch') {
          const glitchStep = Math.floor(timeFactor * 6);
          const glitchNoise = Math.sin(glitchStep * 12345.67) * 100;
          const isGlitchFrame = (glitchNoise - Math.floor(glitchNoise)) > 0.75;
          finalFillStyle = isGlitchFrame ? layer.color2 : layer.color1;
        } else if (layer.animationType === 'liquid') {
          const W = elementWidth;
          const H = elementHeight;
          const L = Math.sqrt(W * W + H * H) || 100;
          const angle = Math.PI / 4;
          const dx = Math.cos(angle);
          const dy = Math.sin(angle);
          const shift = Math.sin(timeFactor * 0.5) * L;
          const x0 = -W/2 + shift * dx - L * dx;
          const y0 = -H/2 + shift * dy - L * dy;
          const x1 = W/2 + shift * dx + L * dx;
          const y1 = H/2 + shift * dy + L * dy;
          const grad = compileCtx.createLinearGradient(x0, y0, x1, y1);
          const col1 = layer.liquidColor1 || '#ff0055';
          const col2 = layer.liquidColor2 || '#7a00ff';
          const col3 = layer.liquidColor3 || '#00e5ff';
          grad.addColorStop(0, col1);
          grad.addColorStop(0.33, col2);
          grad.addColorStop(0.66, col3);
          grad.addColorStop(1, col1);
          finalFillStyle = grad;
        } else if (layer.animationType === 'shimmer') {
          const W = elementWidth;
          const progress = (timeFactor * 0.3) % 2.0;
          const shift = (progress - 1.0) * W;
          const x0 = -W/2 + shift - W/2;
          const y0 = 0;
          const x1 = W/2 + shift + W/2;
          const y1 = 0;
          const grad = compileCtx.createLinearGradient(x0, y0, x1, y1);
          const baseColor = layer.shimmerBaseColor || layer.color1 || '#d97706';
          const lightColor = layer.shimmerLightColor || '#ffffff';
          grad.addColorStop(0, baseColor);
          grad.addColorStop(0.4, baseColor);
          grad.addColorStop(0.5, lightColor);
          grad.addColorStop(0.6, baseColor);
          grad.addColorStop(1, baseColor);
          finalFillStyle = grad;
        }
      }

      // Set stroke outline style inside compiler
      let finalStrokeStyle = layer.strokeColor || layer.color2 || '#0f091c';
      if (layer.type === 'shape' && layer.fillStyle === 'transparent' && ['rainbow', 'flicker', 'glitch', 'liquid', 'shimmer'].includes(layer.animationType)) {
        if (['liquid', 'shimmer'].includes(layer.animationType)) {
          finalStrokeStyle = finalFillStyle;
        } else {
          const baseStroke = layer.strokeColor || layer.color2 || '#0f091c';
          if (layer.animationType === 'flicker') {
            const randSeed = Math.sin(currentFrameTimeMs * 0.05) * 1000;
            finalStrokeStyle = (randSeed - Math.floor(randSeed)) > 0.3 ? baseStroke : (layer.color2 || '#d97706');
          } else if (layer.animationType === 'rainbow') {
            const prismFactor = (t + 1) / 2;
            finalStrokeStyle = interpolateColor(baseStroke, layer.color2 || '#d97706', prismFactor);
          } else if (layer.animationType === 'glitch') {
            const glitchStep = Math.floor(timeFactor * 6);
            const glitchNoise = Math.sin(glitchStep * 12345.67) * 100;
            const isGlitchFrame = (glitchNoise - Math.floor(glitchNoise)) > 0.75;
            finalStrokeStyle = isGlitchFrame ? (layer.color2 || '#d97706') : baseStroke;
          }
        }
      }

      // Apply transformations
      compileCtx.save();
      compileCtx.translate(x, y);
      compileCtx.rotate(rotation);
      compileCtx.scale(scaleX, scaleY);
      if (skewX !== 0) {
        compileCtx.transform(1, 0, skewX, 1, 0, 0);
      }

      // Configure drop shadow if enabled in compiled output
      if (layer.shadowEnabled) {
        compileCtx.shadowColor = layer.shadowColor || 'rgba(0,0,0,0.4)';
        compileCtx.shadowBlur = (layer.shadowBlur !== undefined ? layer.shadowBlur : 10) * scaleMultiplier;
        compileCtx.shadowOffsetX = (layer.shadowOffsetX !== undefined ? layer.shadowOffsetX : 4) * scaleMultiplier;
        compileCtx.shadowOffsetY = (layer.shadowOffsetY !== undefined ? layer.shadowOffsetY : 4) * scaleMultiplier;
      } else {
        compileCtx.shadowColor = 'transparent';
        compileCtx.shadowBlur = 0;
        compileCtx.shadowOffsetX = 0;
        compileCtx.shadowOffsetY = 0;
      }

      if (layer.type === 'text') {
        compileCtx.fillStyle = finalFillStyle;
        compileCtx.fillText((layer.text || '').toUpperCase(), 0, 0);
        if (layer.overlayColor) {
          compileCtx.save();
          compileCtx.shadowColor = 'transparent';
          compileCtx.shadowBlur = 0;
          compileCtx.shadowOffsetX = 0;
          compileCtx.shadowOffsetY = 0;
          compileCtx.fillStyle = buildOverlayStyle(compileCtx, layer, elementWidth, elementHeight, overlayOpacityVal, 0, 0);
          compileCtx.fillText((layer.text || '').toUpperCase(), 0, 0);
          compileCtx.restore();
        }
      } else if (layer.type === 'shape') {
        compileCtx.beginPath();
        const w = (layer.shapeWidth || 160) * scaleMultiplier;
        const h = (layer.shapeHeight || 160) * scaleMultiplier;
        
        if (layer.shapeType === 'rect') {
          const rad = (layer.cornerRadius !== undefined ? layer.cornerRadius : 16) * scaleMultiplier;
          drawRoundedRect(compileCtx, -w / 2, -h / 2, w, h, rad);
        } else if (layer.shapeType === 'circle') {
          const rad = Math.min(w, h) / 2;
          compileCtx.arc(0, 0, rad, 0, Math.PI * 2);
        } else if (layer.shapeType === 'triangle') {
          compileCtx.moveTo(0, -h / 2);
          compileCtx.lineTo(w / 2, h / 2);
          compileCtx.lineTo(-w / 2, h / 2);
          compileCtx.closePath();
        } else if (layer.shapeType === 'star') {
          const spikes = 5;
          const outer = Math.min(w, h) / 2;
          const inner = outer * 0.4;
          let rot = (Math.PI / 2) * 3;
          let step = Math.PI / spikes;
          compileCtx.moveTo(0, -outer);
          for (let idx = 0; idx < spikes; idx++) {
            compileCtx.lineTo(Math.cos(rot) * outer, Math.sin(rot) * outer);
            rot += step;
            compileCtx.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner);
            rot += step;
          }
          compileCtx.lineTo(0, -outer);
          compileCtx.closePath();
        }

        // Shape fills in compiler
        if (layer.fillStyle === 'solid') {
          compileCtx.fillStyle = finalFillStyle;
          compileCtx.fill();
          if (layer.overlayColor) {
            compileCtx.save();
            compileCtx.shadowColor = 'transparent';
            compileCtx.shadowBlur = 0;
            compileCtx.shadowOffsetX = 0;
            compileCtx.shadowOffsetY = 0;
            compileCtx.fillStyle = buildOverlayStyle(compileCtx, layer, w, h, overlayOpacityVal, 0, 0);
            compileCtx.fill();
            compileCtx.restore();
          }
        } else if (layer.fillStyle === 'image' && layer.imageFillUrl) {
          compileCtx.save();
          compileCtx.clip();
          
          if (layer._imgRef && layer._imgRef.complete) {
            const blurVal = (layer.imageBlur || 0) * scaleMultiplier;
            const distortVal = (layer.imageDistort || 0) * scaleMultiplier;
            
            const offscreen = document.createElement('canvas');
            offscreen.width = w;
            offscreen.height = h;
            const oCtx = offscreen.getContext('2d');
            
            oCtx.save();
            if (blurVal > 0) {
              oCtx.filter = `blur(${blurVal}px)`;
            }
            
            const img = layer._imgRef;
            if (distortVal > 0) {
              for (let dy = 0; dy < h; dy += 2) {
                const sineOffset = Math.sin(dy / (12 * scaleMultiplier) + timeFactor * 6) * distortVal * 0.3;
                oCtx.drawImage(
                  img,
                  0, Math.floor(dy * (img.height / h)), img.width, Math.max(1, Math.floor(2 * (img.height / h))),
                  sineOffset, dy, w, 2
                );
              }
            } else {
              oCtx.drawImage(img, 0, 0, w, h);
            }
            oCtx.restore();
            
            if (layer.overlayColor) {
               oCtx.save();
               oCtx.globalCompositeOperation = 'source-atop';
               oCtx.fillStyle = buildOverlayStyle(oCtx, layer, w, h, overlayOpacityVal, w / 2, h / 2);
               oCtx.fillRect(0, 0, w, h);
               oCtx.restore();
             }
            
            compileCtx.drawImage(offscreen, -w / 2, -h / 2);
          }
          compileCtx.restore();
        }

        if (layer.outlineStyle === 'solid') {
          compileCtx.strokeStyle = finalStrokeStyle;
          compileCtx.lineWidth = (layer.strokeWidth || 3) * scaleMultiplier;
          compileCtx.stroke();
          if (layer.overlayColor) {
            compileCtx.save();
            compileCtx.shadowColor = 'transparent';
            compileCtx.shadowBlur = 0;
            compileCtx.shadowOffsetX = 0;
            compileCtx.shadowOffsetY = 0;
            compileCtx.strokeStyle = buildOverlayStyle(compileCtx, layer, w, h, overlayOpacityVal, 0, 0);
            compileCtx.stroke();
            compileCtx.restore();
          }
        }
      } else if (layer.type === 'image') {
        const w = (layer.shapeWidth || 160) * scaleMultiplier;
        const h = (layer.shapeHeight || 160) * scaleMultiplier;
        if (layer.imageFillUrl) {
          if (!layer._imgRef || layer._imgRef.src !== layer.imageFillUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = layer.imageFillUrl;
            img.onload = () => {
              layer._imgRef = img;
            };
          }

          if (layer._imgRef && layer._imgRef.complete) {
            const blurVal = (layer.imageBlur || 0) * scaleMultiplier;
            const distortVal = (layer.imageDistort || 0) * scaleMultiplier;

            const offscreen = document.createElement('canvas');
            offscreen.width = w;
            offscreen.height = h;
            const oCtx = offscreen.getContext('2d');

            oCtx.save();
            if (blurVal > 0) {
              oCtx.filter = `blur(${blurVal}px)`;
            }

            const img = layer._imgRef;
            if (distortVal > 0) {
              for (let dy = 0; dy < h; dy += 2) {
                const sineOffset = Math.sin(dy / (12 * scaleMultiplier) + timeFactor * 6) * distortVal * 0.3;
                oCtx.drawImage(
                  img,
                  0, Math.floor(dy * (img.height / h)), img.width, Math.max(1, Math.floor(2 * (img.height / h))),
                  sineOffset, dy, w, 2
                );
              }
            } else {
              oCtx.drawImage(img, 0, 0, w, h);
            }
            oCtx.restore();

            if (layer.overlayColor) {
              oCtx.save();
              oCtx.globalCompositeOperation = 'source-atop';
              oCtx.fillStyle = buildOverlayStyle(oCtx, layer, w, h, overlayOpacityVal, w / 2, h / 2);
              oCtx.fillRect(0, 0, w, h);
              oCtx.restore();
            }

            compileCtx.drawImage(offscreen, -w / 2, -h / 2);
          }
        }
      }

      compileCtx.restore(); // restore layer transformation matrix
      compileCtx.restore(); // restore layer styles / custom font configuration
    });

    frameSequences.push(compileCanvas.toDataURL('image/png'));
  }

  const designMetadata = {
    elementName,
    layers,
    isTransparent,
    bgColor,
    fps,
    esCanvasWidth,
    esCanvasHeight
  };

  let ext = 'gif';
  if (!hasAnimation) {
    ext = isTransparent ? 'png' : 'jpg';
  }

  let finalFileName = '';
  if (forcedFilename) {
    finalFileName = forcedFilename.replace(/\.(gif|png|jpg|jpeg)$/i, `.${ext}`);
  } else {
    finalFileName = `${elementName.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'StudioElement'}_${Date.now()}.${ext}`;
  }

  try {
    const response = await fetch(`${BACKEND_URL}/api/upload-created-asset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pngFrames: frameSequences,
        width: ULTRA_DPI_WIDTH,
        height: ULTRA_DPI_HEIGHT,
        fps: Math.round(1000 / exactGifFormatDelayMs),
        isTransparent: isTransparent,
        name: finalFileName,
        metadata: designMetadata
      })
    });

    let data;
    const responseText = await response.text();
    try {
      data = JSON.parse(responseText);
    } catch (parseErr) {
      if (response.status === 413) {
        throw new Error('Animation payload exceeds server limit (413 Request Entity Too Large). Try reducing canvas scale or frame count.');
      } else if (response.status === 504) {
        throw new Error('Server timed out generating the animation (504 Gateway Timeout). Please try again with fewer frames.');
      } else if (response.status === 507) {
        throw new Error('Server storage is full (507 Insufficient Storage). Please delete unused files in Storage Management.');
      } else {
        throw new Error(`Server returned error status ${response.status}.`);
      }
    }

    if (!response.ok || !data.success) {
      throw new Error(data?.error || `Server compile failed (Status ${response.status})`);
    }

    setExportedFileSize(data.sizeInBytes);
    alert('High-DPI Compact Layout Exported Successfully with perfect speed alignment!');
  } catch (err) {
    alert('Server compile rejection: ' + err.message);
  } finally {
    setIsCompiling(false);
  }
};

  const handleExportButtonClick = () => {
    if (editingAssetFile) {
      setShowSaveChoiceModal(true);
    } else {
      generateAnimatedModuleGif(null);
    }
  };

  const handleSaveProject = async () => {
    let thumbnailDataUrl = '';
    if (canvasRef.current) {
      thumbnailDataUrl = canvasRef.current.toDataURL('image/png');
    }

    const isNameChanged = currentProjectId && originalProjectName && elementName.trim() !== originalProjectName.trim();
    const saveId = isNameChanged ? null : currentProjectId;

    try {
      const response = await fetch(`${BACKEND_URL}/api/element-studio/projects/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: saveId,
          name: elementName,
          layers,
          isTransparent,
          bgColor,
          fps,
          esCanvasWidth,
          esCanvasHeight,
          thumbnail: thumbnailDataUrl
        })
      });

      let data;
      const responseText = await response.text();
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        if (response.status === 413) {
          throw new Error('Project data exceeds server upload limit (413 Request Entity Too Large).');
        } else if (response.status === 507) {
          throw new Error('Server storage is full (507 Insufficient Storage).');
        } else {
          throw new Error(`Server returned error status ${response.status}.`);
        }
      }

      if (data && data.success) {
        setCurrentProjectId(data.id);
        setOriginalProjectName(elementName);
        const newParams = new URLSearchParams(window.location.search);
        newParams.set('project', data.id);
        navigate(`/element-studio?${newParams.toString()}`, { replace: true });
        alert(isNameChanged ? '✨ Project name changed. Saved as a new project copy!' : '✨ Project progress saved successfully!');
      } else {
        throw new Error(data?.error || 'Unknown error');
      }
    } catch (err) {
      alert('Save Project Failed: ' + err.message);
    }
  };

  const fetchProjectsList = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/element-studio/projects`);
      if (res.ok) {
        const data = await res.json();
        setProjectsList(data);
      }
    } catch (err) {
      console.error('Failed to load projects list:', err);
    }
  };

  const loadProjectIntoEditor = (project) => {
    navigate(`/element-studio?project=${project.id}`, { replace: true });
    setCurrentProjectId(project.id);
    setEditingAssetFile(null);
    setExportedFileSize(null);

    fetch(`${BACKEND_URL}/api/element-studio/projects/${project.id}`)
      .then(res => {
        if (!res.ok) throw new Error('Project not found');
        return res.json();
      })
      .then(data => {
        if (data.layers) setLayers(data.layers);
        if (data.activeLayerId) setActiveLayerId(data.activeLayerId || data.layers[0]?.id || '');
        if (data.elementName) {
          setElementName(data.elementName);
          setOriginalProjectName(data.elementName);
        }
        if (data.isTransparent !== undefined) setIsTransparent(data.isTransparent);
        if (data.bgColor) setBgColor(data.bgColor);
        if (data.esCanvasWidth) setEsCanvasWidth(data.esCanvasWidth);
        if (data.esCanvasHeight) setEsCanvasHeight(data.esCanvasHeight);
        if (data.fps) setFps(data.fps);
        setShowProjectsModal(false);
      })
      .catch(err => alert('Failed to open project: ' + err.message));
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to permanently delete this project?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/element-studio/projects/${projectId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchProjectsList();
        if (currentProjectId === projectId) {
          setCurrentProjectId(null);
          navigate('/element-studio', { replace: true });
        }
      }
    } catch (err) {
      alert('Failed to delete project: ' + err.message);
    }
  };

  const handleDuplicateProject = async (projectId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/element-studio/projects/duplicate/${projectId}`, {
        method: 'POST'
      });
      if (res.ok) {
        fetchProjectsList();
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to duplicate project');
      }
    } catch (err) {
      alert('Failed to duplicate project: ' + err.message);
    }
  };


  const calculateEstimatedFileSize = () => {
    const hasAnimation = layers.some(l => l.animationType && l.animationType !== 'none');
    const ULTRA_DPI_WIDTH = esCanvasWidth * 3.0;
    const ULTRA_DPI_HEIGHT = esCanvasHeight * 3.0;
    const totalPixels = ULTRA_DPI_WIDTH * ULTRA_DPI_HEIGHT;

    let frames = 1;
    if (hasAnimation) {
      const activeLayer = layers.find(l => l.id === activeLayerId) || layers[0] || {};
      const refSpeed = activeLayer.speed || 50;
      const radiansPerMs = refSpeed * 0.0001;
      let singleCycleDurationMs = (2 * Math.PI) / radiansPerMs;
      if (activeLayer.animationType === 'bounce' || activeLayer.animationType === 'breathe') {
        singleCycleDurationMs = singleCycleDurationMs / 2;
      }
      const targetedFrameDelayMs = 1000 / fps;
      const roundedCentiseconds = Math.round(targetedFrameDelayMs / 10);
      const exactGifFormatDelayMs = roundedCentiseconds * 10;
      let baseRequiredFrames = Math.round(singleCycleDurationMs / exactGifFormatDelayMs);
      if ((activeLayer.animationType === 'blinking' || activeLayer.animationType === 'pulse') && baseRequiredFrames % 2 !== 0) {
        baseRequiredFrames += 1;
      }
      let cycleMultiplierPadding = 1;
      if (baseRequiredFrames <= 8) {
        cycleMultiplierPadding = 3;
      } else if (baseRequiredFrames <= 12) {
        cycleMultiplierPadding = 2;
      }
      frames = baseRequiredFrames * cycleMultiplierPadding;
    }

    let baseBytes = 0;
    if (hasAnimation) {
      baseBytes = (totalPixels * 0.00018) + (frames - 1) * (totalPixels * 0.00004);
    } else {
      if (isTransparent) {
        baseBytes = totalPixels * 0.0002;
      } else {
        baseBytes = totalPixels * 0.00012;
      }
    }

    let complexityFactor = 1.0;
    layers.forEach(l => {
      if (l.type === 'image' && l.imageFillUrl) {
        complexityFactor += 0.15;
      } else if (l.type === 'shape' && l.fillStyle === 'image' && l.imageFillUrl) {
        complexityFactor += 0.15;
      } else {
        complexityFactor += 0.05;
      }
      if (l.imageBlur && l.imageBlur > 0) complexityFactor += 0.02;
      if (l.imageDistort && l.imageDistort > 0) complexityFactor += 0.05;
      if (l.shadowEnabled) complexityFactor += 0.03;
    });

    return Math.round(baseBytes * complexityFactor);
  };

  const estimatedSize = calculateEstimatedFileSize();

  return (
    <div style={{ padding: '48px 40px', backgroundColor: '#f8f7fa', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1f2937', position: 'relative', overflow: 'hidden' }}>
      
      {/* Immersive radial glows in background */}
      <div className="float-slow" style={{ position: 'absolute', top: '-10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.04) 0%, transparent 70%)', filter: 'blur(60px)', zIndex: 1 }} />
      <div className="float-slow" style={{ position: 'absolute', bottom: '10%', left: '5%', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.03) 0%, transparent 70%)', filter: 'blur(50px)', zIndex: 1, animationDelay: '-4s' }} />

      <div style={{ position: 'relative', zIndex: 10 }}>
        
        {/* UPPER MAIN HEADER BAR */}
        <div className="glass-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderRadius: '20px', border: '1px solid rgba(168, 85, 247, 0.15)', background: 'rgba(255, 255, 255, 0.7)', marginBottom: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <h1 style={{ margin: '0', fontSize: '26px', fontWeight: '800', letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #0f091c 0%, #a855f7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Element Design Studio</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'rgba(241, 240, 245, 0.5)', border: '1px solid rgba(168, 85, 247, 0.1)', padding: '10px 16px', borderRadius: '16px' }}>
              <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Est. Weight:</span>
              <strong style={{ fontSize: '13px', fontFamily: 'monospace', color: estimatedSize > 2.5 * 1024 * 1024 ? '#ef4444' : BRAND_AMBER_GOLD }}>
                ~{formatFileSize(estimatedSize)}
              </strong>
              {exportedFileSize !== null && (
                <>
                  <span style={{ borderLeft: '1px solid rgba(15, 9, 28, 0.08)', height: '14px', margin: '0 4px' }} />
                  <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Final:</span>
                  <strong style={{ fontSize: '13px', fontFamily: 'monospace', color: exportedFileSize > 2.5 * 1024 * 1024 ? '#ef4444' : BRAND_AMBER_GOLD }}>
                    {formatFileSize(exportedFileSize)}
                  </strong>
                </>
              )}
              {Math.max(estimatedSize, exportedFileSize || 0) > 2.5 * 1024 * 1024 && (
                <span style={{ fontSize: '9px', color: '#ef4444', textTransform: 'uppercase', fontWeight: '900' }}>⚠️ HEAVY</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '6px', borderRight: '1px solid rgba(15, 9, 28, 0.08)', paddingRight: '12px', marginRight: '4px' }}>
              <button 
                onClick={() => {
                  fetchProjectsList();
                  setShowProjectsModal(true);
                }}
                style={{ 
                  backgroundColor: 'rgba(168, 85, 247, 0.08)', 
                  border: '1px solid rgba(168, 85, 247, 0.15)', 
                  color: '#a855f7', 
                  padding: '10px 16px', 
                  borderRadius: '14px', 
                  cursor: 'pointer', 
                  fontWeight: '700', 
                  fontSize: '12px', 
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.08)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                My Projects
              </button>
              <button 
                onClick={handleSaveProject}
                style={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.08)', 
                  border: '1px solid rgba(16, 185, 129, 0.15)', 
                  color: '#10b981', 
                  padding: '10px 16px', 
                  borderRadius: '14px', 
                  cursor: 'pointer', 
                  fontWeight: '700', 
                  fontSize: '12px', 
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.08)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                Save Project
              </button>
            </div>
            <div style={{ display: 'flex', gap: '4px', borderRight: '1px solid rgba(15, 9, 28, 0.08)', paddingRight: '12px', marginRight: '4px' }}>
              <button 
                onClick={handleUndo} 
                disabled={esUndoStack.length === 0} 
                title="Undo (Ctrl+Z)"
                style={{ 
                  backgroundColor: esUndoStack.length === 0 ? 'transparent' : 'rgba(15, 9, 28, 0.02)', 
                  border: '1px solid rgba(15, 9, 28, 0.08)', 
                  color: esUndoStack.length === 0 ? '#b5bac1' : '#1f2937', 
                  padding: '10px 14px', 
                  borderRadius: '14px', 
                  cursor: esUndoStack.length === 0 ? 'not-allowed' : 'pointer', 
                  fontWeight: '700', 
                  fontSize: '12px', 
                  transition: 'all 0.2s' 
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>
                  Undo
                </span>
              </button>
              <button 
                onClick={handleRedo} 
                disabled={esRedoStack.length === 0} 
                title="Redo (Ctrl+Y)"
                style={{ 
                  backgroundColor: esRedoStack.length === 0 ? 'transparent' : 'rgba(15, 9, 28, 0.02)', 
                  border: '1px solid rgba(15, 9, 28, 0.08)', 
                  color: esRedoStack.length === 0 ? '#b5bac1' : '#1f2937', 
                  padding: '10px 14px', 
                  borderRadius: '14px', 
                  cursor: esRedoStack.length === 0 ? 'not-allowed' : 'pointer', 
                  fontWeight: '700', 
                  fontSize: '12px', 
                  transition: 'all 0.2s' 
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                  Redo
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"/></svg>
                </span>
              </button>
            </div>
            <button onClick={() => navigate('/dashboard')} style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', border: '1px solid rgba(15, 9, 28, 0.08)', color: '#1f2937', padding: '10px 18px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.borderColor = BRAND_AMBER_GOLD} onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.08)'}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                Hub Dashboard
              </span>
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start' }}>
          
          {/* LEFT SIDEBAR CONTROLS */}
          <div className="glass-panel" style={{ width: '420px', padding: '24px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.12)', maxHeight: '80vh', overflowY: 'auto', background: 'rgba(255, 255, 255, 0.75)' }}>
            


            {/* Element Asset Name (Shared across both modes for library output naming) */}
            <div style={{ marginBottom: '18px' }}>
              <label style={studioLabelStyle}>Element Asset Name</label>
              <input 
                type="text" 
                value={elementName} 
                onChange={e => setElementName(e.target.value)} 
                className="glass-input" 
                style={{ width: '100%' }} 
                placeholder="e.g. SummerPromoCTA" 
              />
            </div>

            {/* CONDITIONAL MODE PANEL CONTROLS */}
            <>
                {/* LAYERS MANAGER CARD */}
                <div className="glass-panel" style={{ padding: '20px', borderRadius: '24px', marginBottom: '20px', border: '1px solid rgba(168, 85, 247, 0.1)', background: 'rgba(241, 240, 245, 0.5)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                      Layers
                    </span>
                    <span style={{ fontSize: '10px', color: 'rgba(15, 9, 28, 0.45)', fontWeight: '500' }}>click and drag layers to re-order</span>
                  </div>
                  
                  {/* Layers List */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto', paddingRight: '4px', marginBottom: '12px' }}>
                    {[...layers].reverse().map((layer) => {
                      const isActive = layer.id === activeLayerId;
                      const isRenaming = renamingLayerId === layer.id;
                      return (
                        <div 
                          key={layer.id}
                          onClick={() => {
                            setActiveLayerId(layer.id);
                            setRenamingLayerId(null);
                          }}
                          draggable={!isRenaming}
                          onDragStart={(e) => handleDragLayerStart(e, layer.id)}
                          onDragOver={(e) => handleDragLayerOver(e, layer.id)}
                          onDragEnd={handleDragLayerEnd}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 14px',
                            borderRadius: '12px',
                            border: isActive ? `2px solid ${BRAND_AMBER_GOLD}` : '1px solid rgba(168, 85, 247, 0.08)',
                            backgroundColor: isActive ? 'rgba(217, 119, 6, 0.04)' : '#ffffff',
                            cursor: draggedListItemId === layer.id ? 'grabbing' : 'grab',
                            transition: 'all 0.2s ease',
                            boxShadow: isActive ? '0 4px 12px rgba(217, 119, 6, 0.05)' : 'none',
                            opacity: draggedListItemId === layer.id ? 0.4 : 1
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }} onDoubleClick={(e) => {
                            e.stopPropagation();
                            setRenamingLayerId(layer.id);
                            setRenamingName(layer.name);
                          }}>
                            <span style={{ cursor: 'grab', color: 'rgba(15, 9, 28, 0.25)', marginRight: '4px', userSelect: 'none', fontSize: '12px' }}>☰</span>
                            <span style={{ fontSize: '13px', display: 'flex', alignItems: 'center', color: '#4b5563' }}>
                              {layer.type === 'text' ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>
                              )}
                            </span>
                            {isRenaming ? (
                              <input 
                                type="text" 
                                value={renamingName}
                                onChange={e => setRenamingName(e.target.value)}
                                onBlur={() => {
                                  if (renamingName.trim() && renamingName.trim() !== layer.name) {
                                    pushUndoPoint(layers);
                                    setLayers(prev => prev.map(l => l.id === layer.id ? { ...l, name: renamingName.trim() } : l));
                                  }
                                  setRenamingLayerId(null);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    if (renamingName.trim() && renamingName.trim() !== layer.name) {
                                      pushUndoPoint(layers);
                                      setLayers(prev => prev.map(l => l.id === layer.id ? { ...l, name: renamingName.trim() } : l));
                                    }
                                    setRenamingLayerId(null);
                                  } else if (e.key === 'Escape') {
                                    setRenamingLayerId(null);
                                  }
                                }}
                                autoFocus
                                onClick={e => e.stopPropagation()}
                                style={{
                                  fontSize: '12px',
                                  fontWeight: '700',
                                  color: '#1f2937',
                                  border: `1px solid ${BRAND_AMBER_GOLD}`,
                                  borderRadius: '6px',
                                  padding: '2px 6px',
                                  width: '100%',
                                  background: '#ffffff',
                                  outline: 'none'
                                }}
                              />
                            ) : (
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '700',
                                color: isActive ? BRAND_AMBER_GOLD : '#1f2937',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {layer.name} {!layer.visible && ' (Hidden)'}
                              </span>
                            )}
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                            {/* Color Overlay Indicator */}
                            {layer.overlayColor && (
                              <div 
                                style={{
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: layer.overlayColor,
                                  border: '1px solid rgba(15, 9, 28, 0.15)',
                                  cursor: 'help'
                                }}
                                title={`Overlay Active: ${layer.overlayColor} (${layer.overlayOpacity !== undefined ? layer.overlayOpacity : 100}%)`}
                              />
                            )}
                            {/* Visibility toggle */}
                            <button 
                              onClick={() => {
                                setLayers(prev => prev.map(l => l.id === layer.id ? { ...l, visible: !l.visible } : l));
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                color: '#4b5563',
                                opacity: layer.visible ? 0.75 : 0.35
                              }}
                              title={layer.visible ? "Hide Layer" : "Show Layer"}
                            >
                              {layer.visible ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                              ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                              )}
                            </button>
                            {/* Duplicate */}
                            <button 
                              onClick={() => handleDuplicateLayer(layer)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                color: '#4b5563',
                                opacity: 0.75
                              }}
                              title="Duplicate Layer"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            </button>
                            {/* Delete */}
                            <button 
                              onClick={() => handleDeleteLayer(layer.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                color: '#ef4444',
                                opacity: 0.75
                              }}
                              title="Delete Layer"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reorder and Add Action Row */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <button 
                      onClick={handleAddTextLayer} 
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: '10px',
                        border: '1px solid rgba(168, 85, 247, 0.15)',
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        fontWeight: '700',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px'
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Text Layer
                    </button>
                    <button 
                      onClick={handleAddShapeLayer} 
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: '10px',
                        border: '1px solid rgba(168, 85, 247, 0.15)',
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        fontWeight: '700',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px'
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Shape Layer
                    </button>
                    <button 
                      onClick={handleAddImageLayer} 
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: '10px',
                        border: '1px solid rgba(168, 85, 247, 0.15)',
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        fontWeight: '700',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '5px'
                      }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                      Image Layer
                    </button>
                  </div>

                  {activeLayer && activeLayer.id && (
                    <div style={{ 
                      marginTop: '14px', 
                      paddingTop: '12px', 
                      borderTop: '1px solid rgba(168, 85, 247, 0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', fontWeight: '800', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          🎨 Layer Color Overlay
                        </span>
                        {activeLayer.overlayColor && (
                          <button
                            onClick={() => {
                              pushUndoPoint(layers);
                              updateActiveLayerProperty('overlayColor', '');
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#ef4444',
                              fontSize: '9px',
                              fontWeight: '800',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            REMOVE
                          </button>
                        )}
                      </div>

                      {/* Tab selector for Solid vs Gradient */}
                      <div style={{ 
                        display: 'flex', 
                        background: 'rgba(15, 9, 28, 0.03)', 
                        border: '1px solid rgba(15, 9, 28, 0.06)', 
                        borderRadius: '8px', 
                        padding: '2px', 
                        marginBottom: '4px'
                      }}>
                        {['solid', 'gradient'].map(type => (
                          <button
                            key={type}
                            onClick={() => {
                              pushUndoPoint(layers);
                              updateActiveLayerProperty('overlayType', type);
                              if (!activeLayer.overlayColor) {
                                updateActiveLayerProperty('overlayColor', '#ff0000');
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: '4px 0',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '700',
                              fontSize: '9px',
                              backgroundColor: (activeLayer.overlayType || 'solid') === type ? '#ffffff' : 'transparent',
                              color: (activeLayer.overlayType || 'solid') === type ? '#0f091c' : '#5b6275',
                              boxShadow: (activeLayer.overlayType || 'solid') === type ? '0 1px 3px rgba(15, 9, 28, 0.05)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {type.toUpperCase()}
                          </button>
                        ))}
                      </div>

                      {/* Colors row */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {/* Color 1 */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            backgroundColor: '#ffffff', 
                            border: '1px solid rgba(168, 85, 247, 0.12)', 
                            borderRadius: '10px', 
                            padding: '4px 8px',
                            flex: 1
                          }}>
                            <input 
                              type="color" 
                              value={activeLayer.overlayColor || '#ff0000'} 
                              onChange={e => {
                                pushUndoPoint(layers);
                                updateActiveLayerProperty('overlayColor', e.target.value);
                              }}
                              style={{ 
                                width: '16px', 
                                height: '16px', 
                                border: 'none', 
                                borderRadius: '4px', 
                                cursor: 'pointer',
                                padding: 0,
                                background: 'transparent'
                              }} 
                            />
                            <span style={{ fontSize: '9px', fontFamily: 'monospace', fontWeight: '700', color: activeLayer.overlayColor ? '#1f2937' : '#9ca3af' }}>
                              {activeLayer.overlayColor ? activeLayer.overlayColor.toUpperCase() : 'NO OVERLAY'}
                            </span>
                          </div>

                          {/* Color 2 (supports transparent selector) */}
                          {((activeLayer.overlayType || 'solid') === 'gradient') && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '6px', 
                              backgroundColor: '#ffffff', 
                              border: '1px solid rgba(168, 85, 247, 0.12)', 
                              borderRadius: '10px', 
                              padding: '4px 8px',
                              flex: 1
                            }}>
                              {activeLayer.overlayColor2 === 'transparent' ? (
                                <div 
                                  onClick={() => {
                                    pushUndoPoint(layers);
                                    updateActiveLayerProperty('overlayColor2', '#000000');
                                  }}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '4px',
                                    backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                                    backgroundSize: '4px 4px',
                                    backgroundPosition: '0 0, 0 2px, 2px -2px, -2px 0px',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(15, 9, 28, 0.15)'
                                  }}
                                  title="Click to select a solid color"
                                />
                              ) : (
                                <input 
                                  type="color" 
                                  value={activeLayer.overlayColor2 || '#000000'} 
                                  onChange={e => {
                                    pushUndoPoint(layers);
                                    updateActiveLayerProperty('overlayColor2', e.target.value);
                                  }}
                                  style={{ 
                                    width: '16px', 
                                    height: '16px', 
                                    border: 'none', 
                                    borderRadius: '4px', 
                                    cursor: 'pointer',
                                    padding: 0,
                                    background: 'transparent'
                                  }} 
                                />
                              )}
                              <span 
                                onClick={() => {
                                  pushUndoPoint(layers);
                                  updateActiveLayerProperty('overlayColor2', activeLayer.overlayColor2 === 'transparent' ? '#000000' : 'transparent');
                                }}
                                style={{ 
                                  fontSize: '9px', 
                                  fontFamily: 'monospace', 
                                  fontWeight: '700', 
                                  color: '#1f2937',
                                  cursor: 'pointer',
                                  textDecoration: 'underline'
                                }}
                                title="Click to toggle transparent end-stop"
                              >
                                {activeLayer.overlayColor2 === 'transparent' ? 'TRANS' : (activeLayer.overlayColor2 ? activeLayer.overlayColor2.toUpperCase() : '#000000')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Opacity and Angle controls */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '9px', fontWeight: '700', color: '#5b6275', width: '45px' }}>OPACITY</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={activeLayer.overlayOpacity !== undefined ? activeLayer.overlayOpacity : 100}
                            onChange={e => {
                              pushUndoPoint(layers);
                              updateActiveLayerProperty('overlayOpacity', parseInt(e.target.value));
                            }}
                            style={{ flex: 1, accentColor: BRAND_AMBER_GOLD, height: '4px' }}
                          />
                          <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: '700', width: '28px', textAlign: 'right' }}>
                            {activeLayer.overlayOpacity !== undefined ? activeLayer.overlayOpacity : 100}%
                          </span>
                        </div>

                        {((activeLayer.overlayType || 'solid') === 'gradient') && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: '#5b6275', width: '45px' }}>ANGLE</span>
                            <input
                              type="range"
                              min="0"
                              max="360"
                              value={activeLayer.overlayGradientAngle !== undefined ? activeLayer.overlayGradientAngle : 90}
                              onChange={e => {
                                pushUndoPoint(layers);
                                updateActiveLayerProperty('overlayGradientAngle', parseInt(e.target.value));
                              }}
                              style={{ flex: 1, accentColor: BRAND_AMBER_GOLD, height: '4px' }}
                            />
                            <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: '700', width: '28px', textAlign: 'right' }}>
                              {activeLayer.overlayGradientAngle !== undefined ? activeLayer.overlayGradientAngle : 90}°
                            </span>
                          </div>
                        )}

                        {((activeLayer.overlayType || 'solid') === 'gradient') && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '700', color: '#5b6275', width: '45px' }}>SIZE</span>
                            <input
                              type="range"
                              min="10"
                              max="200"
                              value={activeLayer.overlayGradientLength !== undefined ? activeLayer.overlayGradientLength : 100}
                              onChange={e => {
                                pushUndoPoint(layers);
                                updateActiveLayerProperty('overlayGradientLength', parseInt(e.target.value));
                              }}
                              style={{ flex: 1, accentColor: BRAND_AMBER_GOLD, height: '4px' }}
                            />
                            <span style={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: '700', width: '28px', textAlign: 'right' }}>
                              {activeLayer.overlayGradientLength !== undefined ? activeLayer.overlayGradientLength : 100}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>

                {/* PROPERTIES TAB SELECTOR */}
                <div style={{ 
                  display: 'flex', 
                  background: 'rgba(15, 9, 28, 0.03)', 
                  border: '1px solid rgba(15, 9, 28, 0.06)', 
                  borderRadius: '12px', 
                  padding: '3px', 
                  marginBottom: '16px'
                }}>
                  {[
                    { id: 'geometry', label: 'Geometry', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', display: 'inline-block', verticalAlign: '-1px' }}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg> },
                    { id: 'styling', label: 'Styling', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', display: 'inline-block', verticalAlign: '-1px' }}><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"/><path d="M7.5 10.5C8.32843 10.5 9 9.82843 9 9C9 8.17157 8.32843 7.5 7.5 7.5C6.67157 7.5 6 8.17157 6 9C6 9.82843 6.67157 10.5 7.5 10.5Z"/><path d="M11.5 7.5C12.3284 7.5 13 6.82843 13 6C13 5.17157 12.3284 4.5 11.5 4.5C10.6716 4.5 10 5.17157 10 6C10 6.82843 10.6716 7.5 11.5 7.5Z"/><path d="M16.5 9.5C17.3284 9.5 18 8.82843 18 8C18 7.17157 17.3284 6.5 16.5 6.5C15.6716 6.5 15 7.17157 15 8C15 8.82843 15.6716 9.5 16.5 9.5Z"/><path d="M6 14C6 14 7.5 17 12 17C16.5 17 18 14 18 14"/></svg> },
                    { id: 'animation', label: 'Motion', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', display: 'inline-block', verticalAlign: '-1px' }}><polygon points="5 3 19 12 5 21 5 3"/></svg> },
                    { id: 'placement', label: 'Layout', icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', display: 'inline-block', verticalAlign: '-1px' }}><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg> }
                  ].map(tab => (
                    <button 
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)} 
                      style={{ 
                        flex: 1, 
                        padding: '8px 0', 
                        borderRadius: '10px', 
                        border: 'none', 
                        cursor: 'pointer', 
                        fontWeight: '700', 
                        fontSize: '10px',
                        backgroundColor: activeTab === tab.id ? '#ffffff' : 'transparent',
                        color: activeTab === tab.id ? '#0f091c' : '#5b6275',
                        boxShadow: activeTab === tab.id ? '0 2px 6px rgba(15, 9, 28, 0.05)' : 'none',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* DYNAMIC ACTIVE TAB INTERFACE */}
                <div className="glass-panel" style={{ padding: '20px', borderRadius: '24px', marginBottom: '20px', border: '1px solid rgba(168, 85, 247, 0.08)', background: 'rgba(255, 255, 255, 0.6)' }}>
                  
                  {activeTab === 'geometry' && (
                    <div>
                      {activeLayer.type === 'text' ? (
                        <>
                          <div style={{ marginBottom: '16px' }}>
                            <label style={studioLabelStyle}>Display Text</label>
                            <input 
                              type="text" 
                              value={activeLayer.text || ''} 
                              onChange={e => updateActiveLayerProperty('text', e.target.value)} 
                              className="glass-input" 
                              style={{ width: '100%' }} 
                            />
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div>
                              <label style={studioLabelStyle}>Font Family</label>
                              <SearchableFontDropdown
                                value={activeLayer.font || 'Arial'}
                                onChange={val => {
                                  updateActiveLayerProperty('font', val);
                                  const customObj = (customFonts || []).find(cf => cf.family === val);
                                  if (customObj && customObj.variations && customObj.variations.length > 0) {
                                    updateActiveLayerProperty('fontWeight', customObj.variations[0].weight || '400');
                                    updateActiveLayerProperty('fontStyle', customObj.variations[0].style || 'normal');
                                  } else {
                                    updateActiveLayerProperty('fontWeight', '400');
                                    updateActiveLayerProperty('fontStyle', 'normal');
                                  }
                                }}
                                systemFonts={systemFonts}
                                customFonts={customFonts}
                              />
                            </div>
                            <div>
                              <label style={studioLabelStyle}>Sizing (px)</label>
                              <input 
                                type="number" 
                                value={activeLayer.fontSize || 28} 
                                onChange={e => updateActiveLayerProperty('fontSize', parseInt(e.target.value) || 12)} 
                                className="glass-input" 
                                style={{ width: '100%' }} 
                              />
                            </div>
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <label style={studioLabelStyle}>Font Size</label>
                              <span style={{ color: BRAND_AMBER_GOLD, fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace' }}>{activeLayer.fontSize || 28}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="10" 
                              max="120" 
                              value={activeLayer.fontSize || 28} 
                              onChange={e => updateActiveLayerProperty('fontSize', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          {/* Dynamic Typography Variations Selection */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div>
                              <label style={studioLabelStyle}>Font Weight</label>
                              <select
                                value={activeLayer.fontWeight || '400'}
                                onChange={e => updateActiveLayerProperty('fontWeight', e.target.value)}
                                className="glass-input"
                                style={{ width: '100%', padding: '12px' }}
                              >
                                {(() => {
                                  const customObj = (customFonts || []).find(cf => cf.family === activeLayer.font);
                                  if (customObj && customObj.variations) {
                                    const weights = Array.from(new Set(customObj.variations.map(v => v.weight)));
                                    weights.sort((a, b) => parseInt(a) - parseInt(b));
                                    const weightLabelMap = {
                                      '100': 'Thin (100)',
                                      '200': 'ExtraLight (200)',
                                      '300': 'Light (300)',
                                      '400': 'Regular (400)',
                                      '500': 'Medium (500)',
                                      '600': 'SemiBold (600)',
                                      '700': 'Bold (700)',
                                      '800': 'ExtraBold (800)',
                                      '900': 'Black (900)'
                                    };
                                    return weights.map(w => (
                                      <option key={w} value={w}>{weightLabelMap[w] || w}</option>
                                    ));
                                  } else {
                                    return (
                                      <>
                                        <option value="400">Regular (400)</option>
                                        <option value="700">Bold (700)</option>
                                      </>
                                    );
                                  }
                                })()}
                              </select>
                            </div>
                            <div>
                              <label style={studioLabelStyle}>Font Style</label>
                              <select
                                value={activeLayer.fontStyle || 'normal'}
                                onChange={e => updateActiveLayerProperty('fontStyle', e.target.value)}
                                className="glass-input"
                                style={{ width: '100%', padding: '12px' }}
                              >
                                {(() => {
                                  const customObj = (customFonts || []).find(cf => cf.family === activeLayer.font);
                                  if (customObj && customObj.variations) {
                                    const matched = customObj.variations.filter(v => v.weight === (activeLayer.fontWeight || '400'));
                                    const styles = Array.from(new Set(matched.map(v => v.style)));
                                    if (styles.length === 0) {
                                      styles.push(...Array.from(new Set(customObj.variations.map(v => v.style))));
                                    }
                                    return styles.map(s => (
                                      <option key={s} value={s}>{s === 'italic' ? 'Italic' : 'Normal'}</option>
                                    ));
                                  } else {
                                    return (
                                      <>
                                        <option value="normal">Normal</option>
                                        <option value="italic">Italic</option>
                                      </>
                                    );
                                  }
                                })()}
                              </select>
                            </div>
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <label style={studioLabelStyle}>Letter Spacing (Tracking)</label>
                              <span style={{ color: BRAND_AMBER_GOLD, fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace' }}>{activeLayer.letterSpacing || 0}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="-10" 
                              max="50" 
                              value={activeLayer.letterSpacing || 0} 
                              onChange={e => updateActiveLayerProperty('letterSpacing', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <label style={studioLabelStyle}>Layer Opacity</label>
                              <span style={{ color: BRAND_AMBER_GOLD, fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace' }}>{Math.round((activeLayer.opacity !== undefined ? activeLayer.opacity : 1.0) * 100)}%</span>
                            </div>
                            <input 
                              type="range" 
                              min="0.0" 
                              max="1.0" 
                              step="0.05"
                              value={activeLayer.opacity !== undefined ? activeLayer.opacity : 1.0} 
                              onChange={e => updateActiveLayerProperty('opacity', parseFloat(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ marginBottom: '16px' }}>
                            <label style={studioLabelStyle}>Shape Type</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                              {['rect', 'circle', 'triangle', 'star'].map(type => (
                                <button 
                                  key={type} 
                                  onClick={() => updateActiveLayerProperty('shapeType', type)} 
                                  style={{
                                    ...studioBlockButtonStyle,
                                    padding: '10px 0',
                                    fontSize: '10px',
                                    backgroundColor: activeLayer.shapeType === type ? 'rgba(217, 119, 6, 0.1)' : 'rgba(15, 9, 28, 0.02)',
                                    borderColor: activeLayer.shapeType === type ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.06)',
                                    color: activeLayer.shapeType === type ? BRAND_AMBER_GOLD : '#1f2937',
                                    boxShadow: activeLayer.shapeType === type ? BRAND_GOLD_GLOW : 'none'
                                  }}
                                >
                                  {type === 'rect' && '⬛ Rect'}
                                  {type === 'circle' && '🔴 Circle'}
                                  {type === 'triangle' && '🔺 Tri'}
                                  {type === 'star' && '⭐ Star'}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>SHAPE WIDTH</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.shapeWidth || 160}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="2" 
                              max="500" 
                              value={activeLayer.shapeWidth || 160} 
                              onChange={e => {
                                const newW = parseInt(e.target.value);
                                if (lockAspectRatio) {
                                  const currentW = activeLayer.shapeWidth || 160;
                                  const currentH = activeLayer.shapeHeight || 160;
                                  const ratio = currentH / currentW;
                                  const nextH = Math.round(newW * ratio);
                                  updateActiveLayerProperties({ shapeWidth: newW, shapeHeight: Math.min(500, Math.max(2, nextH)) });
                                } else {
                                  updateActiveLayerProperty('shapeWidth', newW);
                                }
                              }} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>SHAPE HEIGHT</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.shapeHeight || 160}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="2" 
                              max="500" 
                              value={activeLayer.shapeHeight || 160} 
                              onChange={e => {
                                const newH = parseInt(e.target.value);
                                if (lockAspectRatio) {
                                  const currentW = activeLayer.shapeWidth || 160;
                                  const currentH = activeLayer.shapeHeight || 160;
                                  const ratio = currentW / currentH;
                                  const nextW = Math.round(newH * ratio);
                                  updateActiveLayerProperties({ shapeHeight: newH, shapeWidth: Math.min(500, Math.max(2, nextW)) });
                                } else {
                                  updateActiveLayerProperty('shapeHeight', newH);
                                }
                              }} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <input 
                              type="checkbox"
                              id="lockAspectToggle"
                              checked={lockAspectRatio}
                              onChange={e => setLockAspectRatio(e.target.checked)}
                              style={{ accentColor: BRAND_AMBER_GOLD, cursor: 'pointer' }}
                            />
                            <label htmlFor="lockAspectToggle" style={{ fontSize: '11px', fontWeight: '750', color: '#5b6275', cursor: 'pointer', userSelect: 'none' }}>
                              🔒 LOCK ASPECT RATIO
                            </label>
                          </div>

                          {activeLayer.shapeType === 'rect' && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#5b6275', fontWeight: '600' }}>CORNER ROUNDING (RADIUS)</span>
                                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.cornerRadius !== undefined ? activeLayer.cornerRadius : 16}px</span>
                              </div>
                              <input 
                                type="range" 
                                min="0" 
                                max="100" 
                                value={activeLayer.cornerRadius !== undefined ? activeLayer.cornerRadius : 16} 
                                onChange={e => updateActiveLayerProperty('cornerRadius', parseInt(e.target.value))} 
                                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                              />
                            </div>
                          )}

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div>
                              <label style={studioLabelStyle}>Fill Style</label>
                              <select 
                                value={activeLayer.fillStyle || 'solid'} 
                                onChange={e => updateActiveLayerProperty('fillStyle', e.target.value)} 
                                className="glass-input" 
                                style={{ width: '100%', padding: '12px' }}
                              >
                                <option value="solid">Solid Fill</option>
                                <option value="transparent">Transparent Fill</option>
                                <option value="image">Image Backdrop</option>
                              </select>
                            </div>
                            <div>
                              <label style={studioLabelStyle}>Outline Style</label>
                              <select 
                                value={activeLayer.outlineStyle || 'transparent'} 
                                onChange={e => updateActiveLayerProperty('outlineStyle', e.target.value)} 
                                className="glass-input" 
                                style={{ width: '100%', padding: '12px' }}
                              >
                                <option value="solid">Solid Outline</option>
                                <option value="transparent">Transparent Outline</option>
                              </select>
                            </div>
                          </div>

                          {activeLayer.outlineStyle === 'solid' && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#5b6275', fontWeight: '600' }}>STROKE WIDTH</span>
                                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.strokeWidth || 3}px</span>
                              </div>
                              <input 
                                type="range" 
                                min="1" 
                                max="25" 
                                value={activeLayer.strokeWidth || 3} 
                                onChange={e => updateActiveLayerProperty('strokeWidth', parseInt(e.target.value))} 
                                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                              />
                            </div>
                          )}

                          <div style={{ marginBottom: '16px', marginTop: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <label style={studioLabelStyle}>Layer Opacity</label>
                              <span style={{ color: BRAND_AMBER_GOLD, fontSize: '12px', fontWeight: 'bold', fontFamily: 'monospace' }}>{Math.round((activeLayer.opacity !== undefined ? activeLayer.opacity : 1.0) * 100)}%</span>
                            </div>
                            <input 
                              type="range" 
                              min="0.0" 
                              max="1.0" 
                              step="0.05"
                              value={activeLayer.opacity !== undefined ? activeLayer.opacity : 1.0} 
                              onChange={e => updateActiveLayerProperty('opacity', parseFloat(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'styling' && (
                    <div>
                      {/* Color Fill Mode Selector */}
                      {(activeLayer.type === 'text' || activeLayer.type === 'shape') && (
                        <div style={{ marginBottom: '16px' }}>
                          <label style={studioLabelStyle}>Fill Mode</label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => updateActiveLayerProperty('fillType', 'solid')}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '10px',
                                border: '1px solid',
                                borderColor: (activeLayer.fillType || 'solid') === 'solid' ? BRAND_AMBER_GOLD : 'rgba(168, 85, 247, 0.15)',
                                backgroundColor: (activeLayer.fillType || 'solid') === 'solid' ? 'rgba(217, 119, 6, 0.08)' : '#ffffff',
                                color: (activeLayer.fillType || 'solid') === 'solid' ? BRAND_AMBER_GOLD : '#1f2937',
                                fontWeight: '700',
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              Solid Color
                            </button>
                            <button
                              onClick={() => updateActiveLayerProperty('fillType', 'gradient')}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '10px',
                                border: '1px solid',
                                borderColor: activeLayer.fillType === 'gradient' ? BRAND_AMBER_GOLD : 'rgba(168, 85, 247, 0.15)',
                                backgroundColor: activeLayer.fillType === 'gradient' ? 'rgba(217, 119, 6, 0.08)' : '#ffffff',
                                color: activeLayer.fillType === 'gradient' ? BRAND_AMBER_GOLD : '#1f2937',
                                fontWeight: '700',
                                fontSize: '11px',
                                cursor: 'pointer'
                              }}
                            >
                              Gradient Fill
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Solid Fill Colors */}
                      {((activeLayer.type === 'text' || activeLayer.type === 'shape') && (activeLayer.fillType || 'solid') === 'solid') && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                          {renderColorInput(
                            activeLayer.type === 'text' ? 'Text Color' : 'Fill Color',
                            activeLayer.color1 || (activeLayer.type === 'text' ? '#0f091c' : '#d97706'),
                            c => updateActiveLayerProperty('color1', c)
                          )}
                          {renderColorInput(
                            activeLayer.type === 'text' ? 'Accent Color' : 'Outline Color',
                            activeLayer.type === 'text' 
                              ? (activeLayer.color2 || '#d97706') 
                              : (activeLayer.strokeColor || activeLayer.color2 || '#0f091c'),
                            c => updateActiveLayerProperty(activeLayer.type === 'text' ? 'color2' : 'strokeColor', c)
                          )}
                        </div>
                      )}

                      {/* Gradient Fill Configuration */}
                      {(activeLayer.type === 'text' || activeLayer.type === 'shape') && activeLayer.fillType === 'gradient' && (
                        <div className="glass-panel" style={{ padding: '16px', borderRadius: '16px', marginBottom: '16px', border: '1px solid rgba(168, 85, 247, 0.1)', background: 'rgba(255,255,255,0.4)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <span style={{ fontSize: '10px', color: BRAND_AMBER_GOLD, fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>🌈 Gradient Configuration</span>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            {renderColorInput(
                              'Color Start',
                              activeLayer.gradientColor1 || activeLayer.color1 || '#d97706',
                              c => updateActiveLayerProperty('gradientColor1', c)
                            )}
                            {!activeLayer.gradientFadeToTransparent && renderColorInput(
                              'Color End',
                              activeLayer.gradientColor2 || activeLayer.color2 || '#0f091c',
                              c => updateActiveLayerProperty('gradientColor2', c)
                            )}
                          </div>

                          <div onClick={() => updateActiveLayerProperty('gradientFadeToTransparent', !activeLayer.gradientFadeToTransparent)} style={{ cursor: 'pointer' }}>
                            <div className="glass-input" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              width: '100%',
                              borderColor: activeLayer.gradientFadeToTransparent ? BRAND_AMBER_GOLD : 'rgba(168, 85, 247, 0.15)',
                              color: activeLayer.gradientFadeToTransparent ? BRAND_AMBER_GOLD : '#1f2937',
                              padding: '10px 12px'
                            }}>
                              <div style={{ width: '14px', height: '14px', borderRadius: '4px', border: '2px solid', borderColor: activeLayer.gradientFadeToTransparent ? BRAND_AMBER_GOLD : '#6b7280', backgroundColor: activeLayer.gradientFadeToTransparent ? BRAND_AMBER_GOLD : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {activeLayer.gradientFadeToTransparent && <div style={{ width: '6px', height: '6px', backgroundColor: BRAND_AMBER_GOLD, borderRadius: '1px' }} />}
                              </div>
                              <span style={{ fontSize: '11px', fontWeight: '700' }}>FADE TO TRANSPARENT</span>
                            </div>
                          </div>

                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>GRADIENT ANGLE</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.gradientAngle !== undefined ? activeLayer.gradientAngle : 90}°</span>
                            </div>
                            <input 
                              type="range" 
                              min="0" 
                              max="360" 
                              value={activeLayer.gradientAngle !== undefined ? activeLayer.gradientAngle : 90} 
                              onChange={e => updateActiveLayerProperty('gradientAngle', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>GRADIENT SPREAD / LENGTH</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.gradientLength !== undefined ? activeLayer.gradientLength : 100}%</span>
                            </div>
                            <input 
                              type="range" 
                              min="10" 
                              max="200" 
                              value={activeLayer.gradientLength !== undefined ? activeLayer.gradientLength : 100} 
                              onChange={e => updateActiveLayerProperty('gradientLength', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>
                        </div>
                      )}

                      {/* Image Source Selection */}
                      {(activeLayer.type === 'image' || (activeLayer.type === 'shape' && activeLayer.fillStyle === 'image')) && (
                        <div className="glass-panel" style={{ padding: '16px', borderRadius: '16px', marginBottom: '16px', border: '1px solid rgba(168, 85, 247, 0.1)', background: 'rgba(255,255,255,0.4)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <span style={{ fontSize: '10px', color: BRAND_AMBER_GOLD, fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>
                            {activeLayer.type === 'image' ? '🖼️ Image Layer Source' : '🖼️ Image Fill Backdrop'}
                          </span>
                          
                          <div>
                            <label style={studioLabelStyle}>Select/Upload Image</label>
                            <div className="glass-input" style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <input 
                                type="file" 
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;

                                  if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) {
                                    const reader = new FileReader();
                                    reader.readAsDataURL(file);
                                    reader.onloadend = () => {
                                      (async () => {
                                        try {
                                          setIsCompiling(true); // show loader spinner
                                          const res = await fetch(`${BACKEND_URL}/api/upload`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ image: reader.result, name: file.name })
                                          });
                                          const data = await res.json();
                                          if (data.url) {
                                            updateActiveLayerProperty('imageFillUrl', data.url);
                                          } else {
                                            alert('Upload failure: ' + (data.error || 'Unknown error'));
                                          }
                                        } catch (err) {
                                          alert('Failed to upload GIF: ' + err.message);
                                        } finally {
                                          setIsCompiling(false);
                                        }
                                      })();
                                    };
                                    e.target.value = '';
                                    return;
                                  }

                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    const img = new Image();
                                    img.src = reader.result;
                                    img.onload = () => {
                                      const wVal = activeLayer ? (activeLayer.shapeWidth || 320) : 320;
                                      const hVal = activeLayer ? (activeLayer.shapeHeight || 80) : 80;
                                      const defaultAspect = wVal / hVal;
                                      setCropAspect(defaultAspect);
                                      setCropOriginalAspect(defaultAspect);
                                      setCropScale('original');
                                      setCropFormat('jpeg');
                                      setCropQuality(0.85);
                                      setCropOriginalSize(file.size);
                                      setCropZoom(1.0);
                                      setCropPanX(0);
                                      setCropPanY(0);
                                      setCropFileName(file.name.split('.')[0] || 'layer_image');
                                      setCropRawImage(reader.result);
                                      setCropModalOpen(true);
                                    };
                                  };
                                  reader.readAsDataURL(file);
                                  e.target.value = '';
                                }}
                                style={{ color: '#1f2937', fontSize: '11px', width: '100%' }}
                              />
                              
                              <button
                                onClick={() => {
                                  setLibrarySelectionTarget({ type: 'element-studio-layer-image', layerId: activeLayer.id });
                                  triggerLibraryFetchOpen();
                                }}
                                style={{
                                  padding: '8px 12px',
                                  borderRadius: '10px',
                                  border: '1px solid rgba(168, 85, 247, 0.2)',
                                  backgroundColor: '#ffffff',
                                  color: BRAND_AMBER_GOLD,
                                  fontWeight: '700',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                📂 Choose from Media Library
                              </button>

                              {activeLayer.imageFillUrl && (
                                <button 
                                  onClick={() => updateActiveLayerProperty('imageFillUrl', null)} 
                                  style={{ 
                                    padding: '4px 10px', 
                                    fontSize: '10px', 
                                    fontWeight: '700', 
                                    color: '#ef4444', 
                                    background: 'rgba(239, 68, 68, 0.08)', 
                                    border: '1px solid rgba(239, 68, 68, 0.2)', 
                                    borderRadius: '6px', 
                                    cursor: 'pointer',
                                    alignSelf: 'flex-start'
                                  }}
                                >
                                  🗑 Remove Image
                                </button>
                              )}
                            </div>
                          </div>

                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>IMAGE BLUR</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.imageBlur || 0}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="0" 
                              max="20" 
                              value={activeLayer.imageBlur || 0} 
                              onChange={e => updateActiveLayerProperty('imageBlur', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>WAVE DISTORTION</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.imageDistort || 0}</span>
                            </div>
                            <input 
                              type="range" 
                              min="0" 
                              max="50" 
                              value={activeLayer.imageDistort || 0} 
                              onChange={e => updateActiveLayerProperty('imageDistort', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>
                        </div>
                      )}

                      {/* Drop Shadow Section */}
                      <div className="glass-panel" style={{ padding: '16px', borderRadius: '16px', marginBottom: '16px', border: '1px solid rgba(168, 85, 247, 0.1)', background: 'rgba(255,255,255,0.4)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '10px', color: BRAND_AMBER_GOLD, fontWeight: '700', textTransform: 'uppercase' }}>👤 Drop Shadow</span>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input 
                              type="checkbox"
                              checked={activeLayer.shadowEnabled || false}
                              onChange={e => updateActiveLayerProperty('shadowEnabled', e.target.checked)}
                              style={{ accentColor: BRAND_AMBER_GOLD }}
                            />
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#1f2937' }}>Enable Shadow</span>
                          </label>
                        </div>

                        {activeLayer.shadowEnabled && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                            {renderColorInput(
                              'Shadow Color',
                              activeLayer.shadowColor || 'rgba(0,0,0,0.4)',
                              c => updateActiveLayerProperty('shadowColor', c)
                            )}

                            <div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#5b6275', fontWeight: '600' }}>SHADOW BLUR</span>
                                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.shadowBlur !== undefined ? activeLayer.shadowBlur : 10}px</span>
                              </div>
                              <input 
                                type="range" 
                                min="0" 
                                max="40" 
                                value={activeLayer.shadowBlur !== undefined ? activeLayer.shadowBlur : 10} 
                                onChange={e => updateActiveLayerProperty('shadowBlur', parseInt(e.target.value))} 
                                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                              />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                  <span style={{ color: '#5b6275', fontWeight: '600' }}>OFFSET X</span>
                                  <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.shadowOffsetX !== undefined ? activeLayer.shadowOffsetX : 4}px</span>
                                </div>
                                <input 
                                  type="range" 
                                  min="-30" 
                                  max="30" 
                                  value={activeLayer.shadowOffsetX !== undefined ? activeLayer.shadowOffsetX : 4} 
                                  onChange={e => updateActiveLayerProperty('shadowOffsetX', parseInt(e.target.value))} 
                                  style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                                />
                              </div>
                              <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                  <span style={{ color: '#5b6275', fontWeight: '600' }}>OFFSET Y</span>
                                  <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.shadowOffsetY !== undefined ? activeLayer.shadowOffsetY : 4}px</span>
                                </div>
                                <input 
                                  type="range" 
                                  min="-30" 
                                  max="30" 
                                  value={activeLayer.shadowOffsetY !== undefined ? activeLayer.shadowOffsetY : 4} 
                                  onChange={e => updateActiveLayerProperty('shadowOffsetY', parseInt(e.target.value))} 
                                  style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Canvas-wide properties */}
                      <div style={{ borderTop: '1px solid rgba(168, 85, 247, 0.1)', paddingTop: '16px', marginTop: '16px' }}>
                        <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', display: 'block', marginBottom: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>🖼️ Canvas Background</span>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                          <div onClick={() => setIsTransparent(!isTransparent)} style={{ cursor: 'pointer' }}>
                            <label style={studioLabelStyle}>Background Mode</label>
                            <div className="glass-input" style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              width: '100%',
                              borderColor: isTransparent ? `${BRAND_AMBER_GOLD} !important` : 'rgba(168, 85, 247, 0.15) !important',
                              color: isTransparent ? BRAND_AMBER_GOLD : '#1f2937',
                              padding: '12px 14px'
                            }}>
                              <div style={{ width: '14px', height: '14px', borderRadius: '4px', border: '2px solid', borderColor: isTransparent ? BRAND_AMBER_GOLD : '#6b7280', backgroundColor: isTransparent ? BRAND_AMBER_GOLD : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {isTransparent && <div style={{ width: '6px', height: '6px', backgroundColor: BRAND_AMBER_GOLD, borderRadius: '1px' }} />}
                              </div>
                              <span style={{ fontSize: '11px', fontWeight: '700' }}>{isTransparent ? 'TRANSPARENT' : 'SOLID BACKGROUND'}</span>
                            </div>
                          </div>
                          {!isTransparent && renderColorInput(
                            'Background Color',
                            bgColor,
                            c => setBgColor(c)
                          )}
                        </div>
                      </div>

                      {/* Canvas Sizing / Dimensions Card */}
                      <div style={{ borderTop: '1px solid rgba(168, 85, 247, 0.1)', paddingTop: '16px', marginTop: '16px' }}>
                        <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', display: 'block', marginBottom: '12px', fontWeight: '700', letterSpacing: '0.5px' }}>📐 Canvas Size / Dimensions</span>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>WIDTH</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{esCanvasWidth}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="200" 
                              max="650" 
                              step="10"
                              value={esCanvasWidth} 
                              onChange={e => setEsCanvasWidth(parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>HEIGHT</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{esCanvasHeight}px</span>
                            </div>
                            <input 
                              type="range" 
                              min="30" 
                              max="330" 
                              step="5"
                              value={esCanvasHeight} 
                              onChange={e => setEsCanvasHeight(parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>
                        </div>
                      </div>

                    </div>
                  )}

                  {activeTab === 'animation' && (
                    <div>
                      {!showMotionSettings ? (
                        <>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '10px',
                            maxHeight: '260px',
                            overflowY: 'auto',
                            paddingRight: '4px',
                            marginBottom: '12px'
                          }}>
                            {motionTypes.map(m => {
                              const isSelected = activeLayer.animationType === m.id;
                              return (
                                <div
                                  key={m.id}
                                  onClick={() => updateActiveLayerProperty('animationType', m.id)}
                                  style={{
                                    padding: '12px 10px',
                                    borderRadius: '16px',
                                    border: isSelected ? `2.5px solid ${BRAND_AMBER_GOLD}` : '1.5px solid rgba(168, 85, 247, 0.12)',
                                    backgroundColor: isSelected ? 'rgba(217, 119, 6, 0.04)' : 'rgba(255, 255, 255, 0.7)',
                                    boxShadow: isSelected ? '0 6px 16px rgba(217, 119, 6, 0.12)' : '0 2px 10px rgba(15, 9, 28, 0.02)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    minHeight: '85px',
                                    backdropFilter: 'blur(8px)',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {isSelected && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0, left: 0, right: 0, bottom: 0,
                                      backgroundColor: 'rgba(255, 255, 255, 0.45)',
                                      backdropFilter: 'blur(1px)',
                                      zIndex: 1
                                    }} />
                                  )}

                                  <div style={{ 
                                    zIndex: 2, 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    opacity: isSelected ? 0.35 : 1, 
                                    transition: 'opacity 0.2s ease'
                                  }}>
                                    {m.icon(isSelected ? BRAND_AMBER_GOLD : '#5b6275')}
                                    <span style={{ 
                                      fontSize: '10px', 
                                      fontWeight: '700', 
                                      color: isSelected ? BRAND_AMBER_GOLD : '#1f2937', 
                                      textAlign: 'center',
                                      letterSpacing: '0.02em',
                                      textTransform: 'uppercase'
                                    }}>
                                      {m.name}
                                    </span>
                                  </div>

                                  {isSelected && m.id !== 'none' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowMotionSettings(true);
                                      }}
                                      style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        backgroundColor: BRAND_AMBER_GOLD,
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        zIndex: 10,
                                        boxShadow: '0 4px 10px rgba(217, 119, 6, 0.35)'
                                      }}
                                    >
                                      <svg 
                                        width="18" 
                                        height="18" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="#ffffff" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                      >
                                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.1a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid rgba(168, 85, 247, 0.1)' }}>
                            <button
                              onClick={() => setShowMotionSettings(false)}
                              style={{
                                backgroundColor: 'rgba(15, 9, 28, 0.04)',
                                color: '#1f2937',
                                border: '1px solid rgba(15, 9, 28, 0.08)',
                                padding: '6px 10px',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontWeight: '700',
                                fontSize: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              ← BACK
                            </button>
                            <span style={{ fontSize: '9px', color: BRAND_AMBER_GOLD, fontWeight: '800', letterSpacing: '0.05em', textTransform: 'uppercase', backgroundColor: 'rgba(217, 119, 6, 0.08)', padding: '3px 8px', borderRadius: '6px' }}>
                              Active: {motionTypes.find(m => m.id === activeLayer.animationType)?.name.toUpperCase() || 'NONE'}
                            </span>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                            <div>
                              <label style={studioLabelStyle}>Transition curves</label>
                              <select 
                                value={activeLayer.smoothingMode || 'linear'} 
                                onChange={e => updateActiveLayerProperty('smoothingMode', e.target.value)} 
                                className="glass-input" 
                                style={{ width: '100%', padding: '10px', fontSize: '11px' }}
                              >
                                <option value="linear">Linear Standard</option>
                                <option value="easeInOut">Smooth step</option>
                                <option value="sharp">Sharp Binary Stepped</option>
                              </select>
                            </div>
                            <div>
                              <label style={studioLabelStyle}>GLOBAL SPEED (FPS)</label>
                              <select 
                                value={fps} 
                                onChange={e => setFps(parseInt(e.target.value))} 
                                className="glass-input" 
                                style={{ width: '100%', padding: '10px', fontSize: '11px' }}
                              >
                                <option value="8">8 FPS (Light Payload)</option>
                                <option value="12">12 FPS (Balanced Baseline)</option>
                                <option value="16">16 FPS (Smooth Compromise)</option>
                                <option value="20">20 FPS (High-Fidelity)</option>
                                <option value="24">24 FPS (Ultra Fluid)</option>
                              </select>
                            </div>
                          </div>

                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                              <span style={{ color: '#5b6275', fontWeight: '600' }}>SPEED RATIO</span>
                              <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.speed || 50}</span>
                            </div>
                            <input 
                              type="range" 
                              min="10" 
                              max="150" 
                              value={activeLayer.speed || 50} 
                              onChange={e => updateActiveLayerProperty('speed', parseInt(e.target.value))} 
                              style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                            />
                          </div>

                          {['bounce', 'spin', 'breathe', 'shake', 'shear', 'drift', 'glitch', 'zoom', 'tilt', 'shadow', 'swing', 'underline', 'spotlight', 'bracket', 'sparkle'].includes(activeLayer.animationType) && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#5b6275', fontWeight: '600' }}>DISTANCE / INTENSITY</span>
                                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.distance || 25}</span>
                              </div>
                              <input 
                                type="range" 
                                min="5" 
                                max="80" 
                                value={activeLayer.distance || 25} 
                                onChange={e => updateActiveLayerProperty('distance', parseInt(e.target.value))} 
                                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                              />
                            </div>
                          )}

                          {['flicker', 'rainbow', 'glitch', 'shadow', 'underline', 'spotlight', 'bracket', 'sparkle'].includes(activeLayer.animationType) && (
                            renderColorInput(
                              'Accent/Effect Color',
                              activeLayer.color2 || (activeLayer.type === 'text' ? '#d97706' : '#0f091c'),
                              c => updateActiveLayerProperty('color2', c)
                            )
                          )}

                          {activeLayer.animationType === 'liquid' && (
                            <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <span style={studioLabelStyle}>Liquid Gradient Colors</span>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '9px', color: '#5b6275', fontWeight: '600', textTransform: 'uppercase' }}>Color 1</span>
                                  <div className="glass-input" style={{ display: 'flex', justifyContent: 'center', padding: '6px' }}>
                                    <input 
                                      type="color" 
                                      value={activeLayer.liquidColor1 || '#ff0055'} 
                                      onChange={e => updateActiveLayerProperty('liquidColor1', e.target.value)} 
                                      onBlur={e => addColorToHistory(e.target.value)}
                                      style={{ width: '28px', height: '28px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
                                    />
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '9px', color: '#5b6275', fontWeight: '600', textTransform: 'uppercase' }}>Color 2</span>
                                  <div className="glass-input" style={{ display: 'flex', justifyContent: 'center', padding: '6px' }}>
                                    <input 
                                      type="color" 
                                      value={activeLayer.liquidColor2 || '#7a00ff'} 
                                      onChange={e => updateActiveLayerProperty('liquidColor2', e.target.value)} 
                                      onBlur={e => addColorToHistory(e.target.value)}
                                      style={{ width: '28px', height: '28px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
                                    />
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '9px', color: '#5b6275', fontWeight: '600', textTransform: 'uppercase' }}>Color 3</span>
                                  <div className="glass-input" style={{ display: 'flex', justifyContent: 'center', padding: '6px' }}>
                                    <input 
                                      type="color" 
                                      value={activeLayer.liquidColor3 || '#00e5ff'} 
                                      onChange={e => updateActiveLayerProperty('liquidColor3', e.target.value)} 
                                      onBlur={e => addColorToHistory(e.target.value)}
                                      style={{ width: '28px', height: '28px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {activeLayer.animationType === 'shimmer' && (
                            <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <span style={studioLabelStyle}>Shimmer Colors</span>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '9px', color: '#5b6275', fontWeight: '600', textTransform: 'uppercase' }}>Base Color</span>
                                  <div className="glass-input" style={{ display: 'flex', justifyContent: 'center', padding: '6px' }}>
                                    <input 
                                      type="color" 
                                      value={activeLayer.shimmerBaseColor || activeLayer.color1 || '#d97706'} 
                                      onChange={e => updateActiveLayerProperty('shimmerBaseColor', e.target.value)} 
                                      onBlur={e => addColorToHistory(e.target.value)}
                                      style={{ width: '28px', height: '28px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
                                    />
                                  </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '9px', color: '#5b6275', fontWeight: '600', textTransform: 'uppercase' }}>Shine Color</span>
                                  <div className="glass-input" style={{ display: 'flex', justifyContent: 'center', padding: '6px' }}>
                                    <input 
                                      type="color" 
                                      value={activeLayer.shimmerLightColor || '#ffffff'} 
                                      onChange={e => updateActiveLayerProperty('shimmerLightColor', e.target.value)} 
                                      onBlur={e => addColorToHistory(e.target.value)}
                                      style={{ width: '28px', height: '28px', border: 'none', borderRadius: '6px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {['bounce', 'pulse', 'wobble'].includes(activeLayer.animationType) && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                <span style={{ color: '#5b6275', fontWeight: '600' }}>SQUISH DISTORTION</span>
                                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.squish || 30}</span>
                              </div>
                              <input 
                                type="range" 
                                min="0" 
                                max="80" 
                                value={activeLayer.squish || 30} 
                                onChange={e => updateActiveLayerProperty('squish', parseInt(e.target.value))} 
                                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                              />
                            </div>
                          )}

                          {activeLayer.animationType === 'bounce' && (
                            <div>
                              <label style={studioLabelStyle}>Direction Flow Vector</label>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                                {['up', 'down', 'left', 'right'].map(d => (
                                  <button 
                                    key={d} 
                                    onClick={() => updateActiveLayerProperty('direction', d)} 
                                    style={{
                                      ...studioBlockButtonStyle,
                                      padding: '6px 0',
                                      fontSize: '9px',
                                      backgroundColor: activeLayer.direction === d ? 'rgba(217, 119, 6, 0.1)' : 'rgba(15, 9, 28, 0.02)',
                                      borderColor: activeLayer.direction === d ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.06)',
                                      color: activeLayer.direction === d ? BRAND_AMBER_GOLD : '#1f2937',
                                      boxShadow: activeLayer.direction === d ? BRAND_GOLD_GLOW : 'none'
                                    }}
                                  >
                                    {d.toUpperCase()}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'placement' && (
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                          <span style={{ color: '#5b6275', fontWeight: '600' }}>X OFFSET POSITION</span>
                          <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.xOffset || 0}px</span>
                        </div>
                        <input 
                          type="range" 
                          min="-300" 
                          max="300" 
                          value={activeLayer.xOffset || 0} 
                          onChange={e => updateActiveLayerProperty('xOffset', parseInt(e.target.value))} 
                          style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                        />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                          <span style={{ color: '#5b6275', fontWeight: '600' }}>Y OFFSET POSITION</span>
                          <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.yOffset || 0}px</span>
                        </div>
                        <input 
                          type="range" 
                          min="-300" 
                          max="300" 
                          value={activeLayer.yOffset || 0} 
                          onChange={e => updateActiveLayerProperty('yOffset', parseInt(e.target.value))} 
                          style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                        />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                          <span style={{ color: '#5b6275', fontWeight: '600' }}>STATIC ROTATION OFFSET</span>
                          <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace', fontWeight: 'bold' }}>{activeLayer.rotationOffset || 0}°</span>
                        </div>
                        <input 
                          type="range" 
                          min="-180" 
                          max="180" 
                          value={activeLayer.rotationOffset || 0} 
                          onChange={e => updateActiveLayerProperty('rotationOffset', parseInt(e.target.value))} 
                          style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} 
                        />
                      </div>
                    </div>
                  )}

                </div>
              </>

                  {/* Live Size Counter before Export */}
                  <div style={{ 
                    marginTop: '12px', 
                    marginBottom: '16px', 
                    padding: '14px 16px', 
                    borderRadius: '16px', 
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(241, 240, 245, 0.9) 100%)', 
                    border: '1px solid rgba(168, 85, 247, 0.15)',
                    boxShadow: '0 4px 15px rgba(15, 9, 28, 0.03)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>Live Size Estimate:</span>
                      <strong style={{ fontSize: '14px', fontFamily: 'monospace', color: estimatedSize > 2.5 * 1024 * 1024 ? '#ef4444' : BRAND_AMBER_GOLD }}>
                        ~{formatFileSize(estimatedSize)}
                      </strong>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ flex: 1, height: '6px', backgroundColor: 'rgba(15, 9, 28, 0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${Math.min(100, (estimatedSize / (2.5 * 1024 * 1024)) * 100)}%`, 
                          height: '100%', 
                          backgroundColor: estimatedSize > 2.5 * 1024 * 1024 ? '#ef4444' : (estimatedSize > 1.5 * 1024 * 1024 ? '#f59e0b' : '#10b981'),
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <span style={{ fontSize: '9px', fontWeight: '800', color: estimatedSize > 2.5 * 1024 * 1024 ? '#ef4444' : (estimatedSize > 1.5 * 1024 * 1024 ? '#f59e0b' : '#10b981') }}>
                        {estimatedSize > 2.5 * 1024 * 1024 ? 'HEAVY' : (estimatedSize > 1.5 * 1024 * 1024 ? 'MEDIUM' : 'OPTIMAL')}
                      </span>
                    </div>
                    
                    {estimatedSize > 2.5 * 1024 * 1024 ? (
                      <div style={{ fontSize: '9.5px', color: '#ef4444', fontWeight: '600', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>⚠️ High weight! Recommended to keep under 2.5 MB.</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: '9.5px', color: '#6b7280', fontWeight: '500', marginTop: '8px' }}>
                        Good weight for fast email delivery.
                      </div>
                    )}
                  </div>

            <button 
              onClick={handleExportButtonClick} 
              disabled={isCompiling} 
              className={isCompiling ? "" : "pulse-gold"}
              style={{ 
                width: '100%', 
                padding: '16px', 
                backgroundColor: 'rgba(15, 9, 28, 0.06)', 
                color: '#5b6275', 
                fontWeight: '800', 
                border: '1px solid rgba(15, 9, 28, 0.08)', 
                borderRadius: '16px', 
                cursor: isCompiling ? 'not-allowed' : 'pointer', 
                boxShadow: 'none', 
                transition: 'all 0.2s', 
                textTransform: 'uppercase', 
                letterSpacing: '0.05em' 
              }}
              onMouseEnter={e => {
                if (!isCompiling) {
                  e.currentTarget.style.backgroundColor = BRAND_AMBER_GOLD;
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.borderColor = 'transparent';
                  e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW;
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'rgba(15, 9, 28, 0.06)';
                e.currentTarget.style.color = '#5b6275';
                e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.08)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {isCompiling ? 'Generating premium output...' : 'Export To Library'}
            </button>
            {exportedFileSize !== null && (
              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: '12px', backgroundColor: 'rgba(168, 85, 247, 0.04)', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
                <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Exported Size:</span>
                <strong style={{ fontSize: '13px', fontFamily: 'monospace', color: exportedFileSize > 2.5 * 1024 * 1024 ? '#ef4444' : BRAND_AMBER_GOLD }}>
                  {formatFileSize(exportedFileSize)}
                </strong>
              </div>
            )}
          </div>

          {/* RIGHT PREVIEW CANVAS */}
          <div className="glass-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.15)', background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(241, 240, 245, 0.8) 100%)' }}>
            <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', marginBottom: '24px', letterSpacing: '1px', fontWeight: '700' }}>Live Asset Preview</span>
            
            <div style={{ 
              width: '100%', 
              maxWidth: '500px', 
              borderRadius: '20px', 
              overflow: 'hidden',
              boxShadow: '0 20px 40px rgba(15, 9, 28, 0.12), 0 0 20px rgba(168, 85, 247, 0.05)',
              border: '1px solid rgba(168, 85, 247, 0.15)',
              backgroundColor: isTransparent ? '#ffffff' : bgColor,
              backgroundSize: '20px 20px',
              backgroundImage: isTransparent ? 'linear-gradient(45deg, #f1f0f5 25%, transparent 25%, transparent 75%, #f1f0f5 75%, #f1f0f5), linear-gradient(45deg, #f1f0f5 25%, #fff 25%, #fff 75%, #f1f0f5 75%, #f1f0f5)' : 'none',
              backgroundPosition: isTransparent ? '0 0, 10px 10px' : 'none',
              transition: 'all 0.3s ease'
            }}>
              <canvas
                ref={canvasRef}
                width={esCanvasWidth}
                height={esCanvasHeight}
                style={{
                  width: '100%',
                  display: 'block',
                  cursor: isCanvasDragging ? 'grabbing' : (hoveredCanvasLayerId ? 'grab' : 'default')
                }}
                onMouseDown={(e) => handleDragStart(e.clientX, e.clientY)}
                onMouseMove={(e) => handleDragMove(e.clientX, e.clientY)}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={(e) => {
                  if (e.touches && e.touches[0]) {
                    handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
                  }
                }}
                onTouchMove={(e) => {
                  if (e.touches && e.touches[0]) {
                    handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
                  }
                }}
                onTouchEnd={handleDragEnd}
              />
            </div>
          </div>

        </div>
      </div>

      {/* MY PROJECTS MODAL */}
      {showProjectsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 9, 28, 0.4)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="glass-panel animate-slide-up" style={{
            width: '90%',
            maxWidth: '650px',
            maxHeight: '80vh',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(168, 85, 247, 0.2)',
            boxShadow: '0 24px 60px rgba(15, 9, 28, 0.15), 0 0 40px rgba(168, 85, 247, 0.05)',
            borderRadius: '32px',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', display: 'block', marginBottom: '4px', fontWeight: '800', letterSpacing: '1px' }}>📁 Project Management</span>
                <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f091c' }}>Saved Design Projects</h3>
              </div>
              <button 
                onClick={() => {
                  if (window.confirm('Start a fresh new project? Unsaved changes on this current canvas will be lost.')) {
                    setCurrentProjectId(null);
                    setOriginalProjectName('');
                    setEditingAssetFile(null);
                    setExportedFileSize(null);
                    setElementName('StudioElement');
                    setLayers([
                      {
                        id: 'l-1',
                        name: 'Text Layer 1',
                        type: 'text',
                        visible: true,
                        xOffset: 0,
                        yOffset: 0,
                        rotationOffset: 0,
                        text: 'WORRIED ABOUT HENRY',
                        font: 'Arial',
                        fontSize: 38,
                        letterSpacing: 0,
                        color1: '#0f091c',
                        color2: '#d97706',
                        strokeColor: '#0f091c',
                        animationType: 'bounce',
                        speed: 50,
                        distance: 25,
                        squish: 30,
                        direction: 'down',
                        smoothingMode: 'linear',
                        shapeType: 'rect',
                        shapeWidth: 200,
                        shapeHeight: 100,
                        fillStyle: 'solid',
                        outlineStyle: 'transparent',
                        strokeWidth: 3
                      }
                    ]);
                    setActiveLayerId('l-1');
                    setIsTransparent(false);
                    setBgColor('#ffffff');
                    setFps(12);
                    navigate('/element-studio', { replace: true });
                    setShowProjectsModal(false);
                  }
                }}
                style={{
                  backgroundColor: 'rgba(217, 119, 6, 0.08)',
                  border: '1px solid rgba(217, 119, 6, 0.2)',
                  color: BRAND_AMBER_GOLD,
                  padding: '8px 14px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                + Start New Design
              </button>
            </div>

            {/* Project Cards Grid */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', marginBottom: '20px' }}>
              {projectsList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#5b6275' }}>
                  <p style={{ margin: '0 0 10px 0', fontSize: '15px', fontWeight: '600' }}>No projects saved yet.</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>Click "Save Project" in the header to preserve your work-in-progress layouts!</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {projectsList.map(proj => (
                    <div 
                      key={proj.id} 
                      className="glass-panel" 
                      style={{ 
                        padding: '12px', 
                        borderRadius: '20px', 
                        border: currentProjectId === proj.id ? '2px solid rgba(217, 119, 6, 0.4)' : '1px solid rgba(168, 85, 247, 0.1)', 
                        background: 'rgba(255, 255, 255, 0.6)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        position: 'relative'
                      }}
                    >
                      {/* Project Thumbnail */}
                      <div style={{
                        width: '100%',
                        height: '100px',
                        borderRadius: '12px',
                        backgroundColor: '#f1f0f5',
                        backgroundImage: proj.thumbnailUrl ? `url(${BACKEND_URL}${proj.thumbnailUrl})` : 'none',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        border: '1px solid rgba(15, 9, 28, 0.05)',
                        position: 'relative'
                      }}>
                        {currentProjectId === proj.id && (
                          <span style={{ position: 'absolute', top: '6px', left: '6px', backgroundColor: BRAND_AMBER_GOLD, color: '#fff', fontSize: '9px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '4px' }}>
                            ACTIVE EDITING
                          </span>
                        )}
                      </div>

                      {/* Project Info & Actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontWeight: '700', fontSize: '14px', color: '#0f091c', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {proj.name}
                        </span>
                        <span style={{ fontSize: '11px', color: '#5b6275' }}>
                          Updated: {new Date(proj.updatedAt).toLocaleString()}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                        <button 
                          onClick={() => loadProjectIntoEditor(proj)}
                          style={{
                            flex: 1,
                            backgroundColor: '#0f091c',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 0',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontWeight: '700',
                            fontSize: '12px',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          ✏️ Load Project
                        </button>
                        <button 
                          onClick={() => handleDuplicateProject(proj.id)}
                          style={{
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                            border: '1px solid rgba(168, 85, 247, 0.2)',
                            color: '#a855f7',
                            width: '36px',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          title="Duplicate/Copy Project"
                        >
                          👥
                        </button>
                        <button 
                          onClick={() => handleDeleteProject(proj.id)}
                          style={{
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.2)',
                            color: '#ef4444',
                            width: '36px',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          title="Delete Project"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowProjectsModal(false)}
                style={{
                  backgroundColor: 'rgba(15, 9, 28, 0.05)',
                  border: '1px solid rgba(15, 9, 28, 0.08)',
                  color: '#1f2937',
                  padding: '10px 24px',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  fontWeight: '700',
                  fontSize: '13px',
                  transition: 'all 0.2s'
                }}
              >
                Close Manager
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CROP MODAL OVERLAY */}
      {cropModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 9, 28, 0.4)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="glass-panel animate-slide-up" style={{
            width: '90%',
            maxWidth: '520px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: `1px solid rgba(217, 119, 6, 0.25)`,
            boxShadow: '0 24px 60px rgba(15, 9, 28, 0.15), 0 0 40px rgba(217, 119, 6, 0.05)',
            borderRadius: '32px',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', display: 'block', marginBottom: '8px', fontWeight: '800', letterSpacing: '1px' }}>✂ IMAGE FIT CONFIGURATION</span>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '800', color: '#0f091c', textAlign: 'center' }}>Adjust Image Crop</h3>
            
            {/* Aspect Locked Crop Canvas Box */}
            <div style={{
              width: '100%',
              maxWidth: '450px',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f1f0f5',
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px dashed rgba(217, 119, 6, 0.2)`,
              position: 'relative',
              boxShadow: 'inset 0 4px 12px rgba(15, 9, 28, 0.05)'
            }}>
              {(() => {
                const aspect = cropAspect || 1.0;
                let viewWidth = 450;
                let viewHeight = 450 / aspect;
                if (viewHeight > 300) {
                  viewHeight = 300;
                  viewWidth = 300 * aspect;
                }
                return (
                  <canvas
                    ref={cropCanvasRef}
                    width={viewWidth}
                    height={viewHeight}
                    onMouseDown={handleCropMouseDown}
                    onMouseMove={handleCropMouseMove}
                    onMouseUp={handleCropMouseUpOrLeave}
                    onMouseLeave={handleCropMouseUpOrLeave}
                    onTouchStart={handleCropTouchStart}
                    onTouchMove={handleCropTouchMove}
                    onTouchEnd={handleCropMouseUpOrLeave}
                    style={{
                      width: `${viewWidth}px`,
                      height: `${viewHeight}px`,
                      cursor: 'move',
                      boxShadow: '0 8px 24px rgba(15, 9, 28, 0.1)',
                      backgroundColor: '#ffffff',
                      display: 'block'
                    }}
                  />
                );
              })()}
            </div>
            
            <p style={{ fontSize: '11px', color: '#6b7280', margin: '12px 0 20px 0', textAlign: 'center', fontWeight: '500' }}>
              Drag to pan image. Select an aspect ratio preset below.
            </p>

            {/* Dynamic Aspect Ratio Selector */}
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                <span>CROP ASPECT RATIO</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '5px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                {[
                  { key: cropOriginalAspect, label: '🖼️ Original' },
                  { key: 1/1, label: '⬜ 1:1 Square' },
                  { key: 16/9, label: '💻 16:9' },
                  { key: 9/16, label: '📱 9:16' },
                  { key: 4/3, label: '📷 4:3' },
                  { key: 3/4, label: '📸 3:4' }
                ].map(item => {
                  const isActive = Math.abs(cropAspect - item.key) < 0.01;
                  return (
                    <button
                      key={item.label}
                      onClick={() => {
                        setCropAspect(item.key);
                        setCropZoom(1.0);
                        setCropPanX(0);
                        setCropPanY(0);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 2px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '9.5px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        background: isActive ? '#ffffff' : 'transparent',
                        color: isActive ? BRAND_AMBER_GOLD : '#5b6275',
                        boxShadow: isActive ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                        transition: 'all 0.2s',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Zoom Slider */}
            <div style={{ width: '100%', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                <span>ZOOM MAGNIFICATION</span>
                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace' }}>{Math.round(cropZoom * 100)}%</span>
              </div>
              <input
                type="range"
                min="1.0"
                max="5.0"
                step="0.02"
                value={cropZoom}
                onChange={e => {
                  const newZoom = parseFloat(e.target.value);
                  setCropZoom(newZoom);
                }}
                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }}
              />
            </div>

            {/* Compression & Resolution Settings */}
            <div style={{
              width: '100%',
              backgroundColor: 'rgba(217, 119, 6, 0.03)',
              border: '1px solid rgba(217, 119, 6, 0.12)',
              borderRadius: '16px',
              padding: '15px',
              marginBottom: '24px',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '10px', color: BRAND_AMBER_GOLD, fontWeight: '800', textTransform: 'uppercase', display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
                ⚙️ Optimize Size & Resolution
              </span>

              {/* Target Scale Row */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                  <span>SCALE</span>
                  <span style={{ color: BRAND_AMBER_GOLD, fontWeight: '800' }}>
                    {cropScale === 'original' ? 'Original' : `${parseFloat(cropScale) * 100}%`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'original', label: 'Original' },
                    { key: '0.75', label: '75%' },
                    { key: '0.5', label: '50%' },
                    { key: '0.33', label: '33%' },
                    { key: '0.25', label: '25%' },
                    { key: '0.1', label: '10%' }
                  ].map(opt => {
                    const isSelected = cropScale === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setCropScale(opt.key)}
                        style={{
                          flex: '1 1 28%',
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: isSelected ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.08)',
                          fontSize: '10px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isSelected ? `linear-gradient(135deg, ${BRAND_AMBER_GOLD}, #f59e0b)` : '#ffffff',
                          color: isSelected ? '#ffffff' : '#5b6275',
                          transition: 'all 0.15s',
                          marginBottom: '4px'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Format Row */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                  <span>OUTPUT FORMAT</span>
                  <span style={{ color: BRAND_AMBER_GOLD, fontWeight: '800', textTransform: 'uppercase' }}>{cropFormat}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[
                    { key: 'png', label: 'PNG (Lossless)' },
                    { key: 'jpeg', label: 'JPEG (Compress)' },
                    { key: 'webp', label: 'WebP (Compact)' }
                  ].map(opt => {
                    const isSelected = cropFormat === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setCropFormat(opt.key)}
                        style={{
                          flex: 1,
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: isSelected ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.08)',
                          fontSize: '10px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isSelected ? `linear-gradient(135deg, ${BRAND_AMBER_GOLD}, #f59e0b)` : '#ffffff',
                          color: isSelected ? '#ffffff' : '#5b6275',
                          transition: 'all 0.15s'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quality Row */}
              {(cropFormat === 'jpeg' || cropFormat === 'webp') && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                    <span>COMPRESSION AMOUNT</span>
                    <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace' }}>{Math.round((1 - cropQuality) * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="90"
                    step="5"
                    value={Math.round((1 - cropQuality) * 100)}
                    onChange={e => setCropQuality(Math.round((1 - parseFloat(e.target.value) / 100) * 100) / 100)}
                    style={{ width: '100%', accentColor: BRAND_AMBER_GOLD, cursor: 'pointer' }}
                  />
                </div>
              )}

              {/* Size Savings Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 9, 28, 0.04)',
                fontSize: '11px',
                fontWeight: '700',
                color: '#5b6275'
              }}>
                <span>ESTIMATED FILE SIZE:</span>
                <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {cropOriginalSize > 0 && (
                    <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontWeight: '500' }}>
                      {(cropOriginalSize / 1024).toFixed(1)} KB
                    </span>
                  )}
                  <span style={{ color: '#10b981', fontWeight: '800' }}>
                    {(cropEstimatedSize / 1024).toFixed(1)} KB
                  </span>
                  {cropOriginalSize > 0 && cropEstimatedSize > 0 && (
                    <span style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '9.5px',
                      fontWeight: '800'
                    }}>
                      -{Math.max(0, Math.round(((cropOriginalSize - cropEstimatedSize) / cropOriginalSize) * 100))}%
                    </span>
                  )}
                </span>
              </div>
            </div>
            
            {/* Modal Actions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', width: '100%' }}>
              <button
                onClick={() => {
                  setCropModalOpen(false);
                  setCropRawImage(null);
                }}
                style={{
                  padding: '12px',
                  borderRadius: '16px',
                  border: '1px solid rgba(15, 9, 28, 0.08)',
                  background: 'rgba(15, 9, 28, 0.02)',
                  color: '#4b5563',
                  fontWeight: '700',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(15, 9, 28, 0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(15, 9, 28, 0.02)'}
              >
                Cancel
              </button>
              <button
                onClick={handleSkipCrop}
                style={{
                  padding: '12px',
                  borderRadius: '16px',
                  border: `1px solid rgba(217, 119, 6, 0.2)`,
                  background: '#ffffff',
                  color: BRAND_AMBER_GOLD,
                  fontWeight: '700',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Skip Crop
              </button>
              <button
                onClick={handleApplyCrop}
                style={{
                  padding: '12px',
                  borderRadius: '16px',
                  border: 'none',
                  background: `linear-gradient(135deg, ${BRAND_AMBER_GOLD}, #f59e0b)`,
                  color: '#ffffff',
                  fontWeight: '800',
                  fontSize: '12px',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(217, 119, 6, 0.25)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(217, 119, 6, 0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(217, 119, 6, 0.25)'; }}
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Element Studio Overwrite / Save Copy Choice Modal */}
      {showSaveChoiceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 9, 28, 0.4)', zIndex: '99999', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(16px)' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '480px', borderRadius: '24px', padding: '30px', display: 'flex', flexDirection: 'column', textAlign: 'center', boxShadow: '0 24px 60px -10px rgba(15, 9, 28, 0.25)', border: '1px solid rgba(168, 85, 247, 0.2)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
            <span style={{ fontSize: '32px', marginBottom: '16px' }}>💾</span>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '20px', fontWeight: '800', color: '#0f091c' }}>Save Element Design</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#5b6275', lineHeight: '1.5' }}>
              You are editing an existing asset: <strong>{editingAssetFile}</strong>. Do you want to overwrite the original design file, or save this as a new copy?
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button 
                onClick={() => {
                  setShowSaveChoiceModal(false);
                  generateAnimatedModuleGif(editingAssetFile);
                }}
                className="gradient-button"
                style={{ 
                  padding: '14px', 
                  borderRadius: '12px', 
                  fontWeight: 'bold', 
                  border: 'none', 
                  cursor: 'pointer', 
                  color: '#fff',
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)'
                }}
              >
                ✏️ Overwrite Original Asset
              </button>
              <button 
                onClick={() => {
                  setShowSaveChoiceModal(false);
                  generateAnimatedModuleGif(null); // will generate a new name
                }}
                style={{ 
                  padding: '14px', 
                  borderRadius: '12px', 
                  fontWeight: 'bold', 
                  border: '1px solid rgba(168, 85, 247, 0.3)', 
                  cursor: 'pointer', 
                  color: '#a855f7',
                  backgroundColor: 'rgba(168, 85, 247, 0.05)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.1)'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.05)'}
              >
                ➕ Save as a New Copy
              </button>
              <button 
                onClick={() => setShowSaveChoiceModal(false)}
                style={{ 
                  padding: '12px', 
                  borderRadius: '12px', 
                  fontWeight: 'bold', 
                  border: 'none', 
                  cursor: 'pointer', 
                  color: '#6b7280',
                  backgroundColor: 'transparent'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REPOSITORY SELECTION LIBRARY INTERACTIVE OVERLAY MODAL */}
      {showLibraryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 9, 28, 0.3)', zIndex: '9999', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(16px)' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '680px', background: 'rgba(255, 255, 255, 0.85) !important', border: '1px solid rgba(168, 85, 247, 0.18) !important', borderRadius: '24px', padding: '25px', display: 'flex', flexDirection: 'column', maxHeight: '85vh', boxShadow: '0 24px 60px -10px rgba(15, 9, 28, 0.15) !important' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#0f091c', fontWeight: '800', letterSpacing: '0.5px' }}>Central Upload Media Library</h3>
              <button onClick={() => setShowLibraryModal(false)} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: '20px', cursor: 'pointer', transition: 'color 0.2s' }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: '10px', backgroundColor: '#f1f0f5', padding: '6px', borderRadius: '16px', marginBottom: '20px', border: '1px solid rgba(168, 85, 247, 0.1)' }}>
              <button onClick={() => setLibraryTab('images')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', background: libraryTab === 'images' ? '#ffffff' : 'transparent', color: libraryTab === 'images' ? '#d97706' : '#5b6275', transition: 'all 0.2s' }}>🖼 IMAGES{libraryTotals.images > 0 ? ` (${libraryTotals.images})` : ''}</button>
              <button onClick={() => setLibraryTab('gifs')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', background: libraryTab === 'gifs' ? '#ffffff' : 'transparent', color: libraryTab === 'gifs' ? '#d97706' : '#5b6275', transition: 'all 0.2s' }}>⚡ GIFs{libraryTotals.gifs > 0 ? ` (${libraryTotals.gifs})` : ''}</button>
              <button onClick={() => setLibraryTab('createdAssets')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', background: libraryTab === 'createdAssets' ? '#ffffff' : 'transparent', color: libraryTab === 'createdAssets' ? '#d97706' : '#5b6275', transition: 'all 0.2s' }}>🎨 CREATED{libraryTotals.createdAssets > 0 ? ` (${libraryTotals.createdAssets})` : ''}</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>

              {/* Loading skeleton */}
              {libraryLoading && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                  {Array.from({ length: LIBRARY_PAGE_SIZE }).map((_, i) => (
                    <div key={i} style={{ borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f1f0f5', border: '1px solid rgba(168, 85, 247, 0.08)' }}>
                      <div style={{ height: '100px', background: 'linear-gradient(90deg, #f1f0f5 25%, #e8e6ef 50%, #f1f0f5 75%)', backgroundSize: '200% 100%', animation: 'libraryShimmer 1.4s infinite' }} />
                      <div style={{ height: '28px', margin: '8px', borderRadius: '6px', background: 'linear-gradient(90deg, #f1f0f5 25%, #e8e6ef 50%, #f1f0f5 75%)', backgroundSize: '200% 100%', animation: 'libraryShimmer 1.4s infinite 0.2s' }} />
                    </div>
                  ))}
                  <style>{`@keyframes libraryShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
                </div>
              )}

              {/* Images tab */}
              {!libraryLoading && libraryTab === 'images' && (
                libraryData.images?.length === 0 ? (
                  <p style={{ color: '#4b5563', textAlign: 'center', padding: '40px' }}>No raw image assets uploaded.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                      {libraryData.images.map((img, idx) => (
                        <div key={idx} onClick={() => handleLibraryItemSelect(img.url)} style={{ position: 'relative', border: '1px solid rgba(168, 85, 247, 0.12)', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease-in-out', boxShadow: '0 4px 12px rgba(15, 9, 28, 0.03)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.12)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8f7fa' }}>
                            <img 
                              src={`${BACKEND_URL}/api/uploads-library/thumb?url=${encodeURIComponent(img.url)}&w=260`} 
                              alt={img.fileName} 
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '10px', color: '#1f2937', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {img.fileName}
                            </div>
                            <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {formatFileSize(img.sizeInBytes)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {libraryData.images.length < libraryTotals.images && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <button 
                          onClick={loadMoreLibraryItems} 
                          style={{ padding: '10px 24px', backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.backgroundColor = '#b45309'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#d97706'; }}
                        >
                          Load More ({libraryData.images.length} of {libraryTotals.images})
                        </button>
                      </div>
                    )}
                  </>
                )
              )}

              {/* GIFs tab */}
              {!libraryLoading && libraryTab === 'gifs' && (
                libraryData.gifs?.length === 0 ? (
                  <p style={{ color: '#4b5563', textAlign: 'center', padding: '40px' }}>No compiled looping GIF assets found.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                      {libraryData.gifs.map((gif, idx) => (
                        <div key={idx} onClick={() => handleLibraryItemSelect(gif.url)} style={{ position: 'relative', border: '1px solid rgba(168, 85, 247, 0.12)', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease-in-out', boxShadow: '0 4px 12px rgba(15, 9, 28, 0.03)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.12)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8f7fa' }}>
                            <img src={`${BACKEND_URL}${gif.url}`} alt={gif.fileName} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {gif.fileName}
                            </div>
                            <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {formatFileSize(gif.sizeInBytes)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {libraryData.gifs.length < libraryTotals.gifs && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <button 
                          onClick={loadMoreLibraryItems} 
                          style={{ padding: '10px 24px', backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.backgroundColor = '#b45309'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#d97706'; }}
                        >
                          Load More ({libraryData.gifs.length} of {libraryTotals.gifs})
                        </button>
                      </div>
                    )}
                  </>
                )
              )}

              {/* Created Assets tab */}
              {!libraryLoading && libraryTab === 'createdAssets' && (
                (libraryData.createdAssets?.length === 0 || !libraryData.createdAssets) ? (
                  <p style={{ color: '#4b5563', textAlign: 'center', padding: '40px' }}>No flattened modular graphics configured in Element Studio yet.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                      {libraryData.createdAssets.map((asset, idx) => (
                        <div key={idx} onClick={() => handleLibraryItemSelect(asset.url)} style={{ position: 'relative', border: '1px solid rgba(168, 85, 247, 0.12)', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease-in-out', boxShadow: '0 4px 12px rgba(15, 9, 28, 0.03)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.12)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8f7fa' }}>
                            <img 
                              src={asset.url.endsWith('.gif') ? `${BACKEND_URL}${asset.url}` : `${BACKEND_URL}/api/uploads-library/thumb?url=${encodeURIComponent(asset.url)}&w=260`} 
                              alt={asset.fileName} 
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {asset.fileName}
                            </div>
                            <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {formatFileSize(asset.sizeInBytes)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {libraryData.createdAssets.length < libraryTotals.createdAssets && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <button 
                          onClick={loadMoreLibraryItems} 
                          style={{ padding: '10px 24px', backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.backgroundColor = '#b45309'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#d97706'; }}
                        >
                          Load More ({libraryData.createdAssets.length} of {libraryTotals.createdAssets})
                        </button>
                      </div>
                    )}
                  </>
                )
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: MAIN STUDIO CONTROL PANEL
// ==========================================
function EditorView() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Last 10 used colors history (EditorView)
  const [usedColors, setUsedColors] = useState(() => {
    try {
      const saved = localStorage.getItem('louder_studio_used_colors');
      return saved ? JSON.parse(saved) : ['#0f091c', '#d97706', '#ffffff', '#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];
    } catch (e) {
      return ['#0f091c', '#d97706', '#ffffff', '#10b981', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1'];
    }
  });

  const addColorToHistory = (color) => {
    if (!color || color === 'transparent') return;
    const hex = color.toLowerCase();
    setUsedColors(prev => {
      const filtered = prev.filter(c => c.toLowerCase() !== hex);
      const updated = [color, ...filtered].slice(0, 10);
      try {
        localStorage.setItem('louder_studio_used_colors', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const renderRecentSwatches = (onClickCallback) => {
    if (!usedColors || usedColors.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '6px', alignItems: 'center' }}>
        {usedColors.slice(0, 10).map((c, idx) => (
          <button
            key={`swatch-${c}-${idx}`}
            title={c}
            type="button"
            onClick={() => onClickCallback(c)}
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '5px',
              backgroundColor: c,
              border: '1px solid rgba(15, 9, 28, 0.12)',
              cursor: 'pointer',
              padding: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'transform 0.1s ease',
              flexShrink: 0
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
          />
        ))}
      </div>
    );
  };

  const renderColorInput = (label, value, onChange, onBlurCallback = null) => {
    const handleBlur = (e) => {
      const val = e.target.value;
      addColorToHistory(val);
      if (onBlurCallback) onBlurCallback(val);
    };

    return (
      <div style={{ marginBottom: '14px', width: '100%' }}>
        {label && <label style={studioLabelStyle}>{label}</label>}
        <div className="glass-input" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', padding: '10px 12px', borderRadius: '16px', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input 
              type="color" 
              value={value || '#ffffff'} 
              onChange={e => onChange(e.target.value)} 
              onBlur={handleBlur}
              style={{ width: '28px', height: '28px', border: 'none', borderRadius: '8px', cursor: 'pointer', background: 'transparent', padding: 0 }} 
            />
            <span style={{ fontSize: '12px', fontFamily: 'monospace', color: '#fff', fontWeight: '700' }}>
              {(value || '#FFFFFF').toUpperCase()}
            </span>
          </div>
          {usedColors && usedColors.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px', borderTop: '1px solid rgba(15, 9, 28, 0.05)', paddingTop: '6px' }}>
              {usedColors.slice(0, 8).map((c, idx) => (
                <div 
                  key={`swatch-picker-${c}-${idx}`}
                  title={c}
                  onClick={() => {
                    onChange(c);
                    addColorToHistory(c);
                    if (onBlurCallback) onBlurCallback(c);
                  }}
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '4px',
                    backgroundColor: c,
                    cursor: 'pointer',
                    border: '1px solid rgba(0,0,0,0.15)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'transform 0.15s ease'
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const [campaignName, setCampaignName] = useState('Untitled Campaign');
  const [canvasModules, setCanvasModules] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [htmlImportValue, setHtmlImportValue] = useState('');
  const [htmlImportUrl, setHtmlImportUrl] = useState('');
  const [importTab, setImportTab] = useState('html'); // 'html' | 'url'
  const [isImporting, setIsImporting] = useState(false);
  const [importStatusText, setImportStatusText] = useState('');
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' | 'tablet' | 'mobile'
  const [isPreviewDarkMode, setIsPreviewDarkMode] = useState(false);
  const [hoveredModuleId, setHoveredModuleId] = useState(null);

  const [collapsedSections, setCollapsedSections] = useState({
    injectBlock: false,
    customBlocks: true,
    bgLayout: true,
    maxWidth: true,
    devicePreview: true,
    blockBgFill: true,
    geminiAssistant: true,
    columnsSplit: true,
    columnsBehavior: true,
    blockProperties: true,
    imageCrop: true,
    flashSequencer: true,
    videoToGif: true,
    textPanel: false
  });

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Master Background Wallpaper Aspect-Locked Cropper states
  const [wpCropModalOpen, setWpCropModalOpen] = useState(false);
  const [wpCropRawImage, setWpCropRawImage] = useState(null);
  const [wpCropZoom, setWpCropZoom] = useState(1.0);
  const [wpCropPanX, setWpCropPanX] = useState(0);
  const [wpCropPanY, setWpCropPanY] = useState(0);
  const [wpCropIsDragging, setWpCropIsDragging] = useState(false);
  const [wpCropDragStart, setWpCropDragStart] = useState({ x: 0, y: 0 });
  const [wpCropFileName, setWpCropFileName] = useState('');
  const [wpCropAspect, setWpCropAspect] = useState(9/16); // default to portrait
  const wpCropCanvasRef = useRef(null);

  // Artwork Cropper Modal states
  const [artworkCropModalOpen, setArtworkCropModalOpen] = useState(false);
  const [artworkCropRawImage, setArtworkCropRawImage] = useState(null);
  const [artworkCropZoom, setArtworkCropZoom] = useState(1.0);
  const [artworkCropPanX, setArtworkCropPanX] = useState(0);
  const [artworkCropPanY, setArtworkCropPanY] = useState(0);
  const [artworkCropAspect, setArtworkCropAspect] = useState(1.0);
  const [artworkCropOriginalAspect, setArtworkCropOriginalAspect] = useState(1.0);
  const [artworkCropFileName, setArtworkCropFileName] = useState('');
  const [artworkCropModuleId, setArtworkCropModuleId] = useState(null);
  const [artworkCropIsDragging, setArtworkCropIsDragging] = useState(false);
  const [artworkCropDragStart, setArtworkCropDragStart] = useState({ x: 0, y: 0 });
  const artworkCropCanvasRef = useRef(null);

  // Artwork compression & scaling state
  const [artworkScale, setArtworkScale] = useState('original'); // 'original', '0.75', '0.5', '0.33', '0.25', '0.1'
  const [artworkFormat, setArtworkFormat] = useState('jpeg'); // 'png', 'jpeg', 'webp'
  const [artworkQuality, setArtworkQuality] = useState(0.85);
  const [artworkOriginalSize, setArtworkOriginalSize] = useState(0); // in bytes
  const [artworkEstimatedSize, setArtworkEstimatedSize] = useState(0); // in bytes

  // Wallpaper compression & scaling state
  const [wpScale, setWpScale] = useState('original'); // 'original', '0.75', '0.5', '0.33', '0.25', '0.1'
  const [wpFormat, setWpFormat] = useState('jpeg'); // 'png', 'jpeg', 'webp'
  const [wpQuality, setWpQuality] = useState(0.85);
  const [wpOriginalSize, setWpOriginalSize] = useState(0); // in bytes
  const [wpEstimatedSize, setWpEstimatedSize] = useState(0); // in bytes

  // Redraw the wallpaper crop canvas when pan, zoom, or aspect ratio changes
  useEffect(() => {
    if (!wpCropModalOpen || !wpCropRawImage || !wpCropCanvasRef.current) return;
    const canvas = wpCropCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = wpCropRawImage;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = canvas.height / img.height;
      } else {
        baseScale = canvas.width / img.width;
      }

      const drawScale = baseScale * wpCropZoom;
      const centerX = (canvas.width - img.width * drawScale) / 2;
      const centerY = (canvas.height - img.height * drawScale) / 2;

      ctx.save();
      ctx.drawImage(
        img,
        centerX + wpCropPanX,
        centerY + wpCropPanY,
        img.width * drawScale,
        img.height * drawScale
      );
      ctx.restore();
    };
  }, [wpCropModalOpen, wpCropRawImage, wpCropZoom, wpCropPanX, wpCropPanY, wpCropAspect]);

  // Effect to estimate cropped artwork file size reactively
  useEffect(() => {
    if (!artworkCropModalOpen || !artworkCropRawImage) {
      setArtworkEstimatedSize(0);
      return;
    }

    const img = new Image();
    img.src = artworkCropRawImage;
    img.onload = () => {
      const aspect = artworkCropAspect || 1;
      let viewWidth = 450;
      let viewHeight = 450 / aspect;
      if (viewHeight > 300) {
        viewHeight = 300;
        viewWidth = 300 * aspect;
      }

      const canvasRatio = viewWidth / viewHeight;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = viewHeight / img.height;
      } else {
        baseScale = viewWidth / img.width;
      }

      const drawScale = baseScale * artworkCropZoom;
      const originalCropWidth = viewWidth / drawScale;

      let outputWidth = originalCropWidth;
      if (artworkScale !== 'original') {
        outputWidth = originalCropWidth * parseFloat(artworkScale);
      }
      outputWidth = Math.round(outputWidth);
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      const resScale = outputWidth / viewWidth;
      const highResScale = drawScale * resScale;
      const highResPanX = artworkCropPanX * resScale;
      const highResPanY = artworkCropPanY * resScale;

      const centerX = (outputWidth - img.width * highResScale) / 2;
      const centerY = (outputHeight - img.height * highResScale) / 2;

      oCtx.save();
      oCtx.drawImage(
        img,
        centerX + highResPanX,
        centerY + highResPanY,
        img.width * highResScale,
        img.height * highResScale
      );
      oCtx.restore();

      const mimeType = artworkFormat === 'png' ? 'image/png' : (artworkFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const quality = artworkFormat === 'png' ? undefined : artworkQuality;
      const dataUrl = offscreen.toDataURL(mimeType, quality);
      const sizeBytes = Math.round((dataUrl.length * 3) / 4);
      setArtworkEstimatedSize(sizeBytes);
    };
  }, [
    artworkCropModalOpen,
    artworkCropRawImage,
    artworkCropZoom,
    artworkCropPanX,
    artworkCropPanY,
    artworkCropAspect,
    artworkScale,
    artworkFormat,
    artworkQuality
  ]);

  // Effect to estimate wallpaper background file size reactively
  useEffect(() => {
    if (!wpCropModalOpen || !wpCropRawImage) {
      setWpEstimatedSize(0);
      return;
    }

    const img = new Image();
    img.src = wpCropRawImage;
    img.onload = () => {
      const aspect = wpCropAspect || (9/16);
      let viewWidth = 450;
      let viewHeight = 450 / aspect;
      if (viewHeight > 300) {
        viewHeight = 300;
        viewWidth = 300 * aspect;
      }

      const canvasRatio = viewWidth / viewHeight;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = viewHeight / img.height;
      } else {
        baseScale = viewWidth / img.width;
      }

      const drawScale = baseScale * wpCropZoom;
      const originalCropWidth = viewWidth / drawScale;

      let outputWidth = originalCropWidth;
      if (wpScale !== 'original') {
        outputWidth = originalCropWidth * parseFloat(wpScale);
      }
      outputWidth = Math.round(outputWidth);
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      const resScale = outputWidth / viewWidth;
      const highResScale = drawScale * resScale;
      const highResPanX = wpCropPanX * resScale;
      const highResPanY = wpCropPanY * resScale;

      const centerX = (outputWidth - img.width * highResScale) / 2;
      const centerY = (outputHeight - img.height * highResScale) / 2;

      oCtx.save();
      oCtx.drawImage(
        img,
        centerX + highResPanX,
        centerY + highResPanY,
        img.width * highResScale,
        img.height * highResScale
      );
      oCtx.restore();

      const mimeType = wpFormat === 'png' ? 'image/png' : (wpFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const quality = wpFormat === 'png' ? undefined : wpQuality;
      const dataUrl = offscreen.toDataURL(mimeType, quality);
      const sizeBytes = Math.round((dataUrl.length * 3) / 4);
      setWpEstimatedSize(sizeBytes);
    };
  }, [
    wpCropModalOpen,
    wpCropRawImage,
    wpCropZoom,
    wpCropPanX,
    wpCropPanY,
    wpCropAspect,
    wpScale,
    wpFormat,
    wpQuality
  ]);

  const handleWpCropMouseDown = (e) => {
    setWpCropIsDragging(true);
    setWpCropDragStart({
      x: e.clientX - wpCropPanX,
      y: e.clientY - wpCropPanY
    });
  };

  const handleWpCropMouseMove = (e) => {
    if (!wpCropIsDragging) return;
    setWpCropPanX(e.clientX - wpCropDragStart.x);
    setWpCropPanY(e.clientY - wpCropDragStart.y);
  };

  const handleWpCropMouseUpOrLeave = () => {
    setWpCropIsDragging(false);
  };

  const handleWpCropTouchStart = (e) => {
    if (e.touches.length === 1) {
      setWpCropIsDragging(true);
      setWpCropDragStart({
        x: e.touches[0].clientX - wpCropPanX,
        y: e.touches[0].clientY - wpCropPanY
      });
    }
  };

  const handleWpCropTouchMove = (e) => {
    if (!wpCropIsDragging || e.touches.length !== 1) return;
    setWpCropPanX(e.touches[0].clientX - wpCropDragStart.x);
    setWpCropPanY(e.touches[0].clientY - wpCropDragStart.y);
  };

  const handleWpApplyCrop = () => {
    if (!wpCropRawImage) return;
    const img = new Image();
    img.src = wpCropRawImage;
    img.onload = () => {
      const aspect = wpCropAspect;

      // Bounding dimensions matches the 450x300 canvas container
      let viewWidth = 450;
      let viewHeight = 450 / aspect;
      if (viewHeight > 300) {
        viewHeight = 300;
        viewWidth = 300 * aspect;
      }

      const canvasRatio = viewWidth / viewHeight;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = viewHeight / img.height;
      } else {
        baseScale = viewWidth / img.width;
      }

      const drawScale = baseScale * wpCropZoom;
      const originalCropWidth = viewWidth / drawScale;
      let outputWidth = originalCropWidth;
      if (wpScale !== 'original') {
        outputWidth = originalCropWidth * parseFloat(wpScale);
      }
      outputWidth = Math.round(outputWidth);
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      const resScale = outputWidth / viewWidth;
      const highResScale = drawScale * resScale;
      const highResPanX = wpCropPanX * resScale;
      const highResPanY = wpCropPanY * resScale;

      const centerX = (outputWidth - img.width * highResScale) / 2;
      const centerY = (outputHeight - img.height * highResScale) / 2;

      oCtx.save();
      oCtx.drawImage(
        img,
        centerX + highResPanX,
        centerY + highResPanY,
        img.width * highResScale,
        img.height * highResScale
      );
      oCtx.restore();

      const mimeType = wpFormat === 'png' ? 'image/png' : (wpFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const croppedDataUrl = wpFormat === 'png'
        ? offscreen.toDataURL(mimeType)
        : offscreen.toDataURL(mimeType, wpQuality);

      const fileExt = wpFormat === 'webp' ? 'webp' : (wpFormat === 'jpeg' ? 'jpg' : 'png');
      const cleanBaseName = wpCropFileName.replace(/\.[^/.]+$/, "");
      const outputFileName = `${cleanBaseName}_Wallpaper.${fileExt}`;

      // Upload cropped wallpaper background image to server
      (async () => {
        try {
          setGeminiGenerating(true); // show loader spinner
          const res = await fetch(`${BACKEND_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: croppedDataUrl, name: outputFileName })
          });
          const data = await res.json();
          if (data.url) {
            updateGlobalBackgroundVariable('bgImageUrl', data.url);
          } else {
            alert('Upload failure.');
          }
        } catch (err) {
          alert('Failed to upload cropped wallpaper: ' + err.message);
        } finally {
          setGeminiGenerating(false);
          setWpCropModalOpen(false);
          setWpCropRawImage(null);
        }
      })();
    };
  };

  // Artwork Cropper Handlers & Effects
  useEffect(() => {
    if (!artworkCropModalOpen || !artworkCropRawImage || !artworkCropCanvasRef.current) return;
    const canvas = artworkCropCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = artworkCropRawImage;
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = canvas.height / img.height;
      } else {
        baseScale = canvas.width / img.width;
      }
      const drawScale = baseScale * artworkCropZoom;
      const centerX = (canvas.width - img.width * drawScale) / 2;
      const centerY = (canvas.height - img.height * drawScale) / 2;
      
      ctx.save();
      ctx.drawImage(
        img,
        centerX + artworkCropPanX,
        centerY + artworkCropPanY,
        img.width * drawScale,
        img.height * drawScale
      );
      ctx.restore();
    };
  }, [artworkCropModalOpen, artworkCropRawImage, artworkCropZoom, artworkCropPanX, artworkCropPanY, artworkCropAspect]);

  const handleArtworkCropMouseDown = (e) => {
    setArtworkCropIsDragging(true);
    setArtworkCropDragStart({
      x: e.clientX - artworkCropPanX,
      y: e.clientY - artworkCropPanY
    });
  };

  const handleArtworkCropMouseMove = (e) => {
    if (!artworkCropIsDragging) return;
    setArtworkCropPanX(e.clientX - artworkCropDragStart.x);
    setArtworkCropPanY(e.clientY - artworkCropDragStart.y);
  };

  const handleArtworkCropMouseUpOrLeave = () => {
    setArtworkCropIsDragging(false);
  };

  const handleArtworkCropTouchStart = (e) => {
    if (e.touches.length === 1) {
      setArtworkCropIsDragging(true);
      setArtworkCropDragStart({
        x: e.touches[0].clientX - artworkCropPanX,
        y: e.touches[0].clientY - artworkCropPanY
      });
    }
  };

  const handleArtworkCropTouchMove = (e) => {
    if (!artworkCropIsDragging || e.touches.length !== 1) return;
    setArtworkCropPanX(e.touches[0].clientX - artworkCropDragStart.x);
    setArtworkCropPanY(e.touches[0].clientY - artworkCropDragStart.y);
  };

  const handleArtworkApplyCrop = () => {
    if (!artworkCropRawImage) return;
    const img = new Image();
    img.src = artworkCropRawImage;
    img.onload = () => {
      const aspect = artworkCropAspect || 1;

      let viewWidth = 450;
      let viewHeight = 450 / aspect;
      if (viewHeight > 300) {
        viewHeight = 300;
        viewWidth = 300 * aspect;
      }

      const canvasRatio = viewWidth / viewHeight;
      const imgRatio = img.width / img.height;
      let baseScale = 1;
      if (imgRatio > canvasRatio) {
        baseScale = viewHeight / img.height;
      } else {
        baseScale = viewWidth / img.width;
      }

      const drawScale = baseScale * artworkCropZoom;
      const originalCropWidth = viewWidth / drawScale;
      let outputWidth = originalCropWidth;
      if (artworkScale !== 'original') {
        outputWidth = originalCropWidth * parseFloat(artworkScale);
      }
      outputWidth = Math.round(outputWidth);
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      const resScale = outputWidth / viewWidth;
      const highResScale = drawScale * resScale;
      const highResPanX = artworkCropPanX * resScale;
      const highResPanY = artworkCropPanY * resScale;

      const centerX = (outputWidth - img.width * highResScale) / 2;
      const centerY = (outputHeight - img.height * highResScale) / 2;

      oCtx.save();
      oCtx.drawImage(
        img,
        centerX + highResPanX,
        centerY + highResPanY,
        img.width * highResScale,
        img.height * highResScale
      );
      oCtx.restore();

      const mimeType = artworkFormat === 'png' ? 'image/png' : (artworkFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const croppedDataUrl = artworkFormat === 'png' 
        ? offscreen.toDataURL(mimeType) 
        : offscreen.toDataURL(mimeType, artworkQuality);

      const fileExt = artworkFormat === 'webp' ? 'webp' : (artworkFormat === 'jpeg' ? 'jpg' : 'png');
      const cleanBaseName = artworkCropFileName.replace(/\.[^/.]+$/, "");
      const outputFileName = `${cleanBaseName}_cropped.${fileExt}`;

      (async () => {
        try {
          setIsCompilingGif(true); // Reuse loading spinner
          const res = await fetch(`${BACKEND_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: croppedDataUrl, name: outputFileName })
          });
          const data = await res.json();
          if (data.url) {
            updateModuleProperty(artworkCropModuleId, 'url', data.url);
            updateModuleProperty(artworkCropModuleId, 'sizeInBytes', data.sizeInBytes || 0);
          } else {
            alert('Upload failure.');
          }
        } catch (err) {
          alert('Failed to upload cropped image: ' + err.message);
        } finally {
          setIsCompilingGif(false);
          setArtworkCropModalOpen(false);
          setArtworkCropRawImage(null);
        }
      })();
    };
  };

  const handleArtworkSkipCrop = () => {
    if (!artworkCropRawImage) return;
    const img = new Image();
    img.src = artworkCropRawImage;
    img.onload = () => {
      let outputWidth = img.width;
      if (artworkScale !== 'original') {
        outputWidth = Math.round(img.width * parseFloat(artworkScale));
      }
      const aspect = img.width / img.height;
      const outputHeight = Math.round(outputWidth / aspect);

      const offscreen = document.createElement('canvas');
      offscreen.width = outputWidth;
      offscreen.height = outputHeight;
      const oCtx = offscreen.getContext('2d');

      oCtx.drawImage(img, 0, 0, outputWidth, outputHeight);

      const mimeType = artworkFormat === 'png' ? 'image/png' : (artworkFormat === 'webp' ? 'image/webp' : 'image/jpeg');
      const processedDataUrl = artworkFormat === 'png'
        ? offscreen.toDataURL(mimeType)
        : offscreen.toDataURL(mimeType, artworkQuality);

      const fileExt = artworkFormat === 'webp' ? 'webp' : (artworkFormat === 'jpeg' ? 'jpg' : 'png');
      const cleanBaseName = artworkCropFileName.replace(/\.[^/.]+$/, "");
      const outputFileName = `${cleanBaseName}_optimized.${fileExt}`;

      (async () => {
        try {
          setIsCompilingGif(true);
          const res = await fetch(`${BACKEND_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: processedDataUrl, name: outputFileName })
          });
          const data = await res.json();
          if (data.url) {
            updateModuleProperty(artworkCropModuleId, 'url', data.url);
            updateModuleProperty(artworkCropModuleId, 'sizeInBytes', data.sizeInBytes || 0);
          } else {
            alert('Upload failure.');
          }
        } catch (err) {
          alert('Failed to upload optimized image: ' + err.message);
        } finally {
          setIsCompilingGif(false);
          setArtworkCropModalOpen(false);
          setArtworkCropRawImage(null);
        }
      })();
    };
    img.onerror = () => {
      (async () => {
        try {
          setIsCompilingGif(true);
          const res = await fetch(`${BACKEND_URL}/api/upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: artworkCropRawImage, name: artworkCropFileName })
          });
          const data = await res.json();
          if (data.url) {
            updateModuleProperty(artworkCropModuleId, 'url', data.url);
            updateModuleProperty(artworkCropModuleId, 'sizeInBytes', data.sizeInBytes || 0);
          } else {
            alert('Upload failure.');
          }
        } catch (err) {
          alert('Failed to upload image: ' + err.message);
        } finally {
          setIsCompilingGif(false);
          setArtworkCropModalOpen(false);
          setArtworkCropRawImage(null);
        }
      })();
    };
  };

  const invertColorForDarkMode = (colorVal, defaultColor) => {
    const c = colorVal || defaultColor;
    if (!isPreviewDarkMode) return c;
    if (c === '#000000' || c === 'black' || c === '#1f2937' || c === '#333333' || c === '#4b5563' || c === '#0f091c' || c === '#5b6275') {
      return '#f3f4f6';
    }
    if (c.startsWith('#') && c.length === 7) {
      const r = parseInt(c.slice(1, 3), 16) || 0;
      const g = parseInt(c.slice(3, 5), 16) || 0;
      const b = parseInt(c.slice(5, 7), 16) || 0;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      if (brightness < 100) {
        return '#f3f4f6'; // invert dark colors to light grey
      }
    }
    return c;
  };

  const getButtonDarkModeColors = (bgColor, textColor) => {
    const bg = bgColor || '#ffffff';
    const txt = textColor || '#000000';
    if (!isPreviewDarkMode) return { bg, txt };

    const getBrightness = (hex) => {
      const h = hex.trim().toLowerCase();
      if (h === '#000000' || h === 'black' || h === '#1f2937' || h === '#333333') return 0;
      if (h === '#ffffff' || h === 'white') return 255;
      if (h.startsWith('#') && h.length === 7) {
        const r = parseInt(h.slice(1, 3), 16) || 0;
        const g = parseInt(h.slice(3, 5), 16) || 0;
        const b = parseInt(h.slice(5, 7), 16) || 0;
        return (r * 299 + g * 587 + b * 114) / 1000;
      }
      return 127;
    };

    const bgBrightness = getBrightness(bg);

    if (bgBrightness > 180) {
      // Light background: invert to dark charcoal and light text
      return { bg: '#1f2937', txt: '#f3f4f6' };
    } else if (bgBrightness < 70) {
      // Dark background: invert to light grey and dark text
      return { bg: '#f3f4f6', txt: '#1f2937' };
    }

    // Accent/mid-tone: keep as-is but ensure readability
    return { bg, txt: bgBrightness > 130 ? '#000000' : '#ffffff' };
  };
  const [activeModuleId, setActiveModuleId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedOverColId, setDraggedOverColId] = useState(null);
  const [isCanvasDraggedOver, setIsCanvasDraggedOver] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [activeSavedImportUrl, setActiveSavedImportUrl] = useState('');
  const [activeColorPickerDropdown, setActiveColorPickerDropdown] = useState(null);
  const colorDropdownRef = useRef(null);
  const blockStrokePanelRef = useRef(null);
  const blockPaddingPanelRef = useRef(null);
  const [activeLinkPopup, setActiveLinkPopup] = useState(null);
  const linkPopupRef = useRef(null);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedColIdxToEdit, setSelectedColIdxToEdit] = useState(0);
  const [savedBlocks, setSavedBlocks] = useState([]);
  const [savedGradients, setSavedGradients] = useState([]);
  const [showGradientSaveDropdown, setShowGradientSaveDropdown] = useState(false);
  const [gradientSaveName, setGradientSaveName] = useState('');
  const [blockSearchQuery, setBlockSearchQuery] = useState('');
  const [showSavedBlocksModal, setShowSavedBlocksModal] = useState(false);
  const [showGeminiModal, setShowGeminiModal] = useState(false);
  const [geminiPrompt, setGeminiPrompt] = useState('');
  const [geminiTone, setGeminiTone] = useState('creative');
  const [geminiPhase, setGeminiPhase] = useState('signup');
  const [geminiSuggestions, setGeminiSuggestions] = useState([]);
  const [geminiGenerating, setGeminiGenerating] = useState(false);

  // Auto-Save Infrastructure
  const hasBeenSavedRef = useRef(id !== 'new'); // existing templates are already saved
  const savedTemplateIdRef = useRef(id !== 'new' ? id : null);
  const autoSaveTimerRef = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const [globalSettings, setGlobalSettings] = useState({
    bgType: 'solid',         // Options: 'solid', 'gradient', or 'image'
    bgColor: '#ffffff',
    gradientAngle: 180,
    gradientColor1: '#ffffff',
    gradientColor2: '#ffffff',
    overrideBlockBg: false,
    maxWidth: 500,
    // ==========================================
    // BACKEND IMAGE EXTENSION CONSTANTS
    // ==========================================
    bgImageUrl: '',          // Holds the absolute or relative uploaded asset URL
    fallbackBgColor: '#ffffff', // Matches your target default backdrop color hex
    fallbackBgType: 'solid',
    fallbackBgGradientColor1: '#111215',
    fallbackBgGradientColor2: '#000000',
    fallbackBgGradientAngle: 180,
    rowPaddingTop: 10,
    rowPaddingBottom: 10,
    rowPaddingLeft: 10,
    rowPaddingRight: 10
  });

  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [libraryTab, setLibraryTab] = useState('images'); 
  const [libraryData, setLibraryData] = useState({ images: [], gifs: [], createdAssets: [] });
  const [libraryTotals, setLibraryTotals] = useState({ images: 0, gifs: 0, createdAssets: 0 });
  const [libraryPage, setLibraryPage] = useState(1);
  const LIBRARY_PAGE_SIZE = 18;
  const [libraryLoading, setLibraryLoading] = useState(false);




  const [systemFonts, setSystemFonts] = useState([]);
  const [customFonts, setCustomFonts] = useState([]);

  const [activeTab, setActiveTab] = useState('blocks'); 
  const [mediaLabSubTab, setMediaLabSubTab] = useState('imageLoop'); 
  
  const [detectedWidth, setDetectedWidth] = useState(500);
  const [detectedHeight, setDetectedHeight] = useState(500);
  const [scaleFactor, setScaleFactor] = useState(1); 
  const [totalMailerSizeMb, setTotalMailerSizeMb] = useState(0);

  const [cropWidth, setCropWidth] = useState(500);
  const [cropHeight, setCropHeight] = useState(500);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [compressionBitrate, setCompressionBitrate] = useState(10); 
  const [isCompilingGif, setIsCompilingGif] = useState(false);

  const getEstimatedGifSize = (width, height, frameCount, sampleInterval) => {
    const intervalVal = parseInt(sampleInterval) || 10;
    // Calculate a factor from 0.03 to 0.15 based on sample interval (1 is best quality/largest, 20 is lowest quality/smallest)
    const factor = 0.15 - ((intervalVal - 1) / 19) * 0.12;
    const bytes = Math.round(width * height * frameCount * factor);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const [gifLoopQueue, setGifLoopQueue] = useState([]);
  const [imageFrameDelay, setImageFrameDelay] = useState(400);
  
  // Undo/Redo Stacks & Refs
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  
  const lastHistoryPushRef = useRef(0);
  const lastUpdatedPropertyRef = useRef('');
  const lastUpdatedModuleIdRef = useRef('');

  const pushHistory = (modulesToSave = canvasModules, settingsToSave = globalSettings) => {
    const modulesCopy = JSON.parse(JSON.stringify(modulesToSave));
    const settingsCopy = JSON.parse(JSON.stringify(settingsToSave));
    
    setUndoStack(prev => {
      const next = [...prev, { canvasModules: modulesCopy, globalSettings: settingsCopy }];
      if (next.length > 7) {
        return next.slice(-7);
      }
      return next;
    });
    setRedoStack([]);
  };

  const pushHistoryDebounced = (mid, property, modulesToSave = canvasModules, settingsToSave = globalSettings) => {
    const now = Date.now();
    if (
      mid !== lastUpdatedModuleIdRef.current ||
      property !== lastUpdatedPropertyRef.current ||
      now - lastHistoryPushRef.current > 1200
    ) {
      pushHistory(modulesToSave, settingsToSave);
      lastHistoryPushRef.current = now;
      lastUpdatedPropertyRef.current = property;
      lastUpdatedModuleIdRef.current = mid;
    }
  };

  const renderColorPopover = (pickerType, currentColor, onSelectColor, titleText = 'Recent Colors') => {
    if (activeColorPickerDropdown !== pickerType) return null;
    return (
      <div 
        ref={colorDropdownRef}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: '110%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(168, 85, 247, 0.15)',
          borderRadius: '16px',
          padding: '12px',
          width: '180px',
          boxShadow: '0 10px 25px -5px rgba(15, 9, 28, 0.15), 0 8px 10px -6px rgba(15, 9, 28, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{titleText}</div>
        {(!usedColors || usedColors.length === 0) ? (
          <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', padding: '4px' }}>No recent colors</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
            {usedColors.slice(0, 10).map((c, idx) => (
              <div
                key={`popover-${pickerType}-${c}-${idx}`}
                title={c}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectColor(c);
                  addColorToHistory(c);
                  setActiveColorPickerDropdown(null);
                }}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  backgroundColor: c,
                  border: '1px solid rgba(15, 9, 28, 0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            ))}
          </div>
        )}
        <div 
          onClick={e => e.stopPropagation()}
          style={{ borderTop: '1px solid rgba(15, 9, 28, 0.05)', paddingTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', position: 'relative', cursor: 'pointer' }}
        >
          <div style={{ position: 'relative', width: '20px', height: '20px', borderRadius: '4px', border: '1px solid rgba(15, 9, 28, 0.12)', overflow: 'hidden', backgroundColor: currentColor, cursor: 'pointer' }}>
            <input 
              ref={inputEl => {
                if (inputEl) {
                  inputEl.onchange = (e) => {
                    addColorToHistory(e.target.value);
                  };
                }
              }}
              type="color" 
              value={currentColor} 
              onChange={e => onSelectColor(e.target.value)}
              style={{ position: 'absolute', top: '-5px', left: '-5px', width: '30px', height: '30px', border: 'none', padding: 0, cursor: 'pointer', opacity: 0 }} 
            />
          </div>
          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#1f2937' }}>Custom Color...</span>
        </div>
      </div>
    );
  };

  const renderLinkPopover = (mod) => {
    if (activeLinkPopup !== mod.id) return null;
    return (
      <div 
        ref={linkPopupRef}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: '115%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(168, 85, 247, 0.15)',
          borderRadius: '16px',
          padding: '16px',
          width: '260px',
          boxShadow: '0 10px 25px -5px rgba(15, 9, 28, 0.15), 0 8px 10px -6px rgba(15, 9, 28, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          textAlign: 'left'
        }}
      >
        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>🔗</span> Block Link Redirect Settings
        </div>
        
        <div>
          <label style={{ fontSize: '9px', fontWeight: 'bold', color: '#5b6275', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Redirect URL</label>
          <input 
            type="text" 
            placeholder="https://example.com" 
            value={mod.linkUrl || ''} 
            onChange={e => updateModuleProperty(mod.id, 'linkUrl', e.target.value)} 
            className="glass-input" 
            style={{ width: '100%', height: '32px', padding: '6px 10px', fontSize: '12px', boxSizing: 'border-box' }}
          />
        </div>

        <div>
          <label style={{ fontSize: '9px', fontWeight: 'bold', color: '#5b6275', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Open Behaviour</label>
          <select 
            value={mod.linkTarget || '_blank'} 
            onChange={e => updateModuleProperty(mod.id, 'linkTarget', e.target.value)} 
            className="glass-input" 
            style={{ width: '100%', height: '32px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer', boxSizing: 'border-box' }}
          >
            <option value="_blank">New Tab (default)</option>
            <option value="_self">Same Page (current window)</option>
            <option value="_parent">Parent Frame</option>
            <option value="_top">Full Body</option>
          </select>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
          <button 
            type="button"
            onClick={() => setActiveLinkPopup(null)}
            className="pulse-gold"
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
              border: 'none',
              color: '#ffffff',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    
    const currentModulesCopy = JSON.parse(JSON.stringify(canvasModules));
    const currentSettingsCopy = JSON.parse(JSON.stringify(globalSettings));
    
    setRedoStack(prev => {
      const next = [...prev, { canvasModules: currentModulesCopy, globalSettings: currentSettingsCopy }];
      if (next.length > 7) {
        return next.slice(-7);
      }
      return next;
    });

    const prevStates = [...undoStack];
    const prevState = prevStates.pop();
    setUndoStack(prevStates);

    setCanvasModules(prevState.canvasModules);
    setGlobalSettings(prevState.globalSettings);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;

    const currentModulesCopy = JSON.parse(JSON.stringify(canvasModules));
    const currentSettingsCopy = JSON.parse(JSON.stringify(globalSettings));

    setUndoStack(prev => {
      const next = [...prev, { canvasModules: currentModulesCopy, globalSettings: currentSettingsCopy }];
      if (next.length > 7) {
        return next.slice(-7);
      }
      return next;
    });

    const nextStates = [...redoStack];
    const nextState = nextStates.pop();
    setRedoStack(nextStates);

    setCanvasModules(nextState.canvasModules);
    setGlobalSettings(nextState.globalSettings);
  };

  // Keyboard shortcut listener for Ctrl+Z / Ctrl+Y
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            handleRedo();
          } else {
            handleUndo();
          }
        } else if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          handleRedo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoStack, redoStack, canvasModules, globalSettings]);

  const [videoFps, setVideoFps] = useState(12); 
  const [videoDuration, setVideoDuration] = useState(3); 
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);

  const fetchSavedBlocks = () => {
    fetch(`${BACKEND_URL}/api/saved-blocks`)
      .then(res => res.json())
      .then(data => {
        setSavedBlocks(data || []);
      })
      .catch(err => console.error("Saved blocks load crash path: ", err));
  };

  const fetchSavedGradients = () => {
    fetch(`${BACKEND_URL}/api/saved-gradients`)
      .then(res => res.json())
      .then(data => setSavedGradients(data || []))
      .catch(err => console.error('Saved gradients load error:', err));
  };

  const saveGradient = (name, gradientData) => {
    fetch(`${BACKEND_URL}/api/saved-gradients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, ...gradientData })
    })
      .then(res => res.json())
      .then(() => fetchSavedGradients())
      .catch(err => console.error('Save gradient error:', err));
  };

  const deleteGradient = (id) => {
    fetch(`${BACKEND_URL}/api/saved-gradients/${id}`, { method: 'DELETE' })
      .then(() => fetchSavedGradients())
      .catch(err => console.error('Delete gradient error:', err));
  };

  const deduplicateBlockIds = (modules) => {
    const seenIds = new Set();
    const processList = (list) => {
      return list.map(m => {
        let newId = m.id;
        if (!newId || seenIds.has(newId)) {
          const suffix = Math.floor(Math.random() * 100000);
          newId = m.type === 'columns' ? `row-${Date.now()}-${suffix}` : `m-${Date.now()}-${suffix}`;
          m.rasterizedUrl = null;
          m.rasterizedMobileUrl = null;
        }
        seenIds.add(newId);

        const updatedModule = { ...m, id: newId };

        if (updatedModule.type === 'columns' && updatedModule.columns) {
          updatedModule.columns = updatedModule.columns.map((col, idx) => {
            const newColId = `col-${newId}-${idx}`;
            let colBlocks = [];
            if (col.blocks && Array.isArray(col.blocks)) {
              colBlocks = processList(col.blocks);
            }
            return {
              ...col,
              id: newColId,
              blocks: colBlocks
            };
          });
        }
        return updatedModule;
      });
    };
    return processList(modules);
  };

  const generateUniqueBlockIds = (module) => {
    if (!module || typeof module !== 'object') return module;
    const cloned = JSON.parse(JSON.stringify(module));
    
    // Clear any existing rasterization cache on duplication
    if (cloned.rasterizedUrl) cloned.rasterizedUrl = null;
    if (cloned.rasterizedMobileUrl) cloned.rasterizedMobileUrl = null;

    // Generate new unique ID based on the module type
    const suffix = Math.floor(Math.random() * 100000);
    if (cloned.type === 'columns') {
      const newRowId = `row-${Date.now()}-${suffix}`;
      cloned.id = newRowId;
      if (cloned.columns && Array.isArray(cloned.columns)) {
        cloned.columns = cloned.columns.map((col, idx) => {
          const newColId = `col-${newRowId}-${idx}`;
          let colBlocks = [];
          if (col.blocks && Array.isArray(col.blocks)) {
            colBlocks = col.blocks.map(childBlock => generateUniqueBlockIds(childBlock));
          }
          return {
            ...col,
            id: newColId,
            blocks: colBlocks
          };
        });
      }
    } else {
      cloned.id = `m-${Date.now()}-${suffix}`;
    }
    return cloned;
  };

  const reloadAvailableFontsRepository = () => {
    fetch(`${BACKEND_URL}/api/fonts`)
      .then(res => res.json())
      .then(data => {
        setSystemFonts(data.baseFonts || []);
        setCustomFonts(data.mappedCustomFonts || []);
        
        const styleNodeId = 'dynamic-mailer-font-face-registry-wrapper';
        let styleNode = document.getElementById(styleNodeId);
        if (!styleNode) {
          styleNode = document.createElement('style');
          styleNode.id = styleNodeId;
          document.head.appendChild(styleNode);
        }
        let markup = '';
        (data.mappedCustomFonts || []).forEach(f => {
          (f.variations || []).forEach(v => {
            const absoluteFontUrl = v.url.startsWith('http') ? v.url : `${BACKEND_URL}${v.url}`;
            const urlParts = v.url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            const extIndex = fileName.lastIndexOf('.');
            const ext = extIndex !== -1 ? fileName.substring(extIndex).toLowerCase() : '';
            
            let formatStr = '';
            if (ext === '.ttf') formatStr = " format('truetype')";
            else if (ext === '.otf') formatStr = " format('opentype')";
            else if (ext === '.woff') formatStr = " format('woff')";
            else if (ext === '.woff2') formatStr = " format('woff2')";
            
            // 1. Grouped family mapping (enables bold/italic toggles to resolve natively)
            markup += `@font-face { font-family: '${f.family}'; src: url('${absoluteFontUrl}')${formatStr}; font-weight: ${v.weight}; font-style: ${v.style}; }\n`;
            
            // 2. Direct variation mapping (enables selection as independent font-family)
            const fontName = extIndex !== -1 ? fileName.substring(0, extIndex) : fileName;
            markup += `@font-face { font-family: '${fontName}'; src: url('${absoluteFontUrl}')${formatStr}; }\n`;
          });
        });
        styleNode.innerHTML = markup;
      });
  };

  useEffect(() => { 
    reloadAvailableFontsRepository(); 
    fetchSavedBlocks();
    fetchSavedGradients();
  }, []);

  useEffect(() => {
    function handleClickOutsideColor(event) {
      if (colorDropdownRef.current && colorDropdownRef.current.contains(event.target)) {
        return;
      }
      if (blockStrokePanelRef.current && blockStrokePanelRef.current.contains(event.target)) {
        if (activeColorPickerDropdown === 'blockStrokeColor') {
          setActiveColorPickerDropdown('blockStrokePanel');
        }
        return;
      }
      if (blockPaddingPanelRef.current && blockPaddingPanelRef.current.contains(event.target)) {
        return;
      }
      const currentActiveModule = findModuleById(canvasModules, activeModuleId);
      if (currentActiveModule) {
        let finalColor = null;
        if (activeColorPickerDropdown === 'textColor' || activeColorPickerDropdown === 'dividerColor') {
          finalColor = currentActiveModule.color;
        } else if (activeColorPickerDropdown === 'bgColor') {
          finalColor = currentActiveModule.bgColor;
        } else if (activeColorPickerDropdown === 'blockBgColor') {
          finalColor = currentActiveModule.blockBgColor;
        }
        if (finalColor) {
          addColorToHistory(finalColor);
        }
      }
      setActiveColorPickerDropdown(null);
    }
    if (activeColorPickerDropdown) {
      document.addEventListener('mousedown', handleClickOutsideColor);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideColor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeColorPickerDropdown, canvasModules, activeModuleId]);

  useEffect(() => {
    function handleClickOutsideLink(event) {
      if (linkPopupRef.current && !linkPopupRef.current.contains(event.target)) {
        setActiveLinkPopup(null);
      }
    }
    if (activeLinkPopup) {
      document.addEventListener('mousedown', handleClickOutsideLink);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideLink);
    };
  }, [activeLinkPopup]);

  useEffect(() => {
    if (id && id !== 'new') {
      fetch(`${BACKEND_URL}/api/templates/${id}`)
        .then(res => res.json())
        .then(data => {
          setCampaignName(data.name || 'Untitled Campaign');
          const cleanedModules = deduplicateBlockIds(data.modules || []);
          setCanvasModules(cleanedModules);
          const loadedSettings = data.globalSettings || {};
          setGlobalSettings({
            bgType: 'solid',
            bgColor: '#ffffff',
            gradientColor1: '#ffffff',
            gradientColor2: '#ffffff',
            gradientAngle: 180,
            overrideBlockBg: false,
            bgImageUrl: '',
            fallbackBgColor: '#ffffff',
            fallbackBgType: 'solid',
            fallbackBgGradientColor1: '#111215',
            fallbackBgGradientColor2: '#000000',
            fallbackBgGradientAngle: 180,
            maxWidth: 500,
            rowPaddingTop: 10,
            rowPaddingBottom: 10,
            rowPaddingLeft: 10,
            rowPaddingRight: 10,
            ...loadedSettings
          });
          if (data.modules && data.modules.length > 0) setActiveModuleId(data.modules[0].id);
          setActiveSavedImportUrl(`${window.location.origin}/view/mailer/${id}`);
        })
        .catch(err => console.error("Blueprint data load crash path: ", err));
    } else {
      setCampaignName('Untitled Campaign');
      const rowId = `row-${Date.now()}`;
      const blankRowId = `row-${Date.now()}-blank`;
      const textId = `m-${Date.now()}`;
      setCanvasModules([
        {
          id: rowId,
          type: 'columns',
          blockBgType: 'transparent',
          blockBgColor: '#111215',
          paddingTop: 10,
          paddingBottom: 10,
          columnSplit: '100',
          columns: [
            {
              id: `col-${rowId}-0`,
              blocks: [
                {
                  id: textId,
                  type: 'text',
                  text: 'Click here to open this email in your web browser',
                  linkUrl: '[preview_url]',
                  font: 'Arial',
                  color: '#ffffff',
                  fontSize: 11,
                  letterSpacing: '',
                  lineSpacing: 1.2,
                  isBold: false,
                  isItalic: false,
                  textAlign: 'center',
                  blockBgType: 'transparent',
                  blockBgColor: 'transparent'
                }
              ]
            }
          ]
        },
        {
          id: blankRowId,
          type: 'columns',
          blockBgType: 'transparent',
          blockBgColor: '#111215',
          paddingTop: 10,
          paddingBottom: 10,
          columnSplit: '100',
          columns: [
            {
              id: `col-${blankRowId}-0`,
              blocks: []
            }
          ]
        }
      ]);
      setActiveModuleId(textId);
      setGlobalSettings({
        bgType: 'solid',
        bgColor: '#ffffff',
        gradientColor1: '#ffffff',
        gradientColor2: '#ffffff',
        gradientAngle: 180,
        overrideBlockBg: false,
        bgImageUrl: '',
        fallbackBgColor: '#ffffff',
        fallbackBgType: 'solid',
        fallbackBgGradientColor1: '#111215',
        fallbackBgGradientColor2: '#000000',
        fallbackBgGradientAngle: 180,
        maxWidth: 500,
        rowPaddingTop: 10,
        rowPaddingBottom: 10,
        rowPaddingLeft: 10,
        rowPaddingRight: 10
      });
      setActiveSavedImportUrl('');
    }
  }, [id]);

  // Auto-save interval: fires every 30 seconds after the first manual save
  useEffect(() => {
    autoSaveTimerRef.current = setInterval(() => {
      if (hasBeenSavedRef.current && savedTemplateIdRef.current) {
        executeAutoSave();
      }
    }, 30000); // 30 seconds

    return () => {
      if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasModules, globalSettings, campaignName]);

  useEffect(() => {
    setCropWidth(Math.round(detectedWidth * scaleFactor));
    setCropHeight(Math.round(detectedHeight * scaleFactor));
  }, [scaleFactor, detectedWidth, detectedHeight]);

  useEffect(() => {
    let totalBytes = 0;
    const countBytes = (mod) => {
      if (mod.text) totalBytes += mod.text.length;
      if (mod.htmlContent) totalBytes += mod.htmlContent.length;
      if (mod.type === 'artwork' && mod.sizeInBytes) {
        totalBytes += parseInt(mod.sizeInBytes, 10);
      }
      if (mod.type === 'columns' && mod.columns) {
        mod.columns.forEach(col => {
          if (col.blocks) {
            col.blocks.forEach(sub => countBytes(sub));
          }
        });
      }
    };
    canvasModules.forEach(mod => countBytes(mod));
    const calculatedMb = totalBytes / (1024 * 1024);
    setTotalMailerSizeMb(calculatedMb.toFixed(2));
  }, [canvasModules]);

  const updateGlobalBackgroundVariable = (property, value) => {
    pushHistoryDebounced('global', property);
    setGlobalSettings(prev => ({ ...prev, [property]: value }));
  };

  const handleCustomOtfFontUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('fontFile', file);

    fetch(`${BACKEND_URL}/api/fonts/upload`, {
      method: 'POST',
      body: formData
    })
      .then(res => res.json())
      .then(() => {
        alert("Brand font embedded completely into Studio workspace inventory!");
        reloadAvailableFontsRepository();
      });
  };

  const handleOpenLibraryAssetInCropper = async (url, fileName, e) => {
    e.stopPropagation();
    try {
      setIsCompilingGif(true);
      const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error('Failed to fetch image data from server.');
      
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const aspect = img.width / img.height;
          setArtworkCropAspect(aspect);
          setArtworkCropOriginalAspect(aspect);
          setArtworkCropRawImage(reader.result);
          setArtworkCropModuleId(activeModuleId);
          setArtworkCropFileName(fileName);
          setArtworkCropZoom(1.0);
          setArtworkCropPanX(0);
          setArtworkCropPanY(0);
          
          setShowLibraryModal(false);
          setArtworkCropModalOpen(true);
          setIsCompilingGif(false);
        };
      };
    } catch (err) {
      setIsCompilingGif(false);
      alert('Could not load image for cropping: ' + err.message);
    }
  };

  const handleLibraryItemSelect = (url, sizeInBytes = 0) => {
    updateModuleProperty(activeModuleId, 'url', url);
    updateModuleProperty(activeModuleId, 'sizeInBytes', sizeInBytes);
    setShowLibraryModal(false);
  };

  const triggerLibraryFetchOpen = () => {
    // Open the modal immediately — show skeleton while data loads
    setShowLibraryModal(true);
    setLibraryLoading(true);
    setLibraryPage(1);
    setLibraryData({ images: [], gifs: [], createdAssets: [] });
    fetch(`${BACKEND_URL}/api/uploads-library?page=1&limit=${LIBRARY_PAGE_SIZE}&refresh=1`)
      .then(res => res.json())
      .then(data => {
        setLibraryData({ images: data.images || [], gifs: data.gifs || [], createdAssets: data.createdAssets || [] });
        setLibraryTotals(data.totals || { images: data.images?.length || 0, gifs: data.gifs?.length || 0, createdAssets: data.createdAssets?.length || 0 });
        setLibraryLoading(false);
      })
      .catch(() => setLibraryLoading(false));
  };

  const loadMoreLibraryItems = () => {
    const nextPage = libraryPage + 1;
    setLibraryPage(nextPage);
    fetch(`${BACKEND_URL}/api/uploads-library?page=${nextPage}&limit=${LIBRARY_PAGE_SIZE}`)
      .then(res => res.json())
      .then(data => {
        setLibraryData(prev => ({
          images: [...prev.images, ...(data.images || [])],
          gifs: [...prev.gifs, ...(data.gifs || [])],
          createdAssets: [...prev.createdAssets, ...(data.createdAssets || [])]
        }));
        setLibraryTotals(data.totals || libraryTotals);
      });
  };

  const executeAssetDeletion = (url, e) => {
    e.stopPropagation();
    if (window.confirm(`⚠️ Permanently delete this asset from the library on disk?`)) {
      fetch(`${BACKEND_URL}/api/uploads-library?url=${encodeURIComponent(url)}`, { method: 'DELETE' })
        .then(res => {
          if (!res.ok) throw new Error('Server deletion error.');
          return res.json();
        })
        .then(data => {
          if (data.success) {
            // Refetch page 1 with force-refresh to pick up deletion
            fetch(`${BACKEND_URL}/api/uploads-library?page=1&limit=${LIBRARY_PAGE_SIZE}&refresh=1`)
              .then(res => res.json())
              .then(fresh => {
                setLibraryData({ images: fresh.images || [], gifs: fresh.gifs || [], createdAssets: fresh.createdAssets || [] });
                setLibraryTotals(fresh.totals || libraryTotals);
                setLibraryPage(1);
              });
          } else {
            alert('Failed to delete asset: ' + (data.error || 'Unknown error'));
          }
        })
        .catch(err => {
          console.error(err);
          alert('Error deleting asset.');
        });
    }
  };



  const handleArtworkUpload = (e, moduleId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        (async () => {
          try {
            setIsCompilingGif(true); // show loader spinner
            const res = await fetch(`${BACKEND_URL}/api/upload`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ image: reader.result, name: file.name })
            });
            const data = await res.json();
            if (data.url) {
              updateModuleProperty(moduleId, 'url', data.url);
              updateModuleProperty(moduleId, 'sizeInBytes', data.sizeInBytes || 0);
            } else {
              alert('Upload failure: ' + (data.error || 'Unknown error'));
            }
          } catch (err) {
            alert('Failed to upload GIF: ' + err.message);
          } finally {
            setIsCompilingGif(false);
          }
        })();
      };
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        const aspect = img.width / img.height;
        setArtworkCropAspect(aspect);
        setArtworkCropOriginalAspect(aspect);
        setArtworkCropRawImage(reader.result);
        setArtworkCropModuleId(moduleId);
        setArtworkCropFileName(file.name);
        setArtworkCropZoom(1.0);
        setArtworkCropPanX(0);
        setArtworkCropPanY(0);
        setArtworkOriginalSize(file.size);
        setArtworkCropModalOpen(true);
      };
    };
    e.target.value = '';
  };

  const executeDirectFramesUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        setDetectedWidth(img.width);
        setDetectedHeight(img.height);
        setScaleFactor(1); 
      };
    };

    setIsCompilingGif(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    fetch(`${BACKEND_URL}/api/media-lab/upload-frames`, {
      method: 'POST',
      body: formData
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            throw new Error(err.error || `Server returned status ${res.status}`);
          });
        }
        return res.json();
      })
      .then(data => {
        if (data.urls) {
          setGifLoopQueue(data.urls);
          alert(`Logged ${data.urls.length} direct frames into pipeline queue!`);
        } else {
          alert('Upload completed but no frame URLs were returned.');
        }
      })
      .catch(err => {
        console.error("❌ Direct frames upload error:", err);
        alert(`Direct frames upload failed: ${err.message}`);
      })
      .finally(() => setIsCompilingGif(false));
  };

  const compileMediaLabGif = async () => {
    if (gifLoopQueue.length < 2) {
      alert('Upload at least 2 images directly to build a sequential flash loop timeline.');
      return;
    }
    setIsCompilingGif(true);
    try {
      const calculatedOutputDelay = parseInt(imageFrameDelay) || 400;
      const customName = gifLoopQueue.length > 0 
        ? `${gifLoopQueue[0].split('/').pop().replace(/\.[^/.]+$/, "").replace(/_\d+$/, '')}_sequenced.gif`
        : `LabLoop.gif`;

      const response = await fetch(`${BACKEND_URL}/api/media-lab/compile-gif`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frameUrls: gifLoopQueue,
          width: cropWidth,
          height: cropHeight,
          cropX: cropX,
          cropY: cropY,
          delay: calculatedOutputDelay,
          quality: 10,
          name: customName
        })
      });

      let data;
      const responseText = await response.text();
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        if (response.status === 413) {
          throw new Error('Frames payload exceeds server limit (413 Request Entity Too Large). Try compiling fewer or smaller images.');
        } else if (response.status === 504) {
          throw new Error('Server timed out compiling the image loop (504 Gateway Timeout).');
        } else if (response.status === 507) {
          throw new Error('Server storage is full (507 Insufficient Storage).');
        } else {
          throw new Error(`Server returned error status ${response.status}.`);
        }
      }

      if (data && data.success) {
        alert(`Image Loop Sequenced and Saved Successfully!`);
        if (activeModule?.type === 'artwork') {
          updateModuleProperty(activeModule.id, 'url', data.url);
          updateModuleProperty(activeModule.id, 'sizeInBytes', data.sizeInBytes || 0);
        }
        setGifLoopQueue([]);
        setActiveTab('blocks');
      } else {
        throw new Error(data?.error || `Compilation failed (Status ${response.status})`);
      }
    } catch (err) {
      console.error(err);
      alert('Compilation error: ' + err.message);
    } finally {
      setIsCompilingGif(false);
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedVideoFile(file);
    const videoUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = videoUrl;

    video.onloadedmetadata = () => {
      setDetectedWidth(video.videoWidth);
      setDetectedHeight(video.videoHeight);
      setScaleFactor(0.5); 
      URL.revokeObjectURL(videoUrl);
    };
  };

  const compileVideoToGif = async () => {
    if (!selectedVideoFile) return;
    setIsCompilingGif(true);

    const decoderContainerId = 'background-video-decoder-node';
    let videoElement = document.getElementById(decoderContainerId);
    if (videoElement) videoElement.remove();

    try {
      const videoUrl = URL.createObjectURL(selectedVideoFile);
      
      videoElement = document.createElement('video');
      videoElement.id = decoderContainerId;
      videoElement.src = videoUrl; 
      videoElement.muted = true; 
      videoElement.playsInline = true;
      videoElement.preload = 'auto';

      videoElement.style.position = 'fixed';
      videoElement.style.top = '0';
      videoElement.style.left = '0';
      videoElement.style.width = '1px';
      videoElement.style.height = '1px';
      videoElement.style.opacity = '0.01';
      videoElement.style.pointerEvents = 'none';
      document.body.appendChild(videoElement);

      await new Promise((resolve) => {
        videoElement.oncanplaythrough = () => resolve();
        setTimeout(() => resolve(), 4000);
      });

      const hiddenCanvas = document.createElement('canvas');
      hiddenCanvas.width = cropWidth; 
      hiddenCanvas.height = cropHeight;
      const ctx = hiddenCanvas.getContext('2d', { willReadFrequently: true });

      const imageFramesDataUrls = [];
      const totalFramesToCapture = Math.min(Math.round(videoDuration * videoFps), 40);
      const samplingIntervalInSeconds = videoDuration / totalFramesToCapture;
      const calculatedOutputDelay = Math.round(1000 / videoFps);

      for (let i = 0; i < totalFramesToCapture; i++) {
        await new Promise((resolve, reject) => {
          const targetTime = i * samplingIntervalInSeconds;
          videoElement.currentTime = Math.min(targetTime, videoElement.duration - 0.1);
          
          videoElement.onseeked = () => {
            setTimeout(() => {
              try {
                ctx.clearRect(0, 0, cropWidth, cropHeight);
                ctx.drawImage(
                  videoElement, 
                  cropX, cropY, videoElement.videoWidth - cropX, videoElement.videoHeight - cropY,
                  0, 0, cropWidth, cropHeight
                );
                
                imageFramesDataUrls.push(hiddenCanvas.toDataURL('image/png'));
                resolve();
              } catch (canvasErr) {
                reject(canvasErr);
              }
            }, 60);
          };
          
          videoElement.onerror = () => reject(new Error(`Frame capture broken at: ${i}`));
        });
      }

      videoElement.remove();
      URL.revokeObjectURL(videoUrl);

      if (!gifshot) {
        throw new Error("Local GIF compilation engine module not found.");
      }

      alert("Frames captured successfully! Compiling binary GIF inside your local bundle...");

      gifshot.createGIF({
        images: imageFramesDataUrls,
        gifWidth: cropWidth,
        gifHeight: cropHeight,
        interval: calculatedOutputDelay / 1000, 
        numWorkers: 2,
        sampleInterval: compressionBitrate
      }, function (obj) {
        if (obj.error) {
          setIsCompilingGif(false);
          alert("Local compilation loop failed.");
          return;
        }

        fetch(`${BACKEND_URL}/api/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            image: obj.image, 
            name: selectedVideoFile ? (selectedVideoFile.name.replace(/\.[^/.]+$/, "") + "_compiled.gif") : `video_compiled_${Date.now()}.gif`
          }),
        })
          .then(res => res.json())
          .then(data => {
            if (data.url) {
              alert(`Video Track Converted and Saved Successfully!`);
              if (activeModule?.type === 'artwork') {
                updateModuleProperty(activeModule.id, 'url', data.url);
                updateModuleProperty(activeModule.id, 'sizeInBytes', data.sizeInBytes || 0);
              }
              setSelectedVideoFile(null);
              setActiveTab('blocks');
            }
          })
          .catch(() => alert("Failed uploading completed asset to media registry."))
          .finally(() => setIsCompilingGif(false));
      });

    } catch (err) {
      console.error(err);
      if (videoElement) videoElement.remove();
      alert(err.message || 'Video extraction failure.');
      setIsCompilingGif(false);
    }
  };

  const executePipelineSave = async () => {
    setIsSaving(true);
    if (document.activeElement) document.activeElement.blur();

    try {
      await document.fonts.ready;
    } catch (e) {
      console.warn("Fonts ready promise failed:", e);
    }

    // [/] Implement deep cloning and unique ID regeneration for modules in `server.cjs`
    // [/] Implement disk copy of rasterized image assets for custom-font blocks during duplication in `server.cjs`
    const sanitizedId = id === 'new' ? `tmpl_${Date.now()}` : id;

    // Collect all text and header blocks to rasterize
    const customFontBlocks = [];
    const scanAndCollect = (modules, parentId = null, columnId = null) => {
      modules.forEach(m => {
        if (m.type === 'columns') {
          (m.columns || []).forEach(c => {
            scanAndCollect(c.blocks || [], m.id, c.id);
          });
        } else if (m.type === 'header' || m.type === 'text') {
          customFontBlocks.push({ block: m, parentId, columnId });
        }
      });
    };

    scanAndCollect(canvasModules);

    let updatedModules = JSON.parse(JSON.stringify(canvasModules));
    const failedBlocks = [];

    if (customFontBlocks.length > 0) {
      // Process in batches of 4 blocks at a time to optimize speed without overloading browser memory
      const batchSize = 4;
      for (let i = 0; i < customFontBlocks.length; i += batchSize) {
        const batch = customFontBlocks.slice(i, i + batchSize);
        await Promise.all(batch.map(async (item) => {
          const el = document.getElementById(`rasterize-target-${item.block.id}`);
          if (!el) {
            failedBlocks.push({ id: item.block.id, type: item.block.type, text: item.block.text, reason: 'DOM element target not found' });
            return;
          }

          try {
            // Determine paddings and widths
            const rowPadLeft = globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10;
            const rowPadRight = globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10;
            const totalPadDesk = (item.parentId ? 24 : 0) + rowPadLeft + rowPadRight;
            const deskW = (globalSettings.maxWidth || 500) - totalPadDesk;

            const totalPadMob = (item.parentId ? 24 : 0) + 20; // 10px + 10px on mobile
            const mobW = 360 - totalPadMob;

            // 1. Capture Desktop and Mobile canvas versions concurrently
            const [canvasDesktop, canvasMobile] = await Promise.all([
              html2canvas(el, {
                backgroundColor: null,
                scale: 3.0, // Increased from 2.5 to 3.0 for better pixel density
                logging: false,
                useCORS: true,
                onclone: (clonedDoc) => {
                  const clonedEl = clonedDoc.getElementById(`rasterize-target-${item.block.id}`);
                  if (clonedEl) {
                    clonedEl.style.overflow = 'visible';
                    const userLineSpacing = item.block.lineSpacing;
                    clonedEl.style.lineHeight = userLineSpacing ? `${userLineSpacing}` : (item.block.type === 'header' ? '1.2' : '1.5');
                    clonedEl.style.paddingTop = '8px';
                    clonedEl.style.paddingBottom = '8px';
                    clonedEl.style.width = `${deskW}px`;
                    clonedEl.style.maxWidth = `${deskW}px`;
                    clonedEl.style.webkitFontSmoothing = 'antialiased';
                    clonedEl.style.mozOsxFontSmoothing = 'grayscale';
                    clonedEl.style.fontSmooth = 'always';
                    clonedEl.style.textRendering = 'geometricPrecision';
                  }
                }
              }),
              html2canvas(el, {
                backgroundColor: null,
                scale: 3.0, // Increased from 2.5 to 3.0 for better pixel density
                logging: false,
                useCORS: true,
                onclone: (clonedDoc) => {
                  const clonedEl = clonedDoc.getElementById(`rasterize-target-${item.block.id}`);
                  if (clonedEl) {
                    clonedEl.style.overflow = 'visible';
                    const userLineSpacing = item.block.lineSpacing;
                    clonedEl.style.lineHeight = userLineSpacing ? `${userLineSpacing}` : (item.block.type === 'header' ? '1.2' : '1.5');
                    clonedEl.style.paddingTop = '8px';
                    clonedEl.style.paddingBottom = '8px';
                    clonedEl.style.width = `${mobW}px`;
                    clonedEl.style.maxWidth = `${mobW}px`;
                    clonedEl.style.webkitFontSmoothing = 'antialiased';
                    clonedEl.style.mozOsxFontSmoothing = 'grayscale';
                    clonedEl.style.fontSmooth = 'always';
                    clonedEl.style.textRendering = 'geometricPrecision';
                  }
                }
              })
            ]);

            const dataUrlDesk = canvasDesktop.toDataURL('image/png');
            const dataUrlMob = canvasMobile.toDataURL('image/png');

            // 2. Upload both assets concurrently to the backend
            const [resDesk, resMob] = await Promise.all([
              fetch(`${BACKEND_URL}/api/upload-created-asset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  pngFrames: [dataUrlDesk],
                  width: canvasDesktop.width,
                  height: canvasDesktop.height,
                  name: `rasterized_${item.block.id}_desktop.png`
                })
              }),
              fetch(`${BACKEND_URL}/api/upload-created-asset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  pngFrames: [dataUrlMob],
                  width: canvasMobile.width,
                  height: canvasMobile.height,
                  name: `rasterized_${item.block.id}_mobile.png`
                })
              })
            ]);

            const [dataDesk, dataMob] = await Promise.all([resDesk.json(), resMob.json()]);

            if (dataDesk.url && dataMob.url) {
              const updateTarget = (modules) => {
                for (let m of modules) {
                  if (m.id === item.block.id) {
                    m.rasterizedUrl = dataDesk.url;
                    m.rasterizedMobileUrl = dataMob.url;
                    return true;
                  }
                  if (m.type === 'columns' && m.columns) {
                    for (let c of m.columns) {
                      if (updateTarget(c.blocks || [])) return true;
                    }
                  }
                }
                return false;
              };
              updateTarget(updatedModules);
            } else {
              failedBlocks.push({ id: item.block.id, type: item.block.type, text: item.block.text, reason: `Upload missing URLs (Desk: ${dataDesk.url}, Mob: ${dataMob.url})` });
            }
          } catch (err) {
            console.error('Failed to rasterize block:', item.block.id, err);
            failedBlocks.push({ id: item.block.id, type: item.block.type, text: item.block.text, reason: err.message || err.toString() });
          }
        }));
      }
      
      setCanvasModules(updatedModules);
    }

    fetch(`${BACKEND_URL}/api/templates/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: sanitizedId, name: campaignName, modules: updatedModules, globalSettings }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          const importUrl = `${window.location.origin}/view/mailer/${sanitizedId}`;
          setActiveSavedImportUrl(importUrl);
          
          let alertMsg = `Campaign saved successfully! (All text blocks auto-rasterized)`;
          if (failedBlocks.length > 0) {
            alertMsg += `\n\n⚠️ WARNING: The following ${failedBlocks.length} text block(s) failed to convert to images and will render as raw text instead:\n` + 
              failedBlocks.map(f => `- [${f.type}] "${(f.text || '').substring(0, 30)}..." (Reason: ${f.reason})`).join('\n');
          }
          alert(alertMsg);

          if (id === 'new') navigate(`/editor/${sanitizedId}`, { replace: true });
          // Activate auto-save after first successful manual save
          hasBeenSavedRef.current = true;
          savedTemplateIdRef.current = sanitizedId;
        }
      })
      .finally(() => setIsSaving(false));
  };

  const executeAutoSave = () => {
    if (!hasBeenSavedRef.current || !savedTemplateIdRef.current) return;
    if (isSaving) return; // skip if a manual save is already in progress
    setAutoSaveStatus('saving');
    fetch(`${BACKEND_URL}/api/templates/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: savedTemplateIdRef.current, name: campaignName, modules: canvasModules, globalSettings }),
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setAutoSaveStatus('saved');
          setTimeout(() => setAutoSaveStatus(null), 3000); // fade out after 3s
        } else {
          setAutoSaveStatus('error');
          setTimeout(() => setAutoSaveStatus(null), 5000);
        }
      })
      .catch(() => {
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus(null), 5000);
      });
  };

  const findModuleById = (modules, id) => {
    for (const m of modules) {
      if (m.id === id) return m;
      if (m.type === 'columns' && m.columns) {
        for (const col of m.columns) {
          const found = findModuleById(col.blocks, id);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const selectModule = (id, parentId = null, columnId = null) => {
    setActiveModuleId(id);
    if (parentId && columnId) {
      const parentModule = findModuleById(canvasModules, parentId);
      if (parentModule && parentModule.columns) {
        const idx = parentModule.columns.findIndex(c => c.id === columnId);
        if (idx !== -1) {
          setSelectedColIdxToEdit(idx);
        }
      }
    }
  };

  const findParentColumnsBlock = (modules, childId) => {
    if (!childId) return null;
    for (const m of modules) {
      if (m.type === 'columns' && m.columns) {
        for (const col of m.columns) {
          if (col.blocks && col.blocks.some(b => b.id === childId)) {
            return m;
          }
          const nestedParent = findParentColumnsBlock(col.blocks, childId);
          if (nestedParent) return nestedParent;
        }
      }
    }
    return null;
  };

  const updateColumnProperty = (columnsModuleId, colId, property, value) => {
    pushHistoryDebounced(columnsModuleId, `col-${property}`);
    setCanvasModules(prev => prev.map(m => {
      if (m.id === columnsModuleId && m.columns) {
        return {
          ...m,
          columns: m.columns.map(col => {
            if (col.id === colId) {
              return { ...col, [property]: value };
            }
            return col;
          })
        };
      }
      return m;
    }));
  };

  const updateModuleProperty = (mid, property, value) => {
    pushHistoryDebounced(mid, property);
    const recursiveUpdate = (list) => {
      return list.map(m => {
        if (m.id === mid) {
          return { ...m, [property]: value };
        }
        if (m.type === 'columns' && m.columns) {
          return {
            ...m,
            columns: m.columns.map(col => ({
              ...col,
              blocks: recursiveUpdate(col.blocks)
            }))
          };
        }
        return m;
      });
    };
    setCanvasModules(prev => recursiveUpdate(prev));
  };

  const getInlineStyle = (el, prop) => {
    if (!el) return '';
    let val = el.style ? el.style[prop] || '' : '';
    if (!val && el.getAttribute) {
      if (prop === 'textAlign') {
        val = el.getAttribute('align') || '';
      } else if (prop === 'color') {
        val = el.getAttribute('color') || '';
      } else if (prop === 'fontFamily') {
        val = el.getAttribute('face') || '';
      }
    }
    return val;
  };

  const getCleanColor = (val) => {
    if (!val) return '';
    const trimmed = val.trim().toLowerCase();
    if (trimmed.startsWith('rgb')) {
      const match = trimmed.match(/\d+/g);
      if (match) {
        const r = parseInt(match[0]);
        const g = parseInt(match[1]);
        const b = parseInt(match[2]);
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
      }
    }
    return val;
  };

  const parseDomNode = (node) => {
    if (!node) return null;
    if (node.nodeType === Node.COMMENT_NODE) return null;
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.textContent.trim();
      if (!txt) return null;
      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'text',
        text: txt,
        font: 'Arial',
        color: '#ffffff',
        fontSize: 16,
        lineSpacing: 1.2,
        blockBgType: 'transparent'
      };
    }

    const tagName = node.tagName ? node.tagName.toLowerCase() : '';
    if (!tagName) return null;

    // 1. If it's a table row, check columns or parse recursively
    if (tagName === 'tr') {
      const tds = Array.from(node.children).filter(child => child.tagName.toLowerCase() === 'td');
      if (tds.length > 1) {
        const columns = tds.map((td, index) => {
          const colBlocks = [];
          Array.from(td.childNodes).forEach(child => {
            const parsed = parseDomNode(child);
            if (parsed) {
              if (Array.isArray(parsed)) colBlocks.push(...parsed);
              else colBlocks.push(parsed);
            }
          });
          if (colBlocks.length === 0 && td.textContent.trim()) {
            colBlocks.push({
              id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              type: 'text',
              text: td.textContent.trim(),
              font: 'Arial',
              color: '#ffffff',
              fontSize: 16
            });
          }
          return {
            id: `col-import-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
            blocks: colBlocks
          };
        });

        let splits = columns.map(() => 100 / columns.length);
        tds.forEach((td, idx) => {
          const wAttr = td.getAttribute('width');
          if (wAttr) {
            const wVal = parseInt(wAttr);
            if (!isNaN(wVal)) {
              splits[idx] = wVal;
            }
          }
        });
        const splitStr = splits.map(Math.round).join('-');

        return {
          id: `row-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'columns',
          columnSplit: splitStr,
          blockBgType: 'transparent',
          columns: columns
        };
      } else if (tds.length === 1) {
        return parseDomNode(tds[0]);
      }
    }

    // 2. Images (Artwork) - Check early to prevent block falls
    if (tagName === 'img') {
      const url = node.getAttribute('src') || '';
      const border = getInlineStyle(node, 'border');
      let strokeWidth = 0;
      let strokeColor = '#000000';
      if (border) {
        const match = border.match(/(\d+)px/);
        if (match) strokeWidth = parseInt(match[1]);
      }
      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'artwork',
        url: url,
        sizeInBytes: 0,
        strokeWidth: strokeWidth,
        strokeColor: strokeColor,
        blockBgType: 'transparent'
      };
    }

    // 3. Links wrapping images
    if (tagName === 'a') {
      const linkUrl = node.getAttribute('href') || '';
      const imgElement = node.querySelector && node.querySelector('img');
      if (imgElement) {
        const parsedImg = parseDomNode(imgElement);
        if (parsedImg) {
          parsedImg.linkUrl = linkUrl;
          return parsedImg;
        }
      }
    }

    // 3.5. If the element has NO block-level children (only text, inline tags, br tags), parse it as a single native Text/Header block!
    const hasBlockChildren = node.querySelector && node.querySelector('table, tr, td, div, img, hr, button');
    const isButton = tagName === 'button' || (tagName === 'a' && (getInlineStyle(node, 'background') || getInlineStyle(node, 'backgroundColor') || getInlineStyle(node, 'borderRadius') || getInlineStyle(node, 'padding')));
    const isDivider = tagName === 'hr';
    if (!hasBlockChildren && tagName !== 'body' && tagName !== 'html' && !isButton && !isDivider) {
      const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName) || parseInt(getInlineStyle(node, 'fontSize')) >= 24;
      const align = getInlineStyle(node, 'textAlign') || 'center';
      const color = getCleanColor(getInlineStyle(node, 'color')) || (isHeading ? '#ffffff' : '#aaaaaa');
      const fontSize = parseInt(getInlineStyle(node, 'fontSize')) || (isHeading ? 32 : 16);
      const font = getInlineStyle(node, 'fontFamily').replace(/['"]/g, '') || 'Arial';
      const isBold = getInlineStyle(node, 'fontWeight') === 'bold' || tagName === 'strong' || tagName === 'b';
      const isItalic = getInlineStyle(node, 'fontStyle') === 'italic' || tagName === 'em' || tagName === 'i';

      const bg = getCleanColor(getInlineStyle(node, 'backgroundColor') || getInlineStyle(node, 'background'));
      const blockBgType = bg ? 'solid' : 'transparent';
      const blockBgColor = bg || '';

      const pTop = parseInt(getInlineStyle(node, 'paddingTop')) || 0;
      const pBottom = parseInt(getInlineStyle(node, 'paddingBottom')) || 0;
      const pLeft = parseInt(getInlineStyle(node, 'paddingLeft')) || 0;
      const pRight = parseInt(getInlineStyle(node, 'paddingRight')) || 0;
      const overridePadding = (pTop || pBottom || pLeft || pRight) ? true : false;

      let text = node.innerHTML ? node.innerHTML.trim() : node.textContent.trim();
      if (text) {
        const linkUrl = tagName === 'a' ? (node.getAttribute('href') || '') : '';
        return {
          id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: isHeading ? 'header' : 'text',
          text: text,
          font: font,
          color: color,
          fontSize: fontSize,
          textAlign: align,
          isBold: isBold,
          isItalic: isItalic,
          linkUrl: linkUrl,
          blockBgType: blockBgType,
          blockBgColor: blockBgColor,
          paddingTop: pTop || 10,
          paddingBottom: pBottom || 10,
          paddingLeft: pLeft || 10,
          paddingRight: pRight || 10,
          overridePadding: overridePadding
        };
      }
    }

    // 4. Containers wrapping images - recurse to extract them natively
    const hasImg = node.querySelector && node.querySelector('img');
    if (hasImg || ['table', 'tbody', 'td', 'div', 'section', 'center', 'body', 'html', 'span'].includes(tagName)) {
      const list = [];
      Array.from(node.childNodes).forEach(child => {
        const parsed = parseDomNode(child);
        if (parsed) {
          if (Array.isArray(parsed)) list.push(...parsed);
          else list.push(parsed);
        }
      });
      if (list.length > 0) {
        return list;
      }
    }

    // 5. Buttons (a links styled like buttons, or <button> tags)
    if (tagName === 'button' || (tagName === 'a' && (getInlineStyle(node, 'background') || getInlineStyle(node, 'backgroundColor') || getInlineStyle(node, 'borderRadius') || getInlineStyle(node, 'padding')))) {
      const text = node.textContent.trim();
      const linkUrl = node.getAttribute('href') || '';
      const bgColor = getCleanColor(getInlineStyle(node, 'backgroundColor') || getInlineStyle(node, 'background')) || '#ffffff';
      const textColor = getCleanColor(getInlineStyle(node, 'color')) || '#000000';
      const fontSize = parseInt(getInlineStyle(node, 'fontSize')) || 14;
      const font = getInlineStyle(node, 'fontFamily').replace(/['"]/g, '') || 'Arial';

      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'button',
        text: text || 'SECURE TICKETS',
        linkUrl: linkUrl,
        bgColor: bgColor,
        textColor: textColor,
        fontSize: fontSize,
        font: font,
        blockBgType: 'transparent'
      };
    }

    // 6. Text-only links
    if (tagName === 'a') {
      const linkUrl = node.getAttribute('href') || '';
      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'text',
        text: node.textContent.trim(),
        linkUrl: linkUrl,
        font: 'Arial',
        color: '#ffffff',
        fontSize: 16,
        textAlign: 'center',
        blockBgType: 'transparent'
      };
    }

    // 7. Dividers (<hr> or thin styled divs)
    if (tagName === 'hr') {
      const color = getCleanColor(getInlineStyle(node, 'borderColor') || getInlineStyle(node, 'backgroundColor')) || '#333333';
      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'divider',
        color: color,
        thickness: '2px',
        blockBgType: 'transparent'
      };
    }

    // 8. Headings & Texts
    const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName) || parseInt(getInlineStyle(node, 'fontSize')) >= 24;
    
    if (isHeading || ['p', 'span', 'li', 'ul', 'ol', 'font', 'strong', 'b', 'i', 'em', 'u'].includes(tagName) || !hasBlockChildren) {
      const align = getInlineStyle(node, 'textAlign') || 'center';
      const color = getCleanColor(getInlineStyle(node, 'color')) || (isHeading ? '#ffffff' : '#aaaaaa');
      const fontSize = parseInt(getInlineStyle(node, 'fontSize')) || (isHeading ? 32 : 16);
      const font = getInlineStyle(node, 'fontFamily').replace(/['"]/g, '') || 'Arial';
      const isBold = getInlineStyle(node, 'fontWeight') === 'bold' || tagName === 'strong' || tagName === 'b';
      const isItalic = getInlineStyle(node, 'fontStyle') === 'italic' || tagName === 'em' || tagName === 'i';

      let listType = '';
      let text = '';
      if (tagName === 'ul') {
        listType = 'bullet';
        text = Array.from(node.children).map(li => li.textContent.trim()).join('\n');
      } else if (tagName === 'ol') {
        listType = 'numbered';
        text = Array.from(node.children).map(li => li.textContent.trim()).join('\n');
      } else {
        text = node.textContent.trim();
      }

      if (!text) return null;

      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: isHeading ? 'header' : 'text',
        text: text,
        font: font,
        color: color,
        fontSize: fontSize,
        textAlign: align,
        isBold: isBold,
        isItalic: isItalic,
        listType: listType,
        blockBgType: 'transparent'
      };
    }

    // 9. Custom HTML / fallback block if it contains something complex
    if (node.outerHTML) {
      return {
        id: `m-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'html',
        htmlContent: node.outerHTML,
        blockBgType: 'transparent'
      };
    }

    return null;
  };

  const importHtmlToMailer = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;

    let bgType = 'solid';
    let bgColor = '#000000';
    let bgImageUrl = '';
    let maxWidth = 700;

    if (body) {
      const bodyBg = body.style.background || body.style.backgroundImage || '';
      if (bodyBg.includes('url(')) {
        const match = bodyBg.match(/url\(['"]?([^'")]+)['"]?\)/);
        if (match) {
          bgType = 'image';
          bgImageUrl = match[1];
        }
      }
      const bodyBgColor = body.style.backgroundColor || '';
      if (bodyBgColor) {
        bgColor = getCleanColor(bodyBgColor);
      }
    }

    const wrapperTable = doc.querySelector('table');
    if (wrapperTable) {
      const tableBg = wrapperTable.style.background || '';
      if (tableBg.includes('linear-gradient')) {
        bgType = 'gradient';
      } else {
        const tableBgColor = wrapperTable.style.backgroundColor || wrapperTable.getAttribute('bgcolor');
        if (tableBgColor) {
          bgColor = getCleanColor(tableBgColor);
        }
      }

      const maxW = wrapperTable.style.maxWidth || wrapperTable.getAttribute('width');
      if (maxW) {
        const wVal = parseInt(maxW);
        if (!isNaN(wVal) && wVal > 300) {
          maxWidth = wVal;
        }
      }
    }

    let containerNode = body;
    if (wrapperTable) {
      const mainTd = wrapperTable.querySelector('td');
      if (mainTd) {
        containerNode = mainTd;
      }
    }

    const parsedModules = [];
    if (containerNode) {
      Array.from(containerNode.childNodes).forEach(child => {
        const res = parseDomNode(child);
        if (res) {
          if (Array.isArray(res)) {
            parsedModules.push(...res);
          } else {
            parsedModules.push(res);
          }
        }
      });
    }
    const finalModules = parsedModules.flat(Infinity).filter(Boolean);

    return {
      modules: finalModules,
      globalSettings: {
        bgType,
        bgColor,
        bgImageUrl,
        maxWidth
      }
    };
  };

  const handleConvertImportHtml = async () => {
    // Determine the source HTML content
    let rawHtml = '';
    
    if (importTab === 'url') {
      if (!htmlImportUrl.trim()) {
        alert('Please enter a valid URL.');
        return;
      }
      setIsImporting(true);
      setImportStatusText('🌐 Fetching webpage content...');
      try {
        const response = await fetch(`${BACKEND_URL}/api/import/fetch-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: htmlImportUrl.trim() })
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Failed to fetch the URL.');
        }
        const data = await response.json();
        rawHtml = data.html;
      } catch (err) {
        setIsImporting(false);
        setImportStatusText('');
        alert(`Failed to fetch web URL: ${err.message}`);
        return;
      }
    } else {
      if (!htmlImportValue.trim()) {
        alert('Please paste some HTML code first.');
        return;
      }
      rawHtml = htmlImportValue;
      setIsImporting(true);
    }

    setImportStatusText('⚡ Parsing HTML template elements...');
    try {
      // 1. Parse HTML to Canvas Modules
      const parsed = importHtmlToMailer(rawHtml);
      if (!parsed.modules || parsed.modules.length === 0) {
        throw new Error('Could not extract any layout modules from the HTML. Make sure it contains valid body content.');
      }

      // 2. Identify external image URLs
      setImportStatusText('🔍 Scanning for external image assets...');
      
      const isExternalUrl = (url) => {
        if (!url) return false;
        const u = url.trim();
        if (!u.startsWith('http://') && !u.startsWith('https://')) return false;
        // Skip if already pointing to our backend
        if (u.startsWith(BACKEND_URL)) return false;
        return true;
      };

      const externalUrls = new Set();
      
      if (isExternalUrl(parsed.globalSettings.bgImageUrl)) {
        externalUrls.add(parsed.globalSettings.bgImageUrl);
      }

      const scanBlock = (block) => {
        if (!block) return;
        if (block.type === 'artwork' && isExternalUrl(block.url)) {
          externalUrls.add(block.url);
        }
        if (block.type === 'columns' && block.columns) {
          block.columns.forEach(col => {
            if (col.blocks) col.blocks.forEach(scanBlock);
          });
        }
        if (isExternalUrl(block.bgImageUrl)) {
          externalUrls.add(block.bgImageUrl);
        }
      };

      parsed.modules.forEach(scanBlock);
      const urlList = Array.from(externalUrls);

      // 3. Download external images and localize
      const urlMapping = {};
      if (urlList.length > 0) {
        setImportStatusText(`📥 Localizing ${urlList.length} external image(s)...`);
        
        const downloadPromises = urlList.map(async (url) => {
          try {
            const res = await fetch(`${BACKEND_URL}/api/import/download-image`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ url })
            });
            if (res.ok) {
              const data = await res.json();
              // Store mapped local URL
              urlMapping[url] = `${BACKEND_URL}${data.url}`;
              console.log(`Successfully localized image: ${url} -> ${data.url}`);
            } else {
              console.warn(`Failed downloading image: ${url}`);
              urlMapping[url] = url; // fallback
            }
          } catch (e) {
            console.error(`Failed downloading image ${url}:`, e);
            urlMapping[url] = url; // fallback
          }
        });

        await Promise.all(downloadPromises);
      }

      // 4. Replace external URLs with localized ones
      if (Object.keys(urlMapping).length > 0) {
        setImportStatusText('🔗 Rewriting localized asset paths...');
        
        const getMappedUrl = (url) => {
          if (!url) return '';
          return urlMapping[url] || url;
        };

        if (parsed.globalSettings.bgImageUrl) {
          parsed.globalSettings.bgImageUrl = getMappedUrl(parsed.globalSettings.bgImageUrl);
        }

        const updateBlock = (block) => {
          if (!block) return;
          if (block.type === 'artwork' && block.url) {
            block.url = getMappedUrl(block.url);
          }
          if (block.type === 'columns' && block.columns) {
            block.columns.forEach(col => {
              if (col.blocks) col.blocks.forEach(updateBlock);
            });
          }
          if (block.bgImageUrl) {
            block.bgImageUrl = getMappedUrl(block.bgImageUrl);
          }
        };

        parsed.modules.forEach(updateBlock);
      }

      // 5. Load modules onto Canvas
      pushHistory();
      setCanvasModules(parsed.modules);
      setGlobalSettings(prev => ({
        ...prev,
        bgType: parsed.globalSettings.bgType || prev.bgType,
        bgColor: parsed.globalSettings.bgColor || prev.bgColor,
        bgImageUrl: parsed.globalSettings.bgImageUrl || prev.bgImageUrl,
        maxWidth: parsed.globalSettings.maxWidth || prev.maxWidth,
        fallbackBgColor: parsed.globalSettings.fallbackBgColor !== undefined ? parsed.globalSettings.fallbackBgColor : prev.fallbackBgColor,
        fallbackBgType: parsed.globalSettings.fallbackBgType || prev.fallbackBgType,
        fallbackBgGradientColor1: parsed.globalSettings.fallbackBgGradientColor1 || prev.fallbackBgGradientColor1,
        fallbackBgGradientColor2: parsed.globalSettings.fallbackBgGradientColor2 || prev.fallbackBgGradientColor2,
        fallbackBgGradientAngle: parsed.globalSettings.fallbackBgGradientAngle !== undefined ? parsed.globalSettings.fallbackBgGradientAngle : prev.fallbackBgGradientAngle
      }));

      setIsImportModalOpen(false);
      setHtmlImportValue('');
      setHtmlImportUrl('');
      
      const localizedCount = Object.keys(urlMapping).length;
      if (localizedCount > 0) {
        alert(`Successfully imported ${parsed.modules.length} canvas modules and localized ${localizedCount} external images!`);
      } else {
        alert(`Successfully imported and converted ${parsed.modules.length} canvas modules!`);
      }
    } catch (err) {
      console.error(err);
      alert(`Import failed: ${err.message || 'Malformed HTML content.'}`);
    } finally {
      setIsImporting(false);
      setImportStatusText('');
    }
  };

  const addModule = (type) => {
    pushHistory();
    const newId = `m-${Date.now()}`;
    let base = { id: newId, type, linkUrl: '', blockBgType: 'transparent', blockBgColor: '#111215', isBold: false, isItalic: false, fontSize: '', letterSpacing: '', lineSpacing: '' };
    if (type === 'header') base = { ...base, text: 'SHOW HEADLINE', font: 'Arial', color: '#ffffff', fontSize: 32, lineSpacing: 1.2 };
    else if (type === 'text') base = { ...base, text: 'SUB-TEXT SPECIFICS', font: 'Arial', color: '#aaaaaa', fontSize: 16, lineSpacing: 1.5 };
    else if (type === 'artwork') base = { ...base, url: '', sizeInBytes: 0 };
    else if (type === 'divider') base = { ...base, color: '#333333', thickness: '2px' };
    else if (type === 'button') base = { ...base, text: 'SECURE TICKETS', font: 'Arial', bgColor: '#ffffff', textColor: '#000000', fontSize: 14, lineSpacing: 1 };
    else if (type === 'html') base = { ...base, htmlContent: '<div style="padding: 20px; text-align: center; color: #fff; background-color: #140d22; border: 1px dashed rgba(168, 85, 247, 0.25); border-radius: 8px;">💻 CUSTOM HTML CONTENT</div>' };
    setCanvasModules(prev => [...prev, base]);
    setActiveModuleId(newId);
  };

  const duplicateModule = (moduleId) => {
    pushHistory();
    const cloneAndInsert = (list) => {
      const idx = list.findIndex(m => m.id === moduleId);
      if (idx !== -1) {
        const cloned = generateUniqueBlockIds(list[idx]);
        list.splice(idx + 1, 0, cloned);
        return true;
      }
      for (const m of list) {
        if (m.columns && Array.isArray(m.columns)) {
          for (const col of m.columns) {
            if (col.blocks && Array.isArray(col.blocks)) {
              const found = cloneAndInsert(col.blocks);
              if (found) return true;
            }
          }
        }
      }
      return false;
    };

    const modulesCopy = JSON.parse(JSON.stringify(canvasModules));
    cloneAndInsert(modulesCopy);
    setCanvasModules(modulesCopy);
  };

  const deleteModule = (mid, e) => {
    if (e) e.stopPropagation();
    pushHistory();
    
    const recursiveDelete = (list) => {
      return list
        .filter(m => m.id !== mid)
        .map(m => {
          if (m.type === 'columns' && m.columns) {
            return {
              ...m,
              columns: m.columns.map(col => ({
                ...col,
                blocks: recursiveDelete(col.blocks)
              }))
            };
          }
          return m;
        });
    };

    setCanvasModules(prev => recursiveDelete(prev));
    if (activeModuleId === mid) setActiveModuleId(null);
  };

  const addColumnsRow = () => {
    pushHistory();
    const newId = `row-${Date.now()}`;
    const base = {
      id: newId,
      type: 'columns',
      blockBgType: 'transparent',
      blockBgColor: '#111215',
      paddingTop: 10,
      paddingBottom: 10,
      columnSplit: '50-50',
      columns: [
        { id: `col-${newId}-0`, blocks: [] },
        { id: `col-${newId}-1`, blocks: [] }
      ]
    };
    setCanvasModules(prev => [...prev, base]);
    setActiveModuleId(newId);
  };

  const addChildModule = (parentId, columnId, type) => {
    pushHistory();
    const newId = `m-${Date.now()}`;
    let base = { id: newId, type, linkUrl: '', blockBgType: 'transparent', blockBgColor: '#111215', isBold: false, isItalic: false, fontSize: '', letterSpacing: '', lineSpacing: '' };
    if (type === 'header') base = { ...base, text: 'SHOW HEADLINE', font: 'Arial', color: '#ffffff', fontSize: 32, lineSpacing: 1.2 };
    else if (type === 'text') base = { ...base, text: 'SUB-TEXT SPECIFICS', font: 'Arial', color: '#aaaaaa', fontSize: 16, lineSpacing: 1.5 };
    else if (type === 'artwork') base = { ...base, url: '', sizeInBytes: 0 };
    else if (type === 'divider') base = { ...base, color: '#333333', thickness: '2px' };
    else if (type === 'button') base = { ...base, text: 'SECURE TICKETS', font: 'Arial', bgColor: '#ffffff', textColor: '#000000', fontSize: 14, lineSpacing: 1 };
    else if (type === 'html') base = { ...base, htmlContent: '<div style="padding: 20px; text-align: center; color: #fff; background-color: #140d22; border: 1px dashed rgba(168, 85, 247, 0.25); border-radius: 8px;">💻 CUSTOM HTML CONTENT</div>' };

    const recursiveAdd = (list) => {
      return list.map(m => {
        if (m.id === parentId) {
          return {
            ...m,
            columns: m.columns.map(col => {
              if (col.id === columnId) {
                return { ...col, blocks: [...col.blocks, base] };
              }
              return col;
            })
          };
        }
        if (m.type === 'columns' && m.columns) {
          return {
            ...m,
            columns: m.columns.map(c => ({
              ...c,
              blocks: recursiveAdd(c.blocks)
            }))
          };
        }
        return m;
      });
    };

    setCanvasModules(prev => recursiveAdd(prev));
    setActiveModuleId(newId);
  };

  const deleteChildBlock = (parentId, columnId, childId) => {
    pushHistory();
    const recursiveDeleteChild = (list) => {
      return list.map(m => {
        if (m.id === parentId) {
          return {
            ...m,
            columns: m.columns.map(col => {
              if (col.id === columnId) {
                return { ...col, blocks: col.blocks.filter(b => b.id !== childId) };
              }
              return col;
            })
          };
        }
        if (m.type === 'columns' && m.columns) {
          return {
            ...m,
            columns: m.columns.map(c => ({
              ...c,
              blocks: recursiveDeleteChild(c.blocks)
            }))
          };
        }
        return m;
      });
    };
    setCanvasModules(prev => recursiveDeleteChild(prev));
    if (activeModuleId === childId) {
      setActiveModuleId(parentId);
    }
  };

  const moveChildBlock = (parentId, columnId, index, direction) => {
    pushHistory();
    const recursiveMoveChild = (list) => {
      return list.map(m => {
        if (m.id === parentId) {
          return {
            ...m,
            columns: m.columns.map(col => {
              if (col.id === columnId) {
                const newBlocks = [...col.blocks];
                const targetIndex = index + direction;
                if (targetIndex >= 0 && targetIndex < newBlocks.length) {
                  const temp = newBlocks[index];
                  newBlocks[index] = newBlocks[targetIndex];
                  newBlocks[targetIndex] = temp;
                }
                return { ...col, blocks: newBlocks };
              }
              return col;
            })
          };
        }
        if (m.type === 'columns' && m.columns) {
          return {
            ...m,
            columns: m.columns.map(c => ({
              ...c,
              blocks: recursiveMoveChild(c.blocks)
            }))
          };
        }
        return m;
      });
    };
    setCanvasModules(prev => recursiveMoveChild(prev));
  };

  const getEquivalentSplit = (length) => {
    if (length === 1) return '100';
    if (length === 3) return '33-33-33';
    if (length === 4) return '25-25-25-25';
    return '50-50';
  };

  const changeColumnLayout = (parentId, columnSplit) => {
    pushHistory();
    const count = columnSplit.split('-').length;
    setCanvasModules(prev => prev.map(m => {
      if (m.id === parentId) {
        let currentColumns = [...m.columns];
        if (currentColumns.length < count) {
          for (let i = currentColumns.length; i < count; i++) {
            currentColumns.push({
              id: `col-${parentId}-${i}-${Date.now()}`,
              blocks: []
            });
          }
        } else if (currentColumns.length > count) {
          currentColumns = currentColumns.slice(0, count);
        }
        return {
          ...m,
          columns: currentColumns,
          columnSplit: columnSplit
        };
      }
      return m;
    }));
  };

  const handleDragStart = (idx) => {
    pushHistory();
    setDraggedIndex(idx);
  };
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === idx) return;
    const items = [...canvasModules];
    const target = items[draggedIndex];
    items.splice(draggedIndex, 1);
    items.splice(idx, 0, target);
    setDraggedIndex(idx);
    setCanvasModules(items);
  };

  const handleSidebarDragStart = (e, type) => {
    e.dataTransfer.setData('blockType', type);
    e.dataTransfer.effectAllowed = 'copy';
    setDraggedIndex(null);
  };

  const handleColumnDragOver = (e, colId) => {
    e.preventDefault();
    if (draggedIndex === null) {
      setDraggedOverColId(colId);
    }
  };

  const handleColumnDragLeave = () => {
    setDraggedOverColId(null);
  };

  const handleColumnDrop = (e, parentId, colId) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverColId(null);
    const type = e.dataTransfer.getData('blockType');
    if (type && draggedIndex === null) {
      addChildModule(parentId, colId, type);
    }
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    if (draggedIndex === null) {
      setIsCanvasDraggedOver(true);
    }
  };

  const handleCanvasDragLeave = () => {
    setIsCanvasDraggedOver(false);
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    setIsCanvasDraggedOver(false);
    const type = e.dataTransfer.getData('blockType');
    if (type && draggedIndex === null) {
      addModule(type);
    }
  };

  const activeModule = findModuleById(canvasModules, activeModuleId);
  const parentColumnsBlock = findParentColumnsBlock(canvasModules, activeModuleId);
  const activeColumnsBlock = activeModule?.type === 'columns' ? activeModule : parentColumnsBlock;

  let masterCanvasStyles = { backgroundColor: '#000000' };
  if (globalSettings.bgType === 'gradient') {
    masterCanvasStyles = {
      background: `linear-gradient(${globalSettings.gradientAngle || 180}deg, ${globalSettings.gradientColor1 || '#111215'} 0%, ${globalSettings.gradientColor2 || '#000000'} 100%)`
    };
  } else if (globalSettings.bgType === 'solid' && globalSettings.bgColor) {
    masterCanvasStyles = { backgroundColor: globalSettings.bgColor };
  } else if (globalSettings.bgType === 'image' && globalSettings.bgImageUrl) {
    if (globalSettings.fallbackBgType === 'gradient') {
      masterCanvasStyles = {
        background: `linear-gradient(${globalSettings.fallbackBgGradientAngle || 180}deg, ${globalSettings.fallbackBgGradientColor1 || '#111215'} 0%, ${globalSettings.fallbackBgGradientColor2 || '#000000'} 100%)`
      };
    } else {
      masterCanvasStyles = {
        backgroundColor: globalSettings.fallbackBgColor || '#010717'
      };
    }
  }

  if (isPreviewDarkMode) {
    masterCanvasStyles = {
      backgroundColor: '#121212',
      background: '#121212',
      color: '#ffffff'
    };
  }

  const getBlockLabel = (type) => {
    switch (type) {
      case 'header': return 'Heading';
      case 'artwork': return 'Artwork';
      case 'text': return 'Text';
      case 'button': return 'Button';
      case 'divider': return 'Divider';
      case 'html': return 'HTML';
      case 'columns': return 'Columns';
      default: return type;
    }
  };

  // Strips inline font-size, font-family, line-height, color, letter-spacing from any HTML string
  // so the module-level toolbar settings always win over pasted content styles.
  const stripInlineFontStyles = (html) => {
    if (!html) return html;
    return html.replace(/style="([^"]*)"/gi, (match, styleStr) => {
      const cleaned = styleStr
        .split(';')
        .map(s => s.trim())
        .filter(s => {
          if (!s) return false;
          const prop = s.split(':')[0].trim().toLowerCase();
          return ![
            'font-size', 'font-family', 'line-height',
            'letter-spacing', 'color', 'background-color',
            'background', 'mso-line-height-rule'
          ].includes(prop);
        })
        .join('; ');
      return cleaned ? `style="${cleaned}"` : '';
    });
  };

  // onPaste handler for contentEditable text/header blocks:
  // intercepts the paste, strips formatting, inserts clean HTML.
  const handleBlockPaste = (e, module) => {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    // Prefer plain text to avoid all markup; fall back to HTML
    let text = clipboardData.getData('text/plain');
    let html  = clipboardData.getData('text/html');
    let insertHtml;
    if (html) {
      // Strip the MS Office / Google Docs wrapper tags down to body content
      const bodyMatch = html.match(/<body[^>]*>([\/\s\S]*?)<\/body>/i);
      const inner = bodyMatch ? bodyMatch[1] : html;
      // Strip font/color inline styles but keep bold, italic, underline, links
      insertHtml = stripInlineFontStyles(inner)
        // Remove class attributes (usually carry external CSS)
        .replace(/\s*class="[^"]*"/gi, '')
        // Remove spans with no remaining style (they're wrappers only)
        .replace(/<span>(.*?)<\/span>/gi, '$1')
        .trim();
    } else {
      // Plain text – wrap newlines in <br>
      insertHtml = text.replace(/\n/g, '<br>');
    }
    document.execCommand('insertHTML', false, insertHtml);
    // Save the cleaned content immediately
    setTimeout(() => {
      const el = document.getElementById(`rasterize-target-${module.id}`);
      if (el) updateModuleProperty(module.id, 'text', el.innerHTML);
    }, 0);
  };

  const renderModuleContent = (module, isNested = false, parentId = null, columnId = null) => {
    const fontStyle = module.font ? `'${module.font}', sans-serif` : 'sans-serif';
    const weight = module.isBold ? 'bold' : 'normal';
    const style = module.isItalic ? 'italic' : 'normal';
    
    let computedFontSize = module.fontSize;
    if (isNested && parentId) {
      const parentModule = findModuleById(canvasModules, parentId);
      if (parentModule) {
        const colCount = parentModule.columns ? parentModule.columns.length : 1;
        const behavior = parentModule.mobileBehavior || 'stack';
        const originalSize = module.fontSize || (module.type === 'header' ? 32 : 16);
        
        if (previewMode === 'mobile') {
          const scale = behavior === 'row' ? 0.7 : 0.85;
          computedFontSize = Math.round(originalSize * scale);
        } else if (previewMode === 'tablet') {
          const isStacked = colCount > 2 && behavior !== 'row';
          const scale = isStacked ? 0.95 : 0.85;
          computedFontSize = Math.round(originalSize * scale);
        }
      }
    }

    const size = computedFontSize ? `${computedFontSize}px` : '';
    const tracking = module.letterSpacing ? `${module.letterSpacing}px` : 'normal';
    const leading = module.lineSpacing ? `${module.lineSpacing}` : 'normal';

    let blockBgColorStyle = 'transparent';
    if (!globalSettings.overrideBlockBg && module.blockBgType === 'solid') {
      const baseBg = module.blockBgColor || '#111215';
      if (isPreviewDarkMode) {
        if (baseBg === '#ffffff' || baseBg === 'white') {
          blockBgColorStyle = '#1f2937';
        } else if (baseBg.startsWith('#') && baseBg.length === 7) {
          const r = parseInt(baseBg.slice(1, 3), 16) || 0;
          const g = parseInt(baseBg.slice(3, 5), 16) || 0;
          const b = parseInt(baseBg.slice(5, 7), 16) || 0;
          const brightness = (r * 299 + g * 587 + b * 114) / 1000;
          if (brightness > 150) {
            blockBgColorStyle = '#1f2937'; // light block bg inverted to dark charcoal
          } else {
            blockBgColorStyle = baseBg;
          }
        } else {
          blockBgColorStyle = baseBg;
        }
      } else {
        blockBgColorStyle = baseBg;
      }
    } else if (!globalSettings.overrideBlockBg && module.blockBgType === 'gradient') {
      const angle = module.blockBgGradientAngle !== undefined ? module.blockBgGradientAngle : 135;
      const c1 = module.blockBgGradientColor1 || '#667eea';
      const c2 = module.blockBgGradientColor2 || '#764ba2';
      blockBgColorStyle = `linear-gradient(${angle}deg, ${c1}, ${c2})`;
    } else if (!globalSettings.overrideBlockBg && (module.blockBgType === 'transparent' || !module.blockBgType) && globalSettings.bgType === 'image' && globalSettings.bgImageUrl) {
      // When using an image background, transparent blocks use the fallback base color
      // so block content remains readable and visible over the image.
      if (globalSettings.fallbackBgType === 'gradient') {
        const angle = globalSettings.fallbackBgGradientAngle !== undefined ? globalSettings.fallbackBgGradientAngle : 180;
        const c1 = globalSettings.fallbackBgGradientColor1 || '#111215';
        const c2 = globalSettings.fallbackBgGradientColor2 || '#000000';
        blockBgColorStyle = `linear-gradient(${angle}deg, ${c1}, ${c2})`;
      } else {
        blockBgColorStyle = globalSettings.fallbackBgColor || '#010717';
      }
    }

    // Block stroke/border
    const blockStrokeBorder = (module.blockStrokeWidth && module.blockStrokeWidth > 0)
      ? `${module.blockStrokeWidth}px ${module.blockStrokeStyle || 'solid'} ${module.blockStrokeColor || '#000000'}`
      : 'none';

    const computedModuleStyles = {
      fontFamily: fontStyle,
      fontWeight: weight,
      fontStyle: style,
      fontSize: size,
      letterSpacing: tracking,
      lineHeight: leading,
      textAlign: module.textAlign || 'center',
      textDecoration: [module.isUnderline ? 'underline' : '', module.isStrikethrough ? 'line-through' : ''].filter(Boolean).join(' ') || 'none',
      background: blockBgColorStyle,
      border: blockStrokeBorder,
      borderRadius: module.blockStrokeRadius !== undefined ? `${module.blockStrokeRadius}px` : undefined,
      // Padding for inner content tags (h1, p, etc.) — only applied when block-level override is set.
      // Global row padding is handled by the outer wrapper div. Using 0 as default avoids double-spacing.
      paddingTop: (module.type === 'header' || module.type === 'text') ? (module.paddingTop !== undefined ? `${module.paddingTop}px` : '0px') : undefined,
      paddingBottom: (module.type === 'header' || module.type === 'text') ? (module.paddingBottom !== undefined ? `${module.paddingBottom}px` : '0px') : undefined,
      paddingLeft: (module.type === 'header' || module.type === 'text') ? (module.paddingLeft !== undefined ? `${module.paddingLeft}px` : '0px') : undefined,
      paddingRight: (module.type === 'header' || module.type === 'text') ? (module.paddingRight !== undefined ? `${module.paddingRight}px` : '0px') : undefined,
      whiteSpace: 'pre-line',
      transition: 'all 0.15s ease',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      fontSmooth: 'always',
      textRendering: 'optimizeLegibility'
    };
    if (module.type === 'columns') {
      const colCount = module.columns ? module.columns.length : 1;
      const isHovered = hoveredModuleId === module.id;
      const isActive = module.id === activeModuleId;

      const isStacked = 
        (previewMode === 'mobile' && module.mobileBehavior !== 'row') ||
        (previewMode === 'tablet' && colCount > 2 && module.mobileBehavior !== 'row');
      
      const imagesBehavior = module.mobileImagesBehavior || 'stack';
      const maxBlocks = Math.max(...(module.columns || []).map(col => (col.blocks || []).length), 0);

      if (isStacked && imagesBehavior === 'row' && maxBlocks > 0) {
        const rows = [];
        for (let i = 0; i < maxBlocks; i++) {
          const isImageRow = module.columns.some(col => col.blocks && col.blocks[i] && col.blocks[i].type === 'artwork');
          rows.push({ index: i, isImageRow });
        }
        
        return (
          <div 
            key={module.id} 
            onClick={(e) => { e.stopPropagation(); setActiveModuleId(module.id); }} 
            onMouseEnter={(e) => { e.stopPropagation(); setHoveredModuleId(module.id); }}
            onMouseLeave={(e) => { e.stopPropagation(); if (hoveredModuleId === module.id) setHoveredModuleId(null); }}
            style={{ 
              paddingTop: `${module.overridePadding && module.paddingTop !== undefined ? module.paddingTop : (globalSettings.rowPaddingTop !== undefined ? globalSettings.rowPaddingTop : 10)}px`,
              paddingBottom: `${module.overridePadding && module.paddingBottom !== undefined ? module.paddingBottom : (globalSettings.rowPaddingBottom !== undefined ? globalSettings.rowPaddingBottom : 10)}px`,
              paddingLeft: `${previewMode === 'mobile' ? 10 : (module.overridePadding && module.paddingLeft !== undefined ? module.paddingLeft : (globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10))}px`,
              paddingRight: `${previewMode === 'mobile' ? 10 : (module.overridePadding && module.paddingRight !== undefined ? module.paddingRight : (globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10))}px`,
              position: 'relative', 
              border: isHovered 
                ? '2px solid #3b82f6' 
                : (isActive ? `2px solid ${BRAND_AMBER_GOLD}` : '2px solid transparent'), 
              boxShadow: isHovered 
                ? '0 0 12px rgba(59, 130, 246, 0.3)' 
                : (isActive ? '0 0 20px rgba(245, 158, 11, 0.4)' : 'none'),
              background: blockBgColorStyle, 
              cursor: 'pointer',
              borderRadius: module.blockStrokeRadius !== undefined ? `${module.blockStrokeRadius}px` : '8px',
              margin: '0px',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {isHovered && (
              <div style={{
                position: 'absolute',
                bottom: '0',
                right: '0',
                transform: 'translateY(100%)',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: '800',
                padding: '3px 8px',
                borderRadius: '0 0 4px 4px',
                zIndex: 1000,
                textTransform: 'capitalize',
                pointerEvents: 'none'
              }}>
                {getBlockLabel(module.type)}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {rows.map(row => (
                <div 
                  key={row.index} 
                  style={{
                    display: 'grid',
                    gridTemplateColumns: row.isImageRow 
                      ? (module.columnSplit ? module.columnSplit.split('-').map(s => `${s}fr`).join(' ') : `repeat(${colCount}, 1fr)`)
                      : '1fr',
                    gap: '12px'
                  }}
                >
                  {module.columns.map((col, colIdx) => {
                    const subMod = col.blocks && col.blocks[row.index];
                    if (!subMod) return <div key={colIdx} />;
                    return (
                      <div 
                        key={colIdx}
                        style={{
                          paddingTop: `${col.paddingTop !== undefined ? col.paddingTop : 4}px`,
                          paddingBottom: `${col.paddingBottom !== undefined ? col.paddingBottom : 4}px`,
                          paddingLeft: `${col.paddingLeft !== undefined ? col.paddingLeft : 4}px`,
                          paddingRight: `${col.paddingRight !== undefined ? col.paddingRight : 4}px`,
                        }}
                      >
                        {renderModuleContent(subMod, true, module.id, col.id)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      }

      return (
        <div 
          key={module.id} 
          onClick={(e) => { e.stopPropagation(); setActiveModuleId(module.id); }} 
          onMouseEnter={(e) => { e.stopPropagation(); setHoveredModuleId(module.id); }}
          onMouseLeave={(e) => { e.stopPropagation(); if (hoveredModuleId === module.id) setHoveredModuleId(null); }}
          style={{ 
            paddingTop: `${module.overridePadding && module.paddingTop !== undefined ? module.paddingTop : (globalSettings.rowPaddingTop !== undefined ? globalSettings.rowPaddingTop : 10)}px`,
            paddingBottom: `${module.overridePadding && module.paddingBottom !== undefined ? module.paddingBottom : (globalSettings.rowPaddingBottom !== undefined ? globalSettings.rowPaddingBottom : 10)}px`,
            paddingLeft: `${previewMode === 'mobile' ? 10 : (module.overridePadding && module.paddingLeft !== undefined ? module.paddingLeft : (globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10))}px`,
            paddingRight: `${previewMode === 'mobile' ? 10 : (module.overridePadding && module.paddingRight !== undefined ? module.paddingRight : (globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10))}px`,
            position: 'relative', 
            border: isHovered 
              ? '2px solid #3b82f6' 
              : (isActive ? `2px solid ${BRAND_AMBER_GOLD}` : '2px solid transparent'), 
            boxShadow: isHovered 
              ? '0 0 12px rgba(59, 130, 246, 0.3)' 
              : (isActive ? '0 0 20px rgba(245, 158, 11, 0.4)' : 'none'),
            background: blockBgColorStyle, 
            cursor: 'pointer',
            borderRadius: module.blockStrokeRadius !== undefined ? `${module.blockStrokeRadius}px` : '8px',
            margin: '0px',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {isHovered && (
            <div style={{
              position: 'absolute',
              bottom: '0',
              right: '0',
              transform: 'translateY(100%)',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontSize: '10px',
              fontWeight: '800',
              padding: '3px 8px',
              borderRadius: '0 0 4px 4px',
              zIndex: 1000,
              textTransform: 'capitalize',
              pointerEvents: 'none'
            }}>
              Columns
            </div>
          )}
          {isHovered && (
            <div style={{
              position: 'absolute',
              right: '-16px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
              boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)',
              cursor: 'grab',
              pointerEvents: 'none'
            }}>
              ✥
            </div>
          )}

          <>
            <button 
              onClick={e => { e.stopPropagation(); duplicateModule(module.id); }} 
              title="Duplicate Columns Row"
              style={{ 
                position: 'absolute', 
                left: 'calc(100% + 16px)', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                backgroundColor: '#0b57d0', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '50%', 
                zIndex: '99', 
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(11, 87, 208, 0.3)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                lineHeight: '1',
                opacity: (isHovered || isActive) ? 1 : 0,
                pointerEvents: (isHovered || isActive) ? 'auto' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                e.currentTarget.style.backgroundColor = '#0842a0';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(11, 87, 208, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.0)';
                e.currentTarget.style.backgroundColor = '#0b57d0';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(11, 87, 208, 0.3)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            </button>

            <button 
              onClick={e => deleteModule(module.id, e)} 
              title="Delete Columns Row"
              style={{ 
                position: 'absolute', 
                left: 'calc(100% + 50px)', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                backgroundColor: '#ef4444', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '50%', 
                zIndex: '99', 
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                lineHeight: '1',
                opacity: (isHovered || isActive) ? 1 : 0,
                pointerEvents: (isHovered || isActive) ? 'auto' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                e.currentTarget.style.backgroundColor = '#dc2626';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(239, 68, 68, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.0)';
                e.currentTarget.style.backgroundColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            </button>
          </>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 
              (previewMode === 'mobile' && module.mobileBehavior !== 'row')
                ? '1fr'
                : (previewMode === 'tablet' && colCount > 2 && module.mobileBehavior !== 'row')
                  ? '1fr'
                  : (module.columnSplit 
                      ? module.columnSplit.split('-').map(s => `${s}fr`).join(' ') 
                      : `repeat(${colCount}, 1fr)`), 
            gap: '12px', 
            padding: '0px' 
          }}>
            {module.columns && module.columns.map((col, colIdx) => (
              <div 
                key={col.id || colIdx} 
                onDragOver={(e) => handleColumnDragOver(e, col.id)}
                onDragLeave={handleColumnDragLeave}
                onDrop={(e) => handleColumnDrop(e, module.id, col.id)}
                style={{ 
                  border: draggedOverColId === col.id 
                    ? `2px solid ${BRAND_AMBER_GOLD}` 
                    : (col.blocks && col.blocks.length === 0 
                        ? (isPreviewDarkMode ? '1px dashed rgba(255, 255, 255, 0.15)' : '1px dashed rgba(168, 85, 247, 0.45)') 
                        : 'none'), 
                  boxShadow: draggedOverColId === col.id ? BRAND_GOLD_GLOW : 'none',
                  borderRadius: '12px',
                  paddingTop: `${col.paddingTop !== undefined ? col.paddingTop : 4}px`,
                  paddingBottom: `${col.paddingBottom !== undefined ? col.paddingBottom : 4}px`,
                  paddingLeft: `${col.paddingLeft !== undefined ? col.paddingLeft : 4}px`,
                  paddingRight: `${col.paddingRight !== undefined ? col.paddingRight : 4}px`,
                  backgroundColor: draggedOverColId === col.id 
                    ? 'rgba(245, 158, 11, 0.15)' 
                    : (col.blocks && col.blocks.length === 0 
                        ? (isPreviewDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(168, 85, 247, 0.08)') 
                        : 'transparent'),
                  minHeight: col.blocks && col.blocks.length > 0 ? 'auto' : '80px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                {col.blocks && col.blocks.length > 0 ? (
                  col.blocks.map(subMod => renderModuleContent(subMod, true, module.id, col.id))
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 10px',
                    textAlign: 'center',
                    gap: '8px',
                    height: '100%',
                    minHeight: '80px'
                  }}>
                    <span style={{ 
                      fontSize: '11px', 
                      color: isPreviewDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(15, 9, 28, 0.55)', 
                      fontWeight: '600' 
                    }}>
                      No content here. Drag content from left.
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab('blocks');
                        alert("👈 Choose a block from the 'Inject layout block' panel on the left sidebar, then drag it here or click to add!");
                      }}
                      style={{
                        backgroundColor: 'rgba(168, 85, 247, 0.1)',
                        border: '1px solid rgba(168, 85, 247, 0.25)',
                        color: '#a855f7',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '10px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = BRAND_AMBER_GOLD;
                        e.currentTarget.style.borderColor = BRAND_AMBER_GOLD;
                        e.currentTarget.style.color = '#ffffff';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.25)';
                        e.currentTarget.style.color = '#a855f7';
                      }}
                    >
                      Add Content
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    const isHovered = hoveredModuleId === module.id;
    const isActive = module.id === activeModuleId;
    return (
      <div 
        key={module.id} 
        onClick={(e) => { e.stopPropagation(); selectModule(module.id, parentId, columnId); }} 
        onMouseEnter={(e) => { e.stopPropagation(); setHoveredModuleId(module.id); }}
        onMouseLeave={(e) => { e.stopPropagation(); if (hoveredModuleId === module.id) setHoveredModuleId(null); }}
        style={{ 
          paddingTop: isNested ? '0px' : `${globalSettings.rowPaddingTop !== undefined ? globalSettings.rowPaddingTop : 10}px`,
          paddingBottom: isNested ? '0px' : `${globalSettings.rowPaddingBottom !== undefined ? globalSettings.rowPaddingBottom : 10}px`,
          paddingLeft: isNested ? '0px' : `${previewMode === 'mobile' ? 10 : (globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10)}px`,
          paddingRight: isNested ? '0px' : `${previewMode === 'mobile' ? 10 : (globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10)}px`, 
          position: 'relative', 
          border: isHovered 
            ? '2px solid #3b82f6' 
            : (isActive ? `2px solid ${BRAND_AMBER_GOLD}` : '2px solid transparent'), 
          boxShadow: isHovered 
            ? '0 0 12px rgba(59, 130, 246, 0.3)' 
            : (isActive ? '0 0 20px rgba(245, 158, 11, 0.4)' : 'none'),
          background: blockBgColorStyle, 
          cursor: 'pointer',
          borderRadius: module.blockStrokeRadius !== undefined ? `${module.blockStrokeRadius}px` : (isNested ? '6px' : '0'),
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {isHovered && (
          <div style={{
            position: 'absolute',
            bottom: '0',
            right: '0',
            transform: 'translateY(100%)',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            fontSize: '10px',
            fontWeight: '800',
            padding: '3px 8px',
            borderRadius: '0 0 4px 4px',
            zIndex: 1000,
            textTransform: 'capitalize',
            pointerEvents: 'none'
          }}>
            {getBlockLabel(module.type)}
          </div>
        )}
        {isHovered && !isNested && (
          <div style={{
            position: 'absolute',
            right: '-16px',
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            boxShadow: '0 4px 10px rgba(59, 130, 246, 0.3)',
            cursor: 'grab',
            pointerEvents: 'none'
          }}>
            ✥
          </div>
        )}

        {!isNested ? (
          <>
            <button 
              onClick={e => { e.stopPropagation(); duplicateModule(module.id); }} 
              title="Duplicate Block"
              style={{ 
                position: 'absolute', 
                left: 'calc(100% + 16px)', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                backgroundColor: '#0b57d0', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '50%', 
                zIndex: '99', 
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(11, 87, 208, 0.3)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                lineHeight: '1',
                opacity: (isHovered || isActive) ? 1 : 0,
                pointerEvents: (isHovered || isActive) ? 'auto' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                e.currentTarget.style.backgroundColor = '#0842a0';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(11, 87, 208, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.0)';
                e.currentTarget.style.backgroundColor = '#0b57d0';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(11, 87, 208, 0.3)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
            </button>

            <button 
              onClick={e => deleteModule(module.id, e)} 
              title="Delete Block"
              style={{ 
                position: 'absolute', 
                left: 'calc(100% + 50px)', 
                top: '50%', 
                transform: 'translateY(-50%)', 
                backgroundColor: '#ef4444', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '50%', 
                zIndex: '99', 
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                lineHeight: '1',
                opacity: (isHovered || isActive) ? 1 : 0,
                pointerEvents: (isHovered || isActive) ? 'auto' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                e.currentTarget.style.backgroundColor = '#dc2626';
                e.currentTarget.style.boxShadow = '0 0 16px rgba(239, 68, 68, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(-50%) scale(1.0)';
                e.currentTarget.style.backgroundColor = '#ef4444';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
            </button>
          </>
        ) : (
          <button 
            onClick={e => deleteModule(module.id, e)} 
            title="Delete Block"
            style={{ 
              position: 'absolute', 
              right: '6px', 
              top: '6px', 
              backgroundColor: '#ef4444', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '50%', 
              zIndex: '99', 
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              fontSize: '10px',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              lineHeight: '1',
              opacity: (isHovered || isActive) ? 1 : 0,
              pointerEvents: (isHovered || isActive) ? 'auto' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.backgroundColor = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1.0)';
              e.currentTarget.style.backgroundColor = '#ef4444';
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
          </button>
        )}
        
        {module.type === 'header' && (() => {
          const Tag = module.listType === 'bullet' ? 'ul' : (module.listType === 'numbered' ? 'ol' : 'h1');
          const additionalStyles = (module.listType === 'bullet' || module.listType === 'numbered') 
            ? { paddingLeft: '40px', listStyleType: module.listType === 'bullet' ? 'disc' : 'decimal', textAlign: module.textAlign || 'left' }
            : {};
          return (
            <Tag 
              id={`rasterize-target-${module.id}`}
              contentEditable={true}
              suppressContentEditableWarning={true}
              onFocus={() => setIsEditingText(true)}
              onBlur={(e) => {
                setIsEditingText(false);
                const isList = module.listType === 'bullet' || module.listType === 'numbered';
                updateModuleProperty(module.id, 'text', isList ? e.target.innerText : e.target.innerHTML);
              }}
              onPaste={(e) => handleBlockPaste(e, module)}
              onClick={(e) => { e.stopPropagation(); setActiveModuleId(module.id); }}
              style={{ 
                margin: '0', 
                fontSize: '32px', 
                ...computedModuleStyles, 
                color: invertColorForDarkMode(module.color, '#ffffff'),
                outline: 'none',
                cursor: 'text',
                ...additionalStyles
              }}
              dangerouslySetInnerHTML={{
                __html: (module.listType === 'bullet' || module.listType === 'numbered')
                  ? (module.text || '').split('\n').map(line => `<li style="margin-bottom:6px;">${line}</li>`).join('')
                  : stripInlineFontStyles(module.text || '')
              }}
            />
          );
        })()}
        {module.type === 'artwork' && (
          <div style={{ width: '100%', minHeight: module.url ? 'auto' : '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: blockBgColorStyle }}>
            {module.url ? (
              <img 
                src={module.url} 
                alt="Show Graphics" 
                style={{ 
                  width: '100%', 
                  display: 'block',
                  boxSizing: 'border-box',
                  border: module.strokeWidth ? `${module.strokeWidth}px ${module.strokeStyle || 'solid'} ${module.strokeColor || '#000000'}` : 'none'
                }} 
              />
            ) : (
              <span style={{ color: '#4b5563', fontSize: '10px' }}>🖼 Empty Graphic Frame Slot</span>
            )}
          </div>
        )}
        {module.type === 'text' && (() => {
          const Tag = module.listType === 'bullet' ? 'ul' : (module.listType === 'numbered' ? 'ol' : 'p');
          const additionalStyles = (module.listType === 'bullet' || module.listType === 'numbered') 
            ? { paddingLeft: '40px', listStyleType: module.listType === 'bullet' ? 'disc' : 'decimal', textAlign: module.textAlign || 'left' }
            : {};

          return (
            <Tag 
              id={`rasterize-target-${module.id}`}
              contentEditable={true}
              suppressContentEditableWarning={true}
              onFocus={() => setIsEditingText(true)}
              onBlur={(e) => {
                setIsEditingText(false);
                const isList = module.listType === 'bullet' || module.listType === 'numbered';
                updateModuleProperty(module.id, 'text', isList ? e.target.innerText : e.target.innerHTML);
              }}
              onPaste={(e) => handleBlockPaste(e, module)}
              onClick={(e) => { e.stopPropagation(); setActiveModuleId(module.id); }}
              style={{ 
                margin: '0', 
                fontSize: '16px', 
                ...computedModuleStyles, 
                color: invertColorForDarkMode(module.color, '#aaaaaa'),
                outline: 'none',
                cursor: 'text',
                ...additionalStyles
              }}
              dangerouslySetInnerHTML={{
                __html: (module.listType === 'bullet' || module.listType === 'numbered')
                  ? (module.text || '').split('\n').map(line => `<li style="margin-bottom:6px;">${line}</li>`).join('')
                  : stripInlineFontStyles(module.text || '')
              }}
            />
          );
        })()}
        {module.type === 'divider' && (
          <div style={{
            background: blockBgColorStyle,
            paddingTop: module.paddingTop !== undefined ? `${module.paddingTop}px` : '10px',
            paddingBottom: module.paddingBottom !== undefined ? `${module.paddingBottom}px` : '10px',
            paddingLeft: module.paddingLeft !== undefined ? `${module.paddingLeft}px` : '20px',
            paddingRight: module.paddingRight !== undefined ? `${module.paddingRight}px` : '20px',
          }}>
            <hr style={{ borderColor: invertColorForDarkMode(module.color || '#333333', '#333333'), borderWidth: module.thickness || '2px', margin: '0' }} />
          </div>
        )}
        {module.type === 'button' && (() => {
          const btnColors = getButtonDarkModeColors(module.bgColor, module.textColor);
          return (
            <div style={{
              textAlign: module.textAlign || 'center',
              background: blockBgColorStyle,
              paddingTop: module.paddingTop !== undefined ? `${module.paddingTop}px` : '20px',
              paddingBottom: module.paddingBottom !== undefined ? `${module.paddingBottom}px` : '20px',
              paddingLeft: module.paddingLeft !== undefined ? `${module.paddingLeft}px` : '20px',
              paddingRight: module.paddingRight !== undefined ? `${module.paddingRight}px` : '20px',
            }}>
              <span 
                contentEditable={true}
                suppressContentEditableWarning={true}
                onFocus={() => setIsEditingText(true)}
                onBlur={(e) => {
                  setIsEditingText(false);
                  updateModuleProperty(module.id, 'text', e.target.innerText);
                }}
                onClick={(e) => { e.stopPropagation(); setActiveModuleId(module.id); }}
                style={{ 
                  background: btnColors.bg, 
                  color: btnColors.txt, 
                  padding: '14px 40px', 
                  display: 'inline-block', 
                  fontWeight: 'bold', 
                  borderRadius: '4px', 
                  ...computedModuleStyles,
                  backgroundColor: btnColors.bg,
                  outline: 'none',
                  cursor: 'text'
                }}
              >
                {module.text}
              </span>
            </div>
          );
        })()}
        {module.type === 'html' && (
          <div 
            style={{ background: blockBgColorStyle, transition: 'all 0.15s ease' }} 
            dangerouslySetInnerHTML={{ __html: module.htmlContent || '' }} 
          />
        )}
      </div>
    );
  };

  const renderStaticMiniModule = (module) => {
    if (!module) return null;
    const fontStyle = module.font ? `'${module.font}', sans-serif` : 'sans-serif';
    const weight = module.isBold ? 'bold' : 'normal';
    const style = module.isItalic ? 'italic' : 'normal';
    const size = module.fontSize ? `${module.fontSize}px` : '';
    const tracking = module.letterSpacing ? `${module.letterSpacing}px` : 'normal';
    const leading = module.lineSpacing ? `${module.lineSpacing}` : 'normal';

    const blockBgColorStyle = module.blockBgColor || 'transparent';

    const computedStyles = {
      fontFamily: fontStyle,
      fontWeight: weight,
      fontStyle: style,
      fontSize: size,
      letterSpacing: tracking,
      lineHeight: leading,
      background: blockBgColorStyle,
      color: module.color || '#ffffff',
      textAlign: module.textAlign || 'center',
      textDecoration: [module.isUnderline ? 'underline' : '', module.isStrikethrough ? 'line-through' : ''].filter(Boolean).join(' ') || 'none',
      whiteSpace: 'pre-line',

      paddingTop: `${globalSettings.rowPaddingTop !== undefined ? globalSettings.rowPaddingTop : 10}px`,
      paddingBottom: `${globalSettings.rowPaddingBottom !== undefined ? globalSettings.rowPaddingBottom : 10}px`,
      paddingLeft: `${globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10}px`,
      paddingRight: `${globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10}px`,
      boxSizing: 'border-box'
    };

    if (module.type === 'columns') {
      const colCount = module.columns ? module.columns.length : 1;
      return (
        <div 
          key={module.id} 
          style={{ 
            display: 'grid', 
            gridTemplateColumns: module.columnSplit 
              ? module.columnSplit.split('-').map(s => `${s}fr`).join(' ') 
              : `repeat(${colCount}, 1fr)`, 
            gap: '15px', 
            background: blockBgColorStyle, 
            paddingTop: `${module.overridePadding && module.paddingTop !== undefined ? module.paddingTop : (globalSettings.rowPaddingTop !== undefined ? globalSettings.rowPaddingTop : 10)}px`,
            paddingBottom: `${module.overridePadding && module.paddingBottom !== undefined ? module.paddingBottom : (globalSettings.rowPaddingBottom !== undefined ? globalSettings.rowPaddingBottom : 10)}px`,
            paddingLeft: `${module.overridePadding && module.paddingLeft !== undefined ? module.paddingLeft : (globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10)}px`,
            paddingRight: `${module.overridePadding && module.paddingRight !== undefined ? module.paddingRight : (globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10)}px`,
            boxSizing: 'border-box'
          }}
        >
          {module.columns && module.columns.map((col, cIdx) => (
            <div 
              key={cIdx} 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                paddingTop: `${col.paddingTop !== undefined ? col.paddingTop : 5}px`,
                paddingBottom: `${col.paddingBottom !== undefined ? col.paddingBottom : 5}px`,
                paddingLeft: `${col.paddingLeft !== undefined ? col.paddingLeft : 5}px`,
                paddingRight: `${col.paddingRight !== undefined ? col.paddingRight : 5}px`,
                boxSizing: 'border-box'
              }}
            >
              {col.blocks && col.blocks.map((subMod) => renderStaticMiniModule(subMod))}
            </div>
          ))}
        </div>
      );
    }

    if (module.type === 'header') {
      const Tag = module.listType === 'bullet' ? 'ul' : (module.listType === 'numbered' ? 'ol' : 'div');
      const listStyles = (module.listType === 'bullet' || module.listType === 'numbered')
        ? { paddingLeft: '24px', listStyleType: module.listType === 'bullet' ? 'disc' : 'decimal', textAlign: module.textAlign || 'left' }
        : {};
      return (
        <Tag key={module.id} style={{ ...computedStyles, fontSize: '24px', fontWeight: 'bold', margin: '0', ...listStyles }}>
          {(module.listType === 'bullet' || module.listType === 'numbered') ? (
            (module.text || 'HEADER').split('\n').map((line, idx) => (
              <li key={idx} style={{ marginBottom: '4px' }} dangerouslySetInnerHTML={{ __html: line }} />
            ))
          ) : (
            <span dangerouslySetInnerHTML={{ __html: module.text || 'HEADER' }} />
          )}
        </Tag>
      );
    }

    if (module.type === 'text') {
      const Tag = module.listType === 'bullet' ? 'ul' : (module.listType === 'numbered' ? 'ol' : 'div');
      const listStyles = (module.listType === 'bullet' || module.listType === 'numbered')
        ? { paddingLeft: '24px', listStyleType: module.listType === 'bullet' ? 'disc' : 'decimal', textAlign: module.textAlign || 'left' }
        : {};
      return (
        <Tag key={module.id} style={{ ...computedStyles, fontSize: '13px', margin: '0', ...listStyles }}>
          {(module.listType === 'bullet' || module.listType === 'numbered') ? (
            (module.text || 'Paragraph text...').split('\n').map((line, idx) => (
              <li key={idx} style={{ marginBottom: '4px' }} dangerouslySetInnerHTML={{ __html: line }} />
            ))
          ) : (
            <span dangerouslySetInnerHTML={{ __html: module.text || 'Paragraph text...' }} />
          )}
        </Tag>
      );
    }

    if (module.type === 'artwork') {
      const strokeStyle = module.strokeWidth 
        ? `${module.strokeWidth}px ${module.strokeStyle || 'solid'} ${module.strokeColor || '#000000'}` 
        : 'none';
      return (
        <div key={module.id} style={{ ...computedStyles, textAlign: 'center', minHeight: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {module.url ? (
            <img 
              src={module.url} 
              alt="artwork" 
              style={{ 
                maxWidth: '100%', 
                height: 'auto', 
                borderRadius: '4px', 
                display: 'block', 
                margin: '0 auto',
                boxSizing: 'border-box',
                border: strokeStyle
              }} 
            />
          ) : (
            <div style={{ padding: '20px', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '4px', color: '#6b7280', fontSize: '10px' }}>
              🖼️ IMAGE PLACEHOLDER
            </div>
          )}
        </div>
      );
    }

    if (module.type === 'button') {
      return (
        <div key={module.id} style={{ ...computedStyles, textAlign: 'center' }}>
          <span style={{
            display: 'inline-block',
            backgroundColor: module.bgColor || BRAND_AMBER_GOLD,
            color: module.textColor || '#ffffff',
            padding: '10px 24px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            {module.text || 'BUTTON'}
          </span>
        </div>
      );
    }

    if (module.type === 'divider') {
      return (
        <div key={module.id} style={{ ...computedStyles, paddingTop: '4px', paddingBottom: '4px' }}>
          <hr style={{ borderColor: module.color || '#333333', borderWidth: module.thickness || '2px', margin: '0' }} />
        </div>
      );
    }

    if (module.type === 'html') {
      return (
        <div key={module.id} style={{ ...computedStyles, fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>
          ⚙️ CUSTOM HTML CODE
        </div>
      );
    }

    if (module.type === 'spacer') {
      return (
        <div key={module.id} style={{ ...computedStyles, height: `${module.height || 20}px` }} />
      );
    }

    return null;
  };

  return (
    <div className="fade-in-up" style={{ padding: '30px', fontFamily: "'Plus Jakarta Sans', sans-serif", backgroundColor: '#f8f7fa', color: '#1f2937', minHeight: '100vh', position: 'relative' }}>
      
      {/* HEADER TOOLBAR ROW */}
      <div className="glass-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderRadius: '20px', border: '1px solid rgba(168, 85, 247, 0.15)', background: 'rgba(255, 255, 255, 0.7)', marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button onClick={() => navigate('/dashboard')} style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', border: '1px solid rgba(15, 9, 28, 0.08)', color: '#1f2937', padding: '10px 18px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', transition: 'all 0.2s' }} onMouseEnter={e => e.currentTarget.style.borderColor = BRAND_AMBER_GOLD} onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.08)'}>⬅ Hub Dashboard</button>
          
          <input 
            type="text" 
            value={campaignName} 
            onChange={e => setCampaignName(e.target.value)} 
            className="glass-input"
            style={{ width: '240px', fontWeight: '800', fontSize: '15px', padding: '10px 14px' }} 
            placeholder="Campaign Name"
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: 'rgba(241, 240, 245, 0.5)', border: '1px solid rgba(168, 85, 247, 0.1)', padding: '10px 16px', borderRadius: '16px' }}>
            <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Mailer Size:</span>
            <strong style={{ fontSize: '13px', fontFamily: 'monospace', color: totalMailerSizeMb > 2.5 ? '#ef4444' : BRAND_AMBER_GOLD }}>
              {totalMailerSizeMb} MB
            </strong>
            {totalMailerSizeMb > 2.5 && (
              <span style={{ fontSize: '9px', color: '#ef4444', textTransform: 'uppercase', fontWeight: '900' }}>⚠️ HEAVY</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* UNDO / REDO BUTTONS */}
          <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(168, 85, 247, 0.04)', border: '1px solid rgba(168, 85, 247, 0.12)', padding: '4px', borderRadius: '14px' }}>
            <button 
              onClick={handleUndo} 
              disabled={undoStack.length === 0} 
              style={{
                backgroundColor: undoStack.length === 0 ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.7)',
                border: undoStack.length === 0 ? '1px solid rgba(168, 85, 247, 0.05)' : '1px solid rgba(168, 85, 247, 0.15)',
                color: undoStack.length === 0 ? '#b5bac1' : '#1f2937',
                padding: '8px 14px',
                borderRadius: '10px',
                cursor: undoStack.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '11px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={e => { if (undoStack.length > 0) { e.currentTarget.style.borderColor = BRAND_AMBER_GOLD; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { if (undoStack.length > 0) { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.15)'; e.currentTarget.style.transform = 'none'; } }}
            >
              ↶ Undo
            </button>
            <button 
              onClick={handleRedo} 
              disabled={redoStack.length === 0} 
              style={{
                backgroundColor: redoStack.length === 0 ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.7)',
                border: redoStack.length === 0 ? '1px solid rgba(168, 85, 247, 0.05)' : '1px solid rgba(168, 85, 247, 0.15)',
                color: redoStack.length === 0 ? '#b5bac1' : '#1f2937',
                padding: '8px 14px',
                borderRadius: '10px',
                cursor: redoStack.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '11px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={e => { if (redoStack.length > 0) { e.currentTarget.style.borderColor = BRAND_AMBER_GOLD; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { if (redoStack.length > 0) { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.15)'; e.currentTarget.style.transform = 'none'; } }}
            >
              Redo ↷
            </button>
          </div>

          {activeSavedImportUrl && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Import Link:</span>
              <InlineCopyPill targetUrl={activeSavedImportUrl} />
            </div>
          )}
          <button 
            onClick={() => setIsImportModalOpen(true)} 
            style={{ 
              backgroundColor: 'rgba(15, 9, 28, 0.04)', 
              color: '#1f2937', 
              border: '1px solid rgba(15, 9, 28, 0.08)', 
              padding: '12px 24px', 
              borderRadius: '16px', 
              fontWeight: '800', 
              fontSize: '13px', 
              letterSpacing: '0.05em', 
              cursor: 'pointer', 
              transition: 'all 0.2s', 
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND_AMBER_GOLD; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.08)'; }}
          >
            <Icon name="download" size={14} /> Import HTML
          </button>
          <button onClick={executePipelineSave} disabled={isSaving} className="pulse-gold" style={{ backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', border: 'none', padding: '12px 24px', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', boxShadow: BRAND_GOLD_GLOW, transition: 'all 0.2s', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {isSaving ? 'Saving...' : <><Icon name="save" size={14} /> Save Campaign</>}
          </button>
          {/* Auto-save status indicator */}
          {autoSaveStatus && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '700',
              letterSpacing: '0.03em',
              transition: 'all 0.4s ease',
              opacity: autoSaveStatus ? 1 : 0,
              backgroundColor: autoSaveStatus === 'error' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
              border: autoSaveStatus === 'error' ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(16, 185, 129, 0.2)',
              color: autoSaveStatus === 'error' ? '#ef4444' : '#10b981'
            }}>
              {autoSaveStatus === 'saving' && <><span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', animation: 'pulse 1s infinite' }}></span> Auto-saving…</>}
              {autoSaveStatus === 'saved' && <><Icon name="check" size={12} /> Auto-saved</>}
              {autoSaveStatus === 'error' && <><Icon name="warning" size={12} /> Auto-save failed</>}
            </div>
          )}
        </div>
      </div>
 
      <div style={{ display: 'flex', gap: '30px', marginTop: '10px' }}>
        
        {/* LEFT COMPONENT CONTROLS SIDE PANEL */}
        <div className="glass-panel" style={{ width: '390px', padding: '24px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.12)', overflowY: 'auto', maxHeight: 'calc(100vh - 80px)', background: 'rgba(255, 255, 255, 0.75)', position: 'sticky', top: '20px', zIndex: 10 }}>
          
          <div style={{ display: 'flex', gap: '8px', padding: '4px', backgroundColor: 'rgba(15, 9, 28, 0.03)', borderRadius: '16px', marginBottom: '24px', border: '1px solid rgba(15, 9, 28, 0.08)' }}>
            <button onClick={() => setActiveTab('blocks')} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', fontWeight: '800', fontSize: '12px', cursor: 'pointer', background: activeTab === 'blocks' ? '#ffffff' : 'transparent', color: activeTab === 'blocks' ? BRAND_AMBER_GOLD : '#5b6275', boxShadow: activeTab === 'blocks' ? '0 4px 12px rgba(217, 119, 6, 0.08)' : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Icon name="brick" size={13} /> Add Content
            </button>
            <button onClick={() => setActiveTab('medialab')} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', fontWeight: '800', fontSize: '12px', cursor: 'pointer', background: activeTab === 'medialab' ? '#ffffff' : 'transparent', color: activeTab === 'medialab' ? BRAND_AMBER_GOLD : '#5b6275', boxShadow: activeTab === 'medialab' ? '0 4px 12px rgba(217, 119, 6, 0.08)' : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Icon name="lab" size={13} /> GIF Creator
            </button>
          </div>
 
          {activeTab === 'blocks' ? (
            <div>
              <div 
                className="glass-panel" 
                style={{ 
                  padding: '20px', 
                  borderRadius: '24px', 
                  marginBottom: '24px', 
                  background: 'rgba(255, 255, 255, 0.45)', 
                  border: '1px solid rgba(168, 85, 247, 0.12)' 
                }}
              >
                <div 
                  onClick={() => toggleSection('injectBlock')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: collapsedSections.injectBlock ? '0' : '14px' }}
                >
                  <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon name="brick" size={12} /> Inject Layout Block {collapsedSections.injectBlock ? '▶' : '▼'}
                  </span>
                  <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: '700' }}>
                    {collapsedSections.injectBlock ? 'COLLAPSED' : 'EXPANDED'}
                  </span>
                </div>

                {!collapsedSections.injectBlock && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {['header', 'artwork', 'text', 'divider', 'button', 'html'].map(type => (
                      <button 
                        key={type} 
                        onClick={() => addModule(type)} 
                        onMouseEnter={() => setHoveredButton(type)}
                        onMouseLeave={() => setHoveredButton(null)}
                        draggable={true}
                        onDragStart={(e) => handleSidebarDragStart(e, type)}
                        style={{
                          ...studioBlockButtonStyle,
                          cursor: 'grab',
                          backgroundColor: hoveredButton === type ? 'rgba(217, 119, 6, 0.08)' : 'rgba(15, 9, 28, 0.02)',
                          borderColor: hoveredButton === type ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.06)',
                          color: hoveredButton === type ? BRAND_AMBER_GOLD : '#1f2937',
                          boxShadow: hoveredButton === type ? BRAND_GOLD_GLOW : 'none',
                          transform: hoveredButton === type ? 'translateY(-1px)' : 'translateY(0)'
                        }}
                      >
                        + {type === 'artwork' ? 'IMAGE / GIF' : (type === 'html' ? 'HTML' : type.toUpperCase())}
                      </button>
                    ))}
                    <button 
                      onClick={addColumnsRow} 
                      onMouseEnter={() => setHoveredButton('columns')}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{
                        ...studioBlockButtonStyle,
                        gridColumn: 'span 2',
                        backgroundColor: hoveredButton === 'columns' ? 'rgba(217, 119, 6, 0.08)' : 'rgba(15, 9, 28, 0.02)',
                        borderColor: hoveredButton === 'columns' ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.06)',
                        color: hoveredButton === 'columns' ? BRAND_AMBER_GOLD : '#1f2937',
                        boxShadow: hoveredButton === 'columns' ? BRAND_GOLD_GLOW : 'none',
                        transform: hoveredButton === 'columns' ? 'translateY(-1px)' : 'translateY(0)'
                      }}
                    >
                      ⚡ ADD MULTI-COLUMN ROW
                    </button>
                  </div>
                )}
              </div>

              {/* SAVED TEMPLATE BLOCKS PANEL */}
              <div 
                className="glass-panel" 
                style={{ 
                  padding: '20px', 
                  borderRadius: '24px', 
                  marginBottom: '24px', 
                  background: 'rgba(255, 255, 255, 0.45)', 
                  border: '1px solid rgba(168, 85, 247, 0.12)' 
                }}
              >
                <div 
                  onClick={() => toggleSection('customBlocks')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: collapsedSections.customBlocks ? '0' : '14px' }}
                >
                  <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px', display: 'flex', alignItems: 'center' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                    Saved Blocks {collapsedSections.customBlocks ? '▶' : '▼'}
                  </span>
                  <span style={{ fontSize: '10px', color: BRAND_AMBER_GOLD, fontWeight: '800', background: 'rgba(217, 119, 6, 0.08)', padding: '2px 8px', borderRadius: '6px' }}>
                    {savedBlocks.length} Saved
                  </span>
                </div>

                {!collapsedSections.customBlocks && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* Browse Reusable Catalog Button */}
                      <button
                        onClick={() => setShowSavedBlocksModal(true)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '12px',
                          border: '1px solid rgba(217, 119, 6, 0.2)',
                          fontSize: '11px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: 'rgba(217, 119, 6, 0.08)',
                          color: BRAND_AMBER_GOLD,
                          boxShadow: 'none',
                          transition: 'all 0.2s',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = BRAND_AMBER_GOLD; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(217, 119, 6, 0.08)'; e.currentTarget.style.color = BRAND_AMBER_GOLD; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                        Browse Blocks Catalog
                      </button>

                      {/* Select Mode Switch Button */}
                      <button
                        onClick={() => {
                          if (isMultiSelectMode) {
                            setIsMultiSelectMode(false);
                            setSelectedRowIds([]);
                          } else {
                            setIsMultiSelectMode(true);
                            setSelectedRowIds([]);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '12px',
                          border: 'none',
                          fontSize: '11px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isMultiSelectMode ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.04)',
                          color: isMultiSelectMode ? '#ffffff' : '#1f2937',
                          boxShadow: isMultiSelectMode ? BRAND_GOLD_GLOW : 'none',
                          transition: 'all 0.2s',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {isMultiSelectMode ? (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            Selection Mode On
                          </>
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"></path><path d="M13 13l6 6"></path></svg>
                            Select Rows to Save
                          </>
                        )}
                      </button>
                    </div>

                    {isMultiSelectMode && (
                      <div style={{ 
                        marginTop: '12px', 
                        padding: '12px', 
                        borderRadius: '12px', 
                        background: 'rgba(217, 119, 6, 0.04)', 
                        border: '1px dashed rgba(217, 119, 6, 0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '10px', fontWeight: '800', color: '#1f2937', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            Selection Active
                          </span>
                          <span style={{ fontSize: '10px', fontWeight: '800', color: BRAND_AMBER_GOLD, background: 'rgba(217, 119, 6, 0.08)', padding: '2px 8px', borderRadius: '4px' }}>
                            {selectedRowIds.length} {selectedRowIds.length === 1 ? 'Row' : 'Rows'} Selected
                          </span>
                        </div>
                        
                        <div style={{ fontSize: '10px', color: '#5b6275', fontWeight: '600', lineHeight: '1.4' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', marginRight: '4px', verticalAlign: 'middle' }}><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                          Click rows on the canvas, then save or cancel.
                        </div>

                        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                          <button
                            onClick={() => {
                              if (selectedRowIds.length === 0) {
                                alert('Please select at least one top-level row to save.');
                                return;
                              }
                              
                              const blockName = prompt('Enter a name for this saved template block:');
                              if (!blockName || !blockName.trim()) return;

                              const orderedModules = canvasModules.filter(mod => selectedRowIds.includes(mod.id));
                              
                              fetch(`${BACKEND_URL}/api/saved-blocks`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ name: blockName.trim(), modules: orderedModules })
                              })
                                .then(res => res.json())
                                .then(() => {
                                  fetchSavedBlocks();
                                  setIsMultiSelectMode(false);
                                  setSelectedRowIds([]);
                                  alert("Block saved successfully!");
                                })
                                .catch(err => {
                                  console.error("Error saving block:", err);
                                  alert("Failed to save block.");
                                });
                            }}
                            style={{
                              flex: 1,
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '10px',
                              fontWeight: '800',
                              cursor: 'pointer',
                              background: BRAND_AMBER_GOLD,
                              color: '#ffffff',
                              boxShadow: BRAND_GOLD_GLOW,
                              transition: 'all 0.2s',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                            Save Block
                          </button>
                          
                          <button
                            onClick={() => {
                              setIsMultiSelectMode(false);
                              setSelectedRowIds([]);
                            }}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: '1px solid rgba(15, 9, 28, 0.08)',
                              fontSize: '10px',
                              fontWeight: '800',
                              cursor: 'pointer',
                              background: '#ffffff',
                              color: '#1f2937',
                              transition: 'all 0.2s',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="glass-panel" style={{ padding: '20px', borderRadius: '24px', marginBottom: '24px', background: 'rgba(255, 255, 255, 0.4)' }}>
                <div 
                  onClick={() => toggleSection('bgLayout')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: collapsedSections.bgLayout ? '0' : '16px' }}
                >
                  <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.5px', display: 'flex', alignItems: 'center' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                    Master Layout Settings {collapsedSections.bgLayout ? '▶' : '▼'}
                  </span>
                  <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: '700' }}>
                    {collapsedSections.bgLayout ? 'COLLAPSED' : 'EXPANDED'}
                  </span>
                </div>

                {!collapsedSections.bgLayout && (
                  <>
                    {/* MASTER MAILER BACKGROUND TYPE PICKER */}
                    <div style={{ marginBottom: '16px' }}>
                      <label style={studioLabelStyle}>Background type</label>
                      <select 
                        value={globalSettings.bgType} 
                        onChange={e => updateGlobalBackgroundVariable('bgType', e.target.value)} 
                        className="glass-input"
                        style={{ width: '100%', cursor: 'pointer' }}
                      >
                        <option value="solid">Solid single color</option>
                        <option value="gradient">Smooth multi-color gradient</option>
                        <option value="image">Custom Graphic Artwork Wallpaper</option>
                      </select>
                    </div>


                    {/* =========================================================================
                        RENDER CONTROLS FOR CUSTOM BACKGROUND IMAGE UPLOADS
                        ========================================================================= */}
                    {globalSettings.bgType === 'image' && (
                      <div style={{ backgroundColor: 'rgba(15, 9, 28, 0.02)', padding: '12px', borderRadius: '4px', border: '1px dashed rgba(15, 9, 28, 0.1)', marginBottom: '16px' }}>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={studioLabelStyle}>Fallback Base Color Type</label>
                          <select 
                            value={globalSettings.fallbackBgType || 'solid'} 
                            onChange={e => updateGlobalBackgroundVariable('fallbackBgType', e.target.value)}
                            className="glass-input"
                            style={{ width: '100%', cursor: 'pointer' }}
                          >
                            <option value="solid">Solid single color</option>
                            <option value="gradient">Smooth multi-color gradient</option>
                          </select>
                        </div>

                        {(globalSettings.fallbackBgType || 'solid') === 'solid' ? (
                          renderColorInput(
                            'Fallback Base Color',
                            globalSettings.fallbackBgColor || '#010717',
                            c => updateGlobalBackgroundVariable('fallbackBgColor', c)
                          )
                        ) : (
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                              {renderColorInput(
                                'Fallback Color 1',
                                globalSettings.fallbackBgGradientColor1 || '#111215',
                                c => updateGlobalBackgroundVariable('fallbackBgGradientColor1', c)
                              )}
                              {renderColorInput(
                                'Fallback Color 2',
                                globalSettings.fallbackBgGradientColor2 || '#000000',
                                c => updateGlobalBackgroundVariable('fallbackBgGradientColor2', c)
                              )}
                            </div>
                            <div style={{ marginBottom: '16px' }}>
                              <label style={studioLabelStyle}>Fallback flow direction angle (°)</label>
                              <input 
                                type="number" 
                                min="0" 
                                max="360" 
                                value={globalSettings.fallbackBgGradientAngle !== undefined ? globalSettings.fallbackBgGradientAngle : 180} 
                                onChange={e => updateGlobalBackgroundVariable('fallbackBgGradientAngle', parseInt(e.target.value) || 0)} 
                                className="glass-input"
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>
                        )}

                        <div style={{ marginBottom: '10px' }}>
                          <label style={studioLabelStyle}>Upload Background Image</label>
                          <input 
                            type="file" 
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (!file) return;

                              if (file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif')) {
                                const reader = new FileReader();
                                reader.readAsDataURL(file);
                                reader.onloadend = () => {
                                  (async () => {
                                    try {
                                      setIsCompilingGif(true); // show loader spinner
                                      const res = await fetch(`${BACKEND_URL}/api/upload`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ image: reader.result, name: file.name })
                                      });
                                      const data = await res.json();
                                      if (data.url) {
                                        updateGlobalBackgroundVariable('bgImageUrl', data.url);
                                      } else {
                                        alert('Upload failure: ' + (data.error || 'Unknown error'));
                                      }
                                    } catch (err) {
                                      alert('Failed to upload GIF background: ' + err.message);
                                    } finally {
                                      setIsCompilingGif(false);
                                    }
                                  })();
                                };
                                e.target.value = '';
                                return;
                              }

                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setWpCropAspect(9/16); // default to tall portrait wallpaper
                                setWpCropZoom(1.0);
                                setWpCropPanX(0);
                                setWpCropPanY(0);
                                setWpCropFileName(file.name.split('.')[0] || 'wallpaper');
                                setWpCropRawImage(reader.result);
                                setWpOriginalSize(file.size);
                                setWpCropModalOpen(true);
                              };
                              reader.readAsDataURL(file);
                            }}
                            style={{ color: '#5b6275', fontSize: '12px', width: '100%' }}
                          />
                        </div>

                        {globalSettings.bgImageUrl && (
                          <div style={{ marginTop: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '6px' }}>
                              <span>✓ Image linked successfully</span>
                            </div>
                            <div style={{ 
                              position: 'relative', 
                              width: '100%', 
                              height: '60px', 
                              borderRadius: '4px', 
                              backgroundImage: `url(${(globalSettings.bgImageUrl || '').startsWith('http') ? globalSettings.bgImageUrl : BACKEND_URL + globalSettings.bgImageUrl})`, 
                              backgroundSize: 'cover', 
                              backgroundPosition: 'center', 
                              border: '1px solid rgba(255,255,255,0.1)' 
                            }}>
                              <button 
                                onClick={() => updateGlobalBackgroundVariable('bgImageUrl', '')}
                                style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '3px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer', fontWeight: 'bold' }}
                              >
                                REMOVE
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {globalSettings.bgType === 'solid' && (
                      renderColorInput(
                        'Background Color',
                        globalSettings.bgColor,
                        c => updateGlobalBackgroundVariable('bgColor', c)
                      )
                    )}

                    {globalSettings.bgType === 'gradient' && (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                          {renderColorInput(
                            'Gradient color 1',
                            globalSettings.gradientColor1,
                            c => updateGlobalBackgroundVariable('gradientColor1', c)
                          )}
                          {renderColorInput(
                            'Gradient color 2',
                            globalSettings.gradientColor2,
                            c => updateGlobalBackgroundVariable('gradientColor2', c)
                          )}
                        </div>
                        <div style={{ marginBottom: '16px' }}>
                          <label style={studioLabelStyle}>Gradient flow direction angle (°)</label>
                          <input 
                            type="number" 
                            min="0" 
                            max="360" 
                            value={globalSettings.gradientAngle || 180} 
                            onChange={e => updateGlobalBackgroundVariable('gradientAngle', parseInt(e.target.value) || 0)} 
                            className="glass-input"
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Mailer Maximum Width Control */}
                    <div style={{ marginBottom: '16px', marginTop: '16px' }}>
                      <label style={studioLabelStyle}>Mailer Max Width on Desktop (px)</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                        <input 
                          type="range" 
                          min="400" 
                          max="900" 
                          step="10"
                          value={globalSettings.maxWidth || 500} 
                          onChange={e => updateGlobalBackgroundVariable('maxWidth', parseInt(e.target.value) || 500)} 
                          style={{ flex: 1, accentColor: BRAND_AMBER_GOLD }}
                        />
                        <input 
                          type="number" 
                          min="400" 
                          max="900" 
                          value={globalSettings.maxWidth || 500} 
                          onChange={e => updateGlobalBackgroundVariable('maxWidth', Math.max(400, Math.min(900, parseInt(e.target.value) || 500)))} 
                          className="glass-input"
                          style={{ width: '70px', padding: '6px', textAlign: 'center', fontWeight: 'bold', border: '1px solid rgba(15, 9, 28, 0.08)', borderRadius: '8px', fontSize: '12px', outline: 'none' }}
                        />
                      </div>
                    </div>

                    {/* Spacing / Padding four-sided box */}
                    <div style={{ backgroundColor: 'rgba(15, 9, 28, 0.03)', border: '1px solid rgba(168, 85, 247, 0.08)', padding: '14px', borderRadius: '16px', marginBottom: '16px', marginTop: '16px' }}>
                      <span style={{ ...studioLabelStyle, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: BRAND_AMBER_GOLD }}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line></svg>
                        Global Row Padding (px)
                      </span>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginTop: '12px' }}>
                        <div>
                          <label style={{ ...studioLabelStyle, fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px', display: 'block', textAlign: 'center', color: '#6b7280' }}>Top</label>
                          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(15, 9, 28, 0.08)', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                            <input 
                              type="number" 
                              min="0"
                              max="150"
                              value={globalSettings.rowPaddingTop !== undefined ? globalSettings.rowPaddingTop : 10} 
                              onChange={e => updateGlobalBackgroundVariable('rowPaddingTop', Math.max(0, parseInt(e.target.value) || 0))}
                              style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', padding: '8px 2px', fontSize: '12px', fontWeight: 'bold', outline: 'none', color: '#0f091c' }}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label style={{ ...studioLabelStyle, fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px', display: 'block', textAlign: 'center', color: '#6b7280' }}>Bottom</label>
                          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(15, 9, 28, 0.08)', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                            <input 
                              type="number" 
                              min="0"
                              max="150"
                              value={globalSettings.rowPaddingBottom !== undefined ? globalSettings.rowPaddingBottom : 10} 
                              onChange={e => updateGlobalBackgroundVariable('rowPaddingBottom', Math.max(0, parseInt(e.target.value) || 0))}
                              style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', padding: '8px 2px', fontSize: '12px', fontWeight: 'bold', outline: 'none', color: '#0f091c' }}
                            />
                          </div>
                        </div>

                        <div>
                          <label style={{ ...studioLabelStyle, fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px', display: 'block', textAlign: 'center', color: '#6b7280' }}>Left</label>
                          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(15, 9, 28, 0.08)', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                            <input 
                              type="number" 
                              min="0"
                              max="150"
                              value={globalSettings.rowPaddingLeft !== undefined ? globalSettings.rowPaddingLeft : 10} 
                              onChange={e => updateGlobalBackgroundVariable('rowPaddingLeft', Math.max(0, parseInt(e.target.value) || 0))}
                              style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', padding: '8px 2px', fontSize: '12px', fontWeight: 'bold', outline: 'none', color: '#0f091c' }}
                            />
                          </div>
                        </div>

                        <div>
                          <label style={{ ...studioLabelStyle, fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px', display: 'block', textAlign: 'center', color: '#6b7280' }}>Right</label>
                          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(15, 9, 28, 0.08)', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
                            <input 
                              type="number" 
                              min="0"
                              max="150"
                              value={globalSettings.rowPaddingRight !== undefined ? globalSettings.rowPaddingRight : 10} 
                              onChange={e => updateGlobalBackgroundVariable('rowPaddingRight', Math.max(0, parseInt(e.target.value) || 0))}
                              style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', padding: '8px 2px', fontSize: '12px', fontWeight: 'bold', outline: 'none', color: '#0f091c' }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div 
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', borderTop: '1px solid rgba(168, 85, 247, 0.12)', paddingTop: '16px', cursor: 'pointer' }}
                      onClick={() => updateGlobalBackgroundVariable('overrideBlockBg', !globalSettings.overrideBlockBg)}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '6px',
                        border: '2px solid',
                        borderColor: globalSettings.overrideBlockBg ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.25)',
                        backgroundColor: globalSettings.overrideBlockBg ? BRAND_AMBER_GOLD : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        boxShadow: globalSettings.overrideBlockBg ? BRAND_GOLD_GLOW : 'none'
                      }}>
                        {globalSettings.overrideBlockBg && <span style={{ color: '#000', fontSize: '12px', fontWeight: '900' }}>✓</span>}
                      </div>
                      <label style={{ fontSize: '11px', color: globalSettings.overrideBlockBg ? BRAND_AMBER_GOLD : '#ef4444', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase', transition: 'color 0.2s', display: 'inline-flex', alignItems: 'center' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                        Override individual row backgrounds
                      </label>
                    </div>
                  </>
                )}
              </div>

              {/* Selected Columns Split Layout card */}
              {activeColumnsBlock && (
                <div 
                  className="glass-panel" 
                  style={{ 
                    padding: '20px', 
                    borderRadius: '24px', 
                    marginBottom: '24px', 
                    background: 'rgba(255, 255, 255, 0.45)', 
                    border: '1px solid rgba(168, 85, 247, 0.12)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', userSelect: 'none' }}>
                    <span style={{ fontSize: '12px', color: '#0f091c', textTransform: 'uppercase', fontWeight: '800', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: BRAND_AMBER_GOLD }}><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="3" x2="12" y2="21"></line></svg>
                      Columns
                    </span>
                    <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, fontWeight: '800' }}>
                      ▲
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {[
                      { split: '100', cols: [{ width: '100%', label: '100%' }] },
                      { split: '50-50', cols: [{ width: '50%', label: '50%' }, { width: '50%', label: '50%' }] },
                      { split: '33-33-33', cols: [{ width: '33.3%', label: '33%' }, { width: '33.3%', label: '33%' }, { width: '33.3%', label: '33%' }] },
                      { split: '25-25-25-25', cols: [{ width: '25%', label: '25%' }, { width: '25%', label: '25%' }, { width: '25%', label: '25%' }, { width: '25%', label: '25%' }] },
                      { split: '33-66', cols: [{ width: '33.3%', label: '33%' }, { width: '66.6%', label: '67%' }] },
                      { split: '66-33', cols: [{ width: '66.6%', label: '67%' }, { width: '33.3%', label: '33%' }] },
                      { split: '25-50-25', cols: [{ width: '25%', label: '25%' }, { width: '50%', label: '50%' }, { width: '25%', label: '25%' }] },
                      { split: '25-25-50', cols: [{ width: '25%', label: '25%' }, { width: '25%', label: '25%' }, { width: '50%', label: '50%' }] }
                    ].map(option => {
                      const isSelected = (activeColumnsBlock.columnSplit || getEquivalentSplit(activeColumnsBlock.columns.length)) === option.split;
                      return (
                        <button
                          key={option.split}
                          onClick={() => changeColumnLayout(activeColumnsBlock.id, option.split)}
                          style={{
                            width: '100%',
                            height: '42px',
                            padding: '3px',
                            borderRadius: '10px',
                            border: isSelected ? `2.5px solid ${BRAND_AMBER_GOLD}` : '1.5px solid rgba(15, 9, 28, 0.08)',
                            background: '#ffffff',
                            cursor: 'pointer',
                            display: 'flex',
                            gap: '3px',
                            boxShadow: isSelected ? '0 4px 12px rgba(217, 119, 6, 0.08)' : 'none',
                            transition: 'all 0.2s ease-in-out',
                            boxSizing: 'border-box'
                          }}
                        >
                          {option.cols.map((col, cIdx) => (
                            <div
                              key={cIdx}
                              style={{
                                width: col.width,
                                height: '100%',
                                backgroundColor: isSelected ? 'rgba(217, 119, 6, 0.06)' : 'rgba(15, 9, 28, 0.03)',
                                border: isSelected ? '1px solid rgba(217, 119, 6, 0.15)' : '1px solid rgba(15, 9, 28, 0.04)',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '9px',
                                fontWeight: '800',
                                color: isSelected ? BRAND_AMBER_GOLD : '#5b6275',
                                transition: 'all 0.2s',
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {col.label}
                            </div>
                          ))}
                        </button>
                      );
                    })}
                  </div>

                  {/* Mobile Responsive Layout Behavior */}
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(15, 9, 28, 0.06)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                      <span>MOBILE RESPONSIVE BEHAVIOR</span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '4px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                      {[
                        { key: 'stack', label: '🥞 Stack Vertically' },
                        { key: 'row', label: '↔️ Keep Side-by-Side' }
                      ].map(option => {
                        const isSelected = (activeColumnsBlock.mobileBehavior || 'stack') === option.key;
                        return (
                          <button
                            key={option.key}
                            onClick={() => updateModuleProperty(activeColumnsBlock.id, 'mobileBehavior', option.key)}
                            style={{
                              flex: 1,
                              padding: '8px 4px',
                              borderRadius: '8px',
                              border: 'none',
                              fontSize: '10px',
                              fontWeight: '800',
                              cursor: 'pointer',
                              background: isSelected ? '#ffffff' : 'transparent',
                              color: isSelected ? '#a855f7' : '#5b6275',
                              boxShadow: isSelected ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                              transition: 'all 0.2s',
                              textTransform: 'uppercase',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Columns Stacking / Images Behavior */}
                    {(activeColumnsBlock.mobileBehavior || 'stack') === 'stack' && (
                      <div style={{ marginTop: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginBottom: '6px', fontWeight: '700', color: '#6b7280' }}>
                          <span>IMAGES IN STACKED COLUMNS</span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '4px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                          {[
                            { key: 'stack', label: '🥞 Stack Images' },
                            { key: 'row', label: '↔️ Keep Side-by-Side' }
                          ].map(option => {
                            const isSelected = (activeColumnsBlock.mobileImagesBehavior || 'stack') === option.key;
                            return (
                              <button
                                key={option.key}
                                onClick={() => updateModuleProperty(activeColumnsBlock.id, 'mobileImagesBehavior', option.key)}
                                style={{
                                  flex: 1,
                                  padding: '6px 4px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  fontSize: '9px',
                                  fontWeight: '800',
                                  cursor: 'pointer',
                                  background: isSelected ? '#ffffff' : 'transparent',
                                  color: isSelected ? '#a855f7' : '#5b6275',
                                  boxShadow: isSelected ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                                  transition: 'all 0.2s',
                                  textTransform: 'uppercase',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Padding Override Controls */}
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(15, 9, 28, 0.06)' }}>
                    <div 
                      onClick={() => updateModuleProperty(activeColumnsBlock.id, 'overridePadding', !activeColumnsBlock.overridePadding)}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', marginBottom: '12px' }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '6px',
                        border: '2px solid',
                        borderColor: activeColumnsBlock.overridePadding ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.2)',
                        backgroundColor: activeColumnsBlock.overridePadding ? BRAND_AMBER_GOLD : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        boxShadow: activeColumnsBlock.overridePadding ? BRAND_GOLD_GLOW : 'none'
                      }}>
                        {activeColumnsBlock.overridePadding && <span style={{ color: '#000000', fontSize: '12px', fontWeight: '900' }}>✓</span>}
                      </div>
                      <label style={{ fontSize: '11px', color: '#0f091c', fontWeight: '700', cursor: 'pointer', textTransform: 'uppercase' }}>
                        Override Global Padding
                      </label>
                    </div>

                    {activeColumnsBlock.overridePadding && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
                        <div>
                          <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Top</label>
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            value={activeColumnsBlock.paddingTop !== undefined ? activeColumnsBlock.paddingTop : 10} 
                            onChange={e => updateModuleProperty(activeColumnsBlock.id, 'paddingTop', Math.max(0, parseInt(e.target.value) || 0))}
                            className="glass-input" 
                            style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Bottom</label>
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            value={activeColumnsBlock.paddingBottom !== undefined ? activeColumnsBlock.paddingBottom : 10} 
                            onChange={e => updateModuleProperty(activeColumnsBlock.id, 'paddingBottom', Math.max(0, parseInt(e.target.value) || 0))}
                            className="glass-input" 
                            style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Left</label>
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            value={activeColumnsBlock.paddingLeft !== undefined ? activeColumnsBlock.paddingLeft : 10} 
                            onChange={e => updateModuleProperty(activeColumnsBlock.id, 'paddingLeft', Math.max(0, parseInt(e.target.value) || 0))}
                            className="glass-input" 
                            style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Right</label>
                          <input 
                            type="number" 
                            min="0"
                            max="100"
                            value={activeColumnsBlock.paddingRight !== undefined ? activeColumnsBlock.paddingRight : 10} 
                            onChange={e => updateModuleProperty(activeColumnsBlock.id, 'paddingRight', Math.max(0, parseInt(e.target.value) || 0))}
                            className="glass-input" 
                            style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Individual Column Padding Controls */}
                  <div style={{ marginTop: '12px', paddingTop: '16px', borderTop: '1px solid rgba(15, 9, 28, 0.06)' }}>
                    <span style={{ fontSize: '11px', color: '#0f091c', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                      Column Padding Settings
                    </span>

                    {/* Column Selector Tabs */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '3px', borderRadius: '8px' }}>
                      {activeColumnsBlock.columns && activeColumnsBlock.columns.map((col, idx) => {
                        const isColSelected = selectedColIdxToEdit === idx;
                        return (
                          <button
                            key={col.id || idx}
                            onClick={() => setSelectedColIdxToEdit(idx)}
                            style={{
                              flex: 1,
                              padding: '6px',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              backgroundColor: isColSelected ? BRAND_VIOLET_CARD : 'transparent',
                              color: isColSelected ? BRAND_AMBER_GOLD : '#5b6275',
                              boxShadow: isColSelected ? '0 2px 6px rgba(168, 85, 247, 0.15)' : 'none',
                              transition: 'all 0.2s'
                            }}
                          >
                            Col {idx + 1}
                          </button>
                        );
                      })}
                    </div>

                    {/* Selected Column Padding Inputs */}
                    {activeColumnsBlock.columns && activeColumnsBlock.columns[selectedColIdxToEdit] && (() => {
                      const targetCol = activeColumnsBlock.columns[selectedColIdxToEdit];
                      return (
                        <div>
                          {/* Column Padding Inputs */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
                            <div>
                              <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Top</label>
                              <input 
                                type="number" 
                                min="0"
                                max="100"
                                value={targetCol.paddingTop !== undefined ? targetCol.paddingTop : 5} 
                                onChange={e => updateColumnProperty(activeColumnsBlock.id, targetCol.id, 'paddingTop', Math.max(0, parseInt(e.target.value) || 0))}
                                className="glass-input" 
                                style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Bottom</label>
                              <input 
                                type="number" 
                                min="0"
                                max="100"
                                value={targetCol.paddingBottom !== undefined ? targetCol.paddingBottom : 5} 
                                onChange={e => updateColumnProperty(activeColumnsBlock.id, targetCol.id, 'paddingBottom', Math.max(0, parseInt(e.target.value) || 0))}
                                className="glass-input" 
                                style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Left</label>
                              <input 
                                type="number" 
                                min="0"
                                max="100"
                                value={targetCol.paddingLeft !== undefined ? targetCol.paddingLeft : 5} 
                                onChange={e => updateColumnProperty(activeColumnsBlock.id, targetCol.id, 'paddingLeft', Math.max(0, parseInt(e.target.value) || 0))}
                                className="glass-input" 
                                style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                              />
                            </div>
                            <div>
                              <label style={{ fontSize: '9px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Right</label>
                              <input 
                                type="number" 
                                min="0"
                                max="100"
                                value={targetCol.paddingRight !== undefined ? targetCol.paddingRight : 5} 
                                onChange={e => updateColumnProperty(activeColumnsBlock.id, targetCol.id, 'paddingRight', Math.max(0, parseInt(e.target.value) || 0))}
                                className="glass-input" 
                                style={{ width: '100%', height: '28px', fontSize: '11px', textAlign: 'center' }} 
                              />
                            </div>
                          </div>

                          {/* Column Blocks Manager */}
                          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(15, 9, 28, 0.06)' }}>
                            <span style={{ fontSize: '11px', color: '#0f091c', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                              Blocks inside this column
                            </span>
                            
                            {targetCol.blocks && targetCol.blocks.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                {targetCol.blocks.map((subMod, sIdx) => (
                                  <div 
                                    key={subMod.id} 
                                    onClick={() => selectModule(subMod.id, activeColumnsBlock.id, targetCol.id)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      padding: '8px 12px',
                                      borderRadius: '8px',
                                      background: activeModuleId === subMod.id ? 'rgba(168, 85, 247, 0.1)' : 'rgba(15, 9, 28, 0.03)',
                                      border: activeModuleId === subMod.id ? `1.5px solid ${BRAND_AMBER_GOLD}` : '1.5px solid transparent',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: activeModuleId === subMod.id ? BRAND_AMBER_GOLD : '#0f091c' }}>
                                      {sIdx + 1}. {getBlockLabel(subMod.type)}
                                    </span>
                                    
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      {/* Move Up */}
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); moveChildBlock(activeColumnsBlock.id, targetCol.id, sIdx, -1); }}
                                        disabled={sIdx === 0}
                                        style={{ border: 'none', background: 'transparent', padding: '2px 4px', cursor: sIdx === 0 ? 'not-allowed' : 'pointer', opacity: sIdx === 0 ? 0.3 : 1, fontSize: '10px' }}
                                        title="Move Up"
                                      >
                                        ▲
                                      </button>
                                      
                                      {/* Move Down */}
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); moveChildBlock(activeColumnsBlock.id, targetCol.id, sIdx, 1); }}
                                        disabled={sIdx === targetCol.blocks.length - 1}
                                        style={{ border: 'none', background: 'transparent', padding: '2px 4px', cursor: sIdx === targetCol.blocks.length - 1 ? 'not-allowed' : 'pointer', opacity: sIdx === targetCol.blocks.length - 1 ? 0.3 : 1, fontSize: '10px' }}
                                        title="Move Down"
                                      >
                                        ▼
                                      </button>
                                      
                                      {/* Delete */}
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); deleteChildBlock(activeColumnsBlock.id, targetCol.id, subMod.id); }}
                                        style={{ border: 'none', background: 'transparent', padding: '2px 4px', cursor: 'pointer', color: '#ef4444', fontSize: '10px' }}
                                        title="Delete"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: '10px', color: '#5b6275', fontStyle: 'italic', marginBottom: '12px', textAlign: 'center', padding: '8px', backgroundColor: 'rgba(15, 9, 28, 0.02)', borderRadius: '6px' }}>
                                No blocks inside this column
                              </div>
                            )}

                            {/* Add Block Selector */}
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {[
                                { key: 'artwork', label: '🖼️ Image' },
                                { key: 'text', label: '📝 Paragraph' },
                                { key: 'header', label: '🇹 Heading' },
                                { key: 'button', label: '🔘 Button' },
                                { key: 'divider', label: '➖ Line' }
                              ].map(item => (
                                <button
                                  key={item.key}
                                  onClick={() => addChildModule(activeColumnsBlock.id, targetCol.id, item.key)}
                                  className="glass-button"
                                  style={{
                                    flex: '1 1 calc(50% - 3px)',
                                    padding: '6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(168, 85, 247, 0.15)',
                                    background: 'rgba(255, 255, 255, 0.6)',
                                    color: '#0f091c',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.backgroundColor = BRAND_AMBER_GOLD;
                                    e.currentTarget.style.color = '#ffffff';
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.6)';
                                    e.currentTarget.style.color = '#0f091c';
                                  }}
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
          </div>
          ) : (
            /* ISOLATED GIF CREATOR MULTI-TAB MATRIX UTILITY MANAGER PANEL */
            <div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', padding: '4px', backgroundColor: 'rgba(15, 9, 28, 0.03)', borderRadius: '16px', border: '1px solid rgba(15, 9, 28, 0.08)' }}>
                <button onClick={() => setMediaLabSubTab('imageLoop')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', background: mediaLabSubTab === 'imageLoop' ? BRAND_VIOLET_CARD : 'transparent', color: mediaLabSubTab === 'imageLoop' ? BRAND_AMBER_GOLD : '#5b6275', boxShadow: mediaLabSubTab === 'imageLoop' ? BRAND_GOLD_GLOW : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Icon name="image" size={13} /> IMAGE TO GIF
                </button>
                <button onClick={() => setMediaLabSubTab('videoConvert')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', background: mediaLabSubTab === 'videoConvert' ? BRAND_VIOLET_CARD : 'transparent', color: mediaLabSubTab === 'videoConvert' ? BRAND_AMBER_GOLD : '#5b6275', boxShadow: mediaLabSubTab === 'videoConvert' ? BRAND_GOLD_GLOW : 'none', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <Icon name="video" size={13} /> VIDEO TO GIF
                </button>
              </div>

              {/* Group crop and metrics together under the imageCrop section */}
              <div className="glass-panel" style={{ ...studioPanelCard, background: 'rgba(255, 255, 255, 0.4)' }}>
                <div 
                  onClick={() => toggleSection('imageCrop')}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: collapsedSections.imageCrop ? '0' : '16px' }}
                >
                  <h4 style={{ margin: 0, fontSize: '12px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ✂️ Image Crop & Scaling {collapsedSections.imageCrop ? '▶' : '▼'}
                  </h4>
                  <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: '700' }}>
                    {collapsedSections.imageCrop ? 'COLLAPSED' : 'EXPANDED'}
                  </span>
                </div>

                {!collapsedSections.imageCrop && (
                  <>
                    <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Detected file size metrics:</span>
                    <div style={{ fontSize: '14px', fontFamily: 'monospace', color: BRAND_AMBER_GOLD, marginBottom: '16px', fontWeight: 'bold' }}>
                      {detectedWidth}px × {detectedHeight}px <span style={{ color: '#5b6275', fontWeight: 'normal' }}>(Aspect ratio matches)</span>
                    </div>
                    
                    <div style={{ marginBottom: '16px' }}>
                      <label style={studioLabelStyle}>Output sizing downscale factor:</label>
                      <select value={scaleFactor} onChange={e => setScaleFactor(parseFloat(e.target.value))} className="glass-input" style={{ width: '100%' }}>
                        <option value="1">100% (Keep full original file bounds)</option>
                        <option value="0.75">75% Scaled size reduction</option>
                        <option value="0.5">50% Scaled size reduction (Highly Recommended)</option>
                        <option value="0.25">25% Scaled size reduction (Lightweight compress)</option>
                      </select>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(168, 85, 247, 0.12)', paddingTop: '16px', marginTop: '16px' }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700' }}>Adjust image crop offset</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                        <div><label style={studioLabelStyle}>Crop width (px)</label><input type="number" value={cropWidth} readOnly className="glass-input" style={{ width: '100%', opacity: 0.5, cursor: 'not-allowed' }} /></div>
                        <div><label style={studioLabelStyle}>Crop height (px)</label><input type="number" value={cropHeight} readOnly className="glass-input" style={{ width: '100%', opacity: 0.5, cursor: 'not-allowed' }} /></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                        <div><label style={studioLabelStyle}>Offset move X (px)</label><input type="number" value={cropX} onChange={e => setCropX(parseInt(e.target.value) || 0)} className="glass-input" style={{ width: '100%' }} /></div>
                        <div><label style={studioLabelStyle}>Offset move Y (px)</label><input type="number" value={cropY} onChange={e => setCropY(parseInt(e.target.value) || 0)} className="glass-input" style={{ width: '100%' }} /></div>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={studioLabelStyle}>Image quality adjustment (Bitrate compression)</label>
                        <input type="range" min="1" max="20" value={compressionBitrate} onChange={e => setCompressionBitrate(e.target.value)} style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }} />
                      </div>

                      <div style={{ marginTop: '16px', padding: '12px', borderRadius: '12px', background: 'rgba(168, 85, 247, 0.05)', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
                        <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#6b7280', fontWeight: 'bold', marginBottom: '4px' }}>Estimated Output File Size</div>
                        <div style={{ fontSize: '18px', color: BRAND_AMBER_GOLD, fontWeight: '800', fontFamily: 'monospace' }}>
                          {mediaLabSubTab === 'imageLoop'
                            ? getEstimatedGifSize(cropWidth, cropHeight, Math.max(gifLoopQueue.length, 1), compressionBitrate)
                            : getEstimatedGifSize(cropWidth, cropHeight, Math.min(Math.round(videoDuration * videoFps), 40), compressionBitrate)
                          }
                        </div>
                        <div style={{ fontSize: '10px', color: '#5b6275', marginTop: '4px' }}>
                          {mediaLabSubTab === 'imageLoop'
                            ? `Based on ${Math.max(gifLoopQueue.length, 1)} loop frames at ${cropWidth}x${cropHeight}px`
                            : `Based on ${Math.min(Math.round(videoDuration * videoFps), 40)} sample frames at ${cropWidth}x${cropHeight}px`
                          }
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {mediaLabSubTab === 'imageLoop' ? (
                <div className="glass-panel" style={{ border: '1px dashed rgba(217, 119, 6, 0.3)', padding: '20px', borderRadius: '24px', backgroundColor: 'rgba(217, 119, 6, 0.02)' }}>
                  <div 
                    onClick={() => toggleSection('flashSequencer')}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: collapsedSections.flashSequencer ? '0' : '15px' }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: BRAND_AMBER_GOLD, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="image" size={13} /> Image to GIF {collapsedSections.flashSequencer ? '▶' : '▼'}
                    </span>
                    <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: '700' }}>
                      {collapsedSections.flashSequencer ? 'COLLAPSED' : 'EXPANDED'}
                    </span>
                  </div>

                  {!collapsedSections.flashSequencer && (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={studioLabelStyle}>Frame window delay (ms)</label>
                        <input type="number" value={imageFrameDelay} onChange={e => setImageFrameDelay(e.target.value)} className="glass-input" style={{ width: '100%' }} placeholder="400" />
                      </div>
                      <input type="file" accept="image/*" multiple onChange={executeDirectFramesUpload} style={{ color: '#1f2937', fontSize: '11px', width: '100%', marginBottom: '15px' }} />
                      <div style={{ fontSize: '12px', color: '#5b6275', marginBottom: '15px' }}>Queued pipeline frames depth: <strong style={{ color: '#1f2937' }}>{gifLoopQueue.length} files</strong></div>
                      <button 
                        onClick={compileMediaLabGif} 
                        disabled={isCompilingGif || gifLoopQueue.length < 2} 
                        style={{ 
                          width: '100%', 
                          padding: '14px', 
                          background: gifLoopQueue.length >= 2 ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.05)', 
                          border: gifLoopQueue.length >= 2 ? 'none' : '1px solid rgba(15, 9, 28, 0.1)', 
                          color: gifLoopQueue.length >= 2 ? '#ffffff' : '#9ca3af', 
                          fontWeight: 'bold', 
                          borderRadius: '16px', 
                          cursor: gifLoopQueue.length >= 2 ? 'pointer' : 'not-allowed', 
                          boxShadow: gifLoopQueue.length >= 2 ? BRAND_GOLD_GLOW : 'none',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        {isCompilingGif 
                          ? 'Processing loop buffers...' 
                          : gifLoopQueue.length < 2 
                            ? '⚡ Generate GIF (Upload 2+ frames)' 
                            : '⚡ Generate Image to GIF'}
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="glass-panel" style={{ border: '1px dashed rgba(217, 119, 6, 0.3)', padding: '20px', borderRadius: '24px', backgroundColor: 'rgba(217, 119, 6, 0.02)' }}>
                  <div 
                    onClick={() => toggleSection('videoToGif')}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none', marginBottom: collapsedSections.videoToGif ? '0' : '15px' }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: BRAND_AMBER_GOLD, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="video" size={13} /> Video to GIF {collapsedSections.videoToGif ? '▶' : '▼'}
                    </span>
                    <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: '700' }}>
                      {collapsedSections.videoToGif ? 'COLLAPSED' : 'EXPANDED'}
                    </span>
                  </div>

                  {!collapsedSections.videoToGif && (
                    <>
                      <p style={{ margin: '0 0 15px 0', fontSize: '11px', color: '#5b6275' }}>Transcode live runtime clips into small email-safe loops.</p>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={studioLabelStyle}>Target clip frame rate (FPS)</label>
                        <select value={videoFps} onChange={e => setVideoFps(parseInt(e.target.value))} className="glass-input" style={{ width: '100%' }}>
                          <option value="6">6 FPS (Super small email file payload)</option>
                          <option value="12">12 FPS (Balanced fluid runtime movement)</option>
                          <option value="18">18 FPS (High contrast ultra smooth movement)</option>
                        </select>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={studioLabelStyle}>Clip extraction window duration (Seconds)</label>
                        <input type="number" step="0.5" min="0.5" max="6" value={videoDuration} onChange={e => setVideoDuration(parseFloat(e.target.value))} className="glass-input" style={{ width: '100%' }} />
                        <span style={{ fontSize: '10px', color: '#5b6275', marginTop: '6px', display: 'block' }}>Estimated loop length composition: <strong style={{ color: '#1f2937' }}>{Math.round(videoDuration * videoFps)} Total asset frames</strong></span>
                      </div>
                      <label style={{ ...studioLabelStyle, marginBottom: '8px' }}>Select input source video track</label>
                      <input type="file" accept="video/*" onChange={handleVideoFileChange} style={{ color: '#1f2937', fontSize: '11px', width: '100%', marginBottom: '15px' }} />
                      <button 
                        onClick={compileVideoToGif} 
                        disabled={isCompilingGif || !selectedVideoFile} 
                        style={{ 
                          width: '100%', 
                          padding: '14px', 
                          background: selectedVideoFile ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.05)', 
                          border: selectedVideoFile ? 'none' : '1px solid rgba(15, 9, 28, 0.1)', 
                          color: selectedVideoFile ? '#ffffff' : '#9ca3af', 
                          fontWeight: 'bold', 
                          borderRadius: '16px', 
                          cursor: selectedVideoFile ? 'pointer' : 'not-allowed', 
                          boxShadow: selectedVideoFile ? BRAND_GOLD_GLOW : 'none',
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        {isCompilingGif 
                          ? '⏳ Transcoding video track lines...' 
                          : selectedVideoFile 
                            ? '⚙ Transcode clip into mailer layout' 
                            : '⚙ Transcode clip into mailer layout (Select video first)'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT PREVIEW WORK CANVAS DESK LAYOUT GRID MAP */}
        <div style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* DEVICE PREVIEW & DARK MODE TOOLBAR */}
          <div 
            className="glass-panel" 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              gap: '24px', 
              padding: '10px 20px', 
              borderRadius: '20px', 
              border: '1px solid rgba(168, 85, 247, 0.15)', 
              background: 'rgba(255, 255, 255, 0.75)', 
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.04)',
              marginBottom: '20px',
              width: '100%',
              maxWidth: `${(globalSettings.maxWidth || 500)}px`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {/* Device Toggles */}
            <div style={{ display: 'flex', gap: '4px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '4px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
              {[
                { mode: 'desktop', label: '💻 Desktop' },
                { mode: 'tablet', label: '📟 Tablet' },
                { mode: 'mobile', label: '📱 Mobile' }
              ].map(({ mode, label }) => {
                const isActive = previewMode === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => setPreviewMode(mode)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '11px',
                      fontWeight: '800',
                      cursor: 'pointer',
                      background: isActive ? '#ffffff' : 'transparent',
                      color: isActive ? BRAND_AMBER_GOLD : '#5b6275',
                      boxShadow: isActive ? '0 4px 12px rgba(217, 119, 6, 0.08)' : 'none',
                      transition: 'all 0.2s ease-in-out',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setIsPreviewDarkMode(prev => !prev)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: '12px',
                border: isPreviewDarkMode ? `1px solid ${BRAND_AMBER_GOLD}` : '1px solid rgba(15, 9, 28, 0.08)',
                background: isPreviewDarkMode ? 'rgba(217, 119, 6, 0.08)' : 'rgba(255, 255, 255, 0.6)',
                color: isPreviewDarkMode ? BRAND_AMBER_GOLD : '#5b6275',
                boxShadow: isPreviewDarkMode ? BRAND_GOLD_GLOW : 'none',
                cursor: 'pointer',
                fontWeight: '800',
                fontSize: '11px',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              <span>{isPreviewDarkMode ? '🌙 Dark Mode On' : '☀️ Dark Mode Off'}</span>
              <div 
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: isPreviewDarkMode ? BRAND_AMBER_GOLD : '#9ca3af',
                  boxShadow: isPreviewDarkMode ? '0 0 8px #d97706' : 'none',
                  transition: 'all 0.2s'
                }} 
              />
            </button>
          </div>

          {activeModule && (
            <div 
              className="glass-panel fade-in-up" 
              style={{ 
                width: '100%', 
                maxWidth: '960px',
                padding: '10px 16px', 
                borderRadius: '16px', 
                border: '1px solid #c7c7c7', 
                background: '#edf2fa', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.05), 0 12px 24px rgba(0,0,0,0.03)', 
                marginBottom: '20px',
                position: 'sticky',
                top: '10px',
                zIndex: 90,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                flexShrink: 0,
                overflow: 'visible'
              }}
            >
              {/* Row 1: Core Text Formatting & Selection Controls */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%', flexWrap: 'wrap', overflow: 'visible' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', overflow: 'visible' }}>
                  
                  {/* Block Type Label or Selector */}
                  {['header', 'text'].includes(activeModule.type) ? (
                    <select
                      value={activeModule.type}
                      onChange={e => updateModuleProperty(activeModule.id, 'type', e.target.value)}
                      style={{
                        border: '1px solid #c7c7c7',
                        background: '#ffffff',
                        color: '#444746',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        padding: '2px 8px',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        outline: 'none',
                        height: '28px',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                      title="Block Style"
                    >
                      <option value="text">Normal text</option>
                      <option value="header">Heading</option>
                    </select>
                  ) : (
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', padding: '0 8px', textTransform: 'uppercase', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '0.5px' }}>
                      {getBlockLabel(activeModule.type)}
                    </span>
                  )}

                  <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />

                  {/* Fonts */}
                  {['header', 'text', 'button'].includes(activeModule.type) && (
                    <>
                      {/* Font Family Dropdown */}
                      <SearchableFontDropdown
                        value={activeModule.font || 'Arial'}
                        onChange={val => updateModuleProperty(activeModule.id, 'font', val)}
                        systemFonts={systemFonts}
                        customFonts={customFonts}
                        compact={true}
                      />

                      {/* Brand Font Embed Uploader */}
                      <label 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px', 
                          border: '1px dashed rgba(168, 85, 247, 0.4)', 
                          background: 'rgba(168, 85, 247, 0.05)', 
                          color: '#a855f7', 
                          fontSize: '11px', 
                          fontWeight: 'bold', 
                          cursor: 'pointer', 
                          transition: 'all 0.2s',
                          userSelect: 'none'
                        }} 
                        title="Embed Custom Brand Font (.OTF / .TTF)"
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168, 85, 247, 0.1)'; e.currentTarget.style.borderColor = '#a855f7'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(168, 85, 247, 0.05)'; e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.4)'; }}
                      >
                        <span>+F</span>
                        <input 
                          type="file" 
                          accept=".otf,.ttf" 
                          onChange={handleCustomOtfFontUpload} 
                          style={{ display: 'none' }} 
                        />
                      </label>

                      <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />

                      {/* Font Size Decrease/Input/Increase */}
                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #c7c7c7', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#ffffff', height: '28px' }}>
                        <button 
                          onClick={() => { const cur = parseInt(activeModule.fontSize) || (activeModule.type === 'header' ? 32 : 16); const newSize = Math.max(8, cur - 1); updateModuleProperty(activeModule.id, 'fontSize', newSize); if (activeModule.text && typeof activeModule.text === 'string') updateModuleProperty(activeModule.id, 'text', stripInlineFontStyles(activeModule.text)); }} 
                          style={{ border: 'none', background: 'transparent', padding: '0 8px', cursor: 'pointer', fontSize: '13px', color: '#444746', fontWeight: 'bold' }}
                          title="Decrease font size"
                        >
                          −
                        </button>
                        <input 
                          type="number"
                          min="8"
                          max="120"
                          value={activeModule.fontSize !== undefined ? activeModule.fontSize : ''}
                          onChange={e => {
                            updateModuleProperty(activeModule.id, 'fontSize', e.target.value);
                            if (activeModule.text && typeof activeModule.text === 'string') updateModuleProperty(activeModule.id, 'text', stripInlineFontStyles(activeModule.text));
                          }}
                          onBlur={() => {
                            let val = parseInt(activeModule.fontSize);
                            if (isNaN(val)) {
                              val = activeModule.type === 'header' ? 32 : 16;
                            }
                            updateModuleProperty(activeModule.id, 'fontSize', Math.max(8, Math.min(120, val)));
                          }}
                          style={{ 
                            border: 'none', 
                            borderLeft: '1px solid #c7c7c7', 
                            borderRight: '1px solid #c7c7c7', 
                            background: 'transparent', 
                            fontSize: '11px', 
                            fontWeight: 'bold', 
                            width: '32px', 
                            textAlign: 'center', 
                            outline: 'none',
                            color: '#444746',
                            height: '100%'
                          }} 
                        />
                        <button 
                          onClick={() => { const cur = parseInt(activeModule.fontSize) || (activeModule.type === 'header' ? 32 : 16); const newSize = Math.min(120, cur + 1); updateModuleProperty(activeModule.id, 'fontSize', newSize); if (activeModule.text && typeof activeModule.text === 'string') updateModuleProperty(activeModule.id, 'text', stripInlineFontStyles(activeModule.text)); }} 
                          style={{ border: 'none', background: 'transparent', padding: '0 8px', cursor: 'pointer', fontSize: '13px', color: '#444746', fontWeight: 'bold' }}
                          title="Increase font size"
                        >
                          +
                        </button>
                      </div>

                      <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />

                      {/* Text Formatting & Color */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {[
                          { 
                            icon: <span style={{ fontWeight: '800', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '13px' }}>B</span>, 
                            title: 'Bold (Selection or Block)', 
                            action: () => {
                              const sel = window.getSelection();
                              if (sel && sel.rangeCount > 0 && sel.toString().length > 0) {
                                document.execCommand('bold', false, null);
                                const activeEl = document.activeElement;
                                if (activeEl && activeEl.getAttribute('contenteditable') === 'true') {
                                  updateModuleProperty(activeModule.id, 'text', activeEl.innerHTML);
                                }
                              } else {
                                updateModuleProperty(activeModule.id, 'isBold', !activeModule.isBold);
                              }
                            }, 
                            active: activeModule.isBold
                          },
                          { 
                            icon: <span style={{ fontStyle: 'italic', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '800', fontSize: '13px' }}>I</span>, 
                            title: 'Italic (Selection or Block)', 
                            action: () => {
                              const sel = window.getSelection();
                              if (sel && sel.rangeCount > 0 && sel.toString().length > 0) {
                                document.execCommand('italic', false, null);
                                const activeEl = document.activeElement;
                                if (activeEl && activeEl.getAttribute('contenteditable') === 'true') {
                                  updateModuleProperty(activeModule.id, 'text', activeEl.innerHTML);
                                }
                              } else {
                                updateModuleProperty(activeModule.id, 'isItalic', !activeModule.isItalic);
                              }
                            }, 
                            active: activeModule.isItalic
                          },
                          { 
                            icon: <span style={{ textDecoration: 'underline', fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '800', fontSize: '13px' }}>U</span>, 
                            title: 'Underline (Selection or Block)', 
                            action: () => {
                              const sel = window.getSelection();
                              if (sel && sel.rangeCount > 0 && sel.toString().length > 0) {
                                document.execCommand('underline', false, null);
                                const activeEl = document.activeElement;
                                if (activeEl && activeEl.getAttribute('contenteditable') === 'true') {
                                  updateModuleProperty(activeModule.id, 'text', activeEl.innerHTML);
                                }
                              } else {
                                updateModuleProperty(activeModule.id, 'isUnderline', !activeModule.isUnderline);
                              }
                            }, 
                            active: activeModule.isUnderline
                          }
                        ].map((btn, idx) => (
                          <button 
                            key={idx} 
                            onMouseDown={e => e.preventDefault()}
                            onClick={btn.action} 
                            style={{ 
                              border: 'none', 
                              borderRadius: '6px', 
                              background: btn.active ? '#d3e3fd' : 'transparent', 
                              color: btn.active ? '#0b57d0' : '#444746', 
                              cursor: 'pointer', 
                              width: '28px', 
                              height: '28px', 
                              padding: '0', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center'
                            }} 
                            title={btn.title}
                            onMouseEnter={e => { if (!btn.active) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'; }}
                            onMouseLeave={e => { if (!btn.active) e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            {btn.icon}
                          </button>
                        ))}

                        {/* Text Color (Google Docs 'A' style) */}
                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative', overflow: 'visible' }}>
                          <button 
                            onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'textColor' ? null : 'textColor')}
                            style={{ 
                              border: 'none', 
                              background: 'transparent', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              width: '28px', 
                              height: '28px', 
                              padding: '0',
                              borderRadius: '4px'
                            }} 
                            title="Text Color"
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#444746', height: '14px', lineHeight: '14px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>A</span>
                            <div style={{ width: '16px', height: '3.5px', backgroundColor: activeModule.color || '#000000', border: '1px solid rgba(0, 0, 0, 0.25)', borderRadius: '1px', boxSizing: 'border-box', marginTop: '1px' }} />
                          </button>
                          {renderColorPopover('textColor', activeModule.color || '#000000', (val) => updateModuleProperty(activeModule.id, 'color', val))}
                        </div>
                      </div>

                      <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />

                      {/* Redirect Link */}
                      <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                        <button
                          type="button"
                          onClick={() => setActiveLinkPopup(activeLinkPopup === activeModule.id ? null : activeModule.id)}
                          style={{
                            background: activeModule.linkUrl ? '#d3e3fd' : 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: activeModule.linkUrl ? '#0b57d0' : '#444746',
                            transition: 'all 0.2s',
                          }}
                          title={activeModule.type === 'button' ? "Configure Button Redirect Link" : "Configure Block Link Redirect"}
                          onMouseEnter={e => { if (!activeModule.linkUrl) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'; }}
                          onMouseLeave={e => { if (!activeModule.linkUrl) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                        </button>
                        {renderLinkPopover(activeModule)}
                      </div>

                      {/* AI Sparkles */}
                      <button
                        onClick={() => { setGeminiPrompt(activeModule.text || ''); setGeminiSuggestions([]); setGeminiTone('creative'); setGeminiPhase('signup'); setShowGeminiModal(true); }}
                        style={{ 
                          border: 'none', 
                          borderRadius: '6px', 
                          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(99, 102, 241, 0.1))', 
                          color: '#7c3aed', 
                          cursor: 'pointer', 
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="AI Smart Text (Gemini)"
                        onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgba(168, 85, 247, 0.18), rgba(99, 102, 241, 0.18))'}
                        onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), rgba(99, 102, 241, 0.1))'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c0 5.5-4.5 10-10 10 5.5 0 10 4.5 10 10 0-5.5 4.5-10 10-10-5.5 0-10-4.5-10-10z"/></svg>
                      </button>

                      <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />

                      {/* Alignment */}
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {[
                          { value: 'left', title: 'Align Left', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="17" y1="10" x2="3" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="17" y1="18" x2="3" y2="18"></line></svg> },
                          { value: 'center', title: 'Align Center', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="10" x2="6" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="18" y1="18" x2="6" y2="18"></line></svg> },
                          { value: 'right', title: 'Align Right', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="10" x2="7" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="21" y1="18" x2="7" y2="18"></line></svg> }
                        ].map(opt => {
                          const isSel = (activeModule.textAlign || 'center') === opt.value;
                          return (
                            <button 
                              key={opt.value} 
                              onClick={() => updateModuleProperty(activeModule.id, 'textAlign', opt.value)} 
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                width: '28px', 
                                height: '28px', 
                                border: 'none', 
                                borderRadius: '6px', 
                                cursor: 'pointer', 
                                backgroundColor: isSel ? '#d3e3fd' : 'transparent', 
                                color: isSel ? '#0b57d0' : '#444746'
                              }} 
                              title={opt.title}
                              onMouseEnter={e => { if (!isSel) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'; }}
                              onMouseLeave={e => { if (!isSel) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              {opt.icon}
                            </button>
                          );
                        })}
                      </div>

                      {/* Lists */}
                      {['header', 'text'].includes(activeModule.type) && (
                        <>
                          <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {[
                              { 
                                value: 'bullet', 
                                title: 'Bulleted List', 
                                icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="9" y1="6" x2="21" y2="6"></line>
                                    <line x1="9" y1="12" x2="21" y2="12"></line>
                                    <line x1="9" y1="18" x2="21" y2="18"></line>
                                    <circle cx="4" cy="6" r="1.5" fill="currentColor"></circle>
                                    <circle cx="4" cy="12" r="1.5" fill="currentColor"></circle>
                                    <circle cx="4" cy="18" r="1.5" fill="currentColor"></circle>
                                  </svg>
                                )
                              },
                              { 
                                value: 'numbered', 
                                title: 'Numbered List', 
                                icon: (
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="10" y1="6" x2="21" y2="6"></line>
                                    <line x1="10" y1="12" x2="21" y2="12"></line>
                                    <line x1="10" y1="18" x2="21" y2="18"></line>
                                    <path d="M4 4h1v4H3" fill="none"></path>
                                    <path d="M4 14a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H4v1h3" fill="none"></path>
                                  </svg>
                                )
                              }
                            ].map(opt => {
                              const isSel = activeModule.listType === opt.value;
                              return (
                                <button 
                                  key={opt.value} 
                                  onClick={() => updateModuleProperty(activeModule.id, 'listType', isSel ? 'none' : opt.value)} 
                                  style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    width: '28px', 
                                    height: '28px', 
                                    border: 'none', 
                                    borderRadius: '6px', 
                                    cursor: 'pointer', 
                                    backgroundColor: isSel ? '#d3e3fd' : 'transparent', 
                                    color: isSel ? '#0b57d0' : '#444746'
                                  }} 
                                  title={opt.title}
                                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.06)'; }}
                                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  {opt.icon}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Close Button on Right */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setActiveModuleId(null); }} 
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      width: '28px',
                      height: '28px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: '#444746',
                      transition: 'all 0.2s',
                      padding: 0
                    }}
                    title="Close block settings"
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#444746'; }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
              </div>

              {/* Horizontal Divider between rows */}
              <div style={{ height: '1px', backgroundColor: 'rgba(0,0,0,0.08)', width: '100%' }} />

              {/* Row 2: Spacing, Background Fill, and Type-Specific Properties */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', flexWrap: 'wrap', overflow: 'visible' }}>
                
                {/* Spacing Controls (Line Height & Letter Spacing) */}
                {['header', 'text', 'button'].includes(activeModule.type) && (
                  <>
                    {/* Line Height Control */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Line Height:</span>
                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #c7c7c7', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#ffffff', height: '28px' }}>
                        <button 
                          onClick={() => { const cur = parseFloat(activeModule.lineSpacing) || 1.2; updateModuleProperty(activeModule.id, 'lineSpacing', Math.max(0.5, parseFloat((cur - 0.1).toFixed(2)))); }} 
                          style={{ border: 'none', background: 'transparent', padding: '0 8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', color: '#444746' }}
                          title="Decrease line height"
                        >
                          −
                        </button>
                        <input 
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="3.0"
                          value={activeModule.lineSpacing !== undefined ? activeModule.lineSpacing : ''}
                          onChange={e => {
                            updateModuleProperty(activeModule.id, 'lineSpacing', e.target.value);
                          }}
                          onBlur={() => {
                            let val = parseFloat(activeModule.lineSpacing);
                            if (isNaN(val)) {
                              val = 1.2;
                            }
                            updateModuleProperty(activeModule.id, 'lineSpacing', Math.max(0.5, Math.min(3.0, val)));
                          }}
                          style={{ 
                            border: 'none', 
                            borderLeft: '1px solid #c7c7c7', 
                            borderRight: '1px solid #c7c7c7', 
                            background: 'transparent', 
                            fontSize: '11px', 
                            fontWeight: 'bold', 
                            width: '38px', 
                            textAlign: 'center', 
                            outline: 'none',
                            color: '#444746',
                            height: '100%'
                          }} 
                        />
                        <button 
                          onClick={() => { const cur = parseFloat(activeModule.lineSpacing) || 1.2; updateModuleProperty(activeModule.id, 'lineSpacing', Math.min(3.0, parseFloat((cur + 0.1).toFixed(2)))); }} 
                          style={{ border: 'none', background: 'transparent', padding: '0 8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', color: '#444746' }}
                          title="Increase line height"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Letter Spacing Control */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Letter Spacing:</span>
                      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #c7c7c7', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#ffffff', height: '28px' }}>
                        <button 
                          onClick={() => { const cur = parseFloat(activeModule.letterSpacing) || 0; updateModuleProperty(activeModule.id, 'letterSpacing', Math.max(-5, parseFloat((cur - 0.5).toFixed(2)))); }} 
                          style={{ border: 'none', background: 'transparent', padding: '0 8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', color: '#444746' }}
                          title="Decrease letter spacing"
                        >
                          −
                        </button>
                        <input 
                          type="number"
                          step="0.5"
                          min="-5"
                          max="20"
                          value={activeModule.letterSpacing !== undefined ? activeModule.letterSpacing : ''}
                          onChange={e => {
                            updateModuleProperty(activeModule.id, 'letterSpacing', e.target.value);
                          }}
                          onBlur={() => {
                            let val = parseFloat(activeModule.letterSpacing);
                            if (isNaN(val)) {
                              val = 0;
                            }
                            updateModuleProperty(activeModule.id, 'letterSpacing', Math.max(-5, Math.min(20, val)));
                          }}
                          style={{ 
                            border: 'none', 
                            borderLeft: '1px solid #c7c7c7', 
                            borderRight: '1px solid #c7c7c7', 
                            background: 'transparent', 
                            fontSize: '11px', 
                            fontWeight: 'bold', 
                            width: '38px', 
                            textAlign: 'center', 
                            outline: 'none',
                            color: '#444746',
                            height: '100%'
                          }} 
                        />
                        <button 
                          onClick={() => { const cur = parseFloat(activeModule.letterSpacing) || 0; updateModuleProperty(activeModule.id, 'letterSpacing', Math.min(20, parseFloat((cur + 0.5).toFixed(2)))); }} 
                          style={{ border: 'none', background: 'transparent', padding: '0 8px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', color: '#444746' }}
                          title="Increase letter spacing"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />
                  </>
                )}

                {/* Button Settings (Text & Fill Color) */}
                {activeModule.type === 'button' && (
                  <>
                    {/* Button Text */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Button Text:</span>
                      <input 
                        type="text" 
                        value={activeModule.text || ''} 
                        onChange={e => updateModuleProperty(activeModule.id, 'text', e.target.value)} 
                        style={{ border: '1px solid #c7c7c7', borderRadius: '6px', background: '#ffffff', fontSize: '11px', fontWeight: 'bold', outline: 'none', height: '28px', padding: '0 8px', width: '90px', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      />
                    </div>

                    {/* Button Fill Color */}
                    <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                      <button 
                        onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'bgColor' ? null : 'bgColor')}
                        style={{ 
                          border: '1px solid #c7c7c7', 
                          background: '#ffffff', 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          height: '28px', 
                          padding: '0 8px',
                          borderRadius: '6px'
                        }} 
                        title="Button Color"
                      >
                        <div style={{ width: '16px', height: '16px', backgroundColor: activeModule.bgColor || '#ffffff', border: '1px solid rgba(0, 0, 0, 0.25)', borderRadius: '4px', boxSizing: 'border-box' }} />
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Button Fill</span>
                      </button>
                      {renderColorPopover('bgColor', activeModule.bgColor || '#ffffff', (val) => updateModuleProperty(activeModule.id, 'bgColor', val), 'Button BG')}
                    </div>
                  </>
                )}

                {/* Block BG Toggle, Color & Gradient */}
                {['header', 'text', 'button', 'artwork', 'divider', 'html', 'columns'].includes(activeModule.type) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'visible', flexWrap: 'wrap' }}>
                    {/* Mode cycle: transparent → solid → gradient → transparent */}
                    <button
                      onClick={() => {
                        const cur = activeModule.blockBgType || 'transparent';
                        const next = cur === 'transparent' ? 'solid' : cur === 'solid' ? 'gradient' : 'transparent';
                        updateModuleProperty(activeModule.id, 'blockBgType', next);
                        if (next === 'gradient' && !activeModule.blockBgGradientColor1) {
                          updateModuleProperty(activeModule.id, 'blockBgGradientColor1', '#667eea');
                          updateModuleProperty(activeModule.id, 'blockBgGradientColor2', '#764ba2');
                          updateModuleProperty(activeModule.id, 'blockBgGradientAngle', 135);
                        }
                      }}
                      style={{
                        border: '1px solid #c7c7c7',
                        background: activeModule.blockBgType === 'solid'
                          ? '#d3e3fd'
                          : activeModule.blockBgType === 'gradient'
                          ? 'linear-gradient(135deg, #667eea, #764ba2)'
                          : '#ffffff',
                        cursor: 'pointer',
                        height: '28px',
                        padding: '0 10px',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: activeModule.blockBgType === 'solid' ? '#0b57d0' : activeModule.blockBgType === 'gradient' ? '#ffffff' : '#444746',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                      title={`Block Background: ${activeModule.blockBgType || 'transparent'} – click to cycle`}
                    >
                      {activeModule.blockBgType === 'gradient' ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><defs><linearGradient id="gb" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#667eea"/><stop offset="100%" stopColor="#764ba2"/></linearGradient></defs><rect x="2" y="2" width="20" height="20" rx="4" fill="url(#gb)"/></svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22C17.5 22 22 17.5 22 12S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10z"></path></svg>
                      )}
                      <span>Block BG{activeModule.blockBgType === 'gradient' ? ' (Gradient)' : activeModule.blockBgType === 'solid' ? ' (Solid)' : ''}</span>
                    </button>

                    {/* Solid color picker */}
                    {activeModule.blockBgType === 'solid' && (
                      <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                        <button
                          onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'blockBgColor' ? null : 'blockBgColor')}
                          style={{ border: '1px solid #c7c7c7', background: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', height: '28px', padding: '0 8px', borderRadius: '6px' }}
                          title="Block BG Color"
                        >
                          <div style={{ width: '16px', height: '16px', backgroundColor: activeModule.blockBgColor || '#111215', border: '1px solid rgba(0,0,0,0.25)', borderRadius: '4px', boxSizing: 'border-box' }} />
                          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Color</span>
                        </button>
                        {renderColorPopover('blockBgColor', activeModule.blockBgColor || '#111215', (val) => updateModuleProperty(activeModule.id, 'blockBgColor', val), 'Block BG')}
                      </div>
                    )}

                    {/* Gradient controls */}
                    {activeModule.blockBgType === 'gradient' && (
                      <>
                        {/* Color 1 */}
                        <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                          <button
                            onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'blockBgGradientColor1' ? null : 'blockBgGradientColor1')}
                            style={{ border: '1px solid #c7c7c7', background: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 8px', borderRadius: '6px' }}
                            title="Gradient Start Color"
                          >
                            <div style={{ width: '14px', height: '14px', background: `linear-gradient(to right, ${activeModule.blockBgGradientColor1 || '#667eea'}, transparent)`, border: '1px solid rgba(0,0,0,0.2)', borderRadius: '3px' }} />
                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>C1</span>
                          </button>
                          {renderColorPopover('blockBgGradientColor1', activeModule.blockBgGradientColor1 || '#667eea', (val) => updateModuleProperty(activeModule.id, 'blockBgGradientColor1', val), 'Grad Start')}
                        </div>
                        {/* Color 2 */}
                        <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                          <button
                            onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'blockBgGradientColor2' ? null : 'blockBgGradientColor2')}
                            style={{ border: '1px solid #c7c7c7', background: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 8px', borderRadius: '6px' }}
                            title="Gradient End Color"
                          >
                            <div style={{ width: '14px', height: '14px', background: `linear-gradient(to right, transparent, ${activeModule.blockBgGradientColor2 || '#764ba2'})`, border: '1px solid rgba(0,0,0,0.2)', borderRadius: '3px' }} />
                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>C2</span>
                          </button>
                          {renderColorPopover('blockBgGradientColor2', activeModule.blockBgGradientColor2 || '#764ba2', (val) => updateModuleProperty(activeModule.id, 'blockBgGradientColor2', val), 'Grad End')}
                        </div>
                        {/* Angle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f8f9fa', border: '1px solid #c7c7c7', borderRadius: '6px', padding: '0 8px', height: '28px' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#444746" strokeWidth="2.5"><circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="12"/><line x1="12" y1="12" x2="19" y2="12"/></svg>
                          <input
                            type="number"
                            min="0" max="360"
                            value={activeModule.blockBgGradientAngle !== undefined ? activeModule.blockBgGradientAngle : 135}
                            onChange={e => updateModuleProperty(activeModule.id, 'blockBgGradientAngle', parseInt(e.target.value) || 0)}
                            style={{ width: '38px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center', outline: 'none' }}
                            title="Gradient Angle"
                          />
                          <span style={{ fontSize: '10px', color: '#888', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>°</span>
                        </div>
                        {/* Save gradient */}
                        <button
                          onClick={() => {
                            const name = window.prompt('Name this gradient:', 'My Gradient');
                            if (!name) return;
                            fetch(`${BACKEND_URL}/api/saved-gradients`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                name,
                                angle: activeModule.blockBgGradientAngle !== undefined ? activeModule.blockBgGradientAngle : 135,
                                color1: activeModule.blockBgGradientColor1 || '#667eea',
                                color2: activeModule.blockBgGradientColor2 || '#764ba2',
                                type: 'linear'
                              })
                            }).then(r => r.json()).then(() => fetchSavedGradients());
                          }}
                          style={{ border: '1px solid #c7c7c7', background: '#ffffff', cursor: 'pointer', height: '28px', padding: '0 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                          title="Save this gradient"
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                          Save
                        </button>
                        {/* Saved gradients picker */}
                        {savedGradients && savedGradients.length > 0 && (
                          <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                            <button
                              onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'savedGradientsPicker' ? null : 'savedGradientsPicker')}
                              style={{ border: '1px solid #c7c7c7', background: '#ffffff', cursor: 'pointer', height: '28px', padding: '0 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                              title="Load a saved gradient"
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                              Saved
                            </button>
                            {activeColorPickerDropdown === 'savedGradientsPicker' && (
                              <div style={{ position: 'absolute', top: '32px', left: '0', zIndex: 9999, background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '10px', minWidth: '220px', maxHeight: '260px', overflowY: 'auto' }}>
                                <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Saved Gradients</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {savedGradients.map(g => (
                                    <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                                      onClick={() => {
                                        updateModuleProperty(activeModule.id, 'blockBgGradientColor1', g.color1);
                                        updateModuleProperty(activeModule.id, 'blockBgGradientColor2', g.color2);
                                        updateModuleProperty(activeModule.id, 'blockBgGradientAngle', g.angle !== undefined ? g.angle : 135);
                                        setActiveColorPickerDropdown(null);
                                      }}
                                    >
                                      <div style={{ width: '40px', height: '22px', borderRadius: '5px', background: `linear-gradient(${g.angle || 135}deg, ${g.color1}, ${g.color2})`, border: '1px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#333', fontFamily: "'Plus Jakarta Sans', sans-serif", flex: 1 }}>{g.name}</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); fetch(`${BACKEND_URL}/api/saved-gradients/${g.id}`, { method: 'DELETE' }).then(() => fetchSavedGradients()); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#cc0000', display: 'flex', alignItems: 'center' }}
                                        title="Delete gradient"
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Corner Radius */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f8f9fa', border: '1px solid #c7c7c7', borderRadius: '6px', padding: '0 8px', height: '28px' }}>
                      <span style={{ fontSize: '10px', color: '#444746', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Radius:</span>
                      <input
                        type="number"
                        min="0" max="100"
                        value={activeModule.blockStrokeRadius !== undefined ? activeModule.blockStrokeRadius : 0}
                        onChange={e => updateModuleProperty(activeModule.id, 'blockStrokeRadius', parseInt(e.target.value) || 0)}
                        style={{ width: '28px', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: 'center', outline: 'none' }}
                        title="Block Background Corner Radius"
                      />
                      <span style={{ fontSize: '10px', color: '#888', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>px</span>
                    </div>

                    {globalSettings.overrideBlockBg && (
                      <div style={{ color: '#ef4444', fontSize: '10px', fontWeight: '700', marginTop: '4px', width: '100%', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        ⚠️ Global background override is currently active. Custom block backgrounds are hidden.
                      </div>
                    )}

                    {/* Divider before stroke */}
                    <div style={{ width: '1px', height: '18px', backgroundColor: '#e0e0e0', margin: '0 2px' }} />

                    {/* Block Stroke */}
                    <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                      <button
                        onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'blockStrokePanel' ? null : 'blockStrokePanel')}
                        style={{
                          border: `1px solid ${activeModule.blockStrokeWidth > 0 ? '#0b57d0' : '#c7c7c7'}`,
                          background: activeModule.blockStrokeWidth > 0 ? '#d3e3fd' : '#ffffff',
                          cursor: 'pointer',
                          height: '28px',
                          padding: '0 8px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: activeModule.blockStrokeWidth > 0 ? '#0b57d0' : '#444746',
                          fontFamily: "'Plus Jakarta Sans', sans-serif"
                        }}
                        title="Block border / stroke settings"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/></svg>
                        Block Stroke
                      </button>
                      {['blockStrokePanel', 'blockStrokeColor'].includes(activeColorPickerDropdown) && (
                        <div ref={blockStrokePanelRef} style={{ position: 'absolute', top: '32px', left: '0', zIndex: 9999, background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '14px', minWidth: '230px' }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Block Border</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {/* Width */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '55px' }}>Width</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                <input type="range" min="0" max="20" step="1"
                                  value={activeModule.blockStrokeWidth || 0}
                                  onChange={e => updateModuleProperty(activeModule.id, 'blockStrokeWidth', parseInt(e.target.value))}
                                  style={{ flex: 1, accentColor: '#0b57d0' }}
                                />
                                <span style={{ fontSize: '11px', color: '#333', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '24px' }}>{activeModule.blockStrokeWidth || 0}px</span>
                              </div>
                            </div>
                            {/* Color */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '55px' }}>Color</span>
                              <div style={{ position: 'relative', flex: 1 }}>
                                <button
                                  onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'blockStrokeColor' ? 'blockStrokePanel' : 'blockStrokeColor')}
                                  style={{ border: '1px solid #c7c7c7', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', height: '26px', padding: '0 8px', borderRadius: '5px', width: '100%' }}
                                >
                                  <div style={{ width: '14px', height: '14px', backgroundColor: activeModule.blockStrokeColor || '#000000', border: '1px solid rgba(0,0,0,0.2)', borderRadius: '3px' }} />
                                  <span style={{ fontSize: '11px', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{activeModule.blockStrokeColor || '#000000'}</span>
                                </button>
                                {activeColorPickerDropdown === 'blockStrokeColor' && renderColorPopover('blockStrokeColor', activeModule.blockStrokeColor || '#000000', (val) => { updateModuleProperty(activeModule.id, 'blockStrokeColor', val); }, 'Stroke')}
                              </div>
                            </div>
                            {/* Style */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '55px' }}>Style</span>
                              <select
                                value={activeModule.blockStrokeStyle || 'solid'}
                                onChange={e => updateModuleProperty(activeModule.id, 'blockStrokeStyle', e.target.value)}
                                style={{ flex: 1, border: '1px solid #c7c7c7', borderRadius: '5px', fontSize: '11px', color: '#333', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '3px 6px', background: '#fff', height: '26px' }}
                              >
                                <option value="solid">Solid</option>
                                <option value="dashed">Dashed</option>
                                <option value="dotted">Dotted</option>
                                <option value="double">Double</option>
                              </select>
                            </div>
                            {/* Radius */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '55px' }}>Radius</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                <input type="range" min="0" max="40" step="1"
                                  value={activeModule.blockStrokeRadius || 0}
                                  onChange={e => updateModuleProperty(activeModule.id, 'blockStrokeRadius', parseInt(e.target.value))}
                                  style={{ flex: 1, accentColor: '#0b57d0' }}
                                />
                                <span style={{ fontSize: '11px', color: '#333', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '24px' }}>{activeModule.blockStrokeRadius || 0}px</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Divider before spacing */}
                    <div style={{ width: '1px', height: '18px', backgroundColor: '#e0e0e0', margin: '0 2px' }} />

                    {/* Block Spacing */}
                    <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                      <button
                        onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'blockPaddingPanel' ? null : 'blockPaddingPanel')}
                        style={{
                          border: `1px solid ${(activeModule.paddingTop !== undefined || activeModule.paddingBottom !== undefined || activeModule.paddingLeft !== undefined) ? '#0b57d0' : '#c7c7c7'}`,
                          background: (activeModule.paddingTop !== undefined || activeModule.paddingBottom !== undefined || activeModule.paddingLeft !== undefined) ? '#d3e3fd' : '#ffffff',
                          cursor: 'pointer',
                          height: '28px',
                          padding: '0 8px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: (activeModule.paddingTop !== undefined || activeModule.paddingBottom !== undefined || activeModule.paddingLeft !== undefined) ? '#0b57d0' : '#444746',
                          fontFamily: "'Plus Jakarta Sans', sans-serif"
                        }}
                        title="Block inner padding / spacing"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="15" x2="15" y2="15"/><line x1="9" y1="12" x2="15" y2="12"/></svg>
                        Block Spacing
                      </button>
                      {activeColorPickerDropdown === 'blockPaddingPanel' && (
                        <div ref={blockPaddingPanelRef} style={{ position: 'absolute', top: '32px', left: '0', zIndex: 9999, background: '#ffffff', border: '1px solid #e0e0e0', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '14px', minWidth: '230px' }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#888', fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Block Padding</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {/* Top Padding */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '65px' }}>Top</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                <input type="range" min="0" max="100" step="1"
                                  value={activeModule.paddingTop !== undefined ? activeModule.paddingTop : (activeModule.type === 'header' ? 25 : activeModule.type === 'text' || activeModule.type === 'button' ? 20 : 10)}
                                  onChange={e => updateModuleProperty(activeModule.id, 'paddingTop', parseInt(e.target.value))}
                                  style={{ flex: 1, accentColor: '#0b57d0' }}
                                />
                                <span style={{ fontSize: '11px', color: '#333', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '24px' }}>{activeModule.paddingTop !== undefined ? activeModule.paddingTop : (activeModule.type === 'header' ? 25 : activeModule.type === 'text' || activeModule.type === 'button' ? 20 : 10)}px</span>
                              </div>
                            </div>
                            {/* Bottom Padding */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '65px' }}>Bottom</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                <input type="range" min="0" max="100" step="1"
                                  value={activeModule.paddingBottom !== undefined ? activeModule.paddingBottom : (activeModule.type === 'header' ? 25 : activeModule.type === 'text' || activeModule.type === 'button' ? 20 : 10)}
                                  onChange={e => updateModuleProperty(activeModule.id, 'paddingBottom', parseInt(e.target.value))}
                                  style={{ flex: 1, accentColor: '#0b57d0' }}
                                />
                                <span style={{ fontSize: '11px', color: '#333', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '24px' }}>{activeModule.paddingBottom !== undefined ? activeModule.paddingBottom : (activeModule.type === 'header' ? 25 : activeModule.type === 'text' || activeModule.type === 'button' ? 20 : 10)}px</span>
                              </div>
                            </div>
                            {/* Horizontal Padding */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#555', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '65px' }}>Horizontal</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                                <input type="range" min="0" max="100" step="1"
                                  value={activeModule.paddingLeft !== undefined ? activeModule.paddingLeft : (activeModule.type === 'header' ? 25 : activeModule.type === 'text' || activeModule.type === 'button' ? 20 : 10)}
                                  onChange={e => {
                                    const val = parseInt(e.target.value);
                                    updateModuleProperty(activeModule.id, 'paddingLeft', val);
                                    updateModuleProperty(activeModule.id, 'paddingRight', val);
                                  }}
                                  style={{ flex: 1, accentColor: '#0b57d0' }}
                                />
                                <span style={{ fontSize: '11px', color: '#333', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif", minWidth: '24px' }}>{activeModule.paddingLeft !== undefined ? activeModule.paddingLeft : (activeModule.type === 'header' ? 25 : activeModule.type === 'text' || activeModule.type === 'button' ? 20 : 10)}px</span>
                              </div>
                            </div>
                            {/* Reset spacing */}
                            <button
                              onClick={() => {
                                updateModuleProperty(activeModule.id, 'paddingTop', undefined);
                                updateModuleProperty(activeModule.id, 'paddingBottom', undefined);
                                updateModuleProperty(activeModule.id, 'paddingLeft', undefined);
                                updateModuleProperty(activeModule.id, 'paddingRight', undefined);
                              }}
                              style={{
                                marginTop: '5px',
                                border: '1px solid #c7c7c7',
                                background: '#ffffff',
                                color: '#444746',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                height: '24px',
                                fontFamily: "'Plus Jakarta Sans', sans-serif"
                              }}
                            >
                              Reset to Default
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Artwork Settings */}
                {activeModule.type === 'artwork' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', overflow: 'visible' }}>
                    <button onClick={triggerLibraryFetchOpen} className="pulse-gold" style={{ backgroundColor: BRAND_AMBER_GOLD, border: 'none', color: '#ffffff', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', height: '28px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>📁 Library</button>
                    
                    <label 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        padding: '4px 12px', 
                        borderRadius: '6px', 
                        border: '1px solid rgba(15, 9, 28, 0.1)', 
                        background: '#ffffff', 
                        color: '#1f2937', 
                        fontSize: '11px', 
                        fontWeight: 'bold', 
                        cursor: 'pointer', 
                        height: '28px',
                        transition: 'all 0.2s',
                        userSelect: 'none',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                      title="Upload graphic file from your computer"
                      onMouseEnter={e => e.currentTarget.style.borderColor = BRAND_AMBER_GOLD}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(15, 9, 28, 0.1)'}
                    >
                      <span>➕ Upload Image</span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleArtworkUpload(e, activeModule.id)} 
                        style={{ display: 'none' }} 
                      />
                    </label>

                    <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                      <button
                        type="button"
                        onClick={() => setActiveLinkPopup(activeLinkPopup === activeModule.id ? null : activeModule.id)}
                        style={{
                          background: activeModule.linkUrl ? '#d3e3fd' : '#ffffff',
                          border: '1px solid #c7c7c7',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '0 10px',
                          height: '28px',
                          cursor: 'pointer',
                          color: activeModule.linkUrl ? '#0b57d0' : '#444746',
                          transition: 'all 0.2s',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: "'Plus Jakarta Sans', sans-serif"
                        }}
                        title="Configure Image Link Redirect"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                        <span>Image Redirect Link</span>
                      </button>
                      {renderLinkPopover(activeModule)}
                    </div>

                    <div style={{ width: '1px', height: '18px', backgroundColor: '#c7c7c7' }} />

                    {/* Image Borders / Stroke */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'visible' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Border Stroke:</span>
                      <input 
                        type="number" 
                        min="0"
                        max="20"
                        placeholder="0" 
                        value={activeModule.strokeWidth !== undefined ? activeModule.strokeWidth : ''} 
                        onChange={e => updateModuleProperty(activeModule.id, 'strokeWidth', Math.max(0, parseInt(e.target.value) || 0))} 
                        style={{ width: '42px', height: '28px', fontSize: '11px', textAlign: 'center', border: '1px solid #c7c7c7', borderRadius: '6px', padding: 0, background: '#ffffff', fontWeight: 'bold' }} 
                      />
                      <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: 'bold' }}>px</span>
                      
                      {/* Stroke Color */}
                      <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                        <button 
                          onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'imageStrokeColor' ? null : 'imageStrokeColor')}
                          style={{ 
                            border: '1px solid #c7c7c7', 
                            background: '#ffffff', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            height: '28px', 
                            padding: '0 8px',
                            borderRadius: '6px'
                          }} 
                          title="Stroke Color"
                        >
                          <div style={{ width: '16px', height: '16px', backgroundColor: activeModule.strokeColor || '#000000', border: '1px solid rgba(0, 0, 0, 0.25)', borderRadius: '4px', boxSizing: 'border-box' }} />
                          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Color</span>
                        </button>
                        {renderColorPopover('imageStrokeColor', activeModule.strokeColor || '#000000', (val) => updateModuleProperty(activeModule.id, 'strokeColor', val), 'Stroke Color')}
                      </div>

                      {/* Stroke Style */}
                      <select 
                        value={activeModule.strokeStyle || 'solid'} 
                        onChange={e => updateModuleProperty(activeModule.id, 'strokeStyle', e.target.value)} 
                        style={{ height: '28px', padding: '2px 8px', fontSize: '11px', width: '75px', cursor: 'pointer', border: '1px solid #c7c7c7', borderRadius: '6px', outline: 'none', background: '#ffffff', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                        <option value="double">Double</option>
                        <option value="ridge">Ridge</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Divider Settings */}
                {activeModule.type === 'divider' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'visible' }}>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Weight/Thickness:</span>
                    <input type="text" placeholder="e.g. 2px" value={activeModule.thickness || ''} onChange={e => updateModuleProperty(activeModule.id, 'thickness', e.target.value)} style={{ width: '60px', height: '28px', fontSize: '11px', border: '1px solid #c7c7c7', borderRadius: '6px', padding: '0 8px', outline: 'none', background: '#ffffff', fontWeight: 'bold' }} />
                    
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Divider Color:</span>
                    <div style={{ position: 'relative', display: 'inline-block', overflow: 'visible' }}>
                      <button 
                        onClick={() => setActiveColorPickerDropdown(activeColorPickerDropdown === 'dividerColor' ? null : 'dividerColor')}
                        style={{ 
                          border: '1px solid #c7c7c7', 
                          background: '#ffffff', 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          height: '28px', 
                          padding: '0 8px',
                          borderRadius: '6px'
                        }} 
                        title="Divider Color"
                      >
                        <div style={{ width: '16px', height: '16px', backgroundColor: activeModule.color || '#333333', border: '1px solid rgba(0, 0, 0, 0.25)', borderRadius: '4px', boxSizing: 'border-box' }} />
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#444746', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Color</span>
                      </button>
                      {renderColorPopover('dividerColor', activeModule.color || '#333333', (val) => updateModuleProperty(activeModule.id, 'color', val), 'Divider Color')}
                    </div>
                  </div>
                )}

                {/* Spacer Settings */}
                {activeModule.type === 'spacer' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5b6275', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Spacer Height:</span>
                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #c7c7c7', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#ffffff', height: '28px', width: '80px' }}>
                      <input type="number" value={activeModule.height || 20} onChange={e => updateModuleProperty(activeModule.id, 'height', Math.max(5, parseInt(e.target.value) || 20))} style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'center', fontSize: '11px', fontWeight: 'bold', outline: 'none', color: '#444746' }} />
                      <span style={{ fontSize: '10px', color: '#6b7280', paddingRight: '8px', fontWeight: 'bold' }}>px</span>
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}







          {/* FIGMA-STYLE PREMIUM TRANSPARENT CHECKERBOARD ENVELOPE BACKDROP */}
          <div 
            style={{
              width: '100%',
              maxWidth: previewMode === 'desktop' ? `${(globalSettings.maxWidth || 500) + 80}px` : previewMode === 'tablet' ? '530px' : '440px',
              position: 'relative',
              background: globalSettings.bgType === 'image' && globalSettings.bgImageUrl
                ? (globalSettings.fallbackBgType === 'gradient'
                  ? `linear-gradient(${globalSettings.fallbackBgGradientAngle || 180}deg, ${globalSettings.fallbackBgGradientColor1 || '#111215'} 0%, ${globalSettings.fallbackBgGradientColor2 || '#000000'} 100%)`
                  : (globalSettings.fallbackBgColor || '#010717'))
                : '#fbfbfb',
              backgroundImage: globalSettings.bgType === 'image' && globalSettings.bgImageUrl
                ? 'none'
                : 'linear-gradient(45deg, #f0f0f2 25%, transparent 25%), linear-gradient(-45deg, #f0f0f2 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f2 75%), linear-gradient(-45deg, transparent 75%, #f0f0f2 75%)',
              backgroundSize: globalSettings.bgType === 'image' && globalSettings.bgImageUrl ? 'auto' : '16px 16px',
              backgroundPosition: globalSettings.bgType === 'image' && globalSettings.bgImageUrl ? 'auto' : '0 0, 0 8px, 8px -8px, -8px 0px',
              backgroundRepeat: globalSettings.bgType === 'image' && globalSettings.bgImageUrl ? 'no-repeat' : 'repeat',
              padding: previewMode === 'desktop' ? '40px' : '20px',
              borderRadius: '24px',
              border: '1px solid rgba(168, 85, 247, 0.12)',
              boxShadow: 'inset 0 2px 10px rgba(15, 9, 28, 0.02)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden'
            }}
          >
            {/* Static wallpaper background image layer — does not scale with content */}
            {globalSettings.bgType === 'image' && globalSettings.bgImageUrl && (
              <img 
                src={(globalSettings.bgImageUrl || '').startsWith('http') ? globalSettings.bgImageUrl : BACKEND_URL + globalSettings.bgImageUrl}
                alt=""
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: 'auto',
                  minHeight: '100%',
                  objectFit: 'cover',
                  objectPosition: 'top center',
                  pointerEvents: 'none',
                  zIndex: 0,
                  borderRadius: '24px'
                }}
              />
            )}
            <div 
              id="mailer-canvas" 
              onDragOver={handleCanvasDragOver}
              onDragLeave={handleCanvasDragLeave}
              onDrop={handleCanvasDrop}
              style={{ 
                width: '100%', 
                maxWidth: previewMode === 'desktop' ? `${globalSettings.maxWidth || 500}px` : previewMode === 'tablet' ? '450px' : '360px', 
                border: isCanvasDraggedOver ? `2px solid ${BRAND_AMBER_GOLD}` : '1px dashed #cccccc', 
                boxShadow: isCanvasDraggedOver ? BRAND_GOLD_GLOW : '0 10px 40px rgba(15, 9, 28, 0.08)',
                borderRadius: '0px', 
                minHeight: '600px', 
                overflow: 'visible', 
                position: 'relative',
                zIndex: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                ...masterCanvasStyles 
              }}
            >
              {canvasModules.map((module, index) => {
                const isSelected = selectedRowIds.includes(module.id);

                return (
                  <div 
                    key={module.id} 
                    draggable={!isMultiSelectMode && !isEditingText} 
                    onDragStart={isMultiSelectMode ? undefined : () => handleDragStart(index)} 
                    onDragOver={isMultiSelectMode ? undefined : e => handleDragOver(e, index)} 
                    onDragEnd={isMultiSelectMode ? undefined : () => setDraggedIndex(null)}
                    onClickCapture={isMultiSelectMode ? (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedRowIds(prev => {
                        if (prev.includes(module.id)) {
                          return prev.filter(id => id !== module.id);
                        } else {
                          return [...prev, module.id];
                        }
                      });
                    } : undefined}
                    style={{ 
                      position: 'relative',
                      cursor: isMultiSelectMode ? 'pointer' : 'default',
                      border: isMultiSelectMode 
                        ? (isSelected 
                            ? `2px solid ${BRAND_AMBER_GOLD}` 
                            : '2px dashed rgba(217, 119, 6, 0.25)')
                        : 'none',
                      boxShadow: isMultiSelectMode && isSelected
                        ? '0 0 20px rgba(245, 158, 11, 0.45)'
                        : 'none',
                      borderRadius: '16px',
                      margin: isMultiSelectMode ? '10px 0' : '0',
                      padding: isMultiSelectMode ? '6px' : '0',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {isMultiSelectMode && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '12px',
                          left: '12px',
                          zIndex: 999,
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          border: `2px solid ${BRAND_AMBER_GOLD}`,
                          backgroundColor: isSelected ? BRAND_AMBER_GOLD : 'rgba(255, 255, 255, 0.85)',
                          color: isSelected ? '#ffffff' : 'transparent',
                          boxShadow: '0 4px 12px rgba(15, 9, 28, 0.1)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        ✓
                      </div>
                    )}
                    {renderModuleContent(module, false)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>

      {/* HTML IMPORT OVERLAY MODAL */}
      {isImportModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 9, 28, 0.3)', zIndex: '9999', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(16px)' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '680px', background: 'rgba(255, 255, 255, 0.85) !important', border: '1px solid rgba(168, 85, 247, 0.18) !important', borderRadius: '24px', padding: '25px', display: 'flex', flexDirection: 'column', maxHeight: '85vh', boxShadow: '0 24px 60px -10px rgba(15, 9, 28, 0.15) !important', pointerEvents: isImporting ? 'none' : 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#0f091c', fontWeight: '800', letterSpacing: '0.5px' }}>Import & Localize Mailer</h3>
              {!isImporting && (
                <button onClick={() => { setIsImportModalOpen(false); setHtmlImportValue(''); setHtmlImportUrl(''); }} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: '20px', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.target.style.color = '#d97706'; }} onMouseLeave={(e) => { e.target.style.color = '#6b7280'; }}>✕</button>
              )}
            </div>

            {/* TAB SELECTOR */}
            {!isImporting && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', background: 'rgba(15, 9, 28, 0.04)', padding: '4px', borderRadius: '12px' }}>
                <button
                  onClick={() => setImportTab('html')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: importTab === 'html' ? '#ffffff' : 'transparent',
                    color: importTab === 'html' ? '#0f091c' : '#6b7280',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    cursor: 'pointer',
                    boxShadow: importTab === 'html' ? '0 2px 8px rgba(15, 9, 28, 0.05)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  📄 Paste HTML Code
                </button>
                <button
                  onClick={() => setImportTab('url')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: importTab === 'url' ? '#ffffff' : 'transparent',
                    color: importTab === 'url' ? '#0f091c' : '#6b7280',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    cursor: 'pointer',
                    boxShadow: importTab === 'url' ? '0 2px 8px rgba(15, 9, 28, 0.05)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  🌐 Import from Web URL
                </button>
              </div>
            )}

            {isImporting ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', minHeight: '200px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: `3px solid rgba(217, 119, 6, 0.1)`,
                  borderTop: `3px solid ${BRAND_AMBER_GOLD}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginBottom: '20px'
                }} />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
                <p style={{ margin: 0, fontSize: '14px', color: '#0f091c', fontWeight: 'bold' }}>{importStatusText || 'Converting template...'}</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>Please keep this window open while we download and save external assets.</p>
              </div>
            ) : (
              <>
                {importTab === 'html' ? (
                  <>
                    <p style={{ fontSize: '12px', color: '#4b5563', lineHeight: '1.5', marginBottom: '15px', marginTop: 0 }}>
                      Paste raw HTML code of a previous mailer below. The converter will automatically analyze tables, rows, images, links, text blocks, and buttons to map them back into editable canvas components, downloading any external images to save them locally on the server.
                    </p>
                    <textarea 
                      value={htmlImportValue}
                      onChange={e => setHtmlImportValue(e.target.value)}
                      placeholder="Paste <html> or <body> mailer markup here..."
                      style={{
                        width: '100%',
                        height: '250px',
                        padding: '12px',
                        borderRadius: '12px',
                        border: '1px solid rgba(15, 9, 28, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#1f2937',
                        outline: 'none',
                        resize: 'none',
                        marginBottom: '20px'
                      }}
                    />
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: '12px', color: '#4b5563', lineHeight: '1.5', marginBottom: '15px', marginTop: 0 }}>
                      Enter the URL of a previously compiled mailer or webpage below. The system will retrieve the webpage markup, parse its layout modules, and capture all referenced images to download and store them locally.
                    </p>
                    <input 
                      type="url"
                      value={htmlImportUrl}
                      onChange={e => setHtmlImportUrl(e.target.value)}
                      placeholder="https://example.com/newsletter-archive/june-2026.html"
                      style={{
                        width: '100%',
                        padding: '14px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(15, 9, 28, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '13px',
                        color: '#1f2937',
                        outline: 'none',
                        marginBottom: '30px'
                      }}
                    />
                  </>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => { setIsImportModalOpen(false); setHtmlImportValue(''); setHtmlImportUrl(''); }}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15, 9, 28, 0.08)',
                      backgroundColor: 'transparent',
                      color: '#4b5563',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleConvertImportHtml}
                    disabled={importTab === 'html' ? !htmlImportValue.trim() : !htmlImportUrl.trim()}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: BRAND_AMBER_GOLD,
                      color: '#ffffff',
                      fontWeight: 'bold',
                      fontSize: '12px',
                      cursor: (importTab === 'html' ? htmlImportValue.trim() : htmlImportUrl.trim()) ? 'pointer' : 'not-allowed',
                      boxShadow: (importTab === 'html' ? htmlImportValue.trim() : htmlImportUrl.trim()) ? BRAND_GOLD_GLOW : 'none',
                      opacity: (importTab === 'html' ? htmlImportValue.trim() : htmlImportUrl.trim()) ? 1 : 0.6,
                      transition: 'all 0.2s'
                    }}
                  >
                    ⚡ Convert and Load
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* REPOSITORY SELECTION LIBRARY INTERACTIVE OVERLAY MODAL */}
      {showLibraryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 9, 28, 0.3)', zIndex: '9999', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(16px)' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '680px', background: 'rgba(255, 255, 255, 0.85) !important', border: '1px solid rgba(168, 85, 247, 0.18) !important', borderRadius: '24px', padding: '25px', display: 'flex', flexDirection: 'column', maxHeight: '85vh', boxShadow: '0 24px 60px -10px rgba(15, 9, 28, 0.15) !important' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#0f091c', fontWeight: '800', letterSpacing: '0.5px' }}>Central Upload Media Library</h3>
              <button onClick={() => setShowLibraryModal(false)} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: '20px', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.target.style.color = '#d97706'; }} onMouseLeave={(e) => { e.target.style.color = '#6b7280'; }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: '10px', backgroundColor: '#f1f0f5', padding: '6px', borderRadius: '16px', marginBottom: '20px', border: '1px solid rgba(168, 85, 247, 0.1)' }}>
              <button onClick={() => setLibraryTab('images')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', background: libraryTab === 'images' ? '#ffffff' : 'transparent', color: libraryTab === 'images' ? '#d97706' : '#5b6275', boxShadow: libraryTab === 'images' ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none', transition: 'all 0.2s' }}>🖼 IMAGES{libraryTotals.images > 0 ? ` (${libraryTotals.images})` : ''}</button>
              <button onClick={() => setLibraryTab('gifs')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', background: libraryTab === 'gifs' ? '#ffffff' : 'transparent', color: libraryTab === 'gifs' ? '#d97706' : '#5b6275', boxShadow: libraryTab === 'gifs' ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none', transition: 'all 0.2s' }}>⚡ GIFs{libraryTotals.gifs > 0 ? ` (${libraryTotals.gifs})` : ''}</button>
              <button onClick={() => setLibraryTab('createdAssets')} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', background: libraryTab === 'createdAssets' ? '#ffffff' : 'transparent', color: libraryTab === 'createdAssets' ? '#d97706' : '#5b6275', boxShadow: libraryTab === 'createdAssets' ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none', transition: 'all 0.2s' }}>🎨 CREATED{libraryTotals.createdAssets > 0 ? ` (${libraryTotals.createdAssets})` : ''}</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>

              {/* ── Loading skeleton ── */}
              {libraryLoading && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                  {Array.from({ length: LIBRARY_PAGE_SIZE }).map((_, i) => (
                    <div key={i} style={{ borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f1f0f5', border: '1px solid rgba(168, 85, 247, 0.08)' }}>
                      <div style={{ height: '100px', background: 'linear-gradient(90deg, #f1f0f5 25%, #e8e6ef 50%, #f1f0f5 75%)', backgroundSize: '200% 100%', animation: 'libraryShimmer 1.4s infinite' }} />
                      <div style={{ height: '28px', margin: '8px', borderRadius: '6px', background: 'linear-gradient(90deg, #f1f0f5 25%, #e8e6ef 50%, #f1f0f5 75%)', backgroundSize: '200% 100%', animation: 'libraryShimmer 1.4s infinite 0.2s' }} />
                    </div>
                  ))}
                  <style>{`@keyframes libraryShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
                </div>
              )}

              {/* ── Images tab ── */}
              {!libraryLoading && libraryTab === 'images' && (
                libraryData.images?.length === 0 ? (
                  <p style={{ color: '#4b5563', textAlign: 'center', padding: '40px' }}>No raw image assets uploaded.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                      {libraryData.images.map((img, idx) => (
                        <div key={idx} onClick={() => handleLibraryItemSelect(img.url, img.sizeInBytes || 0)} style={{ position: 'relative', border: '1px solid rgba(168, 85, 247, 0.12)', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease-in-out', boxShadow: '0 4px 12px rgba(15, 9, 28, 0.03)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.12)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <button 
                            onClick={(e) => handleOpenLibraryAssetInCropper(img.url, img.fileName, e)} 
                            title="Crop / Optimize Image"
                            style={{ 
                              position: 'absolute', top: '8px', left: '8px', zIndex: '20', 
                              backgroundColor: 'rgba(217, 119, 6, 0.15)', border: '1px solid rgba(217, 119, 6, 0.3)', 
                              color: '#d97706', width: '24px', height: '24px', borderRadius: '50%', 
                              cursor: 'pointer', display: 'flex', alignItems: 'center', 
                              justifyContent: 'center', transition: 'all 0.2s ease'
                            }} 
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.color = '#fff'; }} 
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(217, 119, 6, 0.15)'; e.currentTarget.style.color = '#d97706'; }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                              <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                              <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                            </svg>
                          </button>
                          <button 
                            onClick={(e) => executeAssetDeletion(img.url, e)} 
                            title="Delete Asset File"
                            style={{ 
                              position: 'absolute', top: '8px', right: '8px', zIndex: '20', 
                              backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', 
                              color: '#ef4444', width: '24px', height: '24px', borderRadius: '50%', 
                              cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', 
                              justifyContent: 'center', transition: 'all 0.2s ease', fontSize: '11px'
                            }} 
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.color = '#fff'; }} 
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#ef4444'; }}
                          >🗑</button>
                          <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8f7fa' }}>
                            <img 
                              src={`${BACKEND_URL}/api/uploads-library/thumb?url=${encodeURIComponent(img.url)}&w=260`} 
                              alt={img.fileName} 
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '10px', color: '#1f2937', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {img.fileName}
                            </div>
                            <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {formatFileSize(img.sizeInBytes)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {libraryData.images.length < libraryTotals.images && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <button 
                          onClick={loadMoreLibraryItems} 
                          style={{ padding: '10px 24px', backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.backgroundColor = '#b45309'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#d97706'; }}
                        >
                          Load More ({libraryData.images.length} of {libraryTotals.images})
                        </button>
                      </div>
                    )}
                  </>
                )
              )}

              {/* ── GIFs tab ── */}
              {!libraryLoading && libraryTab === 'gifs' && (
                libraryData.gifs?.length === 0 ? (
                  <p style={{ color: '#4b5563', textAlign: 'center', padding: '40px' }}>No compiled looping GIF assets found.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                      {libraryData.gifs.map((gif, idx) => (
                        <div key={idx} onClick={() => handleLibraryItemSelect(gif.url, gif.sizeInBytes || 0)} style={{ position: 'relative', border: '1px solid rgba(168, 85, 247, 0.12)', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease-in-out', boxShadow: '0 4px 12px rgba(15, 9, 28, 0.03)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.12)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <button 
                            onClick={(e) => executeAssetDeletion(gif.url, e)} 
                            title="Delete Asset File"
                            style={{ 
                              position: 'absolute', top: '8px', right: '8px', zIndex: '20', 
                              backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', 
                              color: '#ef4444', width: '24px', height: '24px', borderRadius: '50%', 
                              cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', 
                              justifyContent: 'center', transition: 'all 0.2s ease', fontSize: '11px'
                            }} 
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.color = '#fff'; }} 
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#ef4444'; }}
                          >🗑</button>
                          <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8f7fa' }}>
                            {/* GIFs served directly — thumbnailing animated GIFs kills animation */}
                            <img src={`${BACKEND_URL}${gif.url}`} alt={gif.fileName} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {gif.fileName}
                            </div>
                            <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {formatFileSize(gif.sizeInBytes)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {libraryData.gifs.length < libraryTotals.gifs && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <button 
                          onClick={loadMoreLibraryItems} 
                          style={{ padding: '10px 24px', backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.backgroundColor = '#b45309'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#d97706'; }}
                        >
                          Load More ({libraryData.gifs.length} of {libraryTotals.gifs})
                        </button>
                      </div>
                    )}
                  </>
                )
              )}

              {/* ── Created Assets tab ── */}
              {!libraryLoading && libraryTab === 'createdAssets' && (
                (libraryData.createdAssets?.length === 0 || !libraryData.createdAssets) ? (
                  <p style={{ color: '#4b5563', textAlign: 'center', padding: '40px' }}>No flattened modular graphics configured in Element Studio yet.</p>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '15px' }}>
                      {libraryData.createdAssets.map((asset, idx) => (
                        <div key={idx} onClick={() => handleLibraryItemSelect(asset.url, asset.sizeInBytes || 0)} style={{ position: 'relative', border: '1px solid rgba(168, 85, 247, 0.12)', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.2s ease-in-out', boxShadow: '0 4px 12px rgba(15, 9, 28, 0.03)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d97706'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; e.currentTarget.style.transform = 'translateY(-2px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.12)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.03)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setShowLibraryModal(false); navigate(`/element-studio?edit=${asset.fileName}`); }} 
                            title="✏️ Re-Edit Design"
                            style={{ 
                              position: 'absolute', top: '8px', left: '8px', zIndex: '20', 
                              backgroundColor: 'rgba(168, 85, 247, 0.15)', border: '1px solid rgba(168, 85, 247, 0.3)', 
                              color: '#a855f7', width: '24px', height: '24px', borderRadius: '50%', 
                              cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', 
                              justifyContent: 'center', transition: 'all 0.2s ease', fontSize: '11px'
                            }} 
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#a855f7'; e.currentTarget.style.color = '#fff'; }} 
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(168, 85, 247, 0.15)'; e.currentTarget.style.color = '#a855f7'; }}
                          >✏️</button>
                          {asset.url.toLowerCase().endsWith('.png') && (
                            <button 
                              onClick={(e) => handleOpenLibraryAssetInCropper(asset.url, asset.fileName, e)} 
                              title="Crop / Optimize Image"
                              style={{ 
                                position: 'absolute', top: '8px', left: '36px', zIndex: '20', 
                                backgroundColor: 'rgba(217, 119, 6, 0.15)', border: '1px solid rgba(217, 119, 6, 0.3)', 
                                color: '#d97706', width: '24px', height: '24px', borderRadius: '50%', 
                                cursor: 'pointer', display: 'flex', alignItems: 'center', 
                                justifyContent: 'center', transition: 'all 0.2s ease'
                              }} 
                              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#d97706'; e.currentTarget.style.color = '#fff'; }} 
                              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(217, 119, 6, 0.15)'; e.currentTarget.style.color = '#d97706'; }}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                                <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" />
                                <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" />
                              </svg>
                            </button>
                          )}
                          <button 
                            onClick={(e) => executeAssetDeletion(asset.url, e)} 
                            title="Delete Asset File"
                            style={{ 
                              position: 'absolute', top: '8px', right: '8px', zIndex: '20', 
                              backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', 
                              color: '#ef4444', width: '24px', height: '24px', borderRadius: '50%', 
                              cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', 
                              justifyContent: 'center', transition: 'all 0.2s ease', fontSize: '11px'
                            }} 
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.color = '#fff'; }} 
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.15)'; e.currentTarget.style.color = '#ef4444'; }}
                          >🗑</button>
                          <div style={{ height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f8f7fa' }}>
                            <img 
                              src={asset.url.endsWith('.gif') ? `${BACKEND_URL}${asset.url}` : `${BACKEND_URL}/api/uploads-library/thumb?url=${encodeURIComponent(asset.url)}&w=260`} 
                              alt={asset.fileName} 
                              loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                          </div>
                          <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '700', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {asset.fileName}
                            </div>
                            <div style={{ fontSize: '9px', color: '#6b7280', fontWeight: 'bold', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                              {formatFileSize(asset.sizeInBytes)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {libraryData.createdAssets.length < libraryTotals.createdAssets && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <button 
                          onClick={loadMoreLibraryItems} 
                          style={{ padding: '10px 24px', backgroundColor: '#d97706', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.2)' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.backgroundColor = '#b45309'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.backgroundColor = '#d97706'; }}
                        >
                          Load More ({libraryData.createdAssets.length} of {libraryTotals.createdAssets})
                        </button>
                      </div>
                    )}
                  </>
                )
              )}
            </div>

          </div>
        </div>
      )}

      {/* SAVED BLOCKS CATALOG MODAL */}
      {showSavedBlocksModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 9, 28, 0.3)', zIndex: '9999', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(16px)' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '960px', background: 'rgba(255, 255, 255, 0.85) !important', border: '1px solid rgba(168, 85, 247, 0.18) !important', borderRadius: '24px', padding: '25px', display: 'flex', flexDirection: 'column', height: '80vh', maxHeight: '720px', boxShadow: '0 24px 60px -10px rgba(15, 9, 28, 0.15) !important' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                <div>
                  <h3 style={{ margin: 0, color: '#0f091c', fontWeight: '800', letterSpacing: '0.5px' }}>Saved Blocks Catalog</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#5b6275', fontWeight: '600' }}>Inject or manage your saved row assemblies</p>
                </div>
              </div>
              <button onClick={() => setShowSavedBlocksModal(false)} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: '22px', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.target.style.color = '#d97706'; }} onMouseLeave={(e) => { e.target.style.color = '#6b7280'; }}>✕</button>
            </div>

            {/* Glassmorphic Search Bar in Catalog */}
            <div style={{ position: 'relative', marginBottom: '20px' }}>
              <input 
                type="text"
                placeholder="🔍 Search saved custom templates by name..."
                value={blockSearchQuery}
                onChange={(e) => setBlockSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 44px',
                  borderRadius: '16px',
                  border: '1px solid rgba(168, 85, 247, 0.18)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#0f091c',
                  fontSize: '13px',
                  fontWeight: '600',
                  outline: 'none',
                  boxShadow: '0 4px 12px rgba(15, 9, 28, 0.02)',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = BRAND_AMBER_GOLD;
                  e.target.style.boxShadow = '0 0 14px rgba(217, 119, 6, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(168, 85, 247, 0.18)';
                  e.target.style.boxShadow = '0 4px 12px rgba(15, 9, 28, 0.02)';
                }}
              />
              {blockSearchQuery && (
                <button
                  onClick={() => setBlockSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'none',
                    color: '#888ea8',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '4px'
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '6px' }}>
              {savedBlocks.length === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center', color: '#6b7280' }}>
                  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 16px auto' }}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                  <h4 style={{ margin: '0 0 8px 0', color: '#0f091c', fontWeight: '800' }}>No Saved Blocks Yet</h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#5b6275', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.5' }}>
                    Activate Selection Mode in the editor sidebar, select one or more canvas rows, and save them as a saved block template.
                  </p>
                </div>
              ) : savedBlocks.filter(b => b.name.toLowerCase().includes(blockSearchQuery.toLowerCase())).length === 0 ? (
                <div style={{ padding: '60px 20px', textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔍</div>
                  <h4 style={{ margin: '0 0 8px 0', color: '#0f091c', fontWeight: '800' }}>No Blocks Found</h4>
                  <p style={{ margin: 0, fontSize: '13px', color: '#5b6275' }}>
                    No templates match your search query "{blockSearchQuery}".
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                  {savedBlocks
                    .filter(block => block.name.toLowerCase().includes(blockSearchQuery.toLowerCase()))
                    .map(block => (
                      <div 
                        key={block.id}
                        className="glass-panel"
                        style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '12px',
                          padding: '16px', 
                          borderRadius: '20px', 
                          background: '#ffffff', 
                          border: '1px solid rgba(168, 85, 247, 0.1)',
                          boxShadow: '0 4px 20px rgba(15, 9, 28, 0.03)',
                          transition: 'all 0.2s ease-in-out',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => { 
                          e.currentTarget.style.borderColor = BRAND_AMBER_GOLD; 
                          e.currentTarget.style.boxShadow = '0 12px 30px rgba(217, 119, 6, 0.08)'; 
                          e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => { 
                          e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.1)'; 
                          e.currentTarget.style.boxShadow = '0 4px 20px rgba(15, 9, 28, 0.03)'; 
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {/* Scaled Render Preview of block content */}
                        <div style={{
                          width: '100%',
                          height: '140px',
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          position: 'relative',
                          border: '1px solid rgba(15, 9, 28, 0.08)',
                          borderRadius: '12px',
                          background: '#111215',
                          boxSizing: 'border-box'
                        }}>
                          <div style={{
                            transform: 'scale(0.42)',
                            transformOrigin: 'top left',
                            width: '238%',
                            pointerEvents: 'none',
                            userSelect: 'none'
                          }}>
                            {block.modules && block.modules.map((mod) => renderStaticMiniModule(mod))}
                          </div>
                        </div>

                        {/* Block Details Info */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minWidth: 0 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 }}>
                            <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f091c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={block.name}>
                              {block.name}
                            </span>
                            <span style={{ fontSize: '10px', color: '#6b7280', fontWeight: '700' }}>
                              📦 {block.modules ? block.modules.length : 0} {block.modules?.length === 1 ? 'Row' : 'Rows'}
                            </span>
                          </div>
                        </div>

                        {/* Action buttons footer inside card */}
                        <div style={{ display: 'flex', gap: '8px', width: '100%', marginTop: 'auto' }}>
                          <button
                            onClick={() => {
                              pushHistory();
                              const clonedModules = block.modules.map(mod => generateUniqueBlockIds(mod));
                              setCanvasModules(prev => [...prev, ...clonedModules]);
                              if (clonedModules.length > 0) {
                                setActiveModuleId(clonedModules[0].id);
                              }
                              setShowSavedBlocksModal(false);
                            }}
                            style={{
                              flex: 1,
                              padding: '10px 14px',
                              borderRadius: '10px',
                              border: 'none',
                              fontSize: '11px',
                              fontWeight: '800',
                              cursor: 'pointer',
                              background: BRAND_AMBER_GOLD,
                              color: '#ffffff',
                              boxShadow: BRAND_GOLD_GLOW,
                              transition: 'all 0.2s',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            📥 Inject Block
                          </button>

                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete the saved block "${block.name}"?`)) {
                                fetch(`${BACKEND_URL}/api/saved-blocks/${block.id}`, { method: 'DELETE' })
                                  .then(res => res.json())
                                  .then(() => fetchSavedBlocks())
                                  .catch(err => console.error("Error deleting block:", err));
                              }
                            }}
                            title="Delete Template Block"
                            style={{
                              padding: '10px 12px',
                              borderRadius: '10px',
                              border: 'none',
                              cursor: 'pointer',
                              background: 'rgba(239, 68, 68, 0.08)',
                              color: '#ef4444',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; e.currentTarget.style.color = '#ef4444'; }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* WALLPAPER CROP MODAL OVERLAY */}
      {wpCropModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 9, 28, 0.4)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="glass-panel animate-slide-up" style={{
            width: '90%',
            maxWidth: '520px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(217, 119, 6, 0.25)',
            boxShadow: '0 24px 60px rgba(15, 9, 28, 0.15), 0 0 40px rgba(217, 119, 6, 0.05)',
            borderRadius: '32px',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '11px', color: BRAND_AMBER_GOLD, textTransform: 'uppercase', display: 'block', marginBottom: '8px', fontWeight: '800', letterSpacing: '1px' }}>✂ WALLPAPER FIT CONFIGURATION</span>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '800', color: '#0f091c', textAlign: 'center' }}>Adjust Mailer Background Wallpaper Crop</h3>
            
            {/* Aspect Locked Crop Canvas Box */}
            <div style={{
              width: '100%',
              maxWidth: '450px',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f1f0f5',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '2px dashed rgba(168, 85, 247, 0.2)',
              position: 'relative',
              boxShadow: 'inset 0 4px 12px rgba(15, 9, 28, 0.05)'
            }}>
              {(() => {
                const aspect = wpCropAspect;
                let viewWidth = 450;
                let viewHeight = 450 / aspect;
                if (viewHeight > 300) {
                  viewHeight = 300;
                  viewWidth = 300 * aspect;
                }
                return (
                  <canvas
                    ref={wpCropCanvasRef}
                    width={viewWidth}
                    height={viewHeight}
                    onMouseDown={handleWpCropMouseDown}
                    onMouseMove={handleWpCropMouseMove}
                    onMouseUp={handleWpCropMouseUpOrLeave}
                    onMouseLeave={handleWpCropMouseUpOrLeave}
                    onTouchStart={handleWpCropTouchStart}
                    onTouchMove={handleWpCropTouchMove}
                    onTouchEnd={handleWpCropMouseUpOrLeave}
                    style={{
                      width: `${viewWidth}px`,
                      height: `${viewHeight}px`,
                      cursor: 'move',
                      boxShadow: '0 8px 24px rgba(15, 9, 28, 0.1)',
                      backgroundColor: '#ffffff',
                      display: 'block'
                    }}
                  />
                );
              })()}
            </div>
            
            <p style={{ fontSize: '11px', color: '#6b7280', margin: '12px 0 20px 0', textAlign: 'center', fontWeight: '500' }}>
              Drag to pan image. Select a landscape or portrait wallpaper aspect ratio below.
            </p>

            {/* Dynamic Wallpaper Aspect Ratio Selector */}
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                <span>WALLPAPER ASPECT RATIO</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '5px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                {[
                  { key: 9/16, label: '📱 9:16 Tall' },
                  { key: 3/4, label: '📷 3:4 Portrait' },
                  { key: 1/1, label: '⬜ 1:1 Square' },
                  { key: 16/9, label: '💻 16:9 Wide' }
                ].map(item => {
                  const isActive = Math.abs(wpCropAspect - item.key) < 0.01;
                  return (
                    <button
                      key={item.key}
                      onClick={() => {
                        setWpCropAspect(item.key);
                        setWpCropZoom(1.0);
                        setWpCropPanX(0);
                        setWpCropPanY(0);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 2px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '9.5px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        background: isActive ? '#ffffff' : 'transparent',
                        color: isActive ? '#a855f7' : '#5b6275',
                        boxShadow: isActive ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                        transition: 'all 0.2s',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Crop Controls */}
            <div style={{ width: '100%', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                <span>ZOOM MAGNIFICATION</span>
                <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace' }}>{Math.round(wpCropZoom * 100)}%</span>
              </div>
              <input
                type="range"
                min="1.0"
                max="5.0"
                step="0.02"
                value={wpCropZoom}
                onChange={e => {
                  const newZoom = parseFloat(e.target.value);
                  setWpCropZoom(newZoom);
                }}
                style={{ width: '100%', accentColor: BRAND_AMBER_GOLD }}
              />
            </div>
            
            {/* Compression & Resolution Settings */}
            <div style={{
              width: '100%',
              backgroundColor: 'rgba(217, 119, 6, 0.03)',
              border: `1px solid rgba(217, 119, 6, 0.12)`,
              borderRadius: '16px',
              padding: '15px',
              marginBottom: '24px',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '10px', color: BRAND_AMBER_GOLD, fontWeight: '800', textTransform: 'uppercase', display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
                ⚙️ Optimize Size & Resolution
              </span>

              {/* Target Scale Row */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                  <span>SCALE</span>
                  <span style={{ color: BRAND_AMBER_GOLD, fontWeight: '800' }}>
                    {wpScale === 'original' ? 'Original' : `${parseFloat(wpScale) * 100}%`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'original', label: 'Original' },
                    { key: '0.75', label: '75%' },
                    { key: '0.5', label: '50%' },
                    { key: '0.33', label: '33%' },
                    { key: '0.25', label: '25%' },
                    { key: '0.1', label: '10%' }
                  ].map(opt => {
                    const isSelected = wpScale === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setWpScale(opt.key)}
                        style={{
                          flex: '1 1 28%',
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: isSelected ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.08)',
                          fontSize: '10px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isSelected ? BRAND_AMBER_GOLD : '#ffffff',
                          color: isSelected ? '#ffffff' : '#5b6275',
                          transition: 'all 0.15s',
                          marginBottom: '4px'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Format Row */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                  <span>OUTPUT FORMAT</span>
                  <span style={{ color: BRAND_AMBER_GOLD, fontWeight: '800', textTransform: 'uppercase' }}>{wpFormat}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[
                    { key: 'png', label: 'PNG (Lossless)' },
                    { key: 'jpeg', label: 'JPEG (Compress)' },
                    { key: 'webp', label: 'WebP (Compact)' }
                  ].map(opt => {
                    const isSelected = wpFormat === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setWpFormat(opt.key)}
                        style={{
                          flex: 1,
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: isSelected ? BRAND_AMBER_GOLD : 'rgba(15, 9, 28, 0.08)',
                          fontSize: '10px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isSelected ? BRAND_AMBER_GOLD : '#ffffff',
                          color: isSelected ? '#ffffff' : '#5b6275',
                          transition: 'all 0.15s'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quality Row */}
              {(wpFormat === 'jpeg' || wpFormat === 'webp') && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                    <span>COMPRESSION AMOUNT</span>
                    <span style={{ color: BRAND_AMBER_GOLD, fontFamily: 'monospace' }}>{Math.round((1 - wpQuality) * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="90"
                    step="5"
                    value={Math.round((1 - wpQuality) * 100)}
                    onChange={e => setWpQuality(Math.round((1 - parseFloat(e.target.value) / 100) * 100) / 100)}
                    style={{ width: '100%', accentColor: BRAND_AMBER_GOLD, cursor: 'pointer' }}
                  />
                </div>
              )}

              {/* Size Savings Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 9, 28, 0.04)',
                fontSize: '11px',
                fontWeight: '700',
                color: '#5b6275'
              }}>
                <span>ESTIMATED FILE SIZE:</span>
                <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {wpOriginalSize > 0 && (
                    <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontWeight: '500' }}>
                      {(wpOriginalSize / 1024).toFixed(1)} KB
                    </span>
                  )}
                  <span style={{ color: '#10b981', fontWeight: '800' }}>
                    {(wpEstimatedSize / 1024).toFixed(1)} KB
                  </span>
                  {wpOriginalSize > 0 && wpEstimatedSize > 0 && (
                    <span style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '9.5px',
                      fontWeight: '800'
                    }}>
                      -{Math.max(0, Math.round(((wpOriginalSize - wpEstimatedSize) / wpOriginalSize) * 100))}%
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', width: '100%' }}>
              <button
                onClick={() => {
                  setWpCropModalOpen(false);
                  setWpCropRawImage(null);
                }}
                style={{
                  padding: '14px',
                  borderRadius: '16px',
                  border: '1px solid rgba(15, 9, 28, 0.08)',
                  background: 'rgba(15, 9, 28, 0.02)',
                  color: '#4b5563',
                  fontWeight: '700',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(15, 9, 28, 0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(15, 9, 28, 0.02)'}
              >
                ✕ Cancel
              </button>
              
              <button
                onClick={handleWpApplyCrop}
                style={{
                  padding: '14px',
                  borderRadius: '16px',
                  border: 'none',
                  background: BRAND_AMBER_GOLD,
                  color: '#ffffff',
                  fontWeight: '850',
                  fontSize: '13px',
                  cursor: 'pointer',
                  boxShadow: BRAND_GOLD_GLOW,
                  transition: 'all 0.2s',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(217, 119, 6, 0.25)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; }}
              >
                ✂ Apply Aspect Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ARTWORK CROP MODAL OVERLAY */}
      {artworkCropModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 9, 28, 0.4)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.25s ease-out'
        }}>
          <div className="glass-panel animate-slide-up" style={{
            width: '90%',
            maxWidth: '520px',
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid rgba(168, 85, 247, 0.25)',
            boxShadow: '0 24px 60px rgba(15, 9, 28, 0.15), 0 0 40px rgba(168, 85, 247, 0.05)',
            borderRadius: '32px',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '11px', color: '#a855f7', textTransform: 'uppercase', display: 'block', marginBottom: '8px', fontWeight: '800', letterSpacing: '1px' }}>✂ IMAGE FIT CONFIGURATION</span>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '800', color: '#0f091c', textAlign: 'center' }}>Adjust Image Crop</h3>
            
            {/* Aspect Locked Crop Canvas Box */}
            <div style={{
              width: '100%',
              maxWidth: '450px',
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f1f0f5',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '2px dashed rgba(168, 85, 247, 0.2)',
              position: 'relative',
              boxShadow: 'inset 0 4px 12px rgba(15, 9, 28, 0.05)'
            }}>
              {(() => {
                const aspect = artworkCropAspect || 1;
                let viewWidth = 450;
                let viewHeight = 450 / aspect;
                if (viewHeight > 300) {
                  viewHeight = 300;
                  viewWidth = 300 * aspect;
                }
                return (
                  <canvas
                    ref={artworkCropCanvasRef}
                    width={viewWidth}
                    height={viewHeight}
                    onMouseDown={handleArtworkCropMouseDown}
                    onMouseMove={handleArtworkCropMouseMove}
                    onMouseUp={handleArtworkCropMouseUpOrLeave}
                    onMouseLeave={handleArtworkCropMouseUpOrLeave}
                    onTouchStart={handleArtworkCropTouchStart}
                    onTouchMove={handleArtworkCropTouchMove}
                    onTouchEnd={handleArtworkCropMouseUpOrLeave}
                    style={{
                      width: `${viewWidth}px`,
                      height: `${viewHeight}px`,
                      cursor: 'move',
                      boxShadow: '0 8px 24px rgba(15, 9, 28, 0.1)',
                      backgroundColor: '#ffffff',
                      display: 'block'
                    }}
                  />
                );
              })()}
            </div>
            
            <p style={{ fontSize: '11px', color: '#6b7280', margin: '12px 0 20px 0', textAlign: 'center', fontWeight: '500' }}>
              Drag to pan image. Select an aspect ratio preset below.
            </p>

            {/* Dynamic Aspect Ratio Selector */}
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                <span>CROP ASPECT RATIO</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '5px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                {[
                  { key: artworkCropOriginalAspect, label: '🖼️ Original' },
                  { key: 1/1, label: '⬜ 1:1 Square' },
                  { key: 16/9, label: '💻 16:9' },
                  { key: 9/16, label: '📱 9:16' },
                  { key: 4/3, label: '📷 4:3' },
                  { key: 3/4, label: '📸 3:4' }
                ].map(item => {
                  const isActive = Math.abs(artworkCropAspect - item.key) < 0.01;
                  return (
                    <button
                      key={item.label}
                      onClick={() => {
                        setArtworkCropAspect(item.key);
                        setArtworkCropZoom(1.0);
                        setArtworkCropPanX(0);
                        setArtworkCropPanY(0);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 2px',
                        borderRadius: '8px',
                        border: 'none',
                        fontSize: '9.5px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        background: isActive ? '#ffffff' : 'transparent',
                        color: isActive ? '#a855f7' : '#5b6275',
                        boxShadow: isActive ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                        transition: 'all 0.2s',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Zoom Slider */}
            <div style={{ width: '100%', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                <span>ZOOM MAGNIFICATION</span>
                <span style={{ color: '#a855f7', fontFamily: 'monospace' }}>{Math.round(artworkCropZoom * 100)}%</span>
              </div>
              <input
                type="range"
                min="1.0"
                max="5.0"
                step="0.02"
                value={artworkCropZoom}
                onChange={e => {
                  const newZoom = parseFloat(e.target.value);
                  setArtworkCropZoom(newZoom);
                }}
                style={{ width: '100%', accentColor: '#a855f7' }}
              />
            </div>
            
            {/* Compression & Resolution Settings */}
            <div style={{
              width: '100%',
              backgroundColor: 'rgba(168, 85, 247, 0.03)',
              border: '1px solid rgba(168, 85, 247, 0.12)',
              borderRadius: '16px',
              padding: '15px',
              marginBottom: '24px',
              boxSizing: 'border-box'
            }}>
              <span style={{ fontSize: '10px', color: '#a855f7', fontWeight: '800', textTransform: 'uppercase', display: 'block', marginBottom: '12px', letterSpacing: '0.5px' }}>
                ⚙️ Optimize Size & Resolution
              </span>

              {/* Target Scale Row */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                  <span>SCALE</span>
                  <span style={{ color: '#a855f7', fontWeight: '800' }}>
                    {artworkScale === 'original' ? 'Original' : `${parseFloat(artworkScale) * 100}%`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'original', label: 'Original' },
                    { key: '0.75', label: '75%' },
                    { key: '0.5', label: '50%' },
                    { key: '0.33', label: '33%' },
                    { key: '0.25', label: '25%' },
                    { key: '0.1', label: '10%' }
                  ].map(opt => {
                    const isSelected = artworkScale === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setArtworkScale(opt.key)}
                        style={{
                          flex: '1 1 28%',
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: isSelected ? '#a855f7' : 'rgba(15, 9, 28, 0.08)',
                          fontSize: '10px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isSelected ? 'linear-gradient(135deg, #a855f7, #6366f1)' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#5b6275',
                          transition: 'all 0.15s',
                          marginBottom: '4px'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Format Row */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                  <span>OUTPUT FORMAT</span>
                  <span style={{ color: '#a855f7', fontWeight: '800', textTransform: 'uppercase' }}>{artworkFormat}</span>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[
                    { key: 'png', label: 'PNG (Lossless)' },
                    { key: 'jpeg', label: 'JPEG (Compress)' },
                    { key: 'webp', label: 'WebP (Compact)' }
                  ].map(opt => {
                    const isSelected = artworkFormat === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => setArtworkFormat(opt.key)}
                        style={{
                          flex: 1,
                          padding: '6px 0',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: isSelected ? '#a855f7' : 'rgba(15, 9, 28, 0.08)',
                          fontSize: '10px',
                          fontWeight: '800',
                          cursor: 'pointer',
                          background: isSelected ? 'linear-gradient(135deg, #a855f7, #6366f1)' : '#ffffff',
                          color: isSelected ? '#ffffff' : '#5b6275',
                          transition: 'all 0.15s'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quality Row */}
              {(artworkFormat === 'jpeg' || artworkFormat === 'webp') && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px', fontWeight: '700', color: '#5b6275' }}>
                    <span>COMPRESSION AMOUNT</span>
                    <span style={{ color: '#a855f7', fontFamily: 'monospace' }}>{Math.round((1 - artworkQuality) * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="90"
                    step="5"
                    value={Math.round((1 - artworkQuality) * 100)}
                    onChange={e => setArtworkQuality(Math.round((1 - parseFloat(e.target.value) / 100) * 100) / 100)}
                    style={{ width: '100%', accentColor: '#a855f7', cursor: 'pointer' }}
                  />
                </div>
              )}

              {/* Size Savings Row */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 9, 28, 0.04)',
                fontSize: '11px',
                fontWeight: '700',
                color: '#5b6275'
              }}>
                <span>ESTIMATED FILE SIZE:</span>
                <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {artworkOriginalSize > 0 && (
                    <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontWeight: '500' }}>
                      {(artworkOriginalSize / 1024).toFixed(1)} KB
                    </span>
                  )}
                  <span style={{ color: '#10b981', fontWeight: '800' }}>
                    {(artworkEstimatedSize / 1024).toFixed(1)} KB
                  </span>
                  {artworkOriginalSize > 0 && artworkEstimatedSize > 0 && (
                    <span style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '9.5px',
                      fontWeight: '800'
                    }}>
                      -{Math.max(0, Math.round(((artworkOriginalSize - artworkEstimatedSize) / artworkOriginalSize) * 100))}%
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', width: '100%' }}>
              <button
                onClick={() => {
                  setArtworkCropModalOpen(false);
                  setArtworkCropRawImage(null);
                }}
                style={{
                  padding: '12px',
                  borderRadius: '16px',
                  border: '1px solid rgba(15, 9, 28, 0.08)',
                  background: 'rgba(15, 9, 28, 0.02)',
                  color: '#4b5563',
                  fontWeight: '700',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(15, 9, 28, 0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(15, 9, 28, 0.02)'}
              >
                ✕ Cancel
              </button>

              <button
                onClick={handleArtworkSkipCrop}
                style={{
                  padding: '12px',
                  borderRadius: '16px',
                  border: '1px solid rgba(15, 9, 28, 0.12)',
                  background: '#ffffff',
                  color: '#1f2937',
                  fontWeight: '700',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}
              >
                Skip Crop
              </button>
              
              <button
                onClick={handleArtworkApplyCrop}
                style={{
                  padding: '12px',
                  borderRadius: '16px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #a855f7, #6366f1)',
                  color: '#ffffff',
                  fontWeight: '800',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                ✂ Crop & Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GEMINI COPYWRITER ASSISTANT MODAL */}
      {showGeminiModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 9, 28, 0.3)', zIndex: '9999', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(16px)' }}>
          <div className="glass-panel" style={{ width: '100%', maxWidth: '640px', background: 'rgba(255, 255, 255, 0.88) !important', border: '1px solid rgba(168, 85, 247, 0.2) !important', borderRadius: '24px', padding: '25px', display: 'flex', flexDirection: 'column', maxHeight: '85vh', boxShadow: '0 24px 60px -10px rgba(15, 9, 28, 0.15) !important' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>✨</span>
                <div>
                  <h3 style={{ margin: 0, color: '#0f091c', fontWeight: '800', letterSpacing: '0.5px' }}>Gemini Copywriter Assistant</h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#5b6275', fontWeight: '600' }}>Brainstorm click-worthy sales copy and call-to-actions</p>
                </div>
              </div>
              <button onClick={() => setShowGeminiModal(false)} style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: '22px', cursor: 'pointer', transition: 'color 0.2s' }} onMouseEnter={(e) => { e.target.style.color = '#d97706'; }} onMouseLeave={(e) => { e.target.style.color = '#6b7280'; }}>✕</button>
            </div>

            {/* Prompt Input Box */}
            <div style={{ marginBottom: '16px' }}>
              <label style={studioLabelStyle}>What should this content be about?</label>
              <textarea 
                rows={3}
                placeholder="e.g. A summer sale discount offering 25% off our premium sunglasses collection..."
                value={geminiPrompt}
                onChange={e => setGeminiPrompt(e.target.value)}
                className="glass-input"
                style={{ width: '100%', resize: 'none', fontSize: '13px', lineHeight: '1.4', padding: '12px', boxSizing: 'border-box' }}
              />
            </div>

            {/* Campaign Phase Selector */}
            <div style={{ marginBottom: '16px' }}>
              <label style={studioLabelStyle}>Select Campaign / Mailer Phase</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '5px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                {[
                  { key: 'signup', label: '🎟️ Pre-Reg', title: 'Pre-registration / Sign Up Hype' },
                  { key: 'lineup', label: '🚀 Lineup', title: 'Lineup & Pre-sale Announcement' },
                  { key: 'warning', label: '🚨 Low Txt', title: 'Low Tickets / Tier Warning' },
                  { key: 'countdown', label: '⏳ Count', title: 'Time Countdown / Last Push' },
                  { key: 'recap', label: '✨ Recap', title: 'Post-Show Thank You & Loyalty Deals' },
                  { key: 'travel', label: '✈️ Travel', title: 'Destination Festivals & Travel Packages' },
                  { key: 'takeover', label: '🤝 Collab', title: 'Co-branded Takeovers & Brand Collaborations' },
                  { key: 'offer', label: '🎁 Offer', title: 'Special Club Opportunities & Partner Deals' }
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setGeminiPhase(item.key)}
                    title={item.title}
                    style={{
                      padding: '8px 2px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '9px',
                      fontWeight: '800',
                      cursor: 'pointer',
                      background: geminiPhase === item.key ? '#ffffff' : 'transparent',
                      color: geminiPhase === item.key ? '#a855f7' : '#5b6275',
                      boxShadow: geminiPhase === item.key ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                      transition: 'all 0.2s',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone Selector Pills */}
            <div style={{ marginBottom: '20px' }}>
              <label style={studioLabelStyle}>Select Tone of Voice</label>
              <div style={{ display: 'flex', gap: '8px', backgroundColor: 'rgba(15, 9, 28, 0.03)', padding: '5px', borderRadius: '12px', border: '1px solid rgba(15, 9, 28, 0.06)' }}>
                {[
                  { key: 'creative', label: '✨ Creative' },
                  { key: 'energetic', label: '⚡ Energetic' },
                  { key: 'urgent', label: '🔥 Urgent' },
                  { key: 'professional', label: '🎯 Professional' }
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setGeminiTone(item.key)}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      borderRadius: '8px',
                      border: 'none',
                      fontSize: '11px',
                      fontWeight: '800',
                      cursor: 'pointer',
                      background: geminiTone === item.key ? '#ffffff' : 'transparent',
                      color: geminiTone === item.key ? '#a855f7' : '#5b6275',
                      boxShadow: geminiTone === item.key ? '0 4px 12px rgba(15, 9, 28, 0.04)' : 'none',
                      transition: 'all 0.2s',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Trigger Button */}
            <button
              onClick={() => {
                if (!geminiPrompt.trim()) {
                  alert('Please enter a brief focus or prompt description.');
                  return;
                }
                setGeminiGenerating(true);
                setGeminiSuggestions([]);

                fetch(`${BACKEND_URL}/api/generate-copy`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    prompt: geminiPrompt.trim(),
                    type: activeModule ? activeModule.type : 'text',
                    tone: geminiTone,
                    phase: geminiPhase
                  })
                })
                  .then(res => res.json())
                  .then(data => {
                    setGeminiSuggestions(data.suggestions || []);
                    setGeminiGenerating(false);
                  })
                  .catch(err => {
                    console.error("AI Generation Error:", err);
                    alert("Failed to connect to copywriting services.");
                    setGeminiGenerating(false);
                  });
              }}
              disabled={geminiGenerating}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                border: 'none',
                fontSize: '12px',
                fontWeight: '900',
                cursor: geminiGenerating ? 'not-allowed' : 'pointer',
                background: geminiGenerating ? 'rgba(168, 85, 247, 0.2)' : 'linear-gradient(135deg, #a855f7, #6366f1)',
                color: geminiGenerating ? '#888ea8' : '#ffffff',
                boxShadow: geminiGenerating ? 'none' : '0 8px 24px rgba(168, 85, 247, 0.3)',
                transition: 'all 0.2s',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {geminiGenerating ? (
                <>
                  <span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid #888ea8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  Brainstorming suggestions...
                </>
              ) : (
                <>✨ Generate AI Copy Suggestions</>
              )}
            </button>

            {/* Generated Suggestion Cards */}
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
              {geminiGenerating && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: '16px' }}>
                  <div style={{ position: 'relative', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ position: 'absolute', width: '100%', height: '100%', borderRadius: '50%', border: '2px dashed rgba(168, 85, 247, 0.3)', animation: 'spin 10s linear infinite' }} />
                    <div style={{ position: 'absolute', width: '80%', height: '80%', borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#a855f7', borderBottomColor: '#6366f1', animation: 'spin 1.5s ease-in-out infinite' }} />
                    <span style={{ fontSize: '20px' }}>🧠</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Brainstorming Click-Worthy Variations...
                  </p>
                </div>
              )}

              {geminiSuggestions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={studioLabelStyle}>Select a Copy suggestion</label>
                  {geminiSuggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      className="glass-panel"
                      style={{
                        padding: '14px 18px',
                        borderRadius: '16px',
                        background: '#ffffff',
                        border: '1px solid rgba(168, 85, 247, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        boxShadow: '0 4px 15px rgba(15, 9, 28, 0.02)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#a855f7';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 85, 247, 0.08)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(15, 9, 28, 0.02)';
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '13px', color: '#0f091c', fontWeight: '700', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                        {suggestion}
                      </p>

                      <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid rgba(15, 9, 28, 0.04)', paddingTop: '8px' }}>
                        <button
                          onClick={() => {
                            pushHistory();
                            updateModuleProperty(activeModule.id, 'text', suggestion);
                            setShowGeminiModal(false);
                          }}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '11px',
                            fontWeight: '800',
                            cursor: 'pointer',
                            background: 'rgba(168, 85, 247, 0.08)',
                            color: '#a855f7',
                            transition: 'all 0.2s',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#a855f7'; e.currentTarget.style.color = '#fff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(168, 85, 247, 0.08)'; e.currentTarget.style.color = '#a855f7'; }}
                        >
                          📥 Inject into Canvas
                        </button>

                        <button
                          onClick={(e) => {
                            copyTextToClipboard(suggestion)
                              .then(() => {
                                const origText = e.currentTarget.innerText;
                                e.currentTarget.innerText = '✅ COPIED!';
                                setTimeout(() => { e.currentTarget.innerText = origText; }, 1200);
                              })
                              .catch((err) => {
                                console.error('Suggestion copy failed:', err);
                              });
                          }}
                          style={{
                            padding: '8px 14px',
                            borderRadius: '8px',
                            border: '1px solid rgba(15, 9, 28, 0.08)',
                            fontSize: '11px',
                            fontWeight: '800',
                            cursor: 'pointer',
                            background: '#ffffff',
                            color: '#5b6275',
                            transition: 'all 0.2s',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}
                        >
                          📋 Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      

    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: ADMIN REPAIR & MANAGEMENT CONSOLE
// ==========================================
function AdminAccessDeniedView() {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f7fa', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
      <div className="float-slow" style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(239, 68, 68, 0.05) 0%, transparent 75%)', filter: 'blur(50px)', zIndex: 1 }} />
      
      <div className="glass-panel fade-in-up" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '480px', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(239, 68, 68, 0.15)', textAlign: 'center', background: 'rgba(255, 255, 255, 0.8)' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '20px' }}>🔐</span>
        <h2 style={{ margin: '0 0 12px 0', letterSpacing: '-0.02em', color: '#0f091c', fontSize: '24px', fontWeight: '800' }}>RESTRICTED ACCESS</h2>
        <p style={{ fontSize: '14px', color: '#5b6275', lineHeight: '1.6', marginBottom: '30px', fontWeight: '500' }}>
          This directory is reserved exclusively for system administrators. Your editor role does not have authorization to view user directory lists or configure permissions.
        </p>
        <button onClick={() => navigate('/hub')} className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '16px', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}>
          Return to Hub
        </button>
      </div>
    </div>
  );
}

function AdminRoute({ children }) {
  const user = getAuthUser();
  if (!user || user.role !== 'admin') {
    return <AdminAccessDeniedView />;
  }
  return children;
}

function SupportAccessDeniedView() {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f7fa', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
      <div className="float-slow" style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 75%)', filter: 'blur(50px)', zIndex: 1 }} />
      
      <div className="glass-panel fade-in-up" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '480px', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(16, 185, 129, 0.15)', textAlign: 'center', background: 'rgba(255, 255, 255, 0.8)' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '20px' }}>🛠️</span>
        <h2 style={{ margin: '0 0 12px 0', letterSpacing: '-0.02em', color: '#0f091c', fontSize: '24px', fontWeight: '800' }}>SUPPORT CONSOLE RESTRICTED</h2>
        <p style={{ fontSize: '14px', color: '#5b6275', lineHeight: '1.6', marginBottom: '30px', fontWeight: '500' }}>
          This directory is reserved for support team personnel and system administrators. Your editor role does not have authorization to view, resolve, or request information on support tickets.
        </p>
        <button onClick={() => navigate('/hub')} className="pulse-gold" style={{ width: '100%', backgroundColor: '#10b981', color: '#ffffff', padding: '16px', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: '0 4px 20px rgba(16, 185, 129, 0.15)', textTransform: 'uppercase' }}>
          Return to Hub
        </button>
      </div>
    </div>
  );
}

function SupportRoute({ children }) {
  const user = getAuthUser();
  if (!user || (user.role !== 'support' && user.role !== 'admin')) {
    return <SupportAccessDeniedView />;
  }
  return children;
}

function AdminView() {
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('editor');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const currentUser = getAuthUser();

  const [activeSubTab, setActiveSubTab] = useState('users'); // 'users' | 'storage'
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileFilter, setFileFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFiles, setDeletingFiles] = useState(false);

  const fetchUsers = () => {
    if (!currentUser) return;
    fetch(`${BACKEND_URL}/api/users`, {
      headers: {
        'x-requester-email': currentUser.email
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to retrieve user registry directory.');
        return res.json();
      })
      .then(data => setUsers(data))
      .catch(err => setError(err.message));
  };

  const fetchFiles = () => {
    if (!currentUser) return;
    setLoadingFiles(true);
    fetch(`${BACKEND_URL}/api/admin/files`, {
      headers: {
        'x-requester-email': currentUser.email
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to retrieve file storage directory.');
        return res.json();
      })
      .then(data => {
        setFiles(data);
        setSelectedFiles([]);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoadingFiles(false));
  };

  const handleBulkDelete = () => {
    if (selectedFiles.length === 0) return;
    if (!window.confirm(`⚠️ Are you sure you want to permanently delete the ${selectedFiles.length} selected file(s) from the server? This action cannot be undone.`)) {
      return;
    }

    setError('');
    setSuccess('');
    setDeletingFiles(true);

    fetch(`${BACKEND_URL}/api/admin/files/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-requester-email': currentUser.email
      },
      body: JSON.stringify({ files: selectedFiles })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Bulk delete error.');
        return data;
      })
      .then(data => {
        const deletedCount = data.deleted ? data.deleted.length : 0;
        const errorCount = data.errors ? data.errors.length : 0;
        
        let msg = `Successfully deleted ${deletedCount} file(s) from server disk.`;
        if (errorCount > 0) {
          msg += ` Warning: ${errorCount} file(s) failed to delete.`;
          console.warn('File delete errors:', data.errors);
        }
        setSuccess(msg);
        fetchFiles();
      })
      .catch(err => setError(err.message))
      .finally(() => setDeletingFiles(false));
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeSubTab === 'storage') {
      fetchFiles();
    }
  }, [activeSubTab]);

  const handleCreateUser = (e) => {
    e.preventDefault();
    if (!newEmail || !newRole) {
      setError('Please fill in all user profile fields.');
      return;
    }
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    fetch(`${BACKEND_URL}/api/users/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-requester-email': currentUser.email
      },
      body: JSON.stringify({ email: newEmail, role: newRole })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'User creation fault.');
        return data;
      })
      .then(data => {
        setSuccess(`Successfully invited and registered new user: ${data.user.email}. An invitation email has been sent!`);
        setNewEmail('');
        setNewRole('editor');
        fetchUsers();
      })
      .catch(err => setError(err.message))
      .finally(() => setIsSubmitting(false));
  };

  const handleDeleteUser = (email) => {
    if (email.toLowerCase() === currentUser.email.toLowerCase()) {
      alert('Self-deletion is blocked. You cannot delete your own active admin account.');
      return;
    }
    if (!window.confirm(`⚠️ Are you sure you want to permanently prune and delete the account for "${email.toUpperCase()}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    fetch(`${BACKEND_URL}/api/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
      headers: {
        'x-requester-email': currentUser.email
      }
    })
      .then(async res => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Pruning fault.');
        }
        return res.json();
      })
      .then(() => {
        setSuccess(`Successfully deleted user account: ${email}`);
        fetchUsers();
      })
      .catch(err => setError(err.message));
  };

  return (
    <div style={{ padding: '48px 40px', backgroundColor: '#f8f7fa', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1f2937', position: 'relative', overflow: 'hidden' }}>
      <div className="float-slow" style={{ position: 'absolute', top: '-10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.04) 0%, transparent 70%)', filter: 'blur(60px)', zIndex: 1 }} />
      <div className="float-slow" style={{ position: 'absolute', bottom: '10%', left: '5%', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.03) 0%, transparent 70%)', filter: 'blur(50px)', zIndex: 1, animationDelay: '-4s' }} />

      <div style={{ position: 'relative', zIndex: 10, maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(168, 85, 247, 0.15)', paddingBottom: '24px', marginBottom: '40px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #0f091c 0%, #a855f7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>System Administration</h1>
            <p style={{ margin: '0', fontSize: '13px', color: '#5b6275', fontWeight: '500' }}>Logged in as Administrator: <span style={{ color: BRAND_AMBER_GOLD, fontWeight: '700' }}>{currentUser?.name}</span></p>
          </div>
          <div>
            <button onClick={() => navigate('/hub')} style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: `1px solid ${BRAND_AMBER_GOLD}`, color: BRAND_AMBER_GOLD, padding: '10px 20px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease', boxShadow: '0 4px 12px rgba(217, 119, 6, 0.1)' }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(217, 119, 6, 0.05)'; e.currentTarget.style.boxShadow = BRAND_GOLD_GLOW; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(217, 119, 6, 0.1)'; }}>
              ⚡ BACK TO HUB
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', paddingBottom: '12px' }}>
          <button 
            onClick={() => setActiveSubTab('users')}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              color: activeSubTab === 'users' ? '#a855f7' : '#6b7280',
              fontWeight: '700',
              fontSize: '14px',
              cursor: 'pointer',
              borderBottom: activeSubTab === 'users' ? '3px solid #a855f7' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            👥 User Management
          </button>
          <button 
            onClick={() => setActiveSubTab('storage')}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              color: activeSubTab === 'storage' ? '#a855f7' : '#6b7280',
              fontWeight: '700',
              fontSize: '14px',
              cursor: 'pointer',
              borderBottom: activeSubTab === 'storage' ? '3px solid #a855f7' : '3px solid transparent',
              transition: 'all 0.2s'
            }}
          >
            💾 Storage Management
          </button>
        </div>

        {error && (
          <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '14px 20px', borderRadius: '16px', marginBottom: '24px', fontSize: '13px', fontWeight: '600' }}>
            ⚠️ {error}
          </div>
        )}
        {success && (
          <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.25)', color: '#22c55e', padding: '14px 20px', borderRadius: '16px', marginBottom: '24px', fontSize: '13px', fontWeight: '600' }}>
            ✓ {success}
          </div>
        )}

        {activeSubTab === 'users' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '40px', alignItems: 'start' }}>
            <div className="glass-panel" style={{ padding: '30px', borderRadius: '24px', border: '1px solid rgba(168, 85, 247, 0.12)', background: 'rgba(255, 255, 255, 0.65)' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '800', color: '#0f091c' }}>Setup New Account</h3>
              
              <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={studioLabelStyle}>EMAIL ADDRESS</label>
                  <input 
                    type="email" 
                    required 
                    value={newEmail} 
                    onChange={e => setNewEmail(e.target.value)} 
                    className="glass-input" 
                    style={{ width: '100%', boxSizing: 'border-box' }} 
                    placeholder="editor@louder.com" 
                  />
                </div>

                <div style={{ padding: '14px 16px', borderRadius: '16px', backgroundColor: 'rgba(217, 119, 6, 0.06)', border: '1px solid rgba(217, 119, 6, 0.15)', fontSize: '12px', color: '#854d0e', fontWeight: '500', lineHeight: '1.5' }}>
                  💡 <strong>Invitation Mode Enabled</strong>: No password is set by administrators. A secure activation email will be automatically sent to this address to set up their password.
                </div>

                <div>
                  <label style={studioLabelStyle}>PERMISSION PRIVILEGE ROLE</label>
                  <div style={{ display: 'flex', gap: '8px', backgroundColor: '#f1f0f5', padding: '4px', borderRadius: '14px', border: '1px solid rgba(168, 85, 247, 0.08)' }}>
                    <button 
                      type="button" 
                      onClick={() => setNewRole('editor')} 
                      style={{ 
                        flex: 1, 
                        padding: '10px', 
                        border: 'none', 
                        borderRadius: '11px', 
                        cursor: 'pointer', 
                        fontWeight: '700', 
                        fontSize: '11px', 
                        background: newRole === 'editor' ? '#ffffff' : 'transparent', 
                        color: newRole === 'editor' ? '#a855f7' : '#5b6275', 
                        boxShadow: newRole === 'editor' ? '0 4px 10px rgba(15, 9, 28, 0.04)' : 'none', 
                        transition: 'all 0.2s' 
                      }}
                    >
                      🎨 EDITOR
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNewRole('support')} 
                      style={{ 
                        flex: 1, 
                        padding: '10px', 
                        border: 'none', 
                        borderRadius: '11px', 
                        cursor: 'pointer', 
                        fontWeight: '700', 
                        fontSize: '11px', 
                        background: newRole === 'support' ? '#ffffff' : 'transparent', 
                        color: newRole === 'support' ? '#10b981' : '#5b6275', 
                        boxShadow: newRole === 'support' ? '0 4px 10px rgba(15, 9, 28, 0.04)' : 'none', 
                        transition: 'all 0.2s' 
                      }}
                    >
                      🛠️ SUPPORT
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNewRole('admin')} 
                      style={{ 
                        flex: 1, 
                        padding: '10px', 
                        border: 'none', 
                        borderRadius: '11px', 
                        cursor: 'pointer', 
                        fontWeight: '700', 
                        fontSize: '11px', 
                        background: newRole === 'admin' ? '#ffffff' : 'transparent', 
                        color: newRole === 'admin' ? '#d97706' : '#5b6275', 
                        boxShadow: newRole === 'admin' ? '0 4px 10px rgba(15, 9, 28, 0.04)' : 'none', 
                        transition: 'all 0.2s' 
                      }}
                    >
                      👑 ADMIN
                    </button>
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="pulse-gold" 
                  style={{ 
                    marginTop: '10px', 
                    backgroundColor: BRAND_AMBER_GOLD, 
                    color: '#ffffff', 
                    padding: '15px', 
                    border: 'none', 
                    borderRadius: '16px', 
                    fontWeight: '800', 
                    fontSize: '13px', 
                    letterSpacing: '0.03em', 
                    cursor: 'pointer', 
                    transition: 'all 0.25s ease', 
                    boxShadow: BRAND_GOLD_GLOW, 
                    textTransform: 'uppercase' 
                  }}
                >
                  {isSubmitting ? 'Registering Account...' : '⚡ Setup New User'}
                </button>
              </form>
            </div>

            <div className="glass-panel" style={{ padding: '30px', borderRadius: '24px', border: '1px solid rgba(168, 85, 247, 0.12)', background: 'rgba(255, 255, 255, 0.65)' }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '800', color: '#0f091c', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>User Directory</span>
                <span style={{ backgroundColor: 'rgba(168, 85, 247, 0.12)', color: '#a855f7', padding: '3px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '700' }}>
                  {users.length} active
                </span>
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {users.map(u => {
                  const isAdmin = u.role === 'admin';
                  const isSupport = u.role === 'support';
                  const badgeBg = isAdmin ? 'rgba(217, 119, 6, 0.1)' : (isSupport ? 'rgba(16, 185, 129, 0.1)' : 'rgba(168, 85, 247, 0.1)');
                  const badgeColor = isAdmin ? '#d97706' : (isSupport ? '#10b981' : '#a855f7');
                  const badgeBorder = isAdmin ? '1px solid rgba(217, 119, 6, 0.2)' : (isSupport ? '1px solid rgba(16, 185, 129, 0.2)' : '1px solid rgba(168, 85, 247, 0.2)');

                  return (
                    <div 
                      key={u.email} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '16px 20px', 
                        borderRadius: '18px', 
                        backgroundColor: '#ffffff', 
                        border: '1px solid rgba(168, 85, 247, 0.08)',
                        boxShadow: '0 4px 12px rgba(15, 9, 28, 0.02)'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '70%' }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f091c', wordBreak: 'break-all' }}>{u.email}</span>
                        <span style={{ fontSize: '11px', color: '#888ea8', fontWeight: '600' }}>PROFILE ID: {u.name}</span>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span 
                          style={{ 
                            backgroundColor: badgeBg, 
                            color: badgeColor, 
                            border: badgeBorder,
                            padding: '4px 10px', 
                            borderRadius: '10px', 
                            fontSize: '9px', 
                            fontWeight: '800',
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          {u.role}
                        </span>

                        {u.email.toLowerCase() !== currentUser?.email.toLowerCase() ? (
                          <button 
                            onClick={() => handleDeleteUser(u.email)} 
                            title="Purge User Account"
                            style={{ 
                              backgroundColor: 'rgba(239, 68, 68, 0.05)', 
                              border: '1px solid rgba(239, 68, 68, 0.15)', 
                              color: '#ef4444', 
                              width: '32px', 
                              height: '32px', 
                              borderRadius: '10px', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              transition: 'all 0.2s ease',
                              fontSize: '13px'
                            }} 
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.color = '#fff'; }} 
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.05)'; e.currentTarget.style.color = '#ef4444'; }}
                          >
                            🗑
                          </button>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#888ea8', cursor: 'default', padding: '0 8px' }} title="Current active session">👑</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="glass-panel" style={{ padding: '30px', borderRadius: '24px', border: '1px solid rgba(168, 85, 247, 0.12)', background: 'rgba(255, 255, 255, 0.65)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '800', color: '#0f091c' }}>Disk Space Management</h3>
                <p style={{ margin: '0', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                  Total Files Scanned: <strong>{files.length}</strong> | Total Space Used: <strong style={{ color: '#d97706' }}>{formatBytes(files.reduce((acc, f) => acc + f.size, 0))}</strong>
                </p>
              </div>
              
              {selectedFiles.length > 0 && (
                <button 
                  onClick={handleBulkDelete}
                  disabled={deletingFiles}
                  style={{
                    backgroundColor: '#ef4444',
                    color: '#ffffff',
                    padding: '10px 20px',
                    borderRadius: '14px',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '12px',
                    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#dc2626'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ef4444'}
                >
                  {deletingFiles ? '⚡ DELETING...' : `🗑️ DELETE SELECTED (${selectedFiles.length})`}
                </button>
              )}
            </div>

            {/* Filter controls */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', backgroundColor: '#f1f0f5', padding: '12px', borderRadius: '16px' }}>
              <div style={{ flex: 2 }}>
                <input 
                  type="text" 
                  className="glass-input" 
                  value={fileFilter}
                  onChange={e => setFileFilter(e.target.value)}
                  placeholder="Search file name..."
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', fontSize: '13px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <select 
                  className="glass-input"
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', fontSize: '13px', cursor: 'pointer' }}
                >
                  <option value="ALL">All Categories</option>
                  <option value="Uploads">Campaign Uploads</option>
                  <option value="Created Assets">Created Assets</option>
                  <option value="Custom Fonts">Custom Fonts</option>
                  <option value="Support Attachments">Support Attachments</option>
                </select>
              </div>
            </div>

            {loadingFiles ? (
              <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
                ⌛ Scanning directories & computing file sizes...
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb', color: '#4b5563' }}>
                      <th style={{ padding: '12px 8px', width: '30px' }}>
                        <input 
                          type="checkbox"
                          checked={files.length > 0 && selectedFiles.length === files.filter(f => {
                            const nameMatch = f.name.toLowerCase().includes(fileFilter.toLowerCase());
                            const catMatch = categoryFilter === 'ALL' || f.category === categoryFilter;
                            return nameMatch && catMatch;
                          }).length}
                          onChange={e => {
                            const filtered = files.filter(f => {
                              const nameMatch = f.name.toLowerCase().includes(fileFilter.toLowerCase());
                              const catMatch = categoryFilter === 'ALL' || f.category === categoryFilter;
                              return nameMatch && catMatch;
                            });
                            if (e.target.checked) {
                              setSelectedFiles(filtered.map(f => f.relativePath));
                            } else {
                              setSelectedFiles([]);
                            }
                          }}
                        />
                      </th>
                      <th style={{ padding: '12px 8px', width: '50px' }}>Preview</th>
                      <th style={{ padding: '12px 8px' }}>Filename</th>
                      <th style={{ padding: '12px 8px' }}>Category</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Size</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right' }}>Modified Time</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center' }}>Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files
                      .filter(f => {
                        const nameMatch = f.name.toLowerCase().includes(fileFilter.toLowerCase());
                        const catMatch = categoryFilter === 'ALL' || f.category === categoryFilter;
                        return nameMatch && catMatch;
                      })
                      .map(f => {
                        const isChecked = selectedFiles.includes(f.relativePath);
                        return (
                          <tr key={f.relativePath} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 8px' }}>
                              <input 
                                type="checkbox"
                                checked={isChecked}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedFiles(prev => [...prev, f.relativePath]);
                                  } else {
                                    setSelectedFiles(prev => prev.filter(p => p !== f.relativePath));
                                  }
                                }}
                              />
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              {/\.(png|jpg|jpeg|gif|webp)$/i.test(f.name) && f.url ? (
                                <img 
                                  src={`${BACKEND_URL}${f.url}`} 
                                  alt="preview" 
                                  style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb', display: 'block' }} 
                                />
                              ) : f.category === 'Custom Fonts' ? (
                                <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(168,85,247,0.08)', borderRadius: '8px', fontSize: '16px', color: '#a855f7' }}>
                                  🔤
                                </div>
                              ) : (
                                <div style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', borderRadius: '8px', fontSize: '16px', color: '#6b7280' }}>
                                  📄
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 8px', wordBreak: 'break-all', fontWeight: '600', color: '#111827' }} title={f.relativePath}>
                              {f.name}
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '700',
                                backgroundColor: f.category === 'Custom Fonts' ? 'rgba(168,85,247,0.1)' : (f.category === 'Created Assets' ? 'rgba(217,119,6,0.1)' : 'rgba(16,185,129,0.1)'),
                                color: f.category === 'Custom Fonts' ? '#a855f7' : (f.category === 'Created Assets' ? '#d97706' : '#10b981')
                              }}>
                                {f.category}
                              </span>
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '700', color: '#374151' }}>
                              {formatBytes(f.size)}
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'right', color: '#6b7280' }}>
                              {new Date(f.mtime).toLocaleDateString()} {new Date(f.mtime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                              {f.url ? (
                                <a 
                                  href={`${BACKEND_URL}${f.url}`} 
                                  target="_blank" 
                                  rel="noreferrer"
                                  style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 'bold' }}
                                >
                                  🔗
                                </a>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
// ==========================================
// VIEWPORT COMPONENT: ACCOUNT ACTIVATION VIEW
// ==========================================
function ActivateAccountView() {
  const { token } = useParams();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/users/activate-check/${token}`)
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid or expired invitation link.');
        return data;
      })
      .then(data => {
        setEmail(data.email);
        setChecking(false);
      })
      .catch(err => {
        setError(err.message);
        setChecking(false);
      });
  }, [token]);

  const handleActivateSubmit = (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    fetch(`${BACKEND_URL}/api/users/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Activation failed.');
        return data;
      })
      .then(() => {
        setSuccess('Your account is now activated! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      })
      .catch(err => setError(err.message))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f7fa', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
      <div className="float-slow" style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, transparent 75%)', filter: 'blur(50px)', zIndex: 1 }} />
      <div className="float-slow" style={{ position: 'absolute', bottom: '10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.04) 0%, transparent 75%)', filter: 'blur(60px)', zIndex: 1, animationDelay: '-3s' }} />

      <div className="glass-panel fade-in-up" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '420px', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
        <div style={{ textAlign: 'center', marginBottom: '35px' }}>
          <h2 style={{ margin: '0 0 8px 0', letterSpacing: '2px', color: '#0f091c', fontSize: '26px', fontWeight: '800' }}>ACTIVATE ACCOUNT</h2>
          <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '600' }}>Louder Mailer Studio</span>
        </div>

        {checking ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#5b6275', fontWeight: '600', fontSize: '14px' }}>
            🔄 Validating invitation link, please wait...
          </div>
        ) : error && !email ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '16px', borderRadius: '16px', marginBottom: '24px', fontSize: '13px', fontWeight: '600' }}>
              ⚠️ {error}
            </div>
            <button onClick={() => navigate('/login')} className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '14px', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', textTransform: 'uppercase' }}>
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleActivateSubmit}>
            {error && (
              <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
                ⚠️ {error}
              </div>
            )}

            {success && (
              <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.25)', color: '#22c55e', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
                ✓ {success}
              </div>
            )}

            <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '14px', backgroundColor: '#f1f0f5', border: '1px solid rgba(168, 85, 247, 0.08)' }}>
              <span style={{ display: 'block', fontSize: '10px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>ACTIVATING ACCOUNT FOR</span>
              <span style={{ fontSize: '14px', color: '#0f091c', fontWeight: '700' }}>{email}</span>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>NEW PASSWORD</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="glass-input" style={{ width: '100%' }} placeholder="••••••••" />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>CONFIRM PASSWORD</label>
              <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="glass-input" style={{ width: '100%' }} placeholder="••••••••" />
            </div>

            <button type="submit" disabled={isSubmitting} className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '16px', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}>
              {isSubmitting ? 'ACTIVATING PROFILE...' : '⚡ ACTIVATE & SIGN IN'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: PASSWORD RECOVERY VIEW
// ==========================================
function ResetPasswordView() {
  const { token } = useParams();
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/reset-check/${token}`)
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid or expired password reset link.');
        return data;
      })
      .then(data => {
        setEmail(data.email);
        setChecking(false);
      })
      .catch(err => {
        setError(err.message);
        setChecking(false);
      });
  }, [token]);

  const handleResetSubmit = (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    fetch(`${BACKEND_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Reset failed.');
        return data;
      })
      .then(() => {
        setSuccess('Password updated successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      })
      .catch(err => setError(err.message))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f7fa', alignItems: 'center', justifyContent: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif", position: 'relative', overflow: 'hidden' }}>
      <div className="float-slow" style={{ position: 'absolute', top: '10%', left: '10%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.05) 0%, transparent 75%)', filter: 'blur(50px)', zIndex: 1 }} />
      <div className="float-slow" style={{ position: 'absolute', bottom: '10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(217, 119, 6, 0.04) 0%, transparent 75%)', filter: 'blur(60px)', zIndex: 1, animationDelay: '-3s' }} />

      <div className="glass-panel fade-in-up" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '420px', padding: '48px 40px', borderRadius: '28px', border: '1px solid rgba(168, 85, 247, 0.12)' }}>
        <div style={{ textAlign: 'center', marginBottom: '35px' }}>
          <h2 style={{ margin: '0 0 8px 0', letterSpacing: '2px', color: '#0f091c', fontSize: '26px', fontWeight: '800' }}>RESET PASSWORD</h2>
          <span style={{ fontSize: '11px', color: '#5b6275', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '600' }}>Louder Mailer Studio</span>
        </div>

        {checking ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#5b6275', fontWeight: '600', fontSize: '14px' }}>
            🔄 Checking recovery token, please wait...
          </div>
        ) : error && !email ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '16px', borderRadius: '16px', marginBottom: '24px', fontSize: '13px', fontWeight: '600' }}>
              ⚠️ {error}
            </div>
            <button onClick={() => navigate('/login')} className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '14px', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', textTransform: 'uppercase' }}>
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleResetSubmit}>
            {error && (
              <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', color: '#ef4444', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
                ⚠️ {error}
              </div>
            )}

            {success && (
              <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.25)', color: '#22c55e', padding: '12px 16px', borderRadius: '12px', marginBottom: '20px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
                ✓ {success}
              </div>
            )}

            <div style={{ marginBottom: '20px', padding: '12px 16px', borderRadius: '14px', backgroundColor: '#f1f0f5', border: '1px solid rgba(168, 85, 247, 0.08)' }}>
              <span style={{ display: 'block', fontSize: '10px', color: '#5b6275', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>RESETTING PASSWORD FOR</span>
              <span style={{ fontSize: '14px', color: '#0f091c', fontWeight: '700' }}>{email}</span>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>NEW PASSWORD</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="glass-input" style={{ width: '100%' }} placeholder="••••••••" />
            </div>

            <div style={{ marginBottom: '32px' }}>
              <label style={{ display: 'block', color: '#5b6275', fontSize: '11px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '700' }}>CONFIRM PASSWORD</label>
              <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="glass-input" style={{ width: '100%' }} placeholder="••••••••" />
            </div>

            <button type="submit" disabled={isSubmitting} className="pulse-gold" style={{ width: '100%', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '16px', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '13px', letterSpacing: '0.05em', cursor: 'pointer', transition: 'all 0.25s ease', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}>
              {isSubmitting ? 'RESETTING PASSWORD...' : '🔑 SET NEW PASSWORD'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ==========================================
// VIEWPORT COMPONENT: GLOBAL SUPPORT TICKET WIDGET
// ==========================================
function SupportTicketWidget({ currentUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Snap Context, 2: Describe & Send, 3: Active Chats
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [snapping, setSnapping] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const widgetRef = useRef(null);

  const fetchMyTickets = () => {
    fetch(`${BACKEND_URL}/api/support/my-tickets?email=${encodeURIComponent(currentUser.email)}`)
      .then(res => res.json())
      .then(data => setMyTickets(data))
      .catch(err => console.error(err));
  };

  useEffect(() => {
    if (isOpen) {
      fetchMyTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCaptureScreen = async () => {
    setSnapping(true);
    setError('');
    
    // Remember original body background color (e.g. #121214) to prevent greyed-out transparent screenshots
    const originalBodyBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#f8f7fa'; // Temporarily match our bright Alabaster theme
    
    // Disable all CSS animations & transitions and force .fade-in-up opacity during capture
    // html2canvas clones the DOM synchronously, capturing fade-in keyframes at initial frame (0 opacity)
    const overrideStyle = document.createElement('style');
    overrideStyle.id = 'html2canvas-animation-override-style';
    overrideStyle.innerHTML = `
      * {
        animation: none !important;
        transition: none !important;
      }
      .fade-in-up {
        opacity: 1 !important;
        transform: none !important;
      }
    `;
    document.head.appendChild(overrideStyle);
    
    // Hide the widget briefly so it doesn't appear in the screenshot
    if (widgetRef.current) widgetRef.current.style.display = 'none';

    // Wait for repaint
    setTimeout(async () => {
      try {
        const canvas = await html2canvas(document.body, {
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#f8f7fa', // Render transparent layers over clean alabaster background
          scale: 0.8 // slightly scaled down for performance and storage
        });
        const dataUrl = canvas.toDataURL('image/png');
        setScreenshot(dataUrl);
        setStep(2);
      } catch (err) {
        console.error(err);
        setError('Screen snapshot capture failed. You can still describe your issue.');
        setStep(2);
      } finally {
        setSnapping(false);
        // Remove style overrides
        const styleNode = document.getElementById('html2canvas-animation-override-style');
        if (styleNode) styleNode.remove();
        
        // Restore the original background color immediately
        document.body.style.backgroundColor = originalBodyBg;
        if (widgetRef.current) widgetRef.current.style.display = 'block';
      }
    }, 150); // Wait for browser repaint cycle
  };

  const handleSubmitTicket = (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setError('Please provide a brief description.');
      return;
    }
    setError('');
    setSubmitting(true);

    fetch(`${BACKEND_URL}/api/support/tickets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: currentUser.email,
        description,
        screenshot,
        pageUrl: window.location.href
      })
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to submit ticket.');
        return data;
      })
      .then(() => {
        setSuccess('Support ticket created successfully!');
        setDescription('');
        setScreenshot(null);
        setTimeout(() => {
          setSuccess('');
          setStep(3); // Navigate to Active Chats list
          fetchMyTickets();
        }, 2000);
      })
      .catch(err => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  const handleSendChatMessage = (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || !activeTicket) return;
    setSendingMsg(true);

    fetch(`${BACKEND_URL}/api/support/tickets/${activeTicket.id}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender: 'user',
        text: chatMessage
      })
    })
      .then(res => res.json())
      .then(updatedTicket => {
        setChatMessage('');
        setActiveTicket(updatedTicket);
        fetchMyTickets();
      })
      .catch(err => alert(err.message))
      .finally(() => setSendingMsg(false));
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="pulse-gold"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9999,
          backgroundColor: '#10b981', // Support emerald green
          color: '#ffffff',
          border: 'none',
          padding: '14px 20px',
          borderRadius: '30px',
          fontWeight: '800',
          fontSize: '12px',
          letterSpacing: '0.04em',
          cursor: 'pointer',
          boxShadow: '0 8px 30px rgba(16, 185, 129, 0.25)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        🛠️ RAISE SUPPORT TICKET
      </button>
    );
  }

  return (
    <div 
      ref={widgetRef}
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '380px',
        height: '520px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 50px rgba(15, 9, 28, 0.15)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
      className="glass-panel"
    >
      {/* HEADER SECTION */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(168, 85, 247, 0.12)', background: 'rgba(255, 255, 255, 0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>🛠️</span>
          <span style={{ fontWeight: '800', fontSize: '13px', color: '#0f091c', letterSpacing: '0.02em', textTransform: 'uppercase' }}>Promoter Support</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button 
            onClick={() => { setStep(step === 3 ? 1 : 3); setActiveTicket(null); }}
            style={{ padding: '4px 8px', fontSize: '10px', fontWeight: '700', borderRadius: '8px', border: 'none', cursor: 'pointer', backgroundColor: step === 3 ? '#e4e4e7' : 'rgba(16, 185, 129, 0.1)', color: step === 3 ? '#3f3f46' : '#10b981' }}
          >
            {step === 3 ? '✏️ NEW TICKET' : '📥 MY TICKETS'}
          </button>
          <button 
            onClick={() => { setIsOpen(false); setActiveTicket(null); setStep(1); }} 
            style={{ border: 'none', background: 'transparent', fontSize: '16px', cursor: 'pointer', color: '#888' }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* CONTENT SCROLL AREA */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        {/* STEP 1: SNAP PROMPT */}
        {step === 1 && (
          <div style={{ textAlign: 'center', paddingTop: '40px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
            <span style={{ fontSize: '48px' }}>📱</span>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#0f091c' }}>Capture Visual Context</h4>
            <p style={{ margin: 0, fontSize: '12px', color: '#5b6275', lineHeight: '1.6', padding: '0 10px' }}>
              We will capture a visual snapshot of your current screen context automatically to help our team diagnose and solve your layout queries instantly.
            </p>

            <button 
              onClick={handleCaptureScreen}
              disabled={snapping}
              className="pulse-gold"
              style={{
                width: '85%',
                backgroundColor: '#10b981',
                color: '#ffffff',
                border: 'none',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: '800',
                fontSize: '12px',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)',
                textTransform: 'uppercase'
              }}
            >
              {snapping ? '📸 Snapping screen...' : '📸 SNAP WORKSPACE'}
            </button>
            
            <button 
              onClick={() => { setScreenshot(null); setStep(2); }}
              style={{ background: 'transparent', border: 'none', color: '#888', fontSize: '11px', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Skip Screenshot & Write Description
            </button>
          </div>
        )}

        {/* STEP 2: DESCRIBE & SEND */}
        {step === 2 && (
          <form onSubmit={handleSubmitTicket} style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
            {error && <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', padding: '10px 14px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>⚠️ {error}</div>}
            {success && <div style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)', color: '#22c55e', padding: '10px 14px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>✓ {success}</div>}

            {screenshot && (
              <div>
                <label style={studioLabelStyle}>Captured Screen Snapshot</label>
                <div style={{ position: 'relative', width: '100%', height: '80px', borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(168, 85, 247, 0.15)' }}>
                  <img src={screenshot} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Context snapshot" />
                  <button 
                    type="button" 
                    onClick={() => setScreenshot(null)}
                    style={{ position: 'absolute', top: '4px', right: '4px', border: 'none', borderRadius: '4px', backgroundColor: '#ef4444', color: '#fff', fontSize: '9px', fontWeight: '700', padding: '2px 6px', cursor: 'pointer' }}
                  >
                    REMOVE
                  </button>
                </div>
              </div>
            )}

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={studioLabelStyle}>Describe the issue</label>
              <textarea 
                required
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what occurred, any font errors, visual glitches, or ticket queries..."
                className="glass-input"
                style={{ width: '100%', height: '160px', resize: 'none', boxSizing: 'border-box', fontSize: '12px', lineHeight: '1.5' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: 'auto' }}>
              <button 
                type="button" 
                onClick={() => setStep(1)}
                style={{ flex: 0.5, border: '1px solid rgba(15, 9, 28, 0.1)', background: 'transparent', color: '#5b6275', padding: '12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
              >
                ← BACK
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="pulse-gold"
                style={{ flex: 1, border: 'none', backgroundColor: BRAND_AMBER_GOLD, color: '#ffffff', padding: '12px', borderRadius: '12px', fontSize: '11px', fontWeight: '800', cursor: 'pointer', boxShadow: BRAND_GOLD_GLOW, textTransform: 'uppercase' }}
              >
                {submitting ? 'Submitting Ticket...' : '🚀 SEND TICKET'}
              </button>
            </div>
          </form>
        )}

        {/* STEP 3: MY TICKETS LIST & CHATS */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', height: '100%' }}>
            {!activeTicket ? (
              <>
                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '800', color: '#0f091c', textTransform: 'uppercase' }}>My Active Support Tickets</h4>
                {myTickets.length === 0 ? (
                  <div style={{ padding: '40px 10px', textAlign: 'center', color: '#888', fontSize: '12px' }}>
                    No support tickets found for your account. Feel free to raise a new one!
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {myTickets.map(t => {
                      const isPending = t.status === 'pending_info';
                      return (
                        <div 
                          key={t.id}
                          onClick={() => setActiveTicket(t)}
                          style={{ padding: '12px 14px', borderRadius: '12px', border: '1px solid rgba(168, 85, 247, 0.1)', backgroundColor: '#fff', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = '#10b981'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.1)'}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '75%' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: '#0f091c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description}</span>
                            <span style={{ fontSize: '10px', color: '#88' }}>{new Date(t.createdAt).toLocaleDateString()}</span>
                          </div>
                          <span style={{ fontSize: '9px', fontWeight: '800', padding: '3px 8px', borderRadius: '6px', backgroundColor: isPending ? 'rgba(217,119,6,0.1)' : 'rgba(16,185,129,0.1)', color: isPending ? '#d97706' : '#10b981' }}>
                            {isPending ? 'PENDING REPLY' : 'OPEN'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              /* CHAT ROOM WITH SUPPORT STAFF */
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '10px' }}>
                <button 
                  type="button"
                  onClick={() => setActiveTicket(null)}
                  style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#10b981', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                >
                  ← BACK TO LIST
                </button>
                <div style={{ borderBottom: '1px solid rgba(15,9,28,0.06)', paddingBottom: '8px' }}>
                  <span style={{ display: 'block', fontSize: '9px', color: '#88', fontWeight: '700' }}>TICKET ID: {activeTicket.id}</span>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#0f091c' }}>{activeTicket.description}</span>
                </div>

                {/* Messages scroll box */}
                <div style={{ flex: 1, minHeight: '180px', maxHeight: '220px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', padding: '10px', backgroundColor: 'rgba(241, 240, 245, 0.5)', borderRadius: '12px', border: '1px solid rgba(15,9,28,0.05)' }}>
                  <div style={{ alignSelf: 'flex-start', backgroundColor: '#e4e4e7', color: '#1f2937', padding: '8px 12px', borderRadius: '12px', fontSize: '11px', maxWidth: '85%', lineHeight: '1.4' }}>
                    <strong style={{ display: 'block', fontSize: '9px', color: '#5b6275', marginBottom: '2px' }}>SYSTEM INITIAL ISSUE</strong>
                    {activeTicket.description}
                  </div>

                  {activeTicket.messages.map((m, idx) => {
                    const isSupport = m.sender === 'support';
                    return (
                      <div 
                        key={idx}
                        style={{
                          alignSelf: isSupport ? 'flex-end' : 'flex-start',
                          backgroundColor: isSupport ? '#10b981' : '#a855f7',
                          color: '#ffffff',
                          padding: '8px 12px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          maxWidth: '85%',
                          lineHeight: '1.4'
                        }}
                      >
                        <strong style={{ display: 'block', fontSize: '9px', color: 'rgba(255,255,255,0.7)', marginBottom: '2px' }}>
                          {isSupport ? '🛠️ SUPPORT STAFF' : 'ME'}
                        </strong>
                        {m.text}
                      </div>
                    );
                  })}
                </div>

                <form onSubmit={handleSendChatMessage} style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
                  <input 
                    type="text"
                    required
                    value={chatMessage}
                    onChange={e => setChatMessage(e.target.value)}
                    placeholder="Type your response..."
                    className="glass-input"
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', fontSize: '12px' }}
                  />
                  <button 
                    type="submit"
                    disabled={sendingMsg}
                    style={{ border: 'none', backgroundColor: '#10b981', color: '#fff', width: '40px', height: '40px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}
                  >
                    {sendingMsg ? '...' : '✈️'}
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SupportTicketWrapper({ children }) {
  const user = getAuthUser();
  if (!user) return children;
  return (
    <>
      <LouderAppSwitcher user={user} />
      {children}
      <SupportTicketWidget currentUser={user} />
    </>
  );
}

// ==========================================
// VIEWPORT COMPONENT: SUPPORT TICKETS CONSOLE HUB
// ==========================================
function SupportDashboardView() {
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [error, setError] = useState('');
  const currentUser = getAuthUser();
  const navigate = useNavigate();

  const fetchTickets = () => {
    fetch(`${BACKEND_URL}/api/support/tickets`, {
      headers: {
        'x-requester-email': currentUser?.email || ''
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Forbidden or database offline.');
        return res.json();
      })
      .then(data => {
        setTickets(data);
        if (data.length > 0) {
          // Keep active ticket references in sync
          if (activeTicket) {
            const current = data.find(t => t.id === activeTicket.id);
            setActiveTicket(current || data[0]);
          } else {
            setActiveTicket(data[0]);
          }
        } else {
          setActiveTicket(null);
        }
      })
      .catch(err => setError(err.message));
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !activeTicket) return;
    setSendingReply(true);

    fetch(`${BACKEND_URL}/api/support/tickets/${activeTicket.id}/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender: 'support',
        text: replyMessage
      })
    })
      .then(res => res.json())
      .then(() => {
        setReplyMessage('');
        fetchTickets();
      })
      .catch(err => alert(err.message))
      .finally(() => setSendingReply(false));
  };

  const handleSolveTicket = () => {
    if (!activeTicket) return;
    if (!window.confirm('⚠️ Resolve ticket? This permanently deletes the ticket history and screenshot from the VPS.')) return;
    setResolving(true);

    fetch(`${BACKEND_URL}/api/support/tickets/${activeTicket.id}`, {
      method: 'DELETE',
      headers: {
        'x-requester-email': currentUser?.email || ''
      }
    })
      .then(res => res.json())
      .then(() => {
        setActiveTicket(null);
        fetchTickets();
      })
      .catch(err => alert(err.message))
      .finally(() => setResolving(false));
  };

  return (
    <div style={{ padding: '48px 40px', backgroundColor: '#f8f7fa', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1f2937', position: 'relative', overflow: 'hidden' }}>
      {/* Dynamic ambient green glows */}
      <div className="float-slow" style={{ position: 'absolute', top: '-10%', right: '10%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(16, 185, 129, 0.04) 0%, transparent 70%)', filter: 'blur(60px)', zIndex: 1 }} />
      
      <div style={{ position: 'relative', zIndex: 10 }}>
        {/* HEADER BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(16, 185, 129, 0.15)', paddingBottom: '24px', marginBottom: '40px' }}>
          <div>
            <h1 style={{ margin: '0 0 6px 0', fontSize: '28px', fontWeight: '800', letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #0f091c 0%, #10b981 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Support Command Console</h1>
            <p style={{ margin: '0', fontSize: '13px', color: '#5b6275', fontWeight: '500' }}>Active Support Staff: <span style={{ color: '#10b981', fontWeight: '700' }}>{currentUser?.name}</span></p>
          </div>
          <button onClick={() => navigate('/dashboard')} style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', border: '1px solid rgba(15, 9, 28, 0.1)', color: '#5b6275', padding: '10px 20px', borderRadius: '14px', cursor: 'pointer', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease' }}>
            ← Return to Dashboard
          </button>
        </div>

        {error ? (
          <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', border: '1px solid rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
            <h2>⚠️ SUPPORT AUTH FAULT</h2>
            <p>{error}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '30px', alignItems: 'start' }}>
            
            {/* LEFT TICKET LIST PANEL */}
            <div className="glass-panel" style={{ padding: '24px', borderRadius: '24px', minHeight: '500px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: '#0f091c', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textTransform: 'uppercase' }}>
                <span>Active Tickets</span>
                <span style={{ fontSize: '11px', color: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.1)', padding: '2px 8px', borderRadius: '10px' }}>{tickets.length}</span>
              </h3>

              {tickets.length === 0 ? (
                <div style={{ padding: '80px 10px', textAlign: 'center', color: '#888', fontSize: '13px', fontWeight: '500' }}>
                  🎉 Zero outstanding support queries! Great job!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {tickets.map(t => {
                    const isActive = activeTicket?.id === t.id;
                    const isPending = t.status === 'pending_info';
                    return (
                      <div
                        key={t.id}
                        onClick={() => setActiveTicket(t)}
                        style={{
                          padding: '16px',
                          borderRadius: '16px',
                          cursor: 'pointer',
                          backgroundColor: isActive ? 'rgba(16, 185, 129, 0.05)' : '#ffffff',
                          border: isActive ? '1px solid #10b981' : '1px solid rgba(168, 85, 247, 0.08)',
                          boxShadow: '0 4px 12px rgba(15, 9, 28, 0.02)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = '#10b981'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.08)'; }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '10px', color: '#88', fontWeight: '700' }}>{t.id}</span>
                          <span style={{ fontSize: '9px', fontWeight: '800', padding: '3px 8px', borderRadius: '6px', backgroundColor: isPending ? 'rgba(217,119,6,0.1)' : 'rgba(16,185,129,0.1)', color: isPending ? '#d97706' : '#10b981' }}>
                            {isPending ? 'PENDING' : 'OPEN'}
                          </span>
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: '#0f091c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description}</span>
                        <span style={{ fontSize: '10px', color: '#5b6275', fontWeight: '600' }}>From: {t.email}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* RIGHT RESOLUTION CONTROL CENTRE */}
            {activeTicket ? (
              <div className="glass-panel" style={{ padding: '40px', borderRadius: '24px', display: 'flex', flexDirection: 'column', gap: '30px' }}>
                
                {/* TICKET DESCRIPTION & META HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', borderBottom: '1px solid rgba(15, 9, 28, 0.06)', paddingBottom: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '70%' }}>
                    <span style={{ fontSize: '11px', color: '#88', fontWeight: '700' }}>TICKET RESOLUTION VIEWPORT</span>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f091c' }}>{activeTicket.description}</h2>
                    <p style={{ margin: 0, fontSize: '12px', color: '#5b6275', fontWeight: '500' }}>
                      Opened by user: <strong style={{ color: '#0f091c' }}>{activeTicket.email}</strong> on {new Date(activeTicket.createdAt).toLocaleString()}
                    </p>
                    {activeTicket.pageUrl && (
                      <p style={{ margin: 0, fontSize: '11px', color: '#88' }}>
                        Origin URL: <a href={activeTicket.pageUrl} target="_blank" rel="noreferrer" style={{ color: '#10b981', textDecoration: 'underline' }}>{activeTicket.pageUrl}</a>
                      </p>
                    )}
                  </div>

                  <button 
                    onClick={handleSolveTicket}
                    disabled={resolving}
                    className="pulse-gold"
                    style={{
                      backgroundColor: '#10b981',
                      color: '#ffffff',
                      border: 'none',
                      padding: '14px 24px',
                      borderRadius: '14px',
                      fontWeight: '800',
                      fontSize: '12px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      textTransform: 'uppercase'
                    }}
                  >
                    ✓ Solve & Purge Disk File
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '30px', alignItems: 'start' }}>
                  
                  {/* SCREENSHOT VIEWPORT BOX */}
                  <div>
                    <h4 style={studioLabelStyle}>Snapped Context Screenshot</h4>
                    {activeTicket.screenshotPath ? (
                      <div 
                        onClick={() => setLightboxOpen(true)}
                        style={{ position: 'relative', width: '100%', height: '240px', borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(168, 85, 247, 0.15)', cursor: 'pointer', boxShadow: '0 8px 24px rgba(15, 9, 28, 0.05)', transition: 'all 0.25s' }}
                        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
                        onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                      >
                        <img src={`${BACKEND_URL}${activeTicket.screenshotPath}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Error snap" />
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px', background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)', color: '#fff', fontSize: '10px', textAlign: 'center', fontWeight: '700' }}>
                          🔍 CLICK TO ENLARGE LIGHTBOX
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: '60px 10px', textAlign: 'center', color: '#888', border: '2px dashed rgba(15,9,28,0.1)', borderRadius: '16px', fontSize: '12px', fontWeight: '600' }}>
                        No Screenshot Attached
                      </div>
                    )}
                  </div>

                  {/* MESSAGING LOG CHAT PLATFORM */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <h4 style={studioLabelStyle}>Communications History Log</h4>

                    {/* Chat Scroll container */}
                    <div style={{ flex: 1, minHeight: '220px', maxHeight: '280px', overflowY: 'auto', padding: '16px', borderRadius: '16px', border: '1px solid rgba(15,9,28,0.06)', backgroundColor: 'rgba(241,240,245,0.4)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ alignSelf: 'flex-start', backgroundColor: '#e4e4e7', color: '#1f2937', padding: '10px 14px', borderRadius: '16px', fontSize: '12px', maxWidth: '85%', lineHeight: '1.5' }}>
                        <strong style={{ display: 'block', fontSize: '9px', color: '#5b6275', marginBottom: '4px' }}>INITIAL USER ISSUE SUBMISSION</strong>
                        {activeTicket.description}
                      </div>

                      {activeTicket.messages.map((msg, index) => {
                        const isSupportMsg = msg.sender === 'support';
                        return (
                          <div 
                            key={index}
                            style={{
                              alignSelf: isSupportMsg ? 'flex-end' : 'flex-start',
                              backgroundColor: isSupportMsg ? '#10b981' : '#a855f7',
                              color: '#ffffff',
                              padding: '10px 14px',
                              borderRadius: '16px',
                              fontSize: '12px',
                              maxWidth: '85%',
                              lineHeight: '1.5'
                            }}
                          >
                            <strong style={{ display: 'block', fontSize: '9px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                              {isSupportMsg ? '🛠️ SUPPORT (ME)' : '👤 PROMOTER'}
                            </strong>
                            {msg.text}
                          </div>
                        );
                      })}
                    </div>

                    {/* Chat Response form */}
                    <form onSubmit={handleSendReply} style={{ display: 'flex', gap: '10px' }}>
                      <input 
                        type="text"
                        required
                        value={replyMessage}
                        onChange={e => setReplyMessage(e.target.value)}
                        placeholder="Request more info or provide resolution details..."
                        className="glass-input"
                        style={{ flex: 1, padding: '12px 16px', borderRadius: '14px', fontSize: '12px' }}
                      />
                      <button 
                        type="submit"
                        disabled={sendingReply}
                        className="pulse-gold"
                        style={{
                          backgroundColor: '#10b981',
                          color: '#ffffff',
                          border: 'none',
                          padding: '0 20px',
                          borderRadius: '14px',
                          fontWeight: '800',
                          fontSize: '12px',
                          cursor: 'pointer',
                          boxShadow: '0 4px 10px rgba(16, 185, 129, 0.15)'
                        }}
                      >
                        {sendingReply ? '...' : '✈️ SEND'}
                      </button>
                    </form>
                  </div>

                </div>

                {/* SCREENSHOT LIGHTBOX MODAL */}
                {lightboxOpen && activeTicket.screenshotPath && (
                  <div 
                    onClick={() => setLightboxOpen(false)}
                    style={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(15, 9, 28, 0.85)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, cursor: 'zoom-out' }}
                  >
                    <img 
                      src={`${BACKEND_URL}${activeTicket.screenshotPath}`} 
                      style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }} 
                      alt="Lightbox snap" 
                    />
                    <button 
                      onClick={() => setLightboxOpen(false)}
                      style={{ position: 'absolute', top: '24px', right: '24px', border: 'none', background: 'transparent', color: '#fff', fontSize: '24px', cursor: 'pointer' }}
                    >
                      ✕
                    </button>
                  </div>
                )}

              </div>
            ) : (
              <div className="glass-panel" style={{ padding: '80px 40px', borderRadius: '24px', textAlign: 'center', color: '#888' }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '20px' }}>📥</span>
                <h3>Select a ticket from the left index directory to review visual snaps and message histories.</h3>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginView />} />
        <Route path="/activate-account/:token" element={<ActivateAccountView />} />
        <Route path="/reset-password/:token" element={<ResetPasswordView />} />
        
        {/* Central Louder Tools Hub / Launchpad */}
        <Route path="/hub" element={<ProtectedRoute><SupportTicketWrapper><LouderHubView /></SupportTicketWrapper></ProtectedRoute>} />
        
        {/* Louder Mailer Studio Routes (100% Intact) */}
        <Route path="/dashboard" element={<ProtectedRoute><SupportTicketWrapper><DashboardView /></SupportTicketWrapper></ProtectedRoute>} />
        <Route path="/mailer" element={<ProtectedRoute><SupportTicketWrapper><DashboardView /></SupportTicketWrapper></ProtectedRoute>} />
        <Route path="/editor/:id" element={<ProtectedRoute><SupportTicketWrapper><EditorView /></SupportTicketWrapper></ProtectedRoute>} />
        <Route path="/element-studio" element={<ProtectedRoute><SupportTicketWrapper><ElementStudioView /></SupportTicketWrapper></ProtectedRoute>} />
        
        {/* Meta Access Tool Routes */}
        <Route path="/meta-access" element={<ProtectedRoute><SupportTicketWrapper><MetaAccessView /></SupportTicketWrapper></ProtectedRoute>} />
        
        {/* Public Artist Manager Onboarding Link (NO LOGIN REQUIRED) */}
        <Route path="/r/:slug" element={<PublicArtistOnboardingView />} />
        
        <Route path="/admin" element={<ProtectedRoute><AdminRoute><SupportTicketWrapper><AdminView /></SupportTicketWrapper></AdminRoute></ProtectedRoute>} />
        <Route path="/support-dashboard" element={<ProtectedRoute><SupportRoute><SupportDashboardView /></SupportRoute></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/hub" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
